// main.js
// Entry point for Parade Panic 2.5D

// [#5] Version internal module imports so a deploy actually busts them on
// iOS/PWA caches. Keep this ASSET_VERSION in sync with the ?v= on main.js and
// style.css in index.html whenever these modules change.
import { Engine } from './engine.js?v=314';
import { GameMap } from './map.js?v=338';
import { Balloon, Tower, Helipad, Blimp, Plane, Float, CelebrateParticle, FloatingText, TOWER_DEFAULTS, getTowerCfg, getBalloonCfg, BALLOON_DEFAULTS, LightningSpellEffect, WindSpellEffect, FloatArmorSpellEffect } from './entities.js?v=330';
import { IntroAnimator } from './intro.js?v=296';
import { waves } from './waves.js?v=1';

let PRECOMPUTED_COURSES = [];

// Draw a route-blueprint radar in the SAME isometric orientation as the game board
// (screenX ∝ gx-gy, screenY ∝ (gx+gy)/2), so the schematic visually matches the
// roads you see. Used by both the difficulty popover and the course-library cards.
function drawRouteBlueprint(ctx, bp, W, H) {
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = '#08111e';
  ctx.fillRect(0, 0, W, H);
  if (!bp || !bp.gridWidth) return;
  const GW = bp.gridWidth, GH = bp.gridHeight;
  const iso = (gx, gy) => ({ ix: gx - gy, iy: (gx + gy) * 0.5 });
  // Fit the diamond into the canvas.
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (let y = 0; y <= GH; y++) for (let x = 0; x <= GW; x++) {
    const { ix, iy } = iso(x, y);
    if (ix < minX) minX = ix; if (ix > maxX) maxX = ix;
    if (iy < minY) minY = iy; if (iy > maxY) maxY = iy;
  }
  const pad = 3;
  const scale = Math.min((W - 2 * pad) / (maxX - minX), (H - 2 * pad) / (maxY - minY));
  const offX = pad - minX * scale + ((W - 2 * pad) - (maxX - minX) * scale) / 2;
  const offY = pad - minY * scale + ((H - 2 * pad) - (maxY - minY) * scale) / 2;
  const C = (gx, gy) => { const { ix, iy } = iso(gx, gy); return { x: offX + ix * scale, y: offY + iy * scale }; };
  const diamond = (gx, gy, hwU, fill, stroke) => {
    const c = C(gx, gy), hw = hwU * scale, hh = hwU * scale * 0.5;
    ctx.beginPath();
    ctx.moveTo(c.x, c.y - hh); ctx.lineTo(c.x + hw, c.y);
    ctx.lineTo(c.x, c.y + hh); ctx.lineTo(c.x - hw, c.y); ctx.closePath();
    if (fill) { ctx.fillStyle = fill; ctx.fill(); }
    if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = 1; ctx.stroke(); }
  };
  // Classify tiles so the radar shows the STREET layout (what the player sees on
  // the board), not just an abstract path line. Grass stays as background; roads
  // are filled asphalt; the rail line is drawn separately. Drawing only the
  // balloon polyline (its old behavior) made the parade's legit back-and-forth
  // through the streets look like "turns that aren't on the route".
  const railPts = [];
  (bp.tiles || []).forEach(t => {
    const id = String(t.typeId);
    if (id.indexOf('terrain') !== -1) return;             // grass -> background
    if (id.indexOf('railroad') !== -1) railPts.push(t);   // track + crossings
    if (id.indexOf('railroad_track') === 0) return;       // pure track isn't a street
    // ROAD / street — bright asphalt so the street network is the dominant,
    // legible feature of the schematic (it's what the player is matching against
    // on the board). Full-tile diamonds so adjacent road tiles fuse into a
    // continuous route.
    diamond(t.x + 0.5, t.y + 0.5, 1, '#9fb1cc', null);
  });

  // Railroad: a distinct dashed brown line down the track column.
  if (railPts.length) {
    railPts.sort((a, b) => (a.x - b.x) || (a.y - b.y));
    ctx.strokeStyle = '#b08050'; ctx.lineWidth = Math.max(1.4, scale * 0.13);
    ctx.setLineDash([scale * 0.5, scale * 0.3]); ctx.lineCap = 'butt';
    ctx.beginPath();
    railPts.forEach((t, i) => { const c = C(t.x + 0.5, t.y + 0.5); if (i === 0) ctx.moveTo(c.x, c.y); else ctx.lineTo(c.x, c.y); });
    ctx.stroke(); ctx.setLineDash([]);
  }

  // Parade route over the streets, with start (green) and end (yellow) markers so
  // direction is clear. Drawn as a dark casing + bright magenta core so it stays
  // legible on the light asphalt tiles (a plain cyan line washed out against them).
  if (bp.pathCoords && bp.pathCoords.length) {
    const tracePath = () => {
      ctx.beginPath();
      bp.pathCoords.forEach((p, i) => { const c = C(p.x + 0.5, p.y + 0.5); if (i === 0) ctx.moveTo(c.x, c.y); else ctx.lineTo(c.x, c.y); });
      ctx.stroke();
    };
    ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    ctx.strokeStyle = 'rgba(0,0,0,0.6)'; ctx.lineWidth = Math.max(3.2, scale * 0.30); tracePath(); // casing
    ctx.strokeStyle = '#FFEB3D'; ctx.lineWidth = Math.max(1.6, scale * 0.16); tracePath();          // core
    const s0 = bp.pathCoords[0], s1 = bp.pathCoords[bp.pathCoords.length - 1];
    diamond(s0.x + 0.5, s0.y + 0.5, 0.6, '#22c55e', '#053b16');
    diamond(s1.x + 0.5, s1.y + 0.5, 0.6, '#facc15', '#5a4300');
  }

  // Buildings: faint outlines only. They used to be bright red and drawn on top,
  // which made the schematic read as a "red building circuit" that didn't visually
  // match the road-dominated board. Now they're a subtle backdrop.
  (bp.buildings || []).forEach(b => { const s = b.size || 1; diamond(b.x + s / 2, b.y + s / 2, s * 0.8, null, 'rgba(148,163,184,0.28)'); });
}

// Build a blueprint straight from the map that is CURRENTLY LOADED — the exact
// tiles, path, and buildings being drawn on the board — rather than regenerating
// from the seed. Regeneration diverges for random target-difficulty maps: those
// are built in non-deterministic mode (with the Expert/Master path bias), but
// getLayoutBlueprint(seed) rebuilds in deterministic mode where that bias is off,
// producing a different route. Reading the live state guarantees the schematic
// matches what the player sees.
function buildLiveBlueprint(map, engine) {
  const ts = map.tileSize, sx = map.startX, sy = map.startY;
  const gx = (v) => Math.floor((v - sx) / ts);
  const gy = (v) => Math.floor((v - sy) / ts);
  return {
    gridWidth: map.gridWidth,
    gridHeight: map.gridHeight,
    tiles: engine.tiles.map(t => ({ x: gx(t.x), y: gy(t.y), typeId: t.typeId })),
    buildings: engine.entities
      .filter(e => (e.kind || e.constructor.name) === 'BuildingFacade' && typeof e.type === 'number')
      .map(b => ({ x: gx(b.x), y: gy(b.y), size: b.footprintSize || 1 })),
    pathCoords: (map.path || []).map(p => ({ x: p.x, y: p.y }))
  };
}
// Module-scoped so the rescore handler can invalidate the course-render cache.
// (Previously a block-scoped `let` inside setup, so the rescore success path
// threw "Can't find variable: coursesRendered".)
let coursesRendered = false;

// [#13] Dev-only crash reporting. The old code POSTed to a hardcoded
// http://localhost:8087 endpoint that fails (and triggers mixed-content
// warnings over HTTPS) in production. Only enable it on localhost dev.
const IS_DEV = ['localhost', '127.0.0.1'].includes(location.hostname);
if (IS_DEV) {
  const reportError = (payload) => {
    fetch(`${window.API_ORIGIN || ''}/log_error`, { method: 'POST', body: JSON.stringify(payload) }).catch(() => {});
  };
  window.onerror = (msg, url, lineNo, columnNo, error) =>
    reportError({ msg, lineNo, columnNo, stack: error ? error.stack : undefined });
  window.addEventListener('unhandledrejection', (event) =>
    reportError({ msg: event.reason ? event.reason.message : undefined, stack: event.reason ? event.reason.stack : undefined }));
}

// Sync tower purchase/upgrade costs (tray buttons + TOWER_INFO) to the loaded
// tower config (admin overrides merged over TOWER_DEFAULTS). Names/flavors are
// left untouched. Function declaration so it's hoisted for the setup flow.
function applyTowerCostConfig() {
  for (const type of Object.keys(TOWER_DEFAULTS)) {
    const cfg = getTowerCfg(engine, type);
    const costs = [cfg.cost, ...((cfg.upgradeCosts) || [])];
    if (window.TOWER_INFO && window.TOWER_INFO[type]) {
      window.TOWER_INFO[type].costs = costs;
    }
    const btn = document.querySelector(`.tower-btn[data-type="${type}"]`);
    if (btn) {
      btn.dataset.cost = String(cfg.cost);
      const label = btn.querySelector('.tower-cost');
      if (label) label.innerHTML = `${PERMIT}${cfg.cost}`;
    }
  }
}

// [#7] Escape untrusted strings before injecting into innerHTML (defense in
// depth alongside server-side username validation).
function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// --- Silly Name Generator ---
function generateSillyName(seed) {
  const adjectives = [
    "Wobbly", "Bouncy", "Squeaky", "Snazzy", "Elastic", "Honking", "Glittery",
    "Giggling", "Puffy", "Zippy", "Twisting", "Dizzy", "Bubbly", "Floaty",
    "Jiggly", "Razzle-Dazzle", "Confetti-Crazed", "Helium-Drunk", "Sausage-Shaped",
    "Splendiferous", "Glistening", "Shimmering", "Starlit", "Neon", "Grand",
    "Jubilant", "Whimsical", "Spectacular", "Majestic", "Sunny", "Breezy",
    "Festive", "Joyful", "Magical", "Radiant", "Gilded", "Golden"
  ];
  const nouns = [
    "Plaza", "Boulevard", "Avenue", "Crescent", "Roundabout", "Esplanade",
    "Promenade", "Turnpike", "Passage", "Meadow", "Junction", "Parkway",
    "Gardens", "District", "Meadows", "Heights", "Valley", "Harbor",
    "Rendezvous", "Carnival", "Fairground", "Courtyard", "Canyon", "Vista"
  ];
  const suffixes = [
    "of Doom", "of Giggles", "of Helium", "of Clowns", "of Pop", "of Wonders",
    "of Floating Dreams", "of Glitter", "of Honking Horns", "of Wobble",
    "of Squeaks", "of Flying Ribbons", "of Drifting Whimsy", "of Shiny Mylar",
    "of Celebration", "of Grandeur", "of Whimsy", "of the Parade", "of Lights",
    "of Winds", "of Sparks", "of Harmony", "of the Carnival"
  ];

  const hash1 = (seed * 9301 + 49297) % 233280;
  const hash2 = (hash1 * 9301 + 49297) % 233280;
  const hash3 = (hash2 * 9301 + 49297) % 233280;
  const hash4 = (hash3 * 9301 + 49297) % 233280;

  const adj = adjectives[hash1 % adjectives.length];
  const noun = nouns[hash2 % nouns.length];
  let suffix = suffixes[hash3 % suffixes.length];

  // Prevent double-repetition of root words (e.g. Wobbly + of Wobble)
  const adjLower = adj.toLowerCase();
  const avoidList = [
    { root: "wobbl", bad: "wobble" },
    { root: "squeak", bad: "squeak" },
    { root: "giggl", bad: "giggle" },
    { root: "bounc", bad: "bounce" },
    { root: "helium", bad: "helium" },
    { root: "honk", bad: "honk" },
    { root: "glitter", bad: "glitter" },
    { root: "confetti", bad: "confetti" }
  ];

  for (const item of avoidList) {
    if (adjLower.includes(item.root) && suffix.toLowerCase().includes(item.bad)) {
      // Pick another suffix using a deterministic offset
      const offset = (hash3 + 7) % suffixes.length;
      suffix = suffixes[offset];
      break;
    }
  }

  // Determine naming pattern randomly based on seed
  const patternType = hash4 % 100;
  if (patternType < 45) {
    // Pattern 1: Adjective Noun (Concise & elegant, e.g. "Glistening Esplanade")
    return `${adj} ${noun}`;
  } else if (patternType < 90) {
    // Pattern 2: Adjective Noun of Suffix (E.g. "Neon Promenade of Wonders")
    return `${adj} ${noun} ${suffix}`;
  } else {
    // Pattern 3: Noun of Suffix (E.g. "Plaza of Floating Dreams")
    return `${noun} ${suffix}`;
  }
}

// Setup Audio and Engine
// --- Music: single system ---------------------------------------------------
// The old <audio>/Web-Audio "theme + intro" player has been removed. The radio
// widget (window.paradeMusic, js/music_widget.js) is now the ONLY music system.
// These inert stubs absorb the old lifecycle calls still scattered through the
// game loop (wave start/stop, victory, pause, restart, settings volume/mute) so
// those sites remain harmless no-ops without editing each one out individually.
const themeMusic = { currentTime: 0, volume: 1, muted: false, src: '', loop: false,
  play() { return Promise.resolve(); }, pause() {}, load() {}, addEventListener() {} };
const introMusic = { currentTime: 0, volume: 0.33, muted: false, src: '', loop: true,
  play() { return Promise.resolve(); }, pause() {}, load() {}, addEventListener() {} };
const playThemeMusic  = () => {};
const pauseThemeMusic = () => {};
const playIntroMusic  = () => {};
const pauseIntroMusic = () => {};

let isDemoMode = false;
let updateMusicVolume = () => {};

function showConfirm(title, message, onConfirm) {
  document.getElementById('confirm-title').innerText = title;
  document.getElementById('confirm-message').innerText = message;
  const modal = document.getElementById('confirm-modal');
  
  const yesBtn = document.getElementById('btn-confirm-yes');
  const noBtn = document.getElementById('btn-confirm-no');
  
  // Clone to remove old listeners
  const newYes = yesBtn.cloneNode(true);
  const newNo = noBtn.cloneNode(true);
  yesBtn.parentNode.replaceChild(newYes, yesBtn);
  noBtn.parentNode.replaceChild(newNo, noBtn);
  
  newYes.addEventListener('click', () => {
    modal.classList.add('hidden');
    onConfirm();
  });
  
  newNo.addEventListener('click', () => {
    modal.classList.add('hidden');
  });
  
  modal.classList.remove('hidden');
}

function showHtmlEmailPreview(username, temporaryPassword) {
  const modal = document.createElement('div');
  modal.id = 'html-email-preview-modal';
  modal.style.cssText = `
    position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
    background: rgba(10, 10, 20, 0.9); backdrop-filter: blur(12px);
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    z-index: 100000; padding: 20px; box-sizing: border-box;
  `;

  const logoUrl = `${window.location.origin}/sprites/ui/logo.png`;
  const siteUrl = `${window.location.origin}/`;

  const emailHtml = `
<div style="background-color: #0c0c14; margin: 0; padding: 40px 20px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
  <div style="max-width: 580px; margin: 0 auto; background-color: #12121e; border: 1px solid rgba(255, 255, 255, 0.12); border-radius: 16px; overflow: hidden; box-shadow: 0 12px 40px 0 rgba(0, 0, 0, 0.55);">
    
    <!-- Header Banner with Logo -->
    <div style="background-color: #1a1a2e; padding: 35px 20px 30px 20px; text-align: center; border-bottom: 3px solid #FFD93D;">
      <img src="${logoUrl}" style="max-height: 85px; max-width: 100%; display: block; margin: 0 auto;" alt="Parade Panic Logo">
      <p style="color: #FFD93D; margin: 12px 0 0 0; font-size: 11px; text-transform: uppercase; letter-spacing: 2px; font-weight: bold;">Secure Credential Update</p>
    </div>
    
    <!-- Body Content -->
    <div style="padding: 40px 30px; color: #F5F5F5; line-height: 1.6;">
      <h2 style="color: #FFFFFF; font-size: 21px; margin-top: 0; margin-bottom: 15px; font-weight: bold;">Hello ${escapeHtml(username)},</h2>
      <p style="color: #cbd5e1; font-size: 15px; margin-bottom: 25px;">Your Parade Panic game account password has been successfully reset by an administrator.</p>
      
      <!-- Password Display Box (Matching Glassmorphic / Golden Accents) -->
      <div style="background-color: #0a0a14; border: 1px solid rgba(255, 217, 61, 0.3); border-radius: 10px; padding: 25px 20px; text-align: center; margin-bottom: 30px; box-shadow: inset 0 2px 6px rgba(0,0,0,0.65);">
        <span style="display: block; color: #88889c; font-size: 11px; text-transform: uppercase; font-weight: bold; margin-bottom: 8px; letter-spacing: 1px;">Temporary Password</span>
        <span style="font-family: 'Courier New', Courier, monospace; font-size: 26px; font-weight: bold; color: #FFD93D; letter-spacing: 2px; text-shadow: 0 0 8px rgba(255, 217, 61, 0.25); user-select: all;">${escapeHtml(temporaryPassword)}</span>
      </div>
      
      <p style="color: #cbd5e1; font-size: 15px; margin-bottom: 30px;">For your security, you will be automatically prompted to select your own permanent password immediately upon your next login.</p>
      
      <!-- CTA Button (Using Parade Panic sunset primary gradient!) -->
      <div style="text-align: center; margin-bottom: 20px;">
        <a href="${siteUrl}" style="display: inline-block; background: linear-gradient(135deg, #FF6B6B 0%, #FFD93D 50%, #6BCBFF 100%); color: #0c0c14; padding: 14px 40px; border-radius: 8px; font-weight: 800; text-decoration: none; font-size: 15px; box-shadow: 0 6px 20px rgba(255, 107, 107, 0.25); text-transform: uppercase; letter-spacing: 1.5px; border: 1px solid rgba(255,255,255,0.15);">🎮 Log In & Play</a>
      </div>
      
      <!-- Link Helper -->
      <p style="color: #88889c; font-size: 12px; text-align: center; margin-top: 25px;">
        Or copy and paste this link in your browser:<br>
        <a href="${siteUrl}" style="color: #6BCBFF; text-decoration: none; font-weight: bold;">${siteUrl}</a>
      </p>
    </div>
    
    <!-- Footer Section -->
    <div style="background-color: #0a0a14; padding: 25px; text-align: center; border-top: 1px solid rgba(255,255,255,0.04); font-size: 12px; color: #5c5c70; line-height: 1.4;">
      <p style="margin: 0 0 5px 0;">This email was generated regarding your security on the Parade Panic gaming platform.</p>
      <p style="margin: 0;">&copy; 2026 Parade Panic Team. All rights reserved.</p>
    </div>
  </div>
</div>
  `;

  modal.innerHTML = `
    <div style="background: #111827; border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 12px; max-width: 650px; width: 100%; max-height: 85vh; display: flex; flex-direction: column; box-shadow: 0 20px 40px rgba(0,0,0,0.6); overflow: hidden;">
      <div style="padding: 16px 20px; border-bottom: 1px solid rgba(255,255,255,0.08); display: flex; justify-content: space-between; align-items: center; background: #1f2937;">
        <span style="font-weight: bold; font-size: 16px; color: #FFD93D; font-family: inherit;">🎨 Styled HTML Email Preview</span>
        <button id="btn-close-email-preview" style="background: none; border: none; color: #94a3b8; font-size: 24px; cursor: pointer; line-height: 1; padding: 0 4px;">&times;</button>
      </div>
      
      <!-- Preview Container -->
      <div style="overflow-y: auto; flex: 1; background: #0c0c14;">
        <div id="email-rendered-container">${emailHtml}</div>
      </div>
      
      <!-- Footer Actions -->
      <div style="padding: 16px 20px; border-top: 1px solid rgba(255,255,255,0.08); display: flex; justify-content: flex-end; gap: 12px; background: #1f2937;">
        <button id="btn-copy-rich-html" style="background: linear-gradient(135deg, #FF6B6B 0%, #FFD93D 50%, #6BCBFF 100%); color: #0c0c14; padding: 10px 20px; border: none; border-radius: 6px; font-weight: 800; font-size: 13px; cursor: pointer; box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3); transition: transform 0.1s; text-transform: uppercase; letter-spacing: 0.5px;">
          📋 Copy Rich-HTML Email
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  const copyBtn = modal.querySelector('#btn-copy-rich-html');
  const closeBtn = modal.querySelector('#btn-close-email-preview');

  closeBtn.addEventListener('click', () => modal.remove());

  copyBtn.addEventListener('click', () => {
    const container = modal.querySelector('#email-rendered-container');
    
    const range = document.createRange();
    range.selectNodeContents(container);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    
    const success = document.execCommand('copy');
    selection.removeAllRanges();

    if (success) {
      copyBtn.innerText = '✓ Copied Rich-HTML!';
      copyBtn.style.background = '#22c55e';
      copyBtn.style.color = '#ffffff';
      copyBtn.style.boxShadow = '0 4px 12px rgba(34, 197, 94, 0.3)';
      setTimeout(() => {
        copyBtn.innerText = '📋 Copy Rich-HTML Email';
        copyBtn.style.background = 'linear-gradient(135deg, #FF6B6B 0%, #FFD93D 50%, #6BCBFF 100%)';
        copyBtn.style.color = '#0c0c14';
        copyBtn.style.boxShadow = '0 4px 12px rgba(255, 107, 107, 0.3)';
      }, 2500);
    } else {
      alert('Failed to copy. Please select the text inside the preview manually.');
    }
  });
}

function showAlert(title, message, onOk) {
  const titleEl = document.getElementById('alert-title');
  const messageEl = document.getElementById('alert-message');
  const modal = document.getElementById('alert-modal');
  const okBtn = document.getElementById('btn-alert-ok');

  if (!titleEl || !messageEl || !modal || !okBtn) {
    const nativeAlert = window._nativeAlert || console.log;
    nativeAlert(`${title}: ${message}`);
    if (onOk) onOk();
    return;
  }

  titleEl.innerText = title;
  messageEl.innerHTML = message;
  
  const newOk = okBtn.cloneNode(true);
  okBtn.parentNode.replaceChild(newOk, okBtn);
  
  newOk.addEventListener('click', () => {
    modal.classList.add('hidden');
    if (onOk) onOk();
  });
  
  modal.classList.remove('hidden');
}

window._nativeAlert = window.alert;
window.alert = function(message) {
  showAlert('Notice', message);
};

// (old Web-Audio music engine, wrappers, and window.musicControls removed)

const playIntro = (e) => {
  // [#6] Do not run this global fallback for buttons that already start music
  // themselves (Start Wave, and the splash "Start Game"). Otherwise the splash
  // tap fires BOTH this fallback (on touchstart AND the synthesized click) and
  // the button's own handler, calling playIntroMusic() 2-3x in one gesture;
  // each call bumps introPlayId and the stale-id guard pauses introMusic,
  // interrupting the real play() -> Safari rejects it as AbortError and the
  // splash music never starts. Excluding these buttons guarantees exactly one
  // playIntroMusic() per gesture.
  if (e && e.target && e.target.closest && (
      e.target.closest('#btn-start') || 
      e.target.closest('#btn-splash-start') ||
      e.target.closest('#music-player-bar') ||
      e.target.closest('#settings-modal') ||
      e.target.closest('#btn-settings')
  )) {
    return;
  }

  if (window.engine && engine.gameState && engine.gameState.waveActive) {
    return; // Avoid interrupting and pausing the theme music that was just started!
  }

  // Music runs through Web Audio now; playIntroMusic() resumes the context within
  // this gesture. (Do NOT play the <audio> elements here — in Safari they DO play
  // the MP3 and would layer a second track over the Web Audio music.)
  playIntroMusic();
};

// Try to play immediately, but also bind to the first user interaction just in case.
const tryPlayIntro = () => {
  playIntroMusic();
  // Attach fallback listeners in case browser blocks initial autoplay
  window.addEventListener('click', playIntro, { once: true });
  window.addEventListener('touchstart', playIntro, { once: true });
};

// Wait for the DOM to be fully loaded before trying to play audio
window.addEventListener('DOMContentLoaded', tryPlayIntro);

// Do NOT suspend the AudioContext when the tab hides. Web Audio keeps playing in
// the background, so music continues through backgrounding and idle demo mode. If
// we suspended on hide, returning to WATCH the demo wouldn't resume it without a
// click (browsers block resume outside a gesture) — the source stays alive so the
// controls read "playing" but you'd hear nothing. Leaving it running avoids that
// entirely. On show, resume as a safety in case the browser auto-suspended it.
document.addEventListener('visibilitychange', () => {
  if (!document.hidden && window.engine) {
    if (engine.audioCtx && engine.audioCtx.state !== 'running') engine.audioCtx.resume().catch(() => {});
    // iPad/Safari: returning from the app switcher, split view, or Stage Manager
    // often leaves the canvas sized to a transient (e.g. half-width) viewport with
    // NO correcting 'resize' event — which left the game drawn in a strip with the
    // rest black. Re-sync the canvas to the real viewport now and again once it has
    // settled, then force a redraw in case the RAF loop was throttled while hidden.
    if (engine.resize) {
      engine.resize();
      setTimeout(() => { try { engine.resize(); if (engine.render) engine.render(); } catch (e) {} }, 250);
    }
  }
});

// visualViewport fires reliably on iOS layout changes (split view / Stage Manager /
// URL-bar show-hide) where a plain window 'resize' sometimes doesn't — keep the
// canvas backing store matched to the actual visible viewport.
if (window.visualViewport) {
  window.visualViewport.addEventListener('resize', () => {
    if (window.engine && engine.resize) engine.resize();
  });
}

// --- API Client ---
// window.API_ORIGIN (set in index.html) is 'https://parade-td.com' inside the packaged
// app (Capacitor) and '' on the web (same-origin) — see index.html for why.
const API_URL = `${window.API_ORIGIN || ''}/api`;
let authToken = localStorage.getItem('parade_token');
let isAdmin = localStorage.getItem('parade_is_admin') === 'true';

// Lightweight transient toast for auth/status feedback (sign-in, sign-out, expiry).
function showToast(msg, kind) {
  try {
    let host = document.getElementById('pp-toast-host');
    if (!host) {
      host = document.createElement('div');
      host.id = 'pp-toast-host';
      // `top` must clear the notch / Dynamic Island. This is position:fixed, so the
      // offset is measured from the raw viewport top — 16px put every toast squarely
      // underneath the island on an iPhone 15 Pro Max (59px inset). #ui-layer gets
      // this right via safe-area padding, but the toast host is not inside it.
      host.style.cssText = 'position:fixed;top:calc(16px + env(safe-area-inset-top));left:50%;transform:translateX(-50%);z-index:100000;display:flex;flex-direction:column;gap:8px;align-items:center;pointer-events:none;';
      document.body.appendChild(host);
    }
    const bg = kind === 'error' ? 'rgba(220,38,38,0.96)' : (kind === 'success' ? 'rgba(34,197,94,0.96)' : 'rgba(30,41,59,0.96)');
    const t = document.createElement('div');
    t.style.cssText = `font-weight:700;color:#fff;background:${bg};padding:10px 18px;border-radius:10px;box-shadow:0 6px 20px rgba(0,0,0,0.45);font-size:14px;opacity:0;transform:translateY(-8px);transition:opacity .2s ease,transform .2s ease;max-width:80vw;text-align:center;`;
    t.textContent = msg;
    host.appendChild(t);
    requestAnimationFrame(() => { t.style.opacity = '1'; t.style.transform = 'translateY(0)'; });
    setTimeout(() => { t.style.opacity = '0'; t.style.transform = 'translateY(-8px)'; setTimeout(() => t.remove(), 260); }, 2600);
  } catch (e) {}
}
window.showToast = showToast;

const apiFetch = async (endpoint, options = {}) => {
  const headers = { 'Content-Type': 'application/json' };
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
  
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: { ...headers, ...options.headers }
  });
  
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    // A 401/403 from the AUTH endpoints themselves (login/register/oauth) means the
    // CREDENTIALS were rejected — NOT that a session expired. Surfacing the real
    // server error ("Invalid credentials") there is essential: otherwise a wrong
    // password gets mislabeled "session expired", which is confusing and makes it
    // look like you can't log back in. Only treat 401/403 on OTHER (authenticated)
    // endpoints as an expired session.
    const isAuthEndpoint = /^\/(login|register|auth\/)/.test(endpoint);

    // A 403 carrying password_change_required is NOT an expired session: the account
    // has require_password_change set, which an admin "temporary password" reset does.
    // While that flag is set the server 403s EVERY authenticated endpoint except the
    // change-password one, so falling through to the handler below wiped the token and
    // bounced the player back to the login screen — logging in with a temp password
    // looked like an instant "session expired" loop.  Collect the new password instead.
    if (response.status === 403 && data && data.password_change_required) {
      if (!window.__pwdChangePromptOpen && typeof showRequirePasswordChangeModal === 'function') {
        window.__pwdChangePromptOpen = true;
        showRequirePasswordChangeModal(() => {
          window.__pwdChangePromptOpen = false;
          window.location.reload();
        });
      }
      throw new Error('Password change required before continuing.');
    }

    if ((response.status === 401 || response.status === 403) && !isAuthEndpoint) {
      authToken = null;
      isAdmin = false;
      localStorage.removeItem('parade_token');
      localStorage.removeItem('parade_username');
      localStorage.removeItem('parade_xp');
      localStorage.removeItem('parade_games');
      localStorage.removeItem('parade_is_admin');
      const authModal = document.getElementById('auth-modal');
      if (authModal) authModal.classList.add('hidden');
      if (typeof updateAuthUI === 'function') {
        try { updateAuthUI(); } catch (e) { console.error(e); }
      }
      showToast('Session expired — please log in again.', 'error');
      setTimeout(() => {
        window.location.reload();
      }, 1500);
      throw new Error('Your session has expired. Please log in again.');
    }
    throw new Error(data.error || 'API Error');
  }
  return data;
};

// --- Persistent cash wallet + per-level progress ---
const BASE_MONEY = 500;                 // baseline starting funds / wallet floor
let playerCash = parseInt(localStorage.getItem('parade_cash') || BASE_MONEY, 10) || BASE_MONEY;

// --- "Rocco's Racket" bailout loan (urban-graft flavored broke-recovery) --------
// A broke player can take a one-time $500 handout from a shady parade fixer. They
// owe $550 back (the extra $50 is "the vig"). Nothing is collected until the wallet
// clears $1,000; then Rocco garnishes 50% of whatever sits ABOVE $1,000 on each
// deposit until the debt is square — so repayment can never shove them back under
// $1,000 or re-trigger the broke spiral. Debt is tracked locally (see note in chat).
const BAILOUT_PRINCIPAL = 500;   // cash handed over
const BAILOUT_TOTAL     = 550;   // what you owe back (principal + $50 vig)
const BROKE_THRESHOLD   = 500;   // "broke" = wallet under this
const REPAY_FLOOR       = 1000;  // no collection until wallet clears this
const REPAY_RATE        = 0.5;   // garnish this fraction of the surplus above the floor
let loanOwed = parseInt(localStorage.getItem('parade_loan_owed') || '0', 10) || 0;
function persistLoan() { try { localStorage.setItem('parade_loan_owed', String(loanOwed)); } catch (e) {} }
function isBroke() { return (playerCash | 0) < BROKE_THRESHOLD; }
function canBailout() { return loanOwed <= 0 && isBroke(); }
let levelProgress = {};                 // level_key -> { best_score, stars, completed }
let currentLevelKey = null;             // set at level start; null = practice (no save/carry)
let routeRevealedThisRound = false;
const SCHEMATIC_PRICES = {
  'Easy': 125,
  'Medium': 250,
  'Hard': 500,
  'Expert': 750,
  'Master': 1000
};
try { levelProgress = JSON.parse(localStorage.getItem('parade_level_progress') || '{}') || {}; } catch (e) { levelProgress = {}; }
// Owned entitlements (route schematics, etc.) as item strings like
// 'schematic:<levelKey>'. Server-authoritative when logged in (populated from
// /progress .purchases and wallet/tx responses); localStorage mirror for guests.
let ownedItems = new Set();
try { (JSON.parse(localStorage.getItem('parade_owned') || '[]') || []).forEach(i => ownedItems.add(i)); } catch (e) {}
try { // migrate the legacy per-level schematic flags into the unified set
  const legacy = JSON.parse(localStorage.getItem('parade_purchased_schematics') || '{}') || {};
  Object.keys(legacy).forEach(k => { if (legacy[k]) ownedItems.add('schematic:' + k); });
} catch (e) {}
function persistOwned() { localStorage.setItem('parade_owned', JSON.stringify([...ownedItems])); }
function ownsItem(item) { return item != null && ownedItems.has(item); }

// A map's identity is (seed + difficulty): generation is deterministic given both,
// so this exactly reproduces the map on replay (verified). The route entitlement is
// keyed on this identity, which is the single source of truth for "do I own this
// route?" and lets My Maps replay the exact map you bought.
function currentMapDifficulty() {
  return (typeof engine !== 'undefined' && engine.gameState && engine.gameState.difficultyLabel)
    ? engine.gameState.difficultyLabel : 'Medium';
}
function routeItem() {
  const s = (typeof map !== 'undefined' && map && map.seed != null) ? map.seed : null;
  return s != null ? ('route:' + s + ':' + currentMapDifficulty()) : null;
}
// Parse a route entitlement id back into { seed, difficulty }. Handles the current
// 'route:<seed>:<difficulty>' form and the legacy seed-only 'schematic:seed:<seed>'.
function parseRouteItem(item) {
  let m = /^route:(\d+):(.+)$/.exec(item || '');
  if (m) return { seed: Number(m[1]), difficulty: m[2] };
  m = /^schematic:seed:(\d+)$/.exec(item || '');
  if (m) return { seed: Number(m[1]), difficulty: null };
  return null;
}

// Do we own the route for the CURRENT map? Matched by SEED ALONE — a map is the
// same map regardless of the difficulty label, and difficulty can differ between
// how it was bought (biased quick-play) and how it's later reloaded (e.g. a
// save/recall regenerates from the seed). Matching only on seed makes the reveal
// robust to that, so a bought route stays unlocked whenever you're on that seed.
function ownsCurrentRoute() {
  const s = (typeof map !== 'undefined' && map && map.seed != null) ? String(map.seed) : null;
  if (s == null) return false;
  for (const it of ownedItems) {
    const r = parseRouteItem(it);
    if (r && String(r.seed) === s) return true;
  }
  return false;
}

// Reward (GUESTS only): a 3-star win unlocks that map's route schematic (the "level
// map") locally. For logged-in players this entitlement is granted server-side in
// /api/session/complete, so this local path is only for the guest (offline) economy.
function unlockRouteFor3Star() {
  const key = routeItem();
  if (!key || ownsCurrentRoute()) return;
  ownedItems.add(key); persistOwned();
  if (typeof showToast === 'function') showToast('🗺️ Level map unlocked — 3-star win! Replay it anytime from My Maps.', 'success');
}

// Every level starts from the same fixed allowance of PERMITS (the in-match build
// resource), plus any extra starting Permits the player bought this run with
// banked Dollars. The bank ($) is NOT carried in 1:1; it's a separate currency
// spent through the shop / withdrawal at a steep rate. On a win, leftover Permits
// are cashed out into the Dollar bank as prize money.
function startMoney() { return BASE_MONEY + (runStartPermits | 0); }

// --- Meta-economy: spend banked Dollars ($) on in-match Permits (🎟) ---
// One tunable block. Dollars = persistent prize wallet; Permits = in-match build
// resource (towers/upgrades/bounties). Because the bank grows large from replays,
// conversions are deliberately steep and per-run benefits are capped by the tiers.
// In-match currency mark: the permit-with-rosette icon as an inline SVG, sized in
// `em` so it scales to whatever text it sits beside. Injected via innerHTML (never
// innerText). Dollars keep the '$' symbol.
const PERMIT = `<svg viewBox="0 0 24 24" width="1em" height="1em" style="vertical-align:-0.16em;display:inline-block;margin-right:1px" fill="none" aria-hidden="true"><rect x="4" y="3" width="12" height="17" rx="1.5" fill="#F3E9D2" stroke="#C9B486" stroke-width="1.2"/><line x1="6.5" y1="7.5" x2="13" y2="7.5" stroke="#B9A783" stroke-width="1.3" stroke-linecap="round"/><line x1="6.5" y1="10.5" x2="13.5" y2="10.5" stroke="#C9B486" stroke-width="1.1" stroke-linecap="round"/><g transform="translate(15.5,15)"><polygon points="-2.2,1 -3.6,7.6 0,4.9" fill="#C0392B"/><polygon points="2.2,1 3.6,7.6 0,4.9" fill="#E4572E"/><circle cx="0" cy="0" r="5.6" fill="#C77F14"/><circle cx="0" cy="0" r="5" fill="none" stroke="#FFE066" stroke-width="1.4" stroke-dasharray="1.4 1"/><circle cx="0" cy="0" r="3.4" fill="#E4572E" stroke="#B23A1C" stroke-width="0.5"/><path transform="scale(0.32)" d="M0,-8 L1.82,-2.51 L7.61,-2.47 L2.94,0.96 L4.70,6.47 L0,3.10 L-4.70,6.47 L-2.94,0.96 L-7.61,-2.47 L-1.82,-2.51 Z" fill="#FFE066"/></g></svg>`;
const ECONOMY = {
  DOLLARS_PER_PERMIT: 10,        // $10 → 1 Permit (mid-match withdrawal)
  WITHDRAW_PERMITS: 250,         // Permits granted per mid-match withdrawal
  START_PERMIT_TIERS: [          // pre-level: extra STARTING Permits above base 500.
    { permits: 250,  dollars: 2000 },   // bulk discount vs the flat $10/Permit
    { permits: 500,  dollars: 3750 },   // withdrawal — rewards committing before the
    { permits: 1000, dollars: 7000 },   // wave ($8 → $7.5 → $7 per Permit).
  ],
  START_LIFE_TIERS: [            // pre-level: extra STARTING lives above base 10
    { lives: 2,  dollars: 2000 },
    { lives: 5,  dollars: 6000 },
    { lives: 10, dollars: 15000 },
  ],
  CONTINUE_BASE_DOLLARS: 5000,   // continue-after-loss; ×(continues+1) each time
  BASE_LIVES: 10,
};

// Per-run purchases — reset to 0 at each fresh level start (see resetRunBoosts()).
let runStartPermits = 0;
let runStartLives = 0;
let runContinues = 0;
function resetRunBoosts() { runStartPermits = 0; runStartLives = 0; runContinues = 0; }

function canAffordDollars(amount) { return (playerCash | 0) >= Math.max(0, Math.floor(amount || 0)); }

let lastTxError = null;

// Unique per-request idempotency key. The server records the outcome of each
// applied transaction under (user, key); a retry of the SAME key replays the
// stored result instead of crediting/charging twice.
function genTxKey() {
  try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (e) {}
  return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

// Failed 'earn' transactions (offline / server down) queue here so winnings are
// NEVER lost — they flush on the next sync. Each queued entry keeps the SAME
// idempotency key it was first sent with, so if the original POST actually
// reached the server (only the response was lost), the flush replays rather than
// double-credits. Stored as [{ key, amount }]; legacy bare-integer values migrate.
function loadPendingEarns() {
  const raw = localStorage.getItem('parade_pending_earn');
  if (!raw) return [];
  try {
    const v = JSON.parse(raw);
    if (Array.isArray(v)) return v.filter(e => e && (e.amount | 0) > 0).map(e => ({ key: e.key || genTxKey(), amount: e.amount | 0 }));
    const n = parseInt(v, 10) || 0; // legacy JSON number
    return n > 0 ? [{ key: genTxKey(), amount: n }] : [];
  } catch (e) {
    const n = parseInt(raw, 10) || 0; // legacy bare integer string
    return n > 0 ? [{ key: genTxKey(), amount: n }] : [];
  }
}
function savePendingEarns(list) {
  localStorage.setItem('parade_pending_earn', JSON.stringify((list || []).filter(e => e && (e.amount | 0) > 0)));
}
function pendingEarnTotal() { return loadPendingEarns().reduce((s, e) => s + (e.amount | 0), 0); }

// Retry every queued earn under its original key. Applied-or-replayed entries are
// dropped; entries whose POST still fails stay queued for the next sync.
async function flushPendingEarns() {
  const list = loadPendingEarns();
  if (!list.length) return;
  const remaining = [];
  for (const e of list) {
    try {
      const res = await apiFetch('/wallet/tx', { method: 'POST', body: JSON.stringify({ kind: 'earn', amount: e.amount, idempotencyKey: e.key }) });
      if (!res || typeof res.cash !== 'number') throw new Error('bad response');
    } catch (err) {
      // Server-authoritative economy now refuses client 'earn' — drop those stale
      // pre-enforcement entries instead of retrying forever; re-queue only transient
      // failures (network, etc.).
      if (!/awarded by the server/i.test(String(err && err.message))) remaining.push(e);
    }
  }
  savePendingEarns(remaining);
}

// ===================== Unified wallet transaction pipeline =====================
// Every money mutation goes through here. When logged in it is SERVER-AUTHORITATIVE:
// POST /api/wallet/tx runs one atomic DB transaction (row-locked; purchases are
// idempotent and grant the entitlement in the same commit) and returns the true
// balance, which we simply reflect — no optimistic client math, no rollback races.
// Guests fall back to the localStorage mirror.
//   kind 'earn'     -> add amount (queued for retry if the POST fails)
//   kind 'spend'    -> subtract amount (rejected if insufficient)
//   kind 'purchase' -> subtract amount AND grant `item`, atomically & idempotently
// Returns { ok, cash, owned, charged, error }.
async function walletTx(kind, amount, item = null) {
  lastTxError = null;
  amount = Math.max(0, Math.floor(amount || 0));

  if (!authToken) { // guest — local mirror only
    if (kind === 'purchase' && item && ownsItem(item)) return { ok: true, cash: playerCash, owned: true, charged: false };
    if ((kind === 'spend' || kind === 'purchase') && playerCash < amount) {
      lastTxError = 'Insufficient funds in your bank account.';
      return { ok: false, error: lastTxError };
    }
    playerCash = kind === 'earn' ? playerCash + amount : playerCash - amount;
    let owned = false, charged = false;
    if (kind === 'purchase' && item) { ownedItems.add(item); persistOwned(); owned = true; charged = true; }
    persistProgressLocal(); updateWalletUI();
    return { ok: true, cash: playerCash, owned, charged };
  }

  const key = genTxKey();
  try {
    const res = await apiFetch('/wallet/tx', { method: 'POST', body: JSON.stringify({ kind, amount, item, idempotencyKey: key }) });
    if (!res || typeof res.cash !== 'number') throw new Error('Unexpected server response');
    playerCash = res.cash;
    // Loan is server-authoritative: sync the debt and surface any garnish the
    // server skimmed out of this earn.
    if (typeof res.loanOwed === 'number') { loanOwed = res.loanOwed; persistLoan(); }
    if (res.repaid > 0 && typeof showToast === 'function') {
      showToast(loanOwed > 0
        ? `💵 Rocco took his cut: −$${res.repaid}. $${loanOwed} left on the tab.`
        : `🤝 Debt squared with Rocco. He tips his hat.`, loanOwed > 0 ? 'info' : 'success');
    }
    if (item && res.owned) { ownedItems.add(item); persistOwned(); }
    persistProgressLocal(); updateWalletUI();
    return { ok: true, cash: res.cash, owned: !!res.owned, charged: !!res.charged };
  } catch (err) {
    lastTxError = err.message || 'Transaction failed. Please try again.';
    if (kind === 'earn') { // never drop winnings: queue (under this key) + show optimistically
      const q = loadPendingEarns(); q.push({ key, amount }); savePendingEarns(q);
      playerCash += amount; persistProgressLocal(); updateWalletUI();
      return { ok: true, cash: playerCash, queued: true };
    }
    return { ok: false, error: lastTxError };
  }
}

// Semantic wrappers over the one pipeline (kept so call sites read clearly).
async function bankEarnings(amount) {
  if (Math.floor(amount) > 0) await walletTx('earn', amount); else updateWalletUI();
  // Logged-in players are garnished server-side inside the earn transaction (and
  // walletTx syncs loanOwed from the response); guests settle the tab locally.
  if (!authToken) await maybeRepayLoan();
}
async function spendDollars(amount) { const r = await walletTx('spend', amount); return r.ok; }

// Rocco the Racket Raccoon — the urban-graft bailout prompt. Self-contained overlay.
function showRoccoBailout() {
  if (document.getElementById('rocco-overlay')) return;
  if (loanOwed > 0) { if (typeof showToast === 'function') showToast('You already owe Rocco. Square the tab first.', 'error'); return; }
  const ov = document.createElement('div');
  ov.id = 'rocco-overlay';
  ov.style.cssText = 'position:fixed;inset:0;z-index:3000;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.72);-webkit-backdrop-filter:blur(3px);backdrop-filter:blur(3px);';
  ov.innerHTML = `
    <div style="max-width:360px;width:90%;background:linear-gradient(160deg,#1a1614,#0d0b0a);border:1px solid rgba(255,215,0,0.25);border-radius:16px;padding:22px;color:#eee;font-family:inherit;box-shadow:0 20px 60px rgba(0,0,0,0.7);text-align:center;">
      <div style="height:48px; display:flex; align-items:center; justify-content:center; margin-bottom:12px;">${GameIcons.get('rocco', 48)}</div>
      <h2 style="margin:0 0 4px;font-size:1.3em;color:#FFD93D;">Psst… need a little scratch?</h2>
      <p style="margin:0 0 14px;font-size:0.9em;color:#bbb;line-height:1.45;">Name's Rocco. Word on the boulevard is you're tapped out. I can float ya <b style="color:#4ade80;">$500</b> right now — no forms, no questions.</p>
      <div style="background:rgba(255,255,255,0.05);border:1px dashed rgba(255,215,0,0.3);border-radius:10px;padding:10px 12px;margin-bottom:16px;font-size:0.82em;color:#cbd5e1;line-height:1.6;text-align:left;">
        <div style="display:flex; align-items:center; gap:6px; margin-bottom:4px;">${GameIcons.get('unlocked', 14)} <span>You get <b>$500</b> now.</span></div>
        <div style="display:flex; align-items:center; gap:6px; margin-bottom:4px;">${GameIcons.get('dollars', 14)} <span>You owe <b>$550</b> back — the extra fifty's the vig.</span></div>
        <div style="display:flex; align-items:start; gap:6px;">${GameIcons.get('bank', 14, 'margin-top:2px;')} <span>I don't collect a dime till your bank clears <b>$1,000</b>. Then I skim a little off the top.</span></div>
      </div>
      <button id="rocco-take" style="width:100%;padding:13px;border:none;border-radius:10px;font-weight:800;font-size:1em;cursor:pointer;background:linear-gradient(135deg,#FFD700,#FFA500);color:#111;text-transform:uppercase;letter-spacing:0.5px;display:flex;align-items:center;justify-content:center;gap:6px;">${GameIcons.get('unlocked', 16, 'filter:brightness(0.1);')} take the $500</button>
      <div id="rocco-decline" style="margin-top:12px;font-size:0.85em;color:#888;cursor:pointer;text-decoration:underline;">Nah, I'm good</div>
    </div>`;
  document.body.appendChild(ov);
  const close = () => ov.remove();
  ov.querySelector('#rocco-decline').addEventListener('click', close);
  ov.addEventListener('click', (e) => { if (e.target === ov) close(); });
  ov.querySelector('#rocco-take').addEventListener('click', async () => {
    const btn = ov.querySelector('#rocco-take');
    btn.disabled = true; btn.textContent = "Countin' it out…";
    const r = await takeBailout();
    close();
    if (typeof showToast === 'function') {
      if (r.ok) showToast(`🕶️ Rocco slid you $500. You owe $${BAILOUT_TOTAL}. Don't be a stranger.`, 'info');
      else showToast(r.reason === 'outstanding' ? 'You already owe Rocco.' : "Rocco only bites when you're truly broke.", 'error');
    }
  });
}
window.showRoccoBailout = showRoccoBailout;

// Dev/test helper: drain the wallet to $0 (through the real server-authoritative
// spend, so it persists), then you can lose a level to trigger Rocco — or call
// window.showRoccoBailout() directly. window.clearLoan() wipes any owed tab.
window.goBroke = async () => {
  const amt = playerCash | 0;
  if (amt > 0) await walletTx('spend', amt);
  updateWalletUI();
  console.log('[dev] wallet drained. playerCash =', playerCash, '| loanOwed =', loanOwed);
  return playerCash;
};
window.clearLoan = () => { loanOwed = 0; persistLoan(); updateWalletUI(); console.log('[dev] loan cleared'); };

// Take the $500 handout. Runs the +$500 through the normal earn pipeline (so the
// cash movement stays server-authoritative and shows in the ledger), then records
// the $550 debt. Returns {ok, reason}.
async function takeBailout() {
  if (loanOwed > 0) return { ok: false, reason: 'outstanding' };
  if (!isBroke()) return { ok: false, reason: 'not-broke' };
  if (!authToken) {
    // Guest: local-only loan (no server account to track the debt).
    await walletTx('earn', BAILOUT_PRINCIPAL);
    loanOwed = BAILOUT_TOTAL; persistLoan(); updateWalletUI();
    return { ok: true };
  }
  // Logged in: the SERVER validates "broke" and records the debt authoritatively —
  // the client can no longer mint the $500 or forget what it owes.
  try {
    const res = await apiFetch('/wallet/loan', { method: 'POST', body: JSON.stringify({}) });
    if (res && typeof res.cash === 'number') {
      playerCash = res.cash;
      loanOwed = typeof res.loanOwed === 'number' ? res.loanOwed : BAILOUT_TOTAL;
      persistProgressLocal(); persistLoan(); updateWalletUI();
      return { ok: true };
    }
    return { ok: false, reason: 'error' };
  } catch (err) {
    return { ok: false, reason: 'error' };
  }
}

// After a deposit, if the wallet is above the floor, garnish half the surplus toward
// the debt (never dipping the wallet below the floor).
async function maybeRepayLoan() {
  if (loanOwed <= 0) return;
  const surplus = (playerCash | 0) - REPAY_FLOOR;
  if (surplus <= 0) return;
  const cut = Math.min(loanOwed, Math.floor(surplus * REPAY_RATE));
  if (cut <= 0) return;
  const r = await walletTx('spend', cut);       // affordable by construction
  if (r && r.ok) {
    loanOwed -= cut;
    persistLoan();
    updateWalletUI();
    if (typeof showToast === 'function') {
      showToast(loanOwed > 0
        ? `💵 Rocco took his cut: −$${cut}. $${loanOwed} left on the tab.`
        : `🤝 Debt squared with Rocco. He tips his hat.`, loanOwed > 0 ? 'info' : 'success');
    }
  }
}

// ── H1/M5 server-authoritative rewards (behind a flag until verified in-game) ──
// The server issues a one-time session token at level start and, at level end,
// computes stars/score/payout itself (bounding the client-reported outcome), so
// the client can't mint currency or forge scores. Enable per-browser for testing
// with window.enableServerRewards(true); default OFF until we flip enforcement on.
// ENFORCED: server-authoritative rewards are always on for logged-in players. The
// server also rejects any client 'earn', so this flag only controls whether the
// client bothers to run the session flow — turning it off just forfeits rewards,
// it can't mint. A debug override (localStorage '0') remains for emergencies.
let SERVER_SESSION_REWARDS = true;
try { if (localStorage.getItem('parade_server_rewards') === '0') SERVER_SESSION_REWARDS = false; } catch (e) {}
window.enableServerRewards = (on = true) => {
  SERVER_SESSION_REWARDS = !!on;
  try { localStorage.setItem('parade_server_rewards', on ? '1' : '0'); } catch (e) {}
  console.log('[dev] server-authoritative rewards:', SERVER_SESSION_REWARDS);
  return SERVER_SESSION_REWARDS;
};
let levelSession = null; // { sessionId, token } for the current run, or null

// Called at the first wave: ask the server for a session token bound to this run.
async function startLevelSession() {
  levelSession = null;
  if (!SERVER_SESSION_REWARDS || !authToken) return;
  const level_key = currentLevelKey || (map && map.seed != null ? 'seed:' + map.seed : null);
  const seed = (map && map.seed != null) ? map.seed : null;
  const difficulty = (engine.gameState && engine.gameState.difficultyLabel) || 'Medium';
  // This is now the ONLY path to rewards for logged-in players, so retry once on a
  // transient failure before giving up (a missing session forfeits the reward).
  for (let attempt = 0; attempt < 2 && !levelSession; attempt++) {
    try {
      const res = await apiFetch('/session/start', { method: 'POST', body: JSON.stringify({ level_key, seed, difficulty }) });
      if (res && res.sessionId && res.token) { levelSession = { sessionId: res.sessionId, token: res.token }; return; }
    } catch (e) { if (attempt === 0) await new Promise(r => setTimeout(r, 400)); }
  }
}

// Called at level end. Returns true if the server session recorded the result
// (payout/progress/xp/leaderboard) so the caller skips the legacy trusted posts.
async function completeLevelSession({ won, lives, waves }) {
  if (!SERVER_SESSION_REWARDS || !authToken || !levelSession) return false;
  const sess = levelSession; levelSession = null; // consume (one-time)
  try {
    const res = await apiFetch('/session/complete', { method: 'POST', body: JSON.stringify({
      sessionId: sess.sessionId, token: sess.token,
      won: !!won,
      isCampaign: !!(engine.gameState && engine.gameState.campaignLevel),
      lives: Math.floor(lives) || 0,
      startPermits: Math.floor((engine.gameState && engine.gameState.startPermitsSnapshot) || 0),
      endPermits: Math.floor((engine.gameState && engine.gameState.money) || 0),
      waves: Math.floor(waves) || 0
    }) });
    if (res && res.success) {
      if (typeof res.cash === 'number') playerCash = res.cash;
      if (typeof res.loanOwed === 'number') { loanOwed = res.loanOwed; persistLoan(); }
      // 3-star wins unlock the map's route schematic server-side; reflect it locally.
      if (res.unlockedRoute && !ownedItems.has(res.unlockedRoute)) {
        ownedItems.add(res.unlockedRoute); persistOwned();
        if (typeof showToast === 'function') showToast('🗺️ Level map unlocked — 3-star win! Replay it anytime from My Maps.', 'success');
      }
      persistProgressLocal(); updateWalletUI();
      return true;
    }
  } catch (e) { return false; }
  return false;
}

// ── "Play On" (endless) bonus wiring ─────────────────────────────────────────
// The base level win is already banked/redeemed at victory. Endless waves earn an
// ADDITIONAL, per-run-capped bonus: a fresh server session is opened when the player
// chooses Play On, then claimed once per completed endless wave (server credits only
// the delta, mirroring reward_rubric.computeEndlessBonus). Guests credit locally.
let endlessSession = null;
let endlessAccrued = { dollars: 0, xp: 0, waves: 0 };
let _endlessPaidLocal = 0;

async function startEndlessSession() {
  endlessSession = null;
  endlessAccrued = { dollars: 0, xp: 0, waves: 0 };
  _endlessPaidLocal = 0;
  if (!SERVER_SESSION_REWARDS || !authToken) return; // guests credit locally on claim
  const level_key = currentLevelKey || (map && map.seed != null ? 'seed:' + map.seed : null);
  const seed = (map && map.seed != null) ? map.seed : null;
  const difficulty = (engine.gameState && engine.gameState.difficultyLabel) || 'Medium';
  try {
    const res = await apiFetch('/session/endless/start', { method: 'POST', body: JSON.stringify({ level_key, seed, difficulty }) });
    if (res && res.sessionId && res.token) endlessSession = { sessionId: res.sessionId, token: res.token };
  } catch (e) {}
}

// Guest-local mirror of reward_rubric.computeEndlessBonus (offline economy only).
function _endlessBonusLocal(paid, total, mult) {
  const perD = 60, perX = 40, capD = 8000, capX = 6000;
  const at = (w) => ({ d: Math.min(capD, Math.floor(w * perD * mult)), x: Math.min(capX, Math.floor(w * perX * mult)) });
  const a = at(paid), b = at(total);
  return { dollars: Math.max(0, b.d - a.d), xp: Math.max(0, b.x - a.x) };
}

// Claim the bonus for surviving `totalWaves` endless waves so far (cumulative).
async function claimEndlessBonus(totalWaves) {
  totalWaves = Math.floor(totalWaves || 0);
  if (totalWaves <= 0) return;
  if (SERVER_SESSION_REWARDS && authToken && endlessSession) {
    try {
      const res = await apiFetch('/session/endless/claim', { method: 'POST', body: JSON.stringify({
        sessionId: endlessSession.sessionId, token: endlessSession.token, waves: totalWaves
      }) });
      if (res && res.success) {
        const d = (res.credited && res.credited.dollars) || 0;
        const x = (res.credited && res.credited.xp) || 0;
        endlessAccrued.dollars += d; endlessAccrued.xp += x; endlessAccrued.waves = totalWaves;
        if (typeof res.cash === 'number') playerCash = res.cash;
        if (typeof res.xp === 'number') localStorage.setItem('parade_xp', String(res.xp));
        updateWalletUI();
        if (d > 0 || x > 0) showEndlessCredit(d, x);
      }
    } catch (e) {}
  } else if (!authToken) {
    const mult = (engine.gameState && engine.gameState.difficultyMultiplier) || 1.0;
    const b = _endlessBonusLocal(_endlessPaidLocal, totalWaves, mult);
    _endlessPaidLocal = totalWaves;
    if (b.dollars > 0) playerCash = Math.min(1e8, (playerCash | 0) + b.dollars);
    if (b.xp > 0) { const cur = parseInt(localStorage.getItem('parade_xp') || '0', 10) || 0; localStorage.setItem('parade_xp', String(cur + b.xp)); }
    endlessAccrued.dollars += b.dollars; endlessAccrued.xp += b.xp; endlessAccrued.waves = totalWaves;
    persistProgressLocal(); updateWalletUI();
    if (b.dollars > 0 || b.xp > 0) showEndlessCredit(b.dollars, b.xp);
  }
}

function showEndlessCredit(d, x) {
  if (typeof showToast === 'function' && (d > 0 || x > 0)) {
    showToast(`Endless bonus: +$${(d || 0).toLocaleString()} · +${(x || 0).toLocaleString()} XP`, 'success');
  }
}

// Update local level-progress display only (no server post) — used when the server
// session already recorded the result, so the UI reflects it immediately.
function applyLocalLevelResult(levelKey, finalScore, stars) {
  if (!levelKey) { persistProgressLocal(); updateWalletUI(); return; }
  const prev = levelProgress[levelKey] || { best_score: -1, stars: 0, completed: false };
  levelProgress[levelKey] = {
    best_score: Math.max(finalScore, prev.best_score || 0, 0),
    stars: Math.max(stars, prev.stars || 0),
    completed: true
  };
  persistProgressLocal(); updateWalletUI();
  if (typeof levelKey === 'string' && levelKey.indexOf('seed:') === 0) coursesRendered = false;
}

function persistProgressLocal() {
  localStorage.setItem('parade_cash', String(playerCash));
  localStorage.setItem('parade_level_progress', JSON.stringify(levelProgress));
}

function updateWalletUI() {
  const fmt = '$' + (playerCash | 0).toLocaleString(); // bank is Dollars
  ['ui-cash', 'profile-cash', 'vic-stat-bank', 'go-stat-bank'].forEach(id => {
    const el = document.getElementById(id);
    if (el) el.innerText = fmt;
  });
}

const syncPlayerProgress = async () => {
  if (!authToken) { updateWalletUI(); return; }
  try {
    // First flush any queued winnings (each under its own idempotency key) so
    // they're baked into the authoritative balance before we read it back.
    await flushPendingEarns();
    const data = await apiFetch('/progress');
    if (data) {
      // Include any still-unflushed winnings so the display never regresses.
      if (typeof data.cash === 'number') playerCash = data.cash + pendingEarnTotal();
      if (typeof data.loan_owed === 'number') { loanOwed = data.loan_owed; persistLoan(); }
      if (data.levels) levelProgress = data.levels;
      if (Array.isArray(data.purchases)) { ownedItems = new Set(data.purchases); persistOwned(); }
      persistProgressLocal();
      updateWalletUI();
    }
  } catch (err) {
    console.error('Failed to sync player progress:', err);
  }
};

// ── Guest → account progress migration ──────────────────────────────────────
//
// Without this, playing as a guest and then registering silently DESTROYS the guest's
// progress: syncPlayerProgress() above does `levelProgress = data.levels` and then
// persists it, so the server's (empty) progress overwrites the local mirror the moment a
// token exists. Punishing someone for creating an account is the worst possible moment
// to lose their work, so this runs FIRST at every login site, before any sync.
//
// Deliberately client-side only, replaying local progress through the EXISTING
// /api/progress/level and /api/campaign/progress endpoints. No new endpoint and no new
// trust boundary: those endpoints already accept client-supplied score/stars, clamp them
// (score <= MAX_CASH, stars 0-3) and merge with GREATEST/Math.max, so a batch replay is
// exactly as trusted as the per-level call the game already makes after every win. It
// also means no server deploy is needed for this feature.
//
// Cash is intentionally NOT migrated. The server keeps currency separate
// (/api/wallet/tx) precisely because it is the cheat-sensitive quantity, and unlike
// stars it is re-earnable in normal play. Levels, stars and the unlocks they gate are
// the durable progress worth carrying over.
const GUEST_MIGRATION_MAX = 50; // scoreLimiter is 60 calls/min/user — stay under it

async function migrateGuestProgress() {
  if (!authToken) return { migrated: 0, skipped: 'no-token' };
  if (localStorage.getItem('parade_guest') !== '1') return { migrated: 0, skipped: 'not-a-guest' };

  // Snapshot BEFORE anything can sync. This is the whole point of the ordering.
  const snapshot = {};
  try {
    for (const [k, v] of Object.entries(levelProgress || {})) {
      if (v && (v.completed || v.stars || v.best_score || v.score)) snapshot[k] = { ...v };
    }
  } catch (e) { /* fall through to the empty case */ }

  const keys = Object.keys(snapshot);
  if (!keys.length) {
    localStorage.removeItem('parade_guest');
    return { migrated: 0, skipped: 'nothing-to-migrate' };
  }

  // Best progress first, so if anything gets truncated or rate-limited the player keeps
  // their most valuable results rather than an arbitrary subset.
  keys.sort((a, b) => {
    const sa = snapshot[a], sb = snapshot[b];
    return ((sb.stars || 0) - (sa.stars || 0)) ||
           ((sb.best_score || sb.score || 0) - (sa.best_score || sa.score || 0));
  });
  const batch = keys.slice(0, GUEST_MIGRATION_MAX);
  const dropped = keys.length - batch.length;

  let migrated = 0;
  for (const level_key of batch) {
    const p = snapshot[level_key];
    try {
      await apiFetch('/progress/level', {
        method: 'POST',
        body: JSON.stringify({
          level_key,
          score: Math.max(0, Math.floor(p.best_score || p.score || 0)),
          stars: Math.max(0, Math.min(3, Math.floor(p.stars || 0)))
        })
      });
      migrated++;
    } catch (e) {
      // Keep going: one rejected level should not abandon the rest.
    }
  }

  // Campaign unlock state is tracked separately from the per-level table.
  let highestCampaign = 0;
  for (let i = 1; i <= 5; i++) {
    const p = snapshot['campaign:' + i];
    if (!p || !(p.completed || p.stars)) continue;
    if (i > highestCampaign) highestCampaign = i;
    try {
      await apiFetch('/campaign/progress', {
        method: 'POST',
        body: JSON.stringify({
          level_id: i,
          score: Math.max(0, Math.floor(p.best_score || p.score || 0)),
          stars: Math.max(0, Math.min(3, Math.floor(p.stars || 0)))
        })
      });
    } catch (e) { /* non-fatal */ }
  }
  if (highestCampaign > 0) {
    try {
      // Unlock the level after the highest one cleared, capped at 5.
      await apiFetch('/campaign/progress', {
        method: 'POST',
        body: JSON.stringify({ unlocked_level: Math.min(5, highestCampaign + 1) })
      });
    } catch (e) { /* non-fatal */ }
  }

  // Only clear the guest flag once we've actually tried — otherwise a failure here would
  // make the progress unrecoverable on a later attempt.
  localStorage.removeItem('parade_guest');

  if (migrated > 0 && typeof showToast === 'function') {
    showToast(`Moved ${migrated} level${migrated === 1 ? '' : 's'} of guest progress to your account.`, 'success');
  }
  return { migrated, dropped, total: keys.length };
}
window.migrateGuestProgress = migrateGuestProgress;

// Lightweight ownership refresh: MERGE the server's entitlements into the local
// set (never drop locally-added ones). Used when the schematic popover shows a map
// that isn't locally owned yet — covers the case where the purchase was made on a
// different device/session, so this session's in-memory set was stale. Deduped per
// seed so we hit the server at most once per map load.
let _ownedRefreshedSeed = null;
async function refreshOwnedFromServer(reason) {
  if (!authToken) return;
  const s = (typeof map !== 'undefined' && map) ? map.seed : null;
  if (s != null && _ownedRefreshedSeed === s) return; // already refreshed this map
  _ownedRefreshedSeed = s;
  try {
    const data = await apiFetch('/progress');
    if (data && Array.isArray(data.purchases)) {
      let added = false;
      data.purchases.forEach(i => { if (!ownedItems.has(i)) { ownedItems.add(i); added = true; } });
      if (added) { persistOwned(); }
    }
  } catch (e) { _ownedRefreshedSeed = null; /* allow retry */ }
}

// Record a finished level's best score/stars (cash is banked separately via
// bankEarnings). Server-authoritative when logged in; localStorage for guests.
async function saveLevelResult(levelKey, finalScore, stars) {
  if (!levelKey) return; // practice maps don't persist progress (they still bank)
  const prev = levelProgress[levelKey] || { best_score: -1, stars: 0, completed: false };
  const isNewBest = finalScore > (prev.best_score != null ? prev.best_score : -1);
  if (authToken) {
    try {
      const res = await apiFetch('/progress/level', {
        method: 'POST',
        body: JSON.stringify({ level_key: levelKey, score: finalScore, stars, completed: true })
      });
      if (res) {
        levelProgress[levelKey] = { best_score: res.best_score, stars: res.stars, completed: true };
      }
    } catch (err) { console.error('Failed to save level result:', err); }
  } else {
    levelProgress[levelKey] = {
      best_score: Math.max(finalScore, prev.best_score || 0, 0),
      stars: Math.max(stars, prev.stars || 0),
      completed: true
    };
  }
  persistProgressLocal();
  updateWalletUI();
  // A curated course was just cleared → invalidate the (cached) course grid so its
  // "played" check mark / stars refresh the next time the library is opened.
  if (typeof levelKey === 'string' && levelKey.indexOf('seed:') === 0) coursesRendered = false;
}

// --- Player rank ladder (64 parade titles) + achievements ---
const RANK_TITLES = [
  'Sidewalk Spectator','Curbside Fan','Confetti Tosser','Balloon Wrangler','Streamer Waver','Noisemaker Novice','Kazoo Kid','Whistle Blower',
  'Triangle Tapper','Tambourine Shaker','Cymbal Clapper','Piccolo Piper','Fife Player','Snare Tapper','Cornet Cadet','Trumpet Trainee',
  'Trombone Slider','Tuba Tooter','Bass Drummer','Flag Twirler','Baton Twirler','Majorette','Color Guard','Banner Bearer',
  'Float Rider','Float Builder','Float Captain','Section Leader','Squad Leader','Drum Sergeant','Drum Major','Bandleader',
  'Bandmaster','Parade Usher','Route Steward','Crowd Warden','Parade Herald','Trumpet Herald','Master Piper','Master Drummer',
  'Grand Baton','Grand Majorette','Grand Bandmaster','Procession Marshal','Boulevard Marshal','Avenue Marshal','Deputy Grand Marshal','Grand Marshal',
  'Parade Director','Pageant Director','Festival Director','Carnival Monarch','Parade Laureate','Master of Ceremonies','Grand Master of Ceremonies','Parade Luminary',
  'Parade Legend','Parade Icon','Parade Virtuoso','Parade Maestro','Grand Maestro','Supreme Grand Marshal','Parade Immortal','Eternal Marshal'
];
// Per 8-rank band glow colors (low → high prestige), used for the profile bloom.
const RANK_BAND_COLORS = ['#9ca3af','#38bdf8','#34d399','#fbbf24','#fb923c','#ef4444','#a855f7','#FFD700'];
function rankSlug(t) { return t.toLowerCase().replace(/'/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''); }
function rankIconPath(idx) { return `sprites/parade_characters/${String(idx + 1).padStart(2, '0')}_${rankSlug(RANK_TITLES[idx])}.png`; }

// Prestige = weighted blend of career XP, stars earned, and levels completed.
function computePrestige(s) {
  return Math.floor((s.xp || 0) / 10) + (s.stars || 0) * 200 + (s.levels || 0) * 150;
}
// Steep-ening curve: rank i (0..63) needs round(75 * i^2.5) prestige.
function rankThreshold(i) { return Math.round(75 * Math.pow(i, 2.5)); }
function rankFromPrestige(p) {
  let idx = 0;
  for (let i = 0; i < RANK_TITLES.length; i++) { if (p >= rankThreshold(i)) idx = i; else break; }
  return idx; // 0-based
}

const CAMPAIGN_DIFFS = { 1: 'Easy', 2: 'Medium', 3: 'Hard', 4: 'Expert', 5: 'Master' };

function getPlayerStats() {
  const xp = parseInt(localStorage.getItem('parade_xp') || 0, 10) || 0;
  const games = parseInt(localStorage.getItem('parade_games') || 0, 10) || 0;
  let stars = 0, levels = 0, threeStars = 0, master3 = 0, maxStars = 0;
  const diffs = new Set();
  for (const key of Object.keys(levelProgress)) {
    const pr = levelProgress[key];
    if (!pr || !pr.completed) continue;
    levels++;
    const st = pr.stars || 0;
    stars += st;
    if (st > maxStars) maxStars = st;
    if (st >= 3) threeStars++;
    let diff = null;
    if (key.indexOf('seed:') === 0) {
      const seed = parseInt(key.slice(5), 10);
      const c = (typeof PRECOMPUTED_COURSES !== 'undefined') ? PRECOMPUTED_COURSES.find(x => x.seed === seed) : null;
      diff = c ? c.difficulty : null;
    } else if (key.indexOf('campaign:') === 0) {
      diff = CAMPAIGN_DIFFS[parseInt(key.slice(9), 10)] || null;
    }
    if (diff) { diffs.add(diff); if (diff === 'Master' && st >= 3) master3++; }
  }
  // Per campaign-level stars (0 if not completed), for campaign achievements.
  const campaign = {};
  for (let i = 1; i <= 5; i++) {
    const p = levelProgress['campaign:' + i];
    campaign[i] = (p && p.completed) ? (p.stars || 0) : 0;
  }
  return { xp, games, stars, levels, threeStars, master3, maxStars, diffs, campaign, cash: playerCash | 0 };
}

const ACH_ICON_DIR = 'sprites/ui/icons/';
const ACHIEVEMENTS = [
  { id: 'first_win',  img: 'first_steps.png',    name: 'First Steps',   desc: 'Complete your first level.',        test: s => s.levels >= 1 },
  { id: 'rising',     img: 'rising_star.png',    name: 'Rising Star',   desc: 'Earn a 3-star rating.',             test: s => s.maxStars >= 3 },
  { id: 'perfect10',  img: 'perfectionist.png',  name: 'Perfectionist', desc: '3-star 10 levels.',                 test: s => s.threeStars >= 10 },
  { id: 'stars100',   img: 'star_collector.png', name: 'Star Collector', desc: 'Collect 100 stars.',               test: s => s.stars >= 100 },
  { id: 'stars250',   img: 'constellation.png',  name: 'Constellation', desc: 'Collect 250 stars.',                test: s => s.stars >= 250 },
  { id: 'lvl10',      img: 'getting_going.png',  name: 'Getting Going', desc: 'Complete 10 levels.',               test: s => s.levels >= 10 },
  { id: 'lvl50',      img: 'road_warrior.png',   name: 'Road Warrior',  desc: 'Complete 50 levels.',               test: s => s.levels >= 50 },
  { id: 'lvl100',     img: 'completionist.png',  name: 'Completionist', desc: 'Complete 100 levels.',              test: s => s.levels >= 100 },
  { id: 'master3',    img: 'master_slayer.png',  name: 'Master Slayer', desc: '3-star a Master-difficulty map.',   test: s => s.master3 >= 1 },
  { id: 'alldiffs',   img: 'all_rounder.png',    name: 'All-Rounder',   desc: 'Clear every difficulty tier.',      test: s => ['Easy','Medium','Hard','Expert','Master'].every(d => s.diffs.has(d)) },
  { id: 'bank5k',     img: 'big_banker.png',     name: 'Big Banker',    desc: 'Grow your wallet to 5,000.',        test: s => s.cash >= 5000 },
  { id: 'bank25k',    img: 'vault_keeper.png',   name: 'Vault Keeper',  desc: 'Grow your wallet to 25,000.',       test: s => s.cash >= 25000 },
  { id: 'games50',    img: 'veteran.png',        name: 'Veteran',       desc: 'Play 50 games.',                    test: s => s.games >= 50 },
  { id: 'games100',   img: 'centurion.png',      name: 'Centurion',     desc: 'Play 100 games.',                   test: s => s.games >= 100 },
  // Campaign tour — each unlocks on a flawless 3-star clear of that level.
  { id: 'camp1', img: 'easy_stroll.png',       name: 'The Easy Stroll',        desc: '3-star Campaign 1: The Easy Stroll.',        test: s => s.campaign[1] >= 3 },
  { id: 'camp2', img: 'midtown_march.png',     name: 'Midtown March',          desc: '3-star Campaign 2: Midtown March.',          test: s => s.campaign[2] >= 3 },
  { id: 'camp3', img: 'broadway_blitz.png',    name: 'Broadway Blitz',         desc: '3-star Campaign 3: Broadway Blitz.',         test: s => s.campaign[3] >= 3 },
  { id: 'camp4', img: 'expert_edition.png',    name: 'Expert Expedition',      desc: '3-star Campaign 4: Expert Expedition.',      test: s => s.campaign[4] >= 3 },
  { id: 'camp5', img: 'masters_marathon.png',  name: "The Master's Marathon",  desc: "3-star Campaign 5: The Master's Marathon.", test: s => s.campaign[5] >= 3 },
  { id: 'campAll', img: 'grand_tour.png',      name: 'Parade Grand Marshal',   desc: '3-star every campaign level.',               test: s => [1,2,3,4,5].every(i => s.campaign[i] >= 3) }
];

function renderProfile() {
  const nameEl = document.getElementById('profile-username');
  if (nameEl) nameEl.innerText = localStorage.getItem('parade_username') || 'User';

  // OAuth-only accounts (Google/Apple) have no password — hide the change-password
  // control entirely so a reset/change is never even offered.
  const oauthOnly = window.profileHasPassword === false;
  const cpBtn = document.getElementById('btn-toggle-change-password');
  if (cpBtn) cpBtn.style.display = oauthOnly ? 'none' : '';
  if (oauthOnly) {
    const cpForm = document.getElementById('profile-change-password-form');
    if (cpForm) cpForm.classList.add('hidden');
  }

  // Delete-account form: OAuth-only accounts (Google/Apple) have no password to
  // confirm with, so hide that field entirely — the server already skips the
  // password check for them too (same distinction as change-password above).
  const deletePwdField = document.getElementById('profile-delete-pwd-field');
  if (deletePwdField) deletePwdField.style.display = oauthOnly ? 'none' : '';

  const s = getPlayerStats();
  const prestige = computePrestige(s);
  const idx = rankFromPrestige(prestige);       // 0-based
  const title = RANK_TITLES[idx];
  const bandColor = RANK_BAND_COLORS[Math.min(RANK_BAND_COLORS.length - 1, Math.floor(idx / 8))];
  const curThresh = rankThreshold(idx);
  const nextThresh = idx < RANK_TITLES.length - 1 ? rankThreshold(idx + 1) : curThresh;
  const pct = idx >= RANK_TITLES.length - 1 ? 100
    : Math.max(0, Math.min(100, Math.round(((prestige - curThresh) / Math.max(1, nextThresh - curThresh)) * 100)));

  const set = (id, val) => { const el = document.getElementById(id); if (el) el.innerText = val; };
  set('profile-title', title);
  // Feature the rank's character icon inside a band-colored glowing bloom.
  const emblemEl = document.getElementById('profile-rank-emblem');
  if (emblemEl) {
    emblemEl.src = rankIconPath(idx);
    emblemEl.style.filter = `drop-shadow(0 0 10px ${bandColor})`;
  }
  const glowEl = document.getElementById('profile-rank-glow');
  if (glowEl) {
    glowEl.style.background = `radial-gradient(circle, ${bandColor}dd 0%, ${bandColor}40 55%, ${bandColor}00 78%)`;
  }
  const titleEl = document.getElementById('profile-title');
  if (titleEl) { titleEl.style.color = bandColor; titleEl.style.textShadow = `0 0 10px ${bandColor}66`; }
  set('profile-rank-num', `Rank ${idx + 1} / ${RANK_TITLES.length}`);
  set('profile-prestige-label', `Prestige ${prestige.toLocaleString()}`);
  set('profile-next-label', idx >= RANK_TITLES.length - 1 ? 'Max rank!' : `Next: ${nextThresh.toLocaleString()}`);
  const bar = document.getElementById('profile-progress-bar'); if (bar) bar.style.width = pct + '%';
  set('profile-stars', s.stars.toLocaleString());
  set('profile-levels', s.levels.toLocaleString());
  set('profile-cash', s.cash.toLocaleString());
  set('profile-xp', s.xp.toLocaleString());
  set('profile-games', s.games.toLocaleString());
  set('profile-prestige', prestige.toLocaleString());

  const grid = document.getElementById('profile-achievements');
  if (grid) {
    let earned = 0;
    grid.innerHTML = ACHIEVEMENTS.map((a, i) => {
      const got = !!a.test(s); if (got) earned++;
      // data-* carry the label text so a tap can surface it (hover title is desktop-only).
      return `<div class="ach-badge" data-idx="${i}" data-got="${got ? '1' : '0'}" title="${a.name} — ${a.desc}${got ? '' : ' (locked)'}" style="aspect-ratio:1; display:flex; align-items:center; justify-content:center; padding:6px; border-radius:8px; cursor:pointer; border:1px solid ${got ? 'rgba(255,215,0,0.5)' : 'rgba(255,255,255,0.08)'}; background:${got ? 'rgba(255,215,0,0.12)' : 'rgba(255,255,255,0.03)'};">`
        + `<img src="${ACH_ICON_DIR}${a.img}" alt="${a.name}" style="width:100%; height:100%; object-fit:contain; pointer-events:none; filter:${got ? 'none' : 'grayscale(1)'}; opacity:${got ? '1' : '0.3'};">`
        + `</div>`;
    }).join('');
    set('profile-ach-count', earned);
    set('profile-ach-total', ACHIEVEMENTS.length);

    // Tap/click a badge to reveal its name, description, and locked/unlocked state in
    // the detail line below the grid (works on touch, where hover tooltips don't).
    const detail = document.getElementById('profile-ach-detail');
    if (detail && !grid._achTapBound) {
      grid._achTapBound = true;
      grid.addEventListener('click', (e) => {
        const badge = e.target.closest('.ach-badge');
        if (!badge || !grid.contains(badge)) return;
        const a = ACHIEVEMENTS[parseInt(badge.dataset.idx, 10)];
        if (!a) return;
        const got = badge.dataset.got === '1';
        detail.innerHTML = `<strong style="color:${got ? '#FFD700' : '#cbd5e1'};">${a.name}</strong>`
          + ` <span style="color:${got ? '#4ade80' : 'rgba(255,255,255,0.4)'}; font-weight:bold; display:inline-flex; align-items:center; gap:3px; vertical-align:middle; margin-left:4px;">${got ? `${GameIcons.get('unlocked', 12)} Unlocked` : `${GameIcons.get('locked', 12)} Locked`}</span>`
          + `<br><span style="color:rgba(255,255,255,0.6);">${a.desc}</span>`;
        // Highlight the selected badge.
        grid.querySelectorAll('.ach-badge').forEach(b => b.style.outline = 'none');
        badge.style.outline = '2px solid #38bdf8';
        badge.style.outlineOffset = '1px';
      });
    }
  }

  renderPurchaseHistory();
}

// Pretty-print an entitlement item id for the inventory. Route schematics are
// keyed by seed ('schematic:seed:<n>'); show the seed so it matches the popover.
function describeItem(item) {
  if (!item) return '';
  const r = parseRouteItem(item);
  if (r) return `Route map · Seed #${r.seed}${r.difficulty ? ' · ' + r.difficulty : ''}`;
  const c = /^schematic:(.+)$/.exec(item);
  if (c) return `Route map · ${c[1]}`;
  return item;
}

// Replay a specific owned map deterministically from its (seed, difficulty). This
// reproduces the EXACT map you bought the route for, with the route pre-revealed
// (ownsCurrentRoute() is true for it). Mirrors the quick-random launch flow.
function loadOwnedMap(seed, difficulty) {
  const authModal = document.getElementById('auth-modal');
  if (authModal) authModal.classList.add('hidden'); // close Profile
  currentLevelKey = null;                 // replay is practice: no carry-in/save
  resetRunBoosts();
  engine.gameState.wave = 1;
  engine.gameState.lives = ECONOMY.BASE_LIVES;
  engine.gameState.money = BASE_MONEY;
  engine.gameState.waveActive = false;
  engine.gameState.campaignLevel = null;
  engine.gameState.campaignTargetDifficulty = difficulty || null;
  try { pauseThemeMusic(); themeMusic.currentTime = 0; } catch (e) {}
  map.generateRandomMap(seed, difficulty || null);
  engine.centerCamera();
  const bs = document.getElementById('btn-start');
  if (bs) { bs.style.display = 'block'; bs.innerHTML = `▶️ Start Wave 1 <span id="wave-countdown" style="opacity: 0.7; font-size: 0.9em; margin-left: 8px;"></span>`; bs.disabled = false; }
  engine.isPaused = false;
  if (typeof syncPausePlayButtons === 'function') syncPausePlayButtons();
  draggingTowerType = null; dragPosLogical = null; selectedTower = null;
  engine.isDragging = false; hideContextMenu();
}

// Purchase History inventory (Profile tab). Server-authoritative when logged in;
// falls back to the local owned-items mirror for guests. Also the diagnostic that
// answers "did a purchase for this seed actually get recorded?".
async function renderPurchaseHistory() {
  const ownedEl = document.getElementById('profile-owned-routes');
  const txEl = document.getElementById('profile-tx-history');
  if (!ownedEl || !txEl) return;

  // Live diagnostic: what does the RUNNING GAME think about the current map?
  // Compares the seed shown in the popover against the in-memory entitlement set,
  // so a mismatch between "purchased in Postgres" and "revealed in game" is visible.
  const diagEl = document.getElementById('profile-route-diag');
  if (diagEl) {
    const ri = routeItem();
    const seenOwned = ownsItem(ri);
    const curSeed = (typeof map !== 'undefined' && map) ? map.seed : '(no map)';
    diagEl.innerHTML =
      `<div style="font-size:0.72em;color:#8b93a6;">Current map seed: <b style="color:#e5e7eb;">#${curSeed}</b></div>` +
      `<div style="font-size:0.72em;color:#8b93a6;">Game entitlement key: <code style="color:#93c5fd;">${escapeHtml(String(ri))}</code></div>` +
      `<div style="font-size:0.72em;color:#8b93a6;">Game sees this map as: <b style="color:${seenOwned ? '#4ade80' : '#f87171'};">${seenOwned ? 'OWNED' : 'NOT OWNED'}</b> · in-memory items: ${ownedItems.size}</div>`;
  }

  // Route entitlements, current form ('route:…') + legacy ('schematic:…').
  const routeRe = /^(route:|schematic:)/;
  // Render owned maps as a "My Maps" list, each with a ▶ Play button that replays
  // the EXACT map deterministically from its (seed, difficulty).
  const renderOwned = (items, dateFor) => {
    if (!items.length) { ownedEl.innerHTML = '<span style="color:#8b93a6;">No route maps purchased yet.</span>'; return; }
    ownedEl.innerHTML = items.map(i => {
      const r = parseRouteItem(i);
      const d = (dateFor && dateFor[i]) ? ` <span style="color:#8b93a6;">· ${new Date(dateFor[i]).toLocaleDateString()}</span>` : '';
      const play = r ? `<button class="btn" data-seed="${r.seed}" data-diff="${r.difficulty || ''}" style="padding:2px 10px; font-size:11px; background:linear-gradient(135deg,#10b981,#059669); border:none; border-radius:4px; color:#fff; flex:none; display:inline-flex; align-items:center; gap:2px;">${GameIcons.get('unlocked', 11, 'filter:brightness(10);')} Play</button>` : '';
      return `<div style="display:flex; justify-content:space-between; align-items:center; gap:8px; padding:5px 0; border-bottom:1px solid rgba(255,255,255,0.06);"><span>${GameIcons.get('unlocked', 13, 'margin-right:2px; vertical-align:middle;')} ${escapeHtml(describeItem(i))}${d}</span>${play}</div>`;
    }).join('');
    ownedEl.querySelectorAll('button[data-seed]').forEach(b => b.addEventListener('click', () => {
      loadOwnedMap(Number(b.dataset.seed), b.dataset.diff || null);
    }));
  };

  // Always show what we know locally first (instant, works offline / for guests).
  const localRoutes = [...ownedItems].filter(i => routeRe.test(i));
  renderOwned(localRoutes, null);
  txEl.innerHTML = '<div style="padding:8px; color:#8b93a6; font-size:0.82em;">Loading…</div>';

  if (!authToken) {
    txEl.innerHTML = '<div style="padding:8px; color:#8b93a6; font-size:0.82em;">Sign in to see your full transaction ledger. (Guest purchases are stored on this device only.)</div>';
    return;
  }

  try {
    const data = await apiFetch('/wallet/history');
    const purchases = (data && data.purchases) || [];
    const txns = (data && data.transactions) || [];

    // Merge server purchases with the local set so nothing is missed.
    const routeItems = [...new Set([...purchases.map(p => p.item), ...localRoutes])].filter(i => routeRe.test(i));
    const dateFor = {}; purchases.forEach(p => { dateFor[p.item] = p.created_at; });
    renderOwned(routeItems, dateFor);

    if (!txns.length) {
      txEl.innerHTML = '<div style="padding:8px; color:#8b93a6; font-size:0.82em;">No transactions recorded yet.</div>';
      return;
    }
    const row = (t) => {
      const when = t.created_at ? new Date(t.created_at).toLocaleString() : '';
      const pos = t.delta > 0;
      const deltaTxt = t.delta === 0 ? '±0' : (pos ? '+' : '−') + '$' + Math.abs(t.delta).toLocaleString();
      const color = t.delta === 0 ? '#8b93a6' : (pos ? '#4ade80' : '#f87171');
      const label = t.kind === 'purchase' ? (t.item ? describeItem(t.item) : 'Purchase')
                  : t.kind === 'earn' ? 'Winnings deposited'
                  : t.item ? describeItem(t.item) : 'Spend';
      const note = (t.kind === 'purchase' && t.delta === 0) ? ' <span style="color:#8b93a6;">(already owned — no charge)</span>' : '';
      return `<div style="display:flex; justify-content:space-between; gap:8px; padding:6px 8px; border-bottom:1px solid rgba(255,255,255,0.06); font-size:0.8em;">
        <div style="flex:1; min-width:0;"><div style="color:#e5e7eb; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${escapeHtml(label)}${note}</div>
        <div style="color:#6b7280; font-size:0.85em;">${escapeHtml(when)}</div></div>
        <div style="color:${color}; font-weight:bold; white-space:nowrap;">${deltaTxt}</div>
        <div style="color:#8b93a6; white-space:nowrap;">$${(t.cash_after||0).toLocaleString()}</div>
      </div>`;
    };
    txEl.innerHTML = txns.map(row).join('');
  } catch (err) {
    txEl.innerHTML = `<div style="padding:8px; color:#f87171; font-size:0.82em;">Couldn't load history: ${escapeHtml(err.message || 'error')}</div>`;
  }
}

const syncCampaignProgress = async () => {
  if (!authToken) return;
  try {
    const data = await apiFetch('/campaign/progress');
    if (data && data.unlocked_level) {
      localStorage.setItem('parade_campaign_unlocked', data.unlocked_level);
      for (let i = 1; i <= 5; i++) {
        localStorage.setItem(`parade_campaign_${i}_score`, data[`level_${i}_score`] || 0);
        localStorage.setItem(`parade_campaign_${i}_stars`, data[`level_${i}_stars`] || 0);
      }
    }
  } catch (err) {
    console.error('Failed to sync campaign progress with PostgreSQL server:', err);
  }
};

const engine = new Engine('game-canvas');
const map = new GameMap(engine);
// Expose live handles (used by audio checks above + the train tuner overlay).
window.engine = engine;
window.map = map;

// iOS native only: hand every registered SFX to @capgo/native-audio so each one is
// preloaded into a native player once, up front. Web is a no-op (Web Audio path).
// The constructor has finished registering sfxUrls by now. Fire-and-forget — the
// game must not block on it, and any sound played before its preload lands simply
// no-ops rather than throwing.
engine.preloadNativeSfx();
// NOTE: Game Center sign-in is NOT kicked off here even though this is the natural
// "native setup" spot. gcAuthenticate() is a hoisted function declaration, but it closes
// over `const gcPlugin`, which is declared much further down and is in its temporal dead
// zone at this point — calling it here throws ReferenceError before its own try/catch can
// catch it, so Game Center would silently never authenticate. The call lives immediately
// after those declarations instead.


// Universal AudioContext wake for iOS / Safari / Chrome.
//
// This handler stays attached for the WHOLE session (it used to remove itself
// after the first unlock). That self-removal was the main reason audio "randomly"
// died after leaving and returning to the window: the browser suspends the
// AudioContext on background/blur, and once the one-shot unlock listeners were
// gone, nothing could resume it outside a user gesture — so SFX and music stayed
// dead until a full reload. Keeping the handler means ANY click/tap/key after
// returning reliably re-wakes the context (a gesture is the one thing browsers
// always honor for resume()) and re-asserts whichever track should be playing.
const wakeAudioOnGesture = () => {
  const ctx = engine && engine.audioCtx;
  if (!ctx) return;

  // Covers 'suspended' AND Safari's 'interrupted' state (both != 'running').
  // The looping music BufferSource resumes automatically once the context does.
  if (ctx.state !== 'running') {
    ctx.resume().catch(() => {});
    // Silent buffer nudges Safari's audio thread to spin up / re-sync.
    try {
      const buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.start(0);
    } catch (e) {}
  }

  // Re-assert persisted audio levels now that the graph is actually live. On reload
  // the gains are first computed during init — sometimes before async settings load
  // finishes or while the context is still suspended — so the saved Master/SFX/Music
  // levels could fail to reach the live nodes (sounds played at full volume). Doing it
  // on the first real gesture (idempotent, cheap) guarantees the live audio matches
  // the saved settings regardless of load-time ordering.
  try { if (engine.updateSfxMasterGain) engine.updateSfxMasterGain(); } catch (e) {}
  try { if (typeof updateMusicVolume === 'function') updateMusicVolume(); } catch (e) {}
};
document.addEventListener('touchstart', wakeAudioOnGesture, { passive: true });
document.addEventListener('click', wakeAudioOnGesture, { passive: true });
document.addEventListener('keydown', wakeAudioOnGesture, { passive: true });

// Basic UI State
engine.gameState = {
  money: 500,
  lives: 10,
  wave: 1,
  score: 0 // [#24] initialize so entities.js `gameState.score += 10` doesn't produce NaN
};

let draggingTowerType = null;
let dragPosLogical = null;
let selectedTower = null;
let cameraMode = 'free'; // 'free', 'action', 'tracking', 'tower'
let trackedEntity = null;
let updateCameraModeUI = () => {};

// Wave configurations moved to js/waves.js (single, unit-tested data source).

engine.gameState.waveActive = false;
engine.gameState.floatSpawns = [];
engine.gameState.balloonSpawns = [];
engine.gameState.floatSpawnTimer = 0;
engine.gameState.balloonSpawnTimer = 0;
engine.gameState.waveCountdown = 0;

// Init Function
async function init() {
  // Show loading
  const loadingOverlay = document.getElementById('loading-overlay');

  // Load public config including dynamic rubric weights
  try {
    const configRes = await fetch(`${window.API_ORIGIN || ''}/api/config`);
    if (configRes.ok) {
      const configData = await configRes.json();
      if (configData.difficulty_rubric) {
        if (!engine.gameState) engine.gameState = {};
        engine.gameState.rubricConfig = configData.difficulty_rubric;
      }
      // Admin-tuned tower balance (overrides TOWER_DEFAULTS in entities.js).
      // Stored on engine so Tower/getTowerCfg read it; applied on this load.
      if (configData.tower_config && typeof configData.tower_config === 'object') {
        engine.towerConfig = configData.tower_config;
      }
      if (configData.balloon_config && typeof configData.balloon_config === 'object') {
        engine.balloonConfig = configData.balloon_config;
      }
      // Admin-tuned water generation (how often water appears + the feature mix).
      // Applied to the engine props that map.js/_addWaterway reads.
      if (configData.water_config && typeof configData.water_config === 'object') {
        const w = configData.water_config;
        if (typeof w.any === 'number') engine.waterFeatureFrequency = w.any;
        if (typeof w.river === 'number') engine.riverOdds = w.river;
        if (typeof w.lake === 'number') engine.lakeOdds = w.lake;
        if (typeof w.coast === 'number') engine.coastOdds = w.coast;
      }
    }
  } catch (err) {
    console.error('Failed to load public configuration:', err);
  }
  
  // Dynamic fetch of precomputed levels from PostgreSQL backend database
  try {
    const res = await fetch(`${window.API_ORIGIN || ''}/api/courses`);
    if (res.ok) {
      PRECOMPUTED_COURSES = await res.json();
      if (typeof window.initializeCourseFilters === 'function') {
        window.initializeCourseFilters();
      }
    } else {
      console.warn('Backend courses API returned error status. Falling back to empty library.');
    }
  } catch (err) {
    console.error('Failed to dynamically fetch precomputed courses from PostgreSQL database:', err);
  }

  // Sync campaign progress with PostgreSQL on startup if logged in
  if (authToken) {
    try {
      const profile = await apiFetch('/profile');
      if (profile) window.profileHasPassword = profile.hasPassword !== false;
      if (profile && profile.settings) {
        const username = profile.username || localStorage.getItem('parade_username');
        const settingsKey = username ? `parade_settings_${username}` : 'parade_settings';
        localStorage.setItem(settingsKey, JSON.stringify(profile.settings));
        localStorage.setItem('parade_settings', JSON.stringify(profile.settings));
        if (typeof window.loadSettings === 'function') {
          window.loadSettings(profile.settings);
        }
      }
    } catch (e) {
      console.error('Failed to sync profile settings on startup:', e);
    }
    await syncCampaignProgress().catch(console.error);
    await syncPlayerProgress().catch(console.error);
  }
  updateWalletUI(); // reflect wallet (server value for members, localStorage for guests)
  updateAuthUI();
  
  // Start loading assets concurrently
  const loadPromise = engine.loadAssets(['parade_td_road_kit_deterministic/road_kit_manifest.json', 'sprites/manifest.json']);
  
  // Ensure assets are fully loaded before doing anything else
  await loadPromise;

  // Preload billboard ad artwork (one image per tier+ad). Registered under keys
  // bb_t{tier}_ad{index} that Tower.render() looks up. Fire-and-forget: the
  // renderer falls back to the procedural sign for any slice not yet loaded
  // (and permanently for tier 5 until its art is supplied).
  (() => {
    const counts = { 1: 10, 2: 8, 3: 10, 4: 10, 5: 11 };
    for (const t of Object.keys(counts)) {
      for (let i = 0; i < counts[t]; i++) {
        const img = new Image();
        const key = `bb_t${t}_ad${i}`;
        img.onload = () => engine.assets.set(key, img);
        img.onerror = () => {};
        img.src = `sprites/towers/billboards/tier_${t}_${i + 1}.png`;
      }
    }
  })();
  
  // Hide loading text and spinner
  const loadSpinner = document.querySelector('#loading-overlay .spinner');
  if (loadSpinner) loadSpinner.style.display = 'none';
  document.getElementById('loading-text').style.display = 'none';
  document.querySelector('#loading-overlay h2').style.display = 'none';
  
  // Load logo image for canvas intro
  const logoImg = new Image();
  logoImg.src = 'sprites/ui/logo.png';
  const logoPromise = new Promise(resolve => {
    logoImg.onload = resolve;
    logoImg.onerror = resolve;
  });

  // Load streamer images
  const streamerImgs = [];
  const streamerPromises = [];
  for (let i = 1; i <= 5; i++) {
    const img = new Image();
    img.src = `sprites/effects/streamers/streamer_${i}.png`;
    streamerPromises.push(new Promise(resolve => {
      img.onload = resolve;
      img.onerror = resolve;
    }));
    streamerImgs.push(img);
  }

  await Promise.all([logoPromise, ...streamerPromises]);

  const introCanvas = document.getElementById('intro-canvas');
  let intro;
  if (introCanvas) {
    intro = new IntroAnimator(introCanvas, logoImg, streamerImgs);
    intro.drawStaticLogo();
  }

  // Onboarding setup helper
  const setupOnboardingCard = (resolveLaunch) => {
    const card = document.getElementById('onboarding-card');
    const title = document.getElementById('onboarding-title');
    const desc = document.getElementById('onboarding-desc');

    // Guest entry — releases the launch promise without ever setting authToken.
    //
    // Nothing else has to change downstream: the economy, progress, wallet, loan and
    // 3-star unlock paths all already branch on `!authToken` and fall back to a
    // localStorage mirror. Those branches existed but were unreachable while the
    // registration wall was absolute, so they are effectively new code in practice and
    // are verified on device rather than assumed to work.
    //
    // The teardown below intentionally mirrors the successful-login teardown further down
    // (fade card, fade the background video + overlay, then hide/stop/resolve after
    // 400ms) so both entry paths animate identically.
    const guestBtn = document.getElementById('onboard-guest-btn');
    if (guestBtn && !guestBtn.dataset.wired) {
      guestBtn.dataset.wired = '1'; // setupOnboardingCard runs again via showOnboardingLogin
      guestBtn.addEventListener('click', () => {
        // Remembered so the profile screen can later offer to migrate this device's
        // progress into a real account instead of silently discarding it.
        try { localStorage.setItem('parade_guest', '1'); } catch (e) {}
        if (typeof updateAuthUI === 'function') updateAuthUI();
        if (typeof showToast === 'function') {
          showToast('Playing as guest — progress saves on this device.', 'info');
        }

        if (card) {
          card.style.opacity = '0';
          card.style.transform = 'translate(-50%, -40%)';
        }
        const guestLoginVideo = document.getElementById('login-success-video');
        if (guestLoginVideo) guestLoginVideo.style.opacity = '0';
        const guestLoginOverlay = document.getElementById('login-video-overlay');
        if (guestLoginOverlay) guestLoginOverlay.style.opacity = '0';

        setTimeout(() => {
          if (card) card.classList.add('hidden');
          if (window.stopLoginVideo) window.stopLoginVideo(); // stop the bg video decoding
          // Same as the login path: clear the mid-game backdrop when the card was opened
          // from the header button. Harmless at launch, where no backdrop exists.
          if (window.hideOnboardingLogin) window.hideOnboardingLogin();
          // Prefer the live launch resolver over the one captured when this button
          // was wired: the card can now be wired mid-game (where there is no launch
          // to resolve) and the listener is attached only once.
          (window.resolveOnboardingLaunch || resolveLaunch || function () {})();
        }, 400);
      });
    }

    const usernameInput = document.getElementById('onboard-username');
    const emailInput = document.getElementById('onboard-email');
    const passwordInput = document.getElementById('onboard-password');

    // Pre-populate remembered username if present
    const rememberedUsername = localStorage.getItem('parade_remembered_username');
    if (rememberedUsername) {
      if (usernameInput) usernameInput.value = rememberedUsername;
      const onboardRememberMe = document.getElementById('onboard-remember-me');
      if (onboardRememberMe) onboardRememberMe.checked = true;
    }
    const submitBtn = document.getElementById('btn-onboard-submit');
    const errorEl = document.getElementById('onboard-error');
    const toggleText = document.getElementById('onboard-toggle-text');
    
    let isRegisterMode = false; // default the card to Log In (returning players)
    if (errorEl) errorEl.innerText = '';

    // Render the card for the current mode. toggleMode (below) flips the flag
    // then re-renders; applyMode is also called once on init so the card opens
    // in Log In mode instead of Create Account.
    const applyMode = () => {
      if (errorEl) errorEl.innerText = '';
      if (isRegisterMode) {
        if (title) title.innerText = 'Create Account';
        if (desc) desc.innerText = 'Register to save your campaign progress, earn stars, and join the leaderboards!';
        if (emailInput) emailInput.style.display = 'block';
        if (submitBtn) submitBtn.innerText = 'Create Account & Play';
        if (toggleText) toggleText.innerHTML = `Already have an account? <span id="onboard-toggle" style="color: #FFD700; font-weight: bold; cursor: pointer; text-decoration: underline;">Log In</span>`;
      } else {
        if (title) title.innerText = 'Log In';
        if (desc) desc.innerText = 'Sign in to your account to restore your progress and achievements.';
        if (emailInput) emailInput.style.display = 'none';
        if (submitBtn) submitBtn.innerText = 'Log In & Play';
        if (toggleText) toggleText.innerHTML = `New player? <span id="onboard-toggle" style="color: #FFD700; font-weight: bold; cursor: pointer; text-decoration: underline;">Create Account</span>`;
      }
      // "Forgot password?" is only meaningful when logging in.
      const _fl = document.getElementById('onboard-forgot-link');
      if (_fl) _fl.style.display = isRegisterMode ? 'none' : 'block';
      if (errorEl) errorEl.style.color = '#ff4a4a';
      const newToggleLink = document.getElementById('onboard-toggle');
      if (newToggleLink) {
        newToggleLink.addEventListener('click', toggleMode);
      }
    };
    const toggleMode = () => { isRegisterMode = !isRegisterMode; applyMode(); };

    // Render the initial card state (Log In by default) and wire the toggle link.
    applyMode();

    // Forgot-password: reveal the email field and POST it to the existing
    // /api/forgot-password endpoint (which always returns a generic message so
    // registered emails can't be enumerated).
    const forgotLink = document.getElementById('onboard-forgot-link');
    if (forgotLink) {
      if (!isRegisterMode) forgotLink.style.display = 'block';
      forgotLink.addEventListener('click', async () => {
        if (emailInput) emailInput.style.display = 'block';
        const email = emailInput ? emailInput.value.trim() : '';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          if (errorEl) { errorEl.style.color = '#FFD93D'; errorEl.innerText = 'Enter your account email above, then tap "Forgot password?" again.'; }
          if (emailInput) emailInput.focus();
          return;
        }
        try {
          const resp = await fetch(`${window.API_ORIGIN || ''}/api/forgot-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
          });
          const data = await resp.json().catch(() => ({}));
          if (errorEl) { errorEl.style.color = '#4ade80'; errorEl.innerText = (data && data.message) || 'If that email is registered, a reset link is on its way — check your inbox and spam.'; }
        } catch (e) {
          if (errorEl) { errorEl.style.color = '#ff4a4a'; errorEl.innerText = 'Could not reach the server. Please try again.'; }
        }
      });
    }
    
    // Wire Google + Apple sign-in on the onboarding card (initialize + renderButton +
    // Apple click). Safe to call before the async SDKs load — it also runs on
    // onGoogleLibraryLoad and a boot fallback, and is idempotent.
    if (typeof window.initOnboardingOAuth === 'function') window.initOnboardingOAuth();

    // Form submission
    const onboardForm = document.getElementById('onboarding-form');
    if (onboardForm) {
      onboardForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const username = usernameInput ? usernameInput.value.trim() : '';
        const email = emailInput ? emailInput.value.trim() : '';
        const password = passwordInput ? passwordInput.value : '';
        if (errorEl) errorEl.innerText = '';
        
        // Client validation
        if (!username) {
          if (errorEl) errorEl.innerText = 'Username is required.';
          return;
        }
        if (username.length < 3 || username.length > 20 || !/^[a-zA-Z0-9_]+$/.test(username)) {
          if (errorEl) errorEl.innerText = 'Username must be 3-20 letters/numbers/underscores.';
          return;
        }
        if (isRegisterMode) {
          if (!email) {
            if (errorEl) errorEl.innerText = 'Email address is required.';
            return;
          }
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            if (errorEl) errorEl.innerText = 'Please enter a valid email address.';
            return;
          }
        }
        if (!password || password.length < 8) {
          if (errorEl) errorEl.innerText = 'Password must be at least 8 characters.';
          return;
        }
        
        submitBtn.disabled = true;
        submitBtn.innerText = 'Processing...';
        
        try {
          // apiFetch() already prepends API_URL ('/api'), so these must NOT include it —
          // '/api/register' here produced '/api/api/register' (404 -> generic "API Error").
          let endpoint = isRegisterMode ? '/register' : '/login';
          const bodyObj = { username, password };
          if (isRegisterMode) {
            bodyObj.email = email;
          }
          
          let data = await apiFetch(endpoint, {
            method: 'POST',
            body: JSON.stringify(bodyObj)
          });
          
          // If we registered, do automatic login
          if (isRegisterMode) {
            data = await apiFetch('/login', {
              method: 'POST',
              body: JSON.stringify({ username, password })
            });
          }
          
          if (data.token) {
            authToken = data.token;
            isAdmin = data.isAdmin || false;
            localStorage.setItem('parade_token', authToken);
            localStorage.setItem('parade_username', data.username);

            // /api/login reports requirePasswordChange, and nothing read it until now:
            // showRequirePasswordChangeModal existed but was never called from anywhere,
            // so an admin-issued temporary password could log in and then immediately
            // fail every authenticated call.  Take the new password here, then reload
            // into a normal session.
            if (data.requirePasswordChange) {
              showRequirePasswordChangeModal(() => window.location.reload());
              return;
            }

            // Support Remember Me check during onboarding login
            const onboardRememberMe = document.getElementById('onboard-remember-me');
            if (onboardRememberMe) {
              if (onboardRememberMe.checked) {
                localStorage.setItem('parade_remembered_username', data.username);
              } else {
                localStorage.removeItem('parade_remembered_username');
              }
            }

            localStorage.setItem('parade_xp', data.xp);
            localStorage.setItem('parade_games', data.totalGames);
            localStorage.setItem('parade_is_admin', isAdmin);
            if (data.settings) {
              const settingsKey = `parade_settings_${data.username}`;
              localStorage.setItem(settingsKey, JSON.stringify(data.settings));
              localStorage.setItem('parade_settings', JSON.stringify(data.settings));
              // This runs on the pre-game login screen, BEFORE init() defines
              // loadSettings/sync*/etc. Settings are already persisted to localStorage
              // above and init() re-loads + re-syncs them on startup, so guard these
              // (an unguarded call here threw "window.loadSettings is not a function").
              if (typeof window.loadSettings === 'function') window.loadSettings(data.settings);
            }
            // MUST run before the syncs below: syncPlayerProgress overwrites
            // levelProgress with the server's copy, which would erase this device's
            // guest progress before we ever got a chance to upload it.
            if (typeof migrateGuestProgress === 'function') await migrateGuestProgress().catch(console.error);
            if (typeof syncCampaignProgress === 'function') await syncCampaignProgress().catch(console.error);
            if (typeof syncPlayerProgress === 'function') await syncPlayerProgress().catch(console.error);
            if (typeof updateAuthUI === 'function') updateAuthUI();
            if (typeof showToast === 'function') showToast('Welcome, ' + data.username + '!', 'success');
            
            if (card) {
              card.style.opacity = '0';
              card.style.transform = 'translate(-50%, -40%)';
            }
            
            // Smoothly fade out the premium login background video and overlay
            const loginVideo = document.getElementById('login-success-video');
            if (loginVideo) {
              loginVideo.style.opacity = '0';
            }
            const loginOverlayEl = document.getElementById('login-video-overlay');
            if (loginOverlayEl) {
              loginOverlayEl.style.opacity = '0';
            }
            
            setTimeout(() => {
              if (card) card.classList.add('hidden');
              if (window.stopLoginVideo) window.stopLoginVideo(); // stop the bg video decoding
              // Also dismiss the mid-game backdrop if this login came from the header
              // button rather than the launch flow — otherwise the card disappears and
              // leaves a dark blurred screen over a live game.
              if (window.hideOnboardingLogin) window.hideOnboardingLogin();
              resolveLaunch();
            }, 400);
          } else {
            if (errorEl) errorEl.innerText = 'Authentication failed.';
            submitBtn.disabled = false;
            submitBtn.innerText = isRegisterMode ? 'Create Account & Play' : 'Log In & Play';
          }
        } catch (err) {
          if (errorEl) errorEl.innerText = err.message || 'Connection failed.';
          submitBtn.disabled = false;
          submitBtn.innerText = isRegisterMode ? 'Create Account & Play' : 'Log In & Play';
        }
      });
    }
    
    // Allow 'Enter' key to submit
    const triggerSubmitOnEnter = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (submitBtn && !submitBtn.disabled) submitBtn.click();
      }
    };
    [usernameInput, emailInput, passwordInput].forEach(input => {
      if (input) input.addEventListener('keydown', triggerSubmitOnEnter);
    });
  };

  // Exposed because this used to be reachable ONLY from the splash "Start Game"
  // handler below, so any other way of showing the card produced a fully dead one.
  // See the call in showOnboardingLogin().
  window.setupOnboardingCard = setupOnboardingCard;

  // Show Start button and wait for user click to enable audio
  const splashStartBtn = document.getElementById('btn-splash-start');
  splashStartBtn.classList.remove('hidden');
  
  await new Promise(resolve => {
    splashStartBtn.addEventListener('click', () => {
      splashStartBtn.classList.add('hidden');
      // Start music via Web Audio inside this gesture (resumes the context).
      playIntroMusic();
      
      // REQUIRE REGISTRATION TO PLAY WALL
      if (authToken) {
        // Player already logged in, enter directly
        resolve();
      } else {
        // Halt and display onboarding registration/login block
        const onboardingCard = document.getElementById('onboarding-card');
        if (onboardingCard) {
          onboardingCard.classList.remove('hidden');
          onboardingCard.style.opacity = '1';
          onboardingCard.style.transform = 'translate(-50%, -50%)';
          
          // Smoothly play and fade in the premium background video and overlay under the login card
          const loginVideo = document.getElementById('login-success-video');
          if (loginVideo) {
            loginVideo.style.opacity = '1';
            loginVideo.play().catch(e => console.log('Video autoplay blocked:', e));
          }
          const loginOverlayEl = document.getElementById('login-video-overlay');
          if (loginOverlayEl) {
            loginOverlayEl.style.opacity = '1';
          }
        }
        window.resolveOnboardingLaunch = resolve;
        setupOnboardingCard(resolve);
      }
    });
  });

  if (intro) {
    intro.onCornerReach = () => {
      // Reveal DOM header logo instantly (no opacity fade) when canvas logo reaches corner
      document.querySelector('.game-logo').classList.add('visible');
      // Fade out loading overlay background to reveal map level view underneath
      loadingOverlay.style.transition = 'opacity 0.6s ease-out';
      loadingOverlay.style.opacity = '0';
      loadingOverlay.style.pointerEvents = 'none';
    };

    intro.start(() => {
      loadingOverlay.style.display = 'none';
      const zControls = document.getElementById('zoom-controls');
      if (zControls) zControls.classList.remove('hidden');
    });
    // Wait for the full intro sequence to finish before starting gameplay
    await new Promise(resolve => setTimeout(resolve, 2900));
  } else {
    document.querySelector('.game-logo').classList.add('visible');
    loadingOverlay.style.display = 'none';
    const zControls = document.getElementById('zoom-controls');
    if (zControls) zControls.classList.remove('hidden');
  }

  // Generate Map (respecting player's saved Startup Map Difficulty settings).
  // NOTE: engine.settings isn't hydrated from localStorage until later in init
  // (see the loadSettings call further down), so reading engine.settings here
  // would always see the default 'random'. Read the saved preference directly.
  let startDiff = engine.settings.firstLoadDifficulty || 'random';
  try {
    const username = localStorage.getItem('parade_username');
    const settingsKey = username ? `parade_settings_${username}` : 'parade_settings';
    const savedSettings = JSON.parse(localStorage.getItem(settingsKey)) || JSON.parse(localStorage.getItem('parade_settings'));
    if (savedSettings && savedSettings.firstLoadDifficulty) startDiff = savedSettings.firstLoadDifficulty;
  } catch (e) {}
  if (startDiff === 'random') {
    const difficulties = ['Easy', 'Medium', 'Hard', 'Expert', 'Master'];
    const randomDiff = difficulties[Math.floor(Math.random() * difficulties.length)];
    engine.gameState.campaignTargetDifficulty = randomDiff;
    map.generateRandomMap(null, randomDiff);
  } else {
    engine.gameState.campaignTargetDifficulty = startDiff;
    map.generateRandomMap(null, startDiff);
  }
  engine.centerCamera();
  
  // Yield thread to allow DOM paint
  await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

  // Spawn some test entities to verify 2.5D sorting
  
  // Center of screen
  const cx = engine.virtualWidth / 2;
  const cy = engine.virtualHeight / 2;

  // Initialize Towers
  // (Removed hardcoded towers)
  // Reset Level function
  const resetLevel = () => {
    engine.gameState.wave = 1;
    engine.gameState.lives = 10;
    engine.gameState.money = currentLevelKey ? startMoney() : BASE_MONEY;
    engine.gameState.score = 0;
    engine.gameState.waveActive = false;
    engine.gameState.status = 'playing';
    engine.gameState.isEndlessMode = false;
    { const endBtn = document.getElementById('btn-end-endless'); if (endBtn) endBtn.classList.add('hidden'); } // no longer in an endless run
    runContinues = 0; // fresh run → continue-cost escalation resets
    engine.gameState.victoryComplete = false; // [#25] otherwise confetti fires instantly on the next win
    engine.gameState.floatSpawns = [];
    engine.gameState.balloonSpawns = [];
    
    // Clear all dynamic entities; scenery and building facades are safely preserved in staticEntities
    engine.entities = [];
    
    // Resume engine if paused from game over
    engine.isPaused = false;
    document.getElementById('game-over-modal').classList.add('hidden');
    document.getElementById('victory-modal').classList.add('hidden');
    const endlessModal = document.getElementById('endless-summary-modal');
    if (endlessModal) endlessModal.classList.add('hidden');
    document.getElementById('btn-start').innerHTML = `▶️ Start Wave 1 <span id="wave-countdown" style="opacity: 0.7; font-size: 0.9em; margin-left: 8px;"></span>`;
    document.getElementById('btn-start').style.display = 'block';
    // Clear campaign variables if this is a generic reset (e.g. from the start button / retry out of campaign)
    // Wait, the campaign modal sets these before calling resetLevel(). So we shouldn't clear them here unconditionally.
    // Instead we need a dedicated exit campaign button or logic. Or we can clear it when clicking the generic "Reset Game" button in the menu (if there is one). Let's see if the logic to clear the watermark triggers properly.
    
    // Regenerate map. Campaign levels use their pinned seed so a retry replays
    // the SAME curated map; other flows regenerate based on the forceReplaySeed flag
    // or the target difficulty.
    if (engine.gameState.campaignLevel && engine.gameState.campaignSeed) {
      map.generateRandomMap(engine.gameState.campaignSeed, null);
    } else if (engine.gameState.forceReplaySeed && map.seed) {
      // Replay/retry the EXACT level just played: pass both the seed AND the
      // difficulty that shaped it. A seed makes generation deterministic (the
      // difficulty-matching retry loop is skipped), and re-supplying the original
      // targetDifficulty reproduces any difficulty-driven path shaping, so the
      // layout is byte-for-byte the same map — not a fresh random one.
      map.generateRandomMap(map.seed, map.targetDifficulty || null);
    } else {
      const targetDiff = engine.gameState.campaignTargetDifficulty || null;
      map.generateRandomMap(null, targetDiff);
    }
    engine.gameState.forceReplaySeed = false; // Reset the flag
    engine.centerCamera();
  };

  // Bind Reset Buttons. "Retry Level" (after a loss) must replay the SAME map the
  // player just lost — not roll a brand-new random level — so it forces a seed replay
  // exactly like the victory "Replay Level" button.
  document.getElementById('btn-retry-level').addEventListener('click', () => {
    engine.gameState.forceReplaySeed = true;
    resetLevel();
  });
  
  // Replay Level Action
  document.getElementById('btn-replay-level').addEventListener('click', () => {
    engine.gameState.forceReplaySeed = true;
    resetLevel();
  });

  // Play On (Endless Mode) Action
  const btnPlayOn = document.getElementById('btn-play-on');
  if (btnPlayOn) {
    btnPlayOn.addEventListener('click', () => {
      document.getElementById('victory-modal').classList.add('hidden');
      engine.gameState.isEndlessMode = true;
      engine.gameState.status = 'playing';
      engine.gameState.victoryComplete = false;
      engine.isPaused = false;

      // Open a fresh server session so each endless wave earns a capped money+XP bonus.
      startEndlessSession();
      // Surface the visible "End Run & Collect" button so ending the run isn't buried in Settings.
      const endBtn = document.getElementById('btn-end-endless');
      if (endBtn) endBtn.classList.remove('hidden');

      // Increment wave to start Endless Wave 6+ (maxWave + 1)
      if (engine.gameState.wave <= waves.length) {
        engine.gameState.wave = waves.length + 1;
      }
      
      // Auto-trigger next wave
      const startBtn = document.getElementById('btn-start');
      if (startBtn) {
        startBtn.style.display = 'block';
        startBtn.click();
      }
    });
  }

  // Return to Map Select from Endless Mode Summary Modal
  const btnEndlessExit = document.getElementById('btn-endless-exit');
  if (btnEndlessExit) {
    btnEndlessExit.addEventListener('click', () => {
      document.getElementById('endless-summary-modal').classList.add('hidden');
      resetLevel();
    });
  }

  // Next Level / Play Another Action
  document.getElementById('btn-next-level').addEventListener('click', () => {
    if (engine.gameState.campaignLevel) {
      const currentCampLvl = engine.gameState.campaignLevel;
      if (currentCampLvl < 5) {
        // Load the next campaign level!
        const nextCampId = currentCampLvl + 1;
        const nextLvl = campaignLevels.find(l => l.id === nextCampId);
        if (nextLvl) {
          currentLevelKey = 'campaign:' + nextLvl.id;
          engine.gameState.campaignLevel = nextLvl.id;
          engine.gameState.campaignTargetDifficulty = nextLvl.diff;
          engine.gameState.campaignSeed = nextLvl.seed;
        }
      } else {
        // Finished campaign level 5! Transition to Custom Mode based on preferred difficulty
        engine.gameState.campaignLevel = null;
        engine.gameState.campaignSeed = null;
        currentLevelKey = null;
        const pref = engine.settings.firstLoadDifficulty || 'random';
        engine.gameState.campaignTargetDifficulty = (pref === 'random' ? null : pref);
      }
    } else {
      // Not in campaign mode. Setup next level using Startup Preference
      engine.gameState.campaignLevel = null;
      engine.gameState.campaignSeed = null;
      currentLevelKey = null;
      const pref = engine.settings.firstLoadDifficulty || 'random';
      engine.gameState.campaignTargetDifficulty = (pref === 'random' ? null : pref);
    }
    
    engine.gameState.forceReplaySeed = false;
    resetLevel();
  });

  // Bind Start Button
  document.getElementById('btn-start').addEventListener('click', () => {
    engine.gameState.waveCountdown = 0;
    const countdownSpan = document.getElementById('wave-countdown');
    if (countdownSpan) countdownSpan.innerText = '';
    
    if ((engine.gameState.wave > waves.length && !engine.gameState.isEndlessMode) || engine.gameState.status === 'lost') {
      resetLevel();
      return; // Wait for them to click start again for the new game
    }
    
    if (!engine.gameState.waveActive && (engine.gameState.wave <= waves.length || engine.gameState.isEndlessMode)) {
      engine.gameState.waveActive = true;
      engine.gameState._waveStallT = 0; engine.gameState._waveAliveCount = -1; // reset stall watchdog

      // Snapshot the run's starting resources at the first wave
      if (engine.gameState.wave === 1) {
        engine.gameState.startLivesSnapshot = Math.floor(engine.gameState.lives);
        engine.gameState.startPermitsSnapshot = Math.floor(engine.gameState.money);
        startLevelSession(); // request a server-authoritative reward session (flagged)
      }

      pauseIntroMusic(); // Stop intro music when level starts
      
      // Resume AudioContext if suspended
      if (engine.audioCtx && engine.audioCtx.state !== 'running') {
        engine.audioCtx.resume();
        
        const buffer = engine.audioCtx.createBuffer(1, 1, engine.audioCtx.sampleRate);
        const source = engine.audioCtx.createBufferSource();
        source.buffer = buffer;
        source.connect(engine.audioCtx.destination);
        source.start(0);
      }

      let waveConfig = waves[engine.gameState.wave - 1];
      if (!waveConfig && engine.gameState.isEndlessMode) {
        const extraWave = engine.gameState.wave - waves.length; // 1, 2, 3...
        const hpMult = Math.pow(1.15, extraWave);
        const floatCount = 6 + Math.min(16, extraWave * 2);
        const balloonCount = 10 + Math.min(30, extraWave * 3);
        // Use REAL sprite types (the old list — basic_clown, dragon_head… — didn't exist,
        // so endless floats were invisible) and always set a spacing (a missing spacing
        // made floatSpawnTimer NaN, which stalled spawning after the first float and hung
        // the whole endless wave). Difficulty ramps via count, density, and hpMult.
        const floatTypes = ['turkey', 'corn', 'star', 'band', 'pumpkin'];
        const balloonTypes = ['hero_balloon', 'speedy_turkey', 'detective_dog', 'dragon', 'electric_fox'];
        const floatSpacing = Math.max(1.2, 3.0 - extraWave * 0.1);
        const balloonSpacing = Math.max(0.5, 1.1 - extraWave * 0.03);
        const composition = [];
        for (let i = 0; i < floatCount; i++)   composition.push({ type: floatTypes[i % floatTypes.length],   isFloat: true,  count: 1, spacing: floatSpacing,   hpMult });
        for (let i = 0; i < balloonCount; i++) composition.push({ type: balloonTypes[i % balloonTypes.length], isFloat: false, count: 1, spacing: balloonSpacing, hpMult });
        waveConfig = composition;
      }

      engine.gameState.floatSpawns = [];
      engine.gameState.balloonSpawns = [];
      
      // Flatten wave config into a queue of spawns
      let waveFloats = [];
      let waveBalloons = [];
      for (const group of waveConfig) {
        for (let i = 0; i < group.count; i++) {
          if (group.isFloat) {
            waveFloats.push({ type: group.type, isFloat: group.isFloat, spacing: group.spacing, hpMult: group.hpMult });
          } else {
            waveBalloons.push({ type: group.type, isFloat: group.isFloat, spacing: group.spacing, hpMult: group.hpMult });
          }
        }
      }
      
      // Shuffle floats
      for (let i = waveFloats.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [waveFloats[i], waveFloats[j]] = [waveFloats[j], waveFloats[i]];
      }
      
      // Shuffle balloons
      for (let i = waveBalloons.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [waveBalloons[i], waveBalloons[j]] = [waveBalloons[j], waveBalloons[i]];
      }

      // ── BOSS ROUND ──────────────────────────────────────────────────────────────
      // Every wave ends with a boss, appended AFTER the shuffle so it always arrives LAST as
      // the finale. HP scales with the wave number (fair early, brutal late; keeps scaling
      // into endless). The roster rotates by wave — only the Oaf so far.
      {
        const BOSS_ROTATION = ['happy_green_oaf']; // add 'mayor_fuzz', 'santa' when built
        const waveNum = engine.gameState.wave;     // 1-based
        const bossType = BOSS_ROTATION[(waveNum - 1) % BOSS_ROTATION.length];
        const bossHpMult = 0.4 + (waveNum - 1) * 0.16; // wave 1 ≈ 0.4×, grows ~0.16/wave
        waveBalloons.push({ type: bossType, isFloat: false, spacing: 2.5, hpMult: bossHpMult, isBoss: true });
      }

      engine.gameState.floatSpawns = waveFloats;
      engine.gameState.balloonSpawns = waveBalloons;
      engine.gameState.floatSpawnTimer = 0.5; // Floats start almost immediately
      engine.gameState.balloonSpawnTimer = 4.0; // Balloons start slightly delayed after the floats
      
      document.getElementById('btn-start').style.display = 'none';
    }
  });

  // Bind Context Menu Buttons
  const ctxGhostClick = () => (Date.now() - (window._ctxOpenedAt || 0) < 600); // swallow the post-tap synthesized click
  for (let i = 1; i <= 5; i++) {
    const btn = document.getElementById(`btn-action-lvl${i}`);
    if (!btn) continue;
    btn.addEventListener('click', () => {
      if (ctxGhostClick()) return; // ignore the synthesized click right after tap-to-select
      if (!selectedTower) return;
      const info = window.TOWER_INFO[selectedTower.type];
      if (!info) return;

      if (i === selectedTower.level + 1) {
        // Upgrade
        const cost = info.costs[selectedTower.level];
        if (engine.gameState.money >= cost) {
          engine.gameState.money -= cost;
          selectedTower.upgrade();
          hideContextMenu();
        }
      } else if (i === selectedTower.level - 1) {
        // Downgrade
        const refund = info.costs[i];
        engine.gameState.money += refund;
        selectedTower.downgrade();
        hideContextMenu();
      }
    });
  }

  document.getElementById('btn-sell').addEventListener('click', () => {
    if (ctxGhostClick()) return;
    if (selectedTower) {
      const info = window.TOWER_INFO[selectedTower.type];
      const totalInvested = info ? info.costs.slice(0, selectedTower.level).reduce((sum, c) => sum + c, 0) : (40 * selectedTower.level);
      const refund = Math.floor(totalInvested * 0.6);
      engine.gameState.money += refund;
      selectedTower.sell();
      selectedTower = null;
      hideContextMenu();
      cameraMode = 'free';
      trackedEntity = null;
      updateCameraModeUI();
    }
  });

  document.getElementById('btn-move').addEventListener('click', () => {
    if (ctxGhostClick()) return;
    if (selectedTower && selectedTower.type === 'street_sweeper') {
      const maxMoves = (getTowerCfg(engine, 'street_sweeper').maxMoves || [2, 4, 6])[selectedTower.level - 1] || 2;
      const remaining = Math.max(0, maxMoves - (selectedTower.movesUsed || 0));
      if (remaining > 0) {
        window.isRelocatingSweeper = selectedTower;
        hideContextMenu();
        showMusicToaster("🚚 Relocation Mode", "Tap any tile on the map to drive the Sweeper there!");
      }
    }
  });

  document.getElementById('btn-close-ctx').addEventListener('click', () => {
    selectedTower = null;
    hideContextMenu();
  });

  // Game Controls Interception for Start Wave Button to replace system emojis with beautiful custom SVG
  const btnStartEl = document.getElementById('btn-start');
  if (btnStartEl) {
    const originalDescriptor = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML');
    Object.defineProperty(btnStartEl, 'innerHTML', {
      set(val) {
        const playIcon = `<svg class="start-wave-svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style="display: inline-block; vertical-align: middle; margin-right: 8px; filter: drop-shadow(0 1px 2px rgba(0,0,0,0.3));"><polygon points="6 4 20 12 6 20 6 4"></polygon></svg>`;
        let cleaned = val.replace('▶️', '').replace('▶️ ', '');
        originalDescriptor.set.call(this, `${playIcon}${cleaned}`);
      },
      get() {
        return originalDescriptor.get.call(this);
      }
    });

    const originalTextDescriptor = Object.getOwnPropertyDescriptor(HTMLElement.prototype, 'innerText');
    Object.defineProperty(btnStartEl, 'innerText', {
      set(val) {
        this.innerHTML = val;
      }
    });
  }

  // Route schematic reveal button
  const btnRevealRoute = document.getElementById('btn-reveal-route');
  if (btnRevealRoute) {
    btnRevealRoute.addEventListener('click', async (e) => {
      e.stopPropagation();
      const diffLabel = engine.gameState.difficultyLabel || 'Medium';
      const price = SCHEMATIC_PRICES[diffLabel] || 250;
      
      const key = routeItem(); // entitlement tied to the map's seed (permanent)
      const revealUI = () => {
        routeRevealedThisRound = true;
        // Belt-and-suspenders: guarantee the current map reads as owned locally the
        // instant we reveal, so the render loop's per-frame ownsCurrentRoute() check
        // can never re-lock it — independent of what walletTx recorded.
        if (key) { ownedItems.add(key); persistOwned(); }
        const popoverCanvas = document.getElementById('popover-blueprint-canvas');
        if (popoverCanvas) {
          popoverCanvas.style.filter = 'none';
          // Repaint the route immediately rather than waiting for the render loop:
          // the loop is halted while paused, and the canvas may still be blank if
          // the popover was opened before it first drew. Also stamp renderedGen so
          // the loop treats it as up-to-date and doesn't redraw a locked version.
          try {
            const ctx = popoverCanvas.getContext('2d');
            if (ctx && typeof map !== 'undefined' && map && map.gridWidth) {
              drawRouteBlueprint(ctx, buildLiveBlueprint(map, engine), popoverCanvas.width, popoverCanvas.height);
              popoverCanvas.dataset.renderedGen = String(map.generationId || map.seed || '');
            }
          } catch (e) { console.error('route reveal redraw failed:', e); }
        }
        const lockOverlay = document.getElementById('popover-blueprint-lock');
        if (lockOverlay) lockOverlay.classList.add('hidden');
        // Pin the popover open so the just-revealed route is visible immediately.
        // On touch the tooltip only showed via emulated :hover, which is lost the
        // moment the confirm dialog takes the tap — so without pinning it vanishes.
        const diffContainer = document.getElementById('ui-difficulty-container');
        if (diffContainer) {
          diffContainer.classList.add('active');
          // One-shot: dismiss the pin on the next tap/click outside the popover.
          const dismiss = (ev) => {
            if (diffContainer.contains(ev.target)) return; // taps inside keep it open
            diffContainer.classList.remove('active');
            document.removeEventListener('pointerdown', dismiss, true);
          };
          // Defer so the current tap that confirmed the purchase doesn't instantly close it.
          setTimeout(() => document.addEventListener('pointerdown', dismiss, true), 0);
        }
      };
      // Already own this map's route (by seed) → just reveal it, no charge, no prompt.
      if (ownsItem(key)) { revealUI(); return; }

      showConfirm('Reveal Route', `Spend $${price} to permanently unlock the route schematic for this map (Seed #${map.seed})?`, async () => {
        // One atomic, idempotent purchase keyed by the map's seed: it charges AND
        // grants the entitlement in the same commit. Buying an already-owned seed
        // returns owned:true / charged:false, so you can never be charged twice.
        const r = await walletTx('purchase', price, key);
        if (r.ok) {
          revealUI();
          if (window.showMusicToaster) {
            window.showMusicToaster('🔓 Route Unlocked',
              r.charged === false
                ? 'You already own this route — no charge.'
                : 'This route is now permanently unlocked for this map!');
          }
        } else {
          showAlert(r.error && /insufficient/i.test(r.error) ? 'Insufficient Funds' : 'Error',
            r.error || 'Transaction failed. Please try again.');
        }
      });
    });
  }

  // Game Controls
  const btnPause = document.getElementById('btn-pause');
  const btnPlay = document.getElementById('btn-play');

  const syncPausePlayButtons = () => {
    if (!btnPause || !btnPlay) return;
    if (engine.isPaused) {
      btnPause.classList.add('hidden');
      btnPlay.classList.remove('hidden');
    } else {
      btnPause.classList.remove('hidden');
      btnPlay.classList.add('hidden');
    }
  };

  let wasRadioPlayingOnPause = false;

  // The pause button now opens a central pause MENU (Resume / Restart / Quit, plus
  // End Run & Collect during an endless run) instead of just freezing the game.
  const pauseModal = document.getElementById('pause-modal');

  const openPauseModal = () => {
    engine.isPaused = true;
    syncPausePlayButtons();
    pauseThemeMusic();
    if (window.paradeMusic && window.paradeMusic.playing) { wasRadioPlayingOnPause = true; window.paradeMusic.pause(); }
    else { wasRadioPlayingOnPause = false; }
    // The endless "End Run & Collect" option only makes sense during a Play On run.
    const pe = document.getElementById('btn-pause-endless');
    if (pe) pe.classList.toggle('hidden', !(engine.gameState && engine.gameState.isEndlessMode));
    if (pauseModal) pauseModal.classList.remove('hidden');
  };

  const resumeGame = () => {
    if (pauseModal) pauseModal.classList.add('hidden');
    engine.isPaused = false;
    syncPausePlayButtons();
    if (engine.gameState.waveActive) playThemeMusic();
    if (wasRadioPlayingOnPause && window.paradeMusic) window.paradeMusic.play();
  };

  // Restart the CURRENT level from Wave 1 (moved out of the removed top-bar icon).
  const restartLevel = () => {
    engine.gameState.wave = 1;
    engine.gameState.lives = 10;
    engine.gameState.money = currentLevelKey ? startMoney() : BASE_MONEY;
    engine.gameState.waveActive = false;
    engine.gameState.isEndlessMode = false;
    engine.gameState.campaignLevel = null;
    engine.gameState.campaignTargetDifficulty = null;
    engine.gameState._waveStallT = 0; engine.gameState._waveAliveCount = -1;
    pauseThemeMusic();
    themeMusic.currentTime = 0;
    // Clear all dynamic entities; scenery and building facades stay in staticEntities.
    engine.entities = [];
    document.getElementById('btn-start').style.display = 'block';
    document.getElementById('btn-start').innerText = "▶️ Start Wave 1";
    document.getElementById('btn-start').disabled = false;
    { const eb = document.getElementById('btn-end-endless'); if (eb) eb.classList.add('hidden'); }
    engine.isPaused = false;
    syncPausePlayButtons();
    draggingTowerType = null;
    dragPosLogical = null;
    selectedTower = null;
    engine.isDragging = false;
    hideContextMenu();
  };

  if (btnPause) btnPause.addEventListener('click', openPauseModal);
  if (btnPlay) btnPlay.addEventListener('click', resumeGame);

  if (pauseModal) {
    const C = document.getElementById('btn-pause-close');    if (C) C.addEventListener('click', resumeGame);
    const R = document.getElementById('btn-pause-resume');   if (R) R.addEventListener('click', resumeGame);
    const RS = document.getElementById('btn-pause-restart'); if (RS) RS.addEventListener('click', () => { pauseModal.classList.add('hidden'); restartLevel(); });
    const Q = document.getElementById('btn-pause-quit');     if (Q) Q.addEventListener('click', () => {
      pauseModal.classList.add('hidden');
      const difficulties = ['Easy', 'Medium', 'Hard', 'Expert', 'Master'];
      const randomDiff = difficulties[Math.floor(Math.random() * difficulties.length)];
      engine.gameState.campaignLevel = null;
      engine.gameState.campaignSeed = null;
      engine.gameState.forceReplaySeed = false;
      engine.gameState.campaignTargetDifficulty = randomDiff;
      resetLevel(); // unpauses the engine and generates a fresh random map across all difficulties
      syncPausePlayButtons(); // re-sync the top-bar icon so the new level isn't stuck "paused"
      if (wasRadioPlayingOnPause && window.paradeMusic) window.paradeMusic.play();
    });
    const E = document.getElementById('btn-pause-endless');  if (E) E.addEventListener('click', () => { pauseModal.classList.add('hidden'); if (window.finishEndlessRun) window.finishEndlessRun(); });
  }

  const mapSelectModal = document.getElementById('map-select-modal');
  const btnCloseMapSelect = document.getElementById('btn-close-map-select');

  document.getElementById('btn-new-map').addEventListener('click', () => {
    mapSelectModal.classList.remove('hidden');
  });

  if (btnCloseMapSelect) {
    btnCloseMapSelect.addEventListener('click', () => {
      mapSelectModal.classList.add('hidden');
    });
  }

  // Quick Random Play Handler
  const btnQuickRandom = document.getElementById('btn-quick-random');
  if (btnQuickRandom) {
    btnQuickRandom.addEventListener('click', () => {
      const difficulties = ['Easy', 'Medium', 'Hard', 'Expert', 'Master'];
      const randomDiff = difficulties[Math.floor(Math.random() * difficulties.length)];
      mapSelectModal.classList.add('hidden');

      currentLevelKey = null; // quick random = practice (no carry-in, no save)
      engine.gameState.wave = 1;
      engine.gameState.lives = 10;
      engine.gameState.money = BASE_MONEY;
      engine.gameState.waveActive = false;
      engine.gameState.campaignLevel = null;
      engine.gameState.campaignTargetDifficulty = randomDiff;
      pauseThemeMusic();
      themeMusic.currentTime = 0;
      
      map.generateRandomMap(null, randomDiff);
      engine.centerCamera();

      document.getElementById('btn-start').style.display = 'block';
      document.getElementById('btn-start').innerHTML = `▶️ Start Wave 1 <span id="wave-countdown" style="opacity: 0.7; font-size: 0.9em; margin-left: 8px;"></span>`;
      document.getElementById('btn-start').disabled = false;
      engine.isPaused = false;
      syncPausePlayButtons();
      
      draggingTowerType = null;
      dragPosLogical = null;
      selectedTower = null;
      engine.isDragging = false;
      hideContextMenu();
    });
  }

  // Dismiss modals by clicking on their background overlay
  [
    document.getElementById('map-select-modal'),
    document.getElementById('course-library-modal'),
    document.getElementById('campaign-modal'),
    document.getElementById('settings-modal'),
    document.getElementById('auth-modal'),
    document.getElementById('leaderboard-modal'),
    document.getElementById('saves-modal'),
    document.getElementById('admin-modal')
  ].forEach(modal => {
    if (modal) {
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          if (modal.id === 'auth-modal' && !authToken) {
            // Force authentication block — do not allow clicking away
            return;
          }
          modal.classList.add('hidden');
        }
      });
    }
  });

  document.querySelectorAll('.diff-select-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const selectedDiff = btn.getAttribute('data-diff');
      mapSelectModal.classList.add('hidden');

      currentLevelKey = null; // difficulty-select random = practice (no carry-in, no save)
      engine.gameState.wave = 1;
      engine.gameState.lives = 10;
      engine.gameState.money = BASE_MONEY;
      engine.gameState.waveActive = false;
      engine.gameState.campaignLevel = null;
      engine.gameState.campaignTargetDifficulty = selectedDiff;
      pauseThemeMusic();
      themeMusic.currentTime = 0;
      
      map.generateRandomMap(null, selectedDiff);
      engine.centerCamera();

      document.getElementById('btn-start').style.display = 'block';
      document.getElementById('btn-start').innerHTML = `▶️ Start Wave 1 <span id="wave-countdown" style="opacity: 0.7; font-size: 0.9em; margin-left: 8px;"></span>`;
      document.getElementById('btn-start').disabled = false;
      engine.isPaused = false;
      syncPausePlayButtons();
      
      draggingTowerType = null;
      dragPosLogical = null;
      selectedTower = null;
      engine.isDragging = false;
      hideContextMenu();
    });
  });

  const btnLoadDeck = document.getElementById('btn-load-deck');
  if (btnLoadDeck) {
    btnLoadDeck.addEventListener('click', () => {
      const seedInput = document.getElementById('deck-seed-input');
      let seedVal = parseInt(seedInput.value);
      if (isNaN(seedVal) || seedVal < 1) seedVal = 101;
      if (seedVal > 9999) seedVal = 9999;
      seedInput.value = seedVal; // sanitize UI input

      mapSelectModal.classList.add('hidden');

      currentLevelKey = 'seed:' + seedVal; // explicit seed = progression level
      engine.gameState.wave = 1;
      engine.gameState.lives = 10;
      engine.gameState.money = startMoney();
      engine.gameState.waveActive = false;
      engine.gameState.campaignLevel = null;
      engine.gameState.campaignTargetDifficulty = null;
      pauseThemeMusic();
      themeMusic.currentTime = 0;

      // Load seed directly
      map.generateRandomMap(seedVal, null);
      engine.centerCamera();

      document.getElementById('btn-start').style.display = 'block';
      document.getElementById('btn-start').innerHTML = `▶️ Start Wave 1 <span id="wave-countdown" style="opacity: 0.7; font-size: 0.9em; margin-left: 8px;"></span>`;
      document.getElementById('btn-start').disabled = false;
      engine.isPaused = false;
      syncPausePlayButtons();
      
      draggingTowerType = null;
      dragPosLogical = null;
      selectedTower = null;
      engine.isDragging = false;
      hideContextMenu();
    });
  }

  const courseLibraryModal = document.getElementById('course-library-modal');
  const btnCourseLibrary = document.getElementById('btn-course-library');
  const btnCloseCourses = document.getElementById('btn-close-courses');
  const coursesGrid = document.getElementById('courses-grid');

  const renderCourses = (force = false) => {
    if (coursesRendered && !force) return;   // cached once per load unless forced
    coursesRendered = true;

    const introEl = document.getElementById('courses-intro-text');
    if (introEl) introEl.textContent = `Browse our collection of ${PRECOMPUTED_COURSES.length} pre-computed courses, complete with real-time tactical radar blueprints!`;

    coursesGrid.innerHTML = '';
    PRECOMPUTED_COURSES.forEach(course => {
      // Compute the actual layout and difficulty dynamically based on the seed
      const computedDiff = course.difficulty;
      const blueprint = map.getLayoutBlueprint(course.seed, computedDiff);
      
      const card = document.createElement('div');
      card.className = `glass-panel course-card filter-${computedDiff}`;
      card.style.cssText = "display: flex; gap: 15px; padding: 12px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1); background: rgba(0,0,0,0.45); transition: transform 0.2s, border-color 0.2s;";
      
      // Color coded tag
      let diffColor = '#4caf50';
      if (computedDiff === 'Medium') diffColor = '#2196f3';
      if (computedDiff === 'Hard') diffColor = '#ff9800';
      if (computedDiff === 'Expert') diffColor = '#f44336';
      if (computedDiff === 'Master') diffColor = '#9c27b0';

      // Saved per-level progress (stars + best score), if the player has completed it.
      const prog = levelProgress['seed:' + course.seed];
      let progressHtml = '';
      if (prog && prog.completed) {
        let s = '';
        for (let i = 0; i < 3; i++) s += i < (prog.stars || 0) ? '★' : '☆';
        progressHtml = `<div style="font-size:0.9em; color:#FFD700; letter-spacing:2px;">${s} <span style="color:#4ade80; font-size:0.75em; letter-spacing:0; margin-left:4px;">Best: ${(prog.best_score || 0).toLocaleString()}</span></div>`;
      }

      card.innerHTML = `
        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px;">
          <canvas class="blueprint-thumb-pregenerated" data-seed="${course.seed}" width="110" height="70" style="background:#050d1a; border:1px solid rgba(255,255,255,0.1); border-radius:4px; box-shadow:0 0 5px rgba(0,0,0,0.5);"></canvas>
          <div style="display: flex; align-items: center; justify-content: center; gap: 4px; font-size: 0.65em; color: rgba(255,255,255,0.45); font-weight: bold; width: 110px;" title="Seed #${course.seed}">
            <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; text-align: center;">Seed #${course.seed}</span>
            <span style="display: inline-flex; align-items: center; justify-content: center; width: 12px; height: 12px; min-width: 12px; border-radius: 50%; border: 1px solid rgba(255,255,255,0.2); font-size: 8px; cursor: help; color: rgba(255,255,255,0.5); font-family: monospace;" title="Nerdy Info: Seed #${course.seed} (This unique serial seed deterministically defines the entire map curve generation!)">i</span>
          </div>
        </div>
        <div style="flex:1; display:flex; flex-direction:column; gap:4px; min-width:0;">
          <div style="display:flex; justify-content:space-between; align-items:center; gap:8px;">
            <h4 style="margin:0; font-size:1.05em; color:#fff; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${course.name}</h4>
            <span style="font-size:0.7em; padding:2px 6px; font-weight:bold; text-transform:uppercase; border-radius:4px; background:rgba(255,255,255,0.1); border:1px solid ${diffColor}; color:${diffColor};">${computedDiff}</span>
          </div>
          ${progressHtml}
          <p style="margin:0; font-size:0.75em; color:#bbb; line-height:1.25; display:-webkit-box; -webkit-line-clamp:3; -webkit-box-orient:vertical; overflow:hidden; text-overflow:ellipsis;">${course.flavor}</p>
          <button class="btn btn-action-card play-course-btn" data-seed="${course.seed}" data-difficulty="${computedDiff}" style="width:100%; padding:4px; font-size:11px; margin-top:auto; background:${diffColor}; color:white; border:none; font-weight:bold; text-transform:uppercase;">Play Course</button>
        </div>
      `;
      coursesGrid.appendChild(card);

      // Draw the blueprint immediately (isometric, matching the board orientation)
      const canvas = card.querySelector('.blueprint-thumb-pregenerated');
      const ctx = canvas.getContext('2d');
      if (ctx) drawRouteBlueprint(ctx, blueprint, canvas.width, canvas.height);
    });

    // Attach play course button handlers
    document.querySelectorAll('.play-course-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const seed = parseInt(btn.getAttribute('data-seed'));
        const difficulty = btn.getAttribute('data-difficulty') || null;
        courseLibraryModal.classList.add('hidden');

        currentLevelKey = 'seed:' + seed; // progression level: carry wallet in, save results
        engine.gameState.wave = 1;
        engine.gameState.lives = 10;
        engine.gameState.money = startMoney();
        engine.gameState.waveActive = false;
        engine.gameState.campaignLevel = null;
        engine.gameState.campaignTargetDifficulty = difficulty;
        pauseThemeMusic();
        themeMusic.currentTime = 0;

        map.generateRandomMap(seed, difficulty);
        engine.centerCamera();

        document.getElementById('btn-start').style.display = 'block';
        document.getElementById('btn-start').innerHTML = `▶️ Start Wave 1 <span id="wave-countdown" style="opacity: 0.7; font-size: 0.9em; margin-left: 8px;"></span>`;
        document.getElementById('btn-start').disabled = false;
        engine.isPaused = false;
        syncPausePlayButtons();
        
        draggingTowerType = null;
        dragPosLogical = null;
        selectedTower = null;
        engine.isDragging = false;
        hideContextMenu();
      });
    });
  };

  // Force a full re-render of every course route preview (they're cached once per
  // page load, so they go stale after a generation change). Callable from anywhere,
  // e.g. the console: window.refreshCoursePreviews()
  window.refreshCoursePreviews = () => {
    renderCourses(true);
    if (typeof window.initializeCourseFilters === 'function') window.initializeCourseFilters();
    return `Re-rendered ${PRECOMPUTED_COURSES.length} course previews.`;
  };

  if (btnCourseLibrary) {
    btnCourseLibrary.addEventListener('click', () => {
      courseLibraryModal.classList.remove('hidden');
      renderCourses();
      if (typeof window.initializeCourseFilters === 'function') {
        window.initializeCourseFilters();
      }
      // Add a one-time "Refresh previews" button to the library header.
      if (!document.getElementById('btn-refresh-previews')) {
        const introEl = document.getElementById('courses-intro-text');
        const host = (introEl && introEl.parentElement) || coursesGrid.parentElement;
        if (host) {
          const rb = document.createElement('button');
          rb.id = 'btn-refresh-previews';
          rb.textContent = '↻ Refresh previews';
          rb.title = 'Re-render all route schematics from the current generator';
          rb.style.cssText = 'margin:6px auto;display:block;background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.18);color:#fff;border-radius:8px;padding:6px 14px;font:12px system-ui;cursor:pointer;';
          rb.onclick = () => { rb.textContent = '↻ Refreshing…'; setTimeout(() => { window.refreshCoursePreviews(); rb.textContent = '↻ Refresh previews'; }, 20); };
          host.insertBefore(rb, coursesGrid);
        }
      }
    });
  }

  if (btnCloseCourses) {
    btnCloseCourses.addEventListener('click', () => {
      courseLibraryModal.classList.add('hidden');
    });
  }

  // Filter tab triggers & dynamic count updates
  document.querySelectorAll('#course-library-modal .filter-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      document.querySelectorAll('#course-library-modal .filter-tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');

      const filter = tab.getAttribute('data-filter');
      document.querySelectorAll('.course-card').forEach(card => {
        if (filter === 'All' || card.classList.contains(`filter-${filter}`)) {
          card.style.display = 'flex';
        } else {
          card.style.display = 'none';
        }
      });
    });
  });

  window.initializeCourseFilters = () => {
    document.querySelectorAll('#course-library-modal .filter-tab').forEach(tab => {
      const filter = tab.getAttribute('data-filter');
      let count = 0;
      if (filter === 'All') {
        count = PRECOMPUTED_COURSES.length;
      } else {
        count = PRECOMPUTED_COURSES.filter(c => c.difficulty === filter).length;
      }
      const labelMap = {
        'All': `${GameIcons.get('map', 14, 'margin-right:4px; vertical-align:middle;')} All`,
        'Easy': `${GameIcons.get('easy', 14, 'margin-right:4px; vertical-align:middle;')} Easy`,
        'Medium': `${GameIcons.get('medium', 14, 'margin-right:4px; vertical-align:middle;')} Medium`,
        'Hard': `${GameIcons.get('hard', 14, 'margin-right:4px; vertical-align:middle;')} Hard`,
        'Expert': `${GameIcons.get('expert', 14, 'margin-right:4px; vertical-align:middle;')} Expert`,
        'Master': `${GameIcons.get('master', 14, 'margin-right:4px; vertical-align:middle;')} Master`
      };
      tab.innerHTML = `<span style="display:inline-flex; align-items:center; gap:2px; vertical-align:middle;">` + (labelMap[filter] || filter) + `</span> <span style="font-size:0.85em; opacity:0.8; font-weight:normal;">(${count})</span>`;
    });
  };

  // Premium Unified Camera Controls State Sync
  updateCameraModeUI = () => {
    const isAutoCam = (cameraMode === 'action' || cameraMode === 'tracking' || cameraMode === 'tower');
    const camBtn = document.querySelector('.camera-toggle-btn');
    if (camBtn) {
      camBtn.classList.toggle('active', isAutoCam);
      camBtn.title = cameraMode === 'action' ? 'Switch to Free Camera (Manual)' : 'Switch to Action Camera (Follow Mode)';
    }
  };



  // Settings Modal Logic
  const settingsModal = document.getElementById('settings-modal');
  document.getElementById('btn-settings').addEventListener('click', () => {
    settingsModal.classList.remove('hidden');

    // Toggle Finish Endless button visibility
    const finishEndlessBtn = document.getElementById('btn-settings-finish-endless');
    if (finishEndlessBtn) {
      if (engine.gameState.isEndlessMode) {
        finishEndlessBtn.classList.remove('hidden');
      } else {
        finishEndlessBtn.classList.add('hidden');
      }
    }

    // Load current values
    document.getElementById('setting-grid').checked = engine.settings.showGrid;
    document.getElementById('setting-health').checked = engine.settings.showHealth;
    document.getElementById('setting-demo-mode').checked = engine.settings.demoModeEnabled !== false;
    document.getElementById('setting-pan-speed').value = engine.settings.panSpeed;
    document.getElementById('setting-pan-speed-val').innerText = engine.settings.panSpeed.toFixed(1) + 'x';
    document.getElementById('setting-volume').value = engine.settings.volume;
    document.getElementById('setting-volume-val').innerText = Math.round(engine.settings.volume * 100) + '%';
    document.getElementById('setting-music-volume').value = engine.settings.musicVolume;
    document.getElementById('setting-music-volume-val').innerText = Math.round(engine.settings.musicVolume * 100) + '%';
    document.getElementById('setting-music-mute').checked = engine.settings.musicMuted;
    document.getElementById('setting-sfx-volume').value = engine.settings.sfxVolume;
    document.getElementById('setting-sfx-volume-val').innerText = Math.round(engine.settings.sfxVolume * 100) + '%';
    document.getElementById('setting-sfx-mute').checked = engine.settings.sfxMuted;
    document.getElementById('setting-first-load-diff').value = engine.settings.firstLoadDifficulty || 'random';
  });

  // Finish Endless Run — shared by the Settings button and the visible on-board button.
  window.finishEndlessRun = () => {
    if (settingsModal) settingsModal.classList.add('hidden');
    engine.gameState.status = 'won';
    engine.gameState.waveActive = false;
    engine.isPaused = true;
    document.getElementById('btn-start').style.display = 'none';
    const endBtn = document.getElementById('btn-end-endless');
    if (endBtn) endBtn.classList.add('hidden');

    const bonusWaves = endlessAccrued.waves || Math.max(0, engine.gameState.wave - 1 - (waves.length || 10));
    document.getElementById('endless-stat-waves').innerText = `+${bonusWaves}`;
    document.getElementById('endless-stat-score').innerText = `$${(endlessAccrued.dollars || 0).toLocaleString()}`;
    const xpEl = document.getElementById('endless-stat-xp');
    if (xpEl) xpEl.innerText = (endlessAccrued.xp || 0).toLocaleString();
    document.getElementById('endless-summary-modal').classList.remove('hidden');
  };
  const btnFinishEndless = document.getElementById('btn-settings-finish-endless');
  if (btnFinishEndless) btnFinishEndless.addEventListener('click', () => window.finishEndlessRun());
  const btnEndEndless = document.getElementById('btn-end-endless');
  if (btnEndEndless) btnEndEndless.addEventListener('click', () => window.finishEndlessRun());

  // Quit Level to Menu Action
  const btnQuitLevel = document.getElementById('btn-settings-quit');
  if (btnQuitLevel) {
    btnQuitLevel.addEventListener('click', () => {
      settingsModal.classList.add('hidden');
      resetLevel();
    });
  }



  document.getElementById('btn-close-settings').addEventListener('click', () => {
    settingsModal.classList.add('hidden');
  });

  const syncSettings = () => {
    const username = localStorage.getItem('parade_username');
    const settingsKey = username ? `parade_settings_${username}` : 'parade_settings';
    localStorage.setItem(settingsKey, JSON.stringify(engine.settings));
    localStorage.setItem('parade_settings', JSON.stringify(engine.settings));
    if (authToken) {
      apiFetch('/settings', {
        method: 'POST',
        body: JSON.stringify({ settings: engine.settings })
      }).catch(e => console.error('Failed to sync settings:', e));
    }
  };

  updateMusicVolume = () => {
    // Music now plays through the radio widget (window.paradeMusic), not the old
    // <audio> elements. Master Volume + Mute Music + demo attenuation form a master
    // factor; the MUSIC LEVEL itself is the SAME value as the widget's own slider
    // (Settings "Music Volume" and the widget slider are one shared knob).
    const w = window.paradeMusic;
    if (!w) return;
    const mult = isDemoMode ? 0.33 : 1.0;
    const masterVol = (engine.settings.volume == null ? 1 : engine.settings.volume);
    const factor = engine.settings.musicMuted ? 0 : mult * masterVol;
    if (w.setMasterFactor) w.setMasterFactor(factor);
    const mv = (engine.settings.musicVolume == null ? 0.7 : engine.settings.musicVolume);
    if (w.setVolume) w.setVolume(mv, true); // silent: don't echo back into Settings
  };

  // The widget's own volume slider writes back here so the two controls stay in sync
  // (they represent the same level). Debounced so a continuous drag doesn't spam the
  // server; the value still lands in engine.settings immediately for correct playback.
  let _musicVolSyncTimer = null;
  window.onWidgetMusicVolume = (v) => {
    if (!window.engine || !engine.settings) return;
    engine.settings.musicVolume = v;
    const el = document.getElementById('setting-music-volume');
    const lab = document.getElementById('setting-music-volume-val');
    if (el) el.value = v;
    if (lab) lab.innerText = Math.round(v * 100) + '%';
    clearTimeout(_musicVolSyncTimer);
    _musicVolSyncTimer = setTimeout(() => syncSettings(), 400);
  };

  window.loadSettings = (settingsData) => {
    if (!settingsData) return;
    engine.settings = { ...engine.settings, ...settingsData };
    // Apply audio settings
    updateMusicVolume();
    applyMute();
    engine.updateSfxMasterGain();
  };

  // Try load from local storage immediately on startup
  try {
    const username = localStorage.getItem('parade_username');
    const settingsKey = username ? `parade_settings_${username}` : 'parade_settings';
    const saved = JSON.parse(localStorage.getItem(settingsKey)) || JSON.parse(localStorage.getItem('parade_settings'));
    if (saved) window.loadSettings(saved);
  } catch(e) {}

  // Update Settings in Realtime
  document.getElementById('setting-grid').addEventListener('change', (e) => {
    engine.settings.showGrid = e.target.checked;
    syncSettings();
  });
  
  document.getElementById('setting-health').addEventListener('change', (e) => {
    engine.settings.showHealth = e.target.checked;
    syncSettings();
  });

  document.getElementById('setting-demo-mode').addEventListener('change', (e) => {
    engine.settings.demoModeEnabled = e.target.checked;
    // If we're currently in a state that the new setting contradicts, correct it now.
    if (!e.target.checked && isDemoMode) stopDemoMode();       // just disabled → leave any running demo
    if (e.target.checked) exitLowPower();                      // just enabled → drop the low-power dim
    resetIdleTimer();                                          // re-arm the idle countdown under the new rule
    syncSettings();
  });

  document.getElementById('setting-first-load-diff').addEventListener('change', (e) => {
    engine.settings.firstLoadDifficulty = e.target.value;
    syncSettings();
  });

  document.getElementById('setting-pan-speed').addEventListener('input', (e) => {
    const val = parseFloat(e.target.value);
    engine.settings.panSpeed = val;
    document.getElementById('setting-pan-speed-val').innerText = val.toFixed(1) + 'x';
    syncSettings();
  });

  document.getElementById('setting-volume').addEventListener('change', (e) => {
    const val = parseFloat(e.target.value);
    engine.settings.volume = val;
    document.getElementById('setting-volume-val').innerText = Math.round(val * 100) + '%';
    updateMusicVolume();
    engine.updateSfxMasterGain();
    syncSettings();
  });

  document.getElementById('setting-music-volume').addEventListener('change', (e) => {
    const val = parseFloat(e.target.value);
    engine.settings.musicVolume = val;
    document.getElementById('setting-music-volume-val').innerText = Math.round(val * 100) + '%';
    updateMusicVolume();
    syncSettings();
  });

  const applyMute = () => {
    updateMusicVolume();                    // music mute flows through the widget master factor
    engine.soundMuted = engine.settings.sfxMuted;
    engine.updateSfxMasterGain();           // apply SFX mute + volume to the live graph
    if (typeof btnSound !== 'undefined' && btnSound) {
      btnSound.classList.toggle('off', engine.settings.sfxMuted);
    }
  };

  // Campaign Gallery Logic
  // Curated, fixed-seed tour. Each seed is pinned so the level is the SAME map
  // every play/retry (deterministic canonical generation), and scores its tier.
  const campaignLevels = [
    { id: 1, title: 'The Easy Stroll',       diff: 'Easy',   seed: 1001, desc: 'A gentle warm-up boulevard with wide sightlines to learn the ropes.' },
    { id: 2, title: 'Midtown March',         diff: 'Medium', seed: 1009, desc: 'The parade picks up — a winding midtown route with a few blind corners.' },
    { id: 3, title: 'Broadway Blitz',        diff: 'Hard',   seed: 1002, desc: 'Tight junctions and cover-heavy blocks test your placement under pressure.' },
    { id: 4, title: 'Expert Expedition',     diff: 'Expert', seed: 1010, desc: 'A punishing, cover-dense network where every tower slot counts.' },
    { id: 5, title: 'The Master\'s Marathon', diff: 'Master', seed: 1006, desc: 'The ultimate gauntlet — relentless pacing and heavy obscurity. Only masters survive.' }
  ];

  const btnCampaign = document.getElementById('btn-campaign');
  const campaignModal = document.getElementById('campaign-modal');
  const btnCloseCampaign = document.getElementById('btn-close-campaign');
  const campaignGrid = document.getElementById('campaign-grid');

  if (btnCampaign) {
    btnCampaign.addEventListener('click', () => {
      renderCampaignGallery();
      campaignModal.classList.remove('hidden');
    });
  }

  if (btnCloseCampaign) {
    btnCloseCampaign.addEventListener('click', () => {
      campaignModal.classList.add('hidden');
    });
  }

  function renderCampaignGallery() {
    campaignGrid.innerHTML = '';
    const unlockedLevel = parseInt(localStorage.getItem('parade_campaign_unlocked') || 1);
    
    campaignLevels.forEach(level => {
      const isLocked = level.id > unlockedLevel;
      const card = document.createElement('div');
      card.className = `campaign-card glass-panel ${isLocked ? 'locked' : ''}`;
      card.style.padding = '15px';
      card.style.borderRadius = '8px';
      card.style.cursor = isLocked ? 'not-allowed' : 'pointer';
      card.style.display = 'flex';
      card.style.justifyContent = 'space-between';
      card.style.alignItems = 'center';
      card.style.position = 'relative';
      card.style.overflow = 'hidden';
      card.style.height = '120px';
      card.style.backgroundImage = `url('sprites/ui/icons/campaign${level.id}.png')`;
      card.style.backgroundSize = 'cover';
      card.style.backgroundPosition = 'center';

      if (isLocked) {
        card.style.filter = 'grayscale(100%) brightness(0.5)';
      } else {
        card.style.border = '2px solid #4a90e2';
        card.onmouseover = () => {
          card.style.transform = 'scale(1.02)';
          card.style.boxShadow = '0 0 15px rgba(74, 144, 226, 0.8)';
        };
        card.onmouseout = () => {
          card.style.transform = 'scale(1)';
          card.style.boxShadow = 'none';
        };
        card.style.transition = 'all 0.2s ease';
      }

      const savedScore = parseInt(localStorage.getItem(`parade_campaign_${level.id}_score`) || 0);
      const savedStars = parseInt(localStorage.getItem(`parade_campaign_${level.id}_stars`) || 0);
      
      let starsHtml = '';
      if (savedStars > 0) {
        for(let i=0; i<3; i++) {
          starsHtml += i < savedStars 
            ? GameIcons.get('stars', 16, 'margin-right:1px; vertical-align:middle;') 
            : `<span style="opacity:0.3; filter:grayscale(1); display:inline-block; vertical-align:middle;">${GameIcons.get('stars', 16, 'margin-right:1px;')}</span>`;
        }
      }
      
      const lockHtml = isLocked 
        ? `<div style="width:28px; height:28px; display:flex; align-items:center; justify-content:center;">${GameIcons.get('locked', 28)}</div>` 
        : `<div style="width:28px; height:28px; display:flex; align-items:center; justify-content:center; opacity:0.85; filter:drop-shadow(0 0 5px rgba(255,255,255,0.4));">${GameIcons.get('unlocked', 28)}</div>`;

      card.innerHTML = `
        <div style="position: absolute; bottom: 0; left: 0; right: 0; padding: 10px; background: linear-gradient(transparent, rgba(0,0,0,0.95));">
          <h3 style="margin:0; color: #fff; text-shadow: 1px 1px 3px black;">${level.id}. ${level.title}</h3>
          <p style="margin:1px 0 0 0; color: #a1c4fd; font-size: 0.85em; font-weight: bold; text-shadow: 1px 1px 2px black;">Difficulty: ${level.diff}</p>
          <p style="margin:2px 0 0 0; color: #ccc; font-size: 0.75em; text-shadow: 1px 1px 2px black; line-height: 1.15; max-width: 85%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${level.desc}</p>
          ${savedStars > 0 ? `<p style="margin:2px 0 0 0; color: #FFD700; font-size: 1.1em; text-shadow: 1px 1px 2px black; display:flex; align-items:center; gap:4px;">${starsHtml} <span style="font-size: 0.8em; color: white;">Score: ${savedScore}</span></p>` : ''}
        </div>
        <div style="position: absolute; top: 10px; right: 15px; text-shadow: 2px 2px 4px black; z-index:5;">
          ${lockHtml}
        </div>
      `;

      if (!isLocked) {
        card.addEventListener('click', () => {
          currentLevelKey = 'campaign:' + level.id; // progression level: carry wallet in, save results
          engine.gameState.campaignLevel = level.id;
          engine.gameState.campaignTargetDifficulty = level.diff;
          engine.gameState.campaignSeed = level.seed; // fixed curated map (deterministic retries)
          resetLevel();
          campaignModal.classList.add('hidden');
        });
      }
      campaignGrid.appendChild(card);
    });
  }

  document.getElementById('setting-music-mute').addEventListener('change', (e) => {
    engine.settings.musicMuted = e.target.checked;
    applyMute();
    syncSettings();
  });

  document.getElementById('setting-sfx-volume').addEventListener('change', (e) => {
    const val = parseFloat(e.target.value);
    engine.settings.sfxVolume = val;
    document.getElementById('setting-sfx-volume-val').innerText = Math.round(val * 100) + '%';
    engine.updateSfxMasterGain();
    syncSettings();
  });

  document.getElementById('setting-sfx-mute').addEventListener('change', (e) => {
    engine.settings.sfxMuted = e.target.checked;
    engine.updateSfxMasterGain();
    syncSettings();
  });

  // Define TOWER_INFO metadata mapping for both the selection tray and the context upgrades menu
  window.TOWER_INFO = {
    'confetti_cannon': { 
      name: 'Confetti Popper', 
      costs: [80, 150, 300],
      flavors: [
        'Standard party blower. Shoots confetti streamers that deal quick single-target damage.',
        'High-velocity blower. Deals twice the damage with a longer range and faster cooldown.',
        'The mother of all parades. Massive area coverage and heavy slowing power.'
      ]
    },
    'street_sweeper': { 
      name: 'Street Sweeper', 
      costs: [130, 150, 300],
      flavors: [
        'A standard motorized brush. Scrubs away balloons on the street.',
        'Upgraded bristle power. Deals more damage over a wider area.',
        'Industrial cleaning unit. Shreds incoming balloons with extreme prejudice.'
      ]
    },
    'marching_band': { 
      name: 'Marching Band', 
      costs: [220, 150, 300],
      flavors: [
        'A small ensemble. Provides a modest attack speed boost to nearby towers.',
        'A full brass section. Increased aura range and stronger speed buffs.',
        'The grand finale. Massive range and an infectious rhythm that supercharges all nearby towers.'
      ]
    },
    'news_chopper': { 
      name: 'News Chopper', 
      costs: [380, 150, 300],
      flavors: [
        'A local news chopper. Patrols the parade route dropping film reels.',
        'Twin-engine broadcast chopper. Patrols faster and drops heavier newsreels.',
        'State-wide media network. Unstoppable aerial assault that tracks the fastest balloons.'
      ]
    },
    'plane': {
      name: 'Banner Plane',
      costs: [100, 200, 300],
      flavors: [
        'Barnstormer. Skywrites "Surrender!" over the parade — enemy balloons hit 10% softer for the whole level.',
        'Stunt plane towing a "Never Surrender!" banner — your floats gain +10% armor (stacks with the balloon debuff).',
        'Airshow jet squadron. Everything above, PLUS periodic formations of 3 that streak in and vaporize the single strongest balloon on the course.'
      ]
    },
    'blimp': {
      name: 'Blimp',
      costs: [365, 150, 300],
      flavors: [
        'Advertising airship. Cruises the parade route dropping leaflets that GLUE enemy balloons — slowing them and weakening their bombs.',
        'Saturation campaign. The glue is so thick balloons FREEZE: they stop advancing and stop attacking entirely.',
        'Full media blitz. Frozen balloons also lose their way, wandering OFF the route — helpless and out of position before finding their way back.'
      ]
    },
    'kickettes': {
      name: 'The Kickettes',
      costs: [450, 150, 300],
      flavors: [
        'Dancing troupe. Kicks balloons back 3 steps along the path.',
        'Improved routine. Kicks balloons back 6 steps.',
        'Showstoppers! Kicks balloons back 9 steps.'
      ]
    },
    'spotlight': {
      name: 'Spotlight',
      costs: [100, 150, 300],
      flavors: [
        'High-intensity arc light. Continuously burns one target over time.',
        'Expanded lens. Burns up to 3 targets simultaneously.',
        'Industrial array. Uncapped multi-target burning power.'
      ]
    },
    'fireboat': {
      name: 'Fireboat',
      costs: [500, 1000, 2000],
      flavors: [
        'Moored fire boat — places ONLY on water. Blasts a water cannon that splashes the target and nearby balloons.',
        'Twin pumps. Longer reach, heavier splash, faster reload.',
        'Full fireboat brigade. Maximum range and a drenching area blast.'
      ]
    },
    'billboard': {
      name: 'Billboard',
      costs: [150, 200, 350, 500, 800],
      flavors: [
        'Plywood signs with crooked hand-painted lettering. Generates passive income ($35) at the end of each wave.',
        'Illuminated with flashing bulbs and spotlight rigs. Generates more wave-end income ($55) and occasionally ($5-$15) attracts crowds.',
        'Large digital display screen. Generates steady wave-time income ($4/sec) and cycles ads to trigger temporary buffs or payout events!',
        'AI-equipped screen that tracks enemy traffic. Income scales with nearby traffic ($6 + $1.5/enemy per sec) and triggers a $75 viral cash blowout!',
        'Holographic megaplex projecting enormous 3D mascots directly over the streets. Generates major wave income ($12/sec) and spawns colossal animated ad campaign buffs!'
      ]
    },
    'lightning_spell': {
      name: 'Lightning Spell',
      costs: [2000],
      flavors: ['Pops up to 6 balloons on the course with a bolt of lightning.']
    },
    'wind_spell': {
      name: 'Wind Spell',
      costs: [2000],
      flavors: ['Slows all balloons and blows 4 of them back.']
    },
    'float_armor_spell': {
      name: 'Float Armor',
      costs: [2500],
      flavors: ['Protects parade floats for 15s with an impenetrable golden shield and repulsor waves.']
    }
  };

  // Sync the purchase/upgrade costs shown in the tray + TOWER_INFO to the loaded
  // tower config (admin overrides merged over TOWER_DEFAULTS). Flavors/names stay.
  applyTowerCostConfig();

  // UI Tower Tray Sliding Drawer Toggle
  const tray = document.getElementById('tower-tray');
  const trayToggle = document.getElementById('tower-tray-toggle');

  // Slide tray open on startup after a tiny delay so the user is WOWed by the sliding animation!
  setTimeout(() => {
    tray.classList.remove('collapsed');
    trayToggle.querySelector('.toggle-icon').innerText = '‹';
  }, 800);

  trayToggle.addEventListener('click', (e) => {
    e.preventDefault();
    tray.classList.toggle('collapsed');
    const isCollapsed = tray.classList.contains('collapsed');
    trayToggle.querySelector('.toggle-icon').innerText = isCollapsed ? '›' : '‹';
    if (isCollapsed) {
      closeTowerInfoPanel();
    }
  });

  // Centralized functions to open/close Tower Info Panel
  window.openTowerInfoPanel = function(type) {
    const info = window.TOWER_INFO[type];
    if (!info) return;

    hideContextMenu(); // Suppress other built tower context panels

    document.getElementById('info-panel-title').innerText = info.name;

    const maxLevels = info.costs.length;

    // Show/hide Level 4 & 5 rows based on tower capacity
    const rowLvl4 = document.getElementById('info-row-lvl4');
    const rowLvl5 = document.getElementById('info-row-lvl5');
    if (rowLvl4) rowLvl4.style.display = maxLevels >= 4 ? 'flex' : 'none';
    if (rowLvl5) rowLvl5.style.display = maxLevels >= 5 ? 'flex' : 'none';

    // Populate levels details
    for (let i = 1; i <= maxLevels; i++) {
      const img = document.getElementById(`info-img-lvl${i}`);
      const costSpan = document.getElementById(`info-cost-lvl${i}`);
      const descPara = document.getElementById(`info-desc-lvl${i}`);
      if (!img || !costSpan || !descPara) continue;

      let spritePath = `sprites/towers/${type}/lvl_${i}.png`;
      if (type === 'kickettes') {
        spritePath = `sprites/towers/kickettes/Kickette_icon.png`;
      } else if (type === 'fireboat') {
        spritePath = `sprites/towers/fireboat/se.png`; // no per-level art — use the regular boat sprite for L1/2/3
      } else if (type === 'blimp') {
        spritePath = `sprites/towers/blimp/blimp${i}_se.png`; // per-level airship art (SE facing as thumbnail)
      } else if (type === 'plane') {
        spritePath = `sprites/towers/plane/plane${i}_e.png`; // per-level plane art (E facing as thumbnail)
      } else if (type === 'spotlight') {
        const spotlightFrame = i === 1 ? '01' : (i === 2 ? '03' : '05');
        spritePath = `sprites/towers/spotlight/spotlight_idle_${spotlightFrame}.png`;
      } else if (type === 'billboard') {
        // Billboard art lives in billboards/tier_{tier}_{slice}.png — use the
        // first slice of each tier as that level's upgrade-menu thumbnail.
        spritePath = `sprites/towers/billboards/tier_${i}_1.png`;
      }
      
      img.src = spritePath;
      costSpan.innerHTML = i === 1 ? `Base Cost: ${PERMIT}${info.costs[0]}` : `Upgrade: ${PERMIT}${info.costs[i - 1]}`;
      descPara.innerText = info.flavors[i - 1];
    }

    // Purchase Button Footer logic
    const btnPurchase = document.getElementById('btn-info-purchase');
    const baseCost = info.costs[0];
    const canAfford = engine.gameState.money >= baseCost;

    if (canAfford) {
      // The label is split into two spans deliberately.  .btn is display:flex and
      // PERMIT is an inline <svg>, so writing this as one string made THREE flex
      // items — "Deploy <name> (", the svg, and "<cost>)" — and the trailing "("
      // got stranded at the end of the first item when the label wrapped, leaving
      // a lone bracket on its own line with the cost pushed away from it.
      // One nowrap span keeps "(icon cost)" together as a single unbreakable item.
      btnPurchase.innerHTML = `<span>Deploy ${info.name}</span><span class="btn-cost-chip">(${PERMIT}${baseCost})</span>`;
      btnPurchase.className = 'btn btn-info-deploy';
      btnPurchase.disabled = false;
    } else {
      btnPurchase.innerHTML = `<span>Insufficient Permits</span><span class="btn-cost-chip">(Need ${PERMIT}${baseCost})</span>`;
      btnPurchase.className = 'btn btn-info-deploy disabled';
      btnPurchase.disabled = true;
    }

    btnPurchase.onclick = (e) => {
      e.preventDefault();
      if (canAfford) {
        draggingTowerType = { type: type, cost: baseCost };
        dragPosLogical = null;
        engine.isDragging = false;
        
        document.querySelectorAll('.tower-btn').forEach(b => b.classList.remove('selected'));
        const matchingBtn = document.querySelector(`.tower-btn[data-type="${type}"]`);
        if (matchingBtn) matchingBtn.classList.add('selected');
        
        // Hide stats card to give the player a clear view of the map for placement
        if (window.closeTowerInfoPanel) window.closeTowerInfoPanel();
      }
    };

    document.getElementById('tower-info-panel').classList.remove('hidden');
  };

  window.closeTowerInfoPanel = function() {
    document.getElementById('tower-info-panel').classList.add('hidden');
    // Clear selection classes on tray buttons when deselecting
    if (!draggingTowerType) {
      document.querySelectorAll('.tower-btn').forEach(b => b.classList.remove('selected'));
    }
  };

  document.getElementById('btn-close-info').addEventListener('click', (e) => {
    e.preventDefault();
    draggingTowerType = null;
    window.closeTowerInfoPanel();
  });

  // UI Tower Selection inside the Tray
  document.querySelectorAll('.tower-btn').forEach(btn => {
    let startX = 0;
    let startY = 0;
    let draggedDistance = 0;
    const type = btn.dataset.type;
    const cost = parseInt(btn.dataset.cost);

    const onMove = (moveEvent) => {
      const clientX = moveEvent.touches ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const clientY = moveEvent.touches ? moveEvent.touches[0].clientY : moveEvent.clientY;
      const dx = clientX - startX;
      const dy = clientY - startY;
      draggedDistance = Math.sqrt(dx * dx + dy * dy);

      // If they dragged beyond 10px, activate the dragging state for active grid placement
      if (draggedDistance >= 10) {
        // On mobile, only trigger tower placement dragging if dragging horizontally out of the drawer!
        // If they drag vertically, it is a list scroll gesture, so we let the browser handle it.
        const isHorizontalDrag = Math.abs(dx) > Math.abs(dy) * 1.2; // 1.2x safety factor
        
        if (moveEvent.type === 'mousemove' || isHorizontalDrag) {
          if (moveEvent.cancelable) {
            moveEvent.preventDefault();
          }
          if (engine.gameState.money >= cost) {
            draggingTowerType = { type: type, cost: cost };
            // Disable map pan while active placement drag is running
            engine.isDragging = false; 
            
            // Collapse detailed popover card immediately so the user can see the board!
            if (window.closeTowerInfoPanel) window.closeTowerInfoPanel();
          }
        }
      }
    };

    const onEnd = (endEvent) => {
      // Clean up temporary move/end event listeners
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);

      // If they barely moved (it was a pure select/click to inspect tower details):
      if (draggedDistance < 10) {
        // Toggle selection off if clicking the same selected button again
        if (btn.classList.contains('selected')) {
          draggingTowerType = null;
          dragPosLogical = null;
          btn.classList.remove('selected');
          window.closeTowerInfoPanel();
          engine.isDragging = false;
          return;
        }

        document.querySelectorAll('.tower-btn').forEach(b => b.classList.remove('selected'));
        btn.classList.add('selected');

        // Pop open the detailed stats & upgrade info card!
        window.openTowerInfoPanel(type);

        // Allow direct deployment clicks inside stats panel
        if (engine.gameState.money >= cost) {
          draggingTowerType = { type: type, cost: cost };
          engine.isDragging = false;
        } else {
          draggingTowerType = null;
        }
      } else {
        // It was a drag action: make sure we don't open the info popover!
        // The normal dragPosLogical update inside window.mousemove will handle positioning on canvas.
      }
    };

    const handleStart = (e) => {
      if (e.type === 'mousedown') {
        e.preventDefault(); // Prevent native image dragging & selection on desktop mouse clicks
      }
      startX = e.touches ? e.touches[0].clientX : e.clientX;
      startY = e.touches ? e.touches[0].clientY : e.clientY;
      draggedDistance = 0;

      // Bind window listeners to trace active movement
      if (e.type === 'touchstart') {
        window.addEventListener('touchmove', onMove, { passive: false }); // MUST be passive: false to allow preventDefault during placement drag!
        window.addEventListener('touchend', onEnd);
      } else {
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onEnd);
      }
    };

    btn.addEventListener('mousedown', handleStart);
    btn.addEventListener('touchstart', handleStart, { passive: false });
  });

  // Handle Dragging over canvas
  const handleMove = (x, y) => {
    if (draggingTowerType) {
      dragPosLogical = engine.screenToLogical(x, y);
    }
  };
  window.addEventListener('mousemove', (e) => handleMove(e.clientX, e.clientY));
  window.addEventListener('touchmove', (e) => {
    if (draggingTowerType && e.touches.length > 0) {
      // e.preventDefault(); // Prevent scrolling while dragging tower (already blocked by touch-action)
      // Offset Y by 75 pixels on mobile so the preview floats cleanly above the user's thumb/finger tip!
      handleMove(e.touches[0].clientX, e.touches[0].clientY - 75);
    }
  }, { passive: false });

  // [#27] Single source of truth for tower placement snapping + validity, used
  // by BOTH the drop handler and the drag-preview renderer so they can never
  // disagree.
  const computeTowerPlacement = (logical) => {
    const snapSize = map.tileSize / 8;
    const relativeX = logical.x - map.startX;
    const relativeY = logical.y - map.startY;
    const snappedX = map.startX + Math.floor(relativeX / snapSize) * snapSize + snapSize / 2;
    const snappedY = map.startY + Math.floor(relativeY / snapSize) * snapSize + snapSize / 2;

    const tile = engine.tiles.find(t =>
      snappedX >= t.x && snappedX < t.x + map.tileSize &&
      snappedY >= t.y && snappedY < t.y + map.tileSize
    );
    const isOnBoard = !!tile;
    // Railroad tiles (track + crossings) are never buildable.
    const isRailroad = !!(tile && typeof tile.typeId === 'string' && tile.typeId.startsWith('railroad_'));
    // Water is no-build for every tower EXCEPT the fireboat (water-only). The
    // bridge deck is no-build for everyone (including the fireboat). Checked by
    // GRID CELL (not the tile under the cursor) since a water cell also has the
    // terrain tile beneath it.
    const _gx = Math.floor((snappedX - map.startX) / map.tileSize);
    const _gy = Math.floor((snappedY - map.startY) / map.tileSize);
    const _ck = _gx + ',' + _gy;
    const isWater = !!(map.waterCells && map.waterCells.has(_ck));
    const isBridge = !!(map.bridgeCells && map.bridgeCells.has(_ck));
    const towerType = (typeof draggingTowerType !== 'undefined' && draggingTowerType) ? draggingTowerType.type : null;
    const isFireboat = towerType === 'fireboat';

    let isOnPavement = false;
    const pavementRadius = 60;
    for (let i = 0; i < map.waypoints.length - 1; i++) {
      const p1 = map.waypoints[i];
      const p2 = map.waypoints[i + 1];
      const minX = Math.min(p1.x, p2.x), maxX = Math.max(p1.x, p2.x);
      const minY = Math.min(p1.y, p2.y), maxY = Math.max(p1.y, p2.y);
      if (snappedX >= minX - pavementRadius && snappedX <= maxX + pavementRadius &&
          snappedY >= minY - pavementRadius && snappedY <= maxY + pavementRadius) {
        isOnPavement = true;
        break;
      }
    }

    const towerRadius = 80;
    const hasTower = engine.entities.some(ent =>
      (ent instanceof Tower || ent instanceof Helipad) &&
      Math.abs(ent.x - snappedX) < towerRadius &&
      Math.abs(ent.y - snappedY) < towerRadius
    );

    const hasBuilding = engine.entities.some(ent => {
      if ((ent.kind || ent.constructor.name) === 'BuildingFacade') {
        const halfSize = (ent.footprintSize * ent.size) / 2;
        return snappedX >= ent.x - halfSize && snappedX < ent.x + halfSize &&
               snappedY >= ent.y - halfSize && snappedY < ent.y + halfSize;
      }
      return false;
    });

    // Fireboat: water ONLY (never land, pavement, or the bridge deck).
    // Everything else: normal land rules, and never on water or a bridge.
    const valid = isFireboat
      ? (isOnBoard && isWater && !isBridge && !hasTower)
      : (isOnBoard && !isOnPavement && !hasTower && !hasBuilding && !isRailroad && !isWater && !isBridge);
    return { snappedX, snappedY, isOnBoard, isOnPavement, hasTower, hasBuilding, isRailroad, isWater, isBridge, isFireboat, valid };
  };

  // Commit an ALREADY-VALIDATED placement: spawn the tower/helipad, charge its cost, and
  // clear the placement UI. Shared by both drag-drop and tap-to-place (was duplicated).
  const commitTowerPlacement = (snappedX, snappedY) => {
    if (draggingTowerType.type === 'news_chopper') {
      engine.addEntity(new Helipad(snappedX, snappedY, engine));
    } else if (draggingTowerType.type === 'blimp') {
      engine.addEntity(new Blimp(snappedX, snappedY, engine));
    } else if (draggingTowerType.type === 'plane') {
      engine.addEntity(new Plane(snappedX, snappedY, engine));
    } else {
      engine.addEntity(new Tower(snappedX, snappedY, draggingTowerType.type, engine));
    }
    engine.gameState.money -= draggingTowerType.cost;
    document.querySelectorAll('.tower-btn').forEach(b => b.classList.remove('selected'));
    if (window.closeTowerInfoPanel) window.closeTowerInfoPanel();
  };

  // Handle Drop — a drop always ends the drag (placed if valid, cancelled otherwise).
  const handleDrop = () => {
    if (draggingTowerType && dragPosLogical) {
      const { snappedX, snappedY, valid } = computeTowerPlacement(dragPosLogical);
      if (valid) commitTowerPlacement(snappedX, snappedY);
      draggingTowerType = null;
      dragPosLogical = null;
    } else if (draggingTowerType) {
      draggingTowerType = null;
    }
  };

  window.addEventListener('mouseup', handleDrop);
  window.addEventListener('touchend', handleDrop);

  const handleCanvasTap = (x, y) => {
    if (draggingTowerType) {
      const clickLogical = engine.screenToLogical(x, y);
      const { snappedX, snappedY, valid } = computeTowerPlacement(clickLogical);
      if (valid) {
        commitTowerPlacement(snappedX, snappedY);
        draggingTowerType = null;
        dragPosLogical = null;
      } else {
        // Update preview position to show why it is blocked/invalid (e.g. red range)
        dragPosLogical = clickLogical;
      }
      return;
    }

    if (window.isRelocatingSweeper) {
      const clickLogical = engine.screenToLogical(x, y);
      const snapSize = map.tileSize / 8;
      const relativeX = clickLogical.x - map.startX;
      const relativeY = clickLogical.y - map.startY;
      const snappedX = map.startX + Math.floor(relativeX / snapSize) * snapSize + snapSize / 2;
      const snappedY = map.startY + Math.floor(relativeY / snapSize) * snapSize + snapSize / 2;

      const isOnBoard = engine.tiles.some(t =>
        snappedX >= t.x && snappedX < t.x + map.tileSize &&
        snappedY >= t.y && snappedY < t.y + map.tileSize
      );

      const hasBuilding = engine.entities.some(ent => {
        if ((ent.kind || ent.constructor.name) === 'BuildingFacade') {
          const halfSize = (ent.footprintSize * ent.size) / 2;
          return snappedX >= ent.x - halfSize && snappedX < ent.x + halfSize &&
                 snappedY >= ent.y - halfSize && snappedY < ent.y + halfSize;
        }
        return false;
      });

      const hasOtherTower = engine.entities.some(ent =>
        ent !== window.isRelocatingSweeper &&
        (ent instanceof Tower || ent instanceof Helipad) &&
        Math.abs(ent.x - snappedX) < 80 &&
        Math.abs(ent.y - snappedY) < 80
      );

      if (isOnBoard && !hasBuilding && !hasOtherTower) {
        // Increment used moves counter
        window.isRelocatingSweeper.movesUsed = (window.isRelocatingSweeper.movesUsed || 0) + 1;
        
        window.isRelocatingSweeper.moveTargetX = snappedX;
        window.isRelocatingSweeper.moveTargetY = snappedY;
        
        if (engine.playTowerSound) engine.playTowerSound('street_sweeper', 1, snappedX, snappedY);
        
        const speeds = [80, 120, 180];
        const speedValue = speeds[window.isRelocatingSweeper.level - 1] || 80;
        showMusicToaster("🚚 Dispatching Sweeper!", `Driving at ${speedValue} speed! Move counter incremented.`);
        window.isRelocatingSweeper = null;
      } else {
        showMusicToaster("🚫 Invalid Location", "Choose a vacant tile on the road or sidewalk.");
      }
      return;
    }

    const clickLogical = engine.screenToLogical(x, y);
    let clickedTower = null;
    let clickedEnemy = null;
    let minTowerDistanceSq = Infinity;
    let minEnemyDistanceSq = Infinity;

    // Find closest tower or enemy within a selection radius
    for (const ent of engine.entities) {
      if (ent instanceof Tower || ent instanceof Helipad) {
        const dx = ent.x - clickLogical.x;
        const dy = ent.y - clickLogical.y;
        const distSq = dx * dx + dy * dy;
        if (distSq < 70 * 70 && distSq < minTowerDistanceSq) {
          minTowerDistanceSq = distSq;
          clickedTower = ent;
        }
      } else if (ent instanceof Balloon || ent instanceof Float) {
        const dx = ent.x - clickLogical.x;
        const dy = ent.y - clickLogical.y;
        const distSq = dx * dx + dy * dy;
        if (distSq < 70 * 70 && distSq < minEnemyDistanceSq) {
          minEnemyDistanceSq = distSq;
          clickedEnemy = ent;
        }
      }
    }

    if (clickedTower) {
      selectedTower = clickedTower;
      showContextMenu(clickedTower, x, y);
      cameraMode = 'tower';
      trackedEntity = clickedTower;
      updateCameraModeUI();
    } else {
      selectedTower = null;
      hideContextMenu();
      if (window.closeTowerInfoPanel) window.closeTowerInfoPanel();
      if (clickedEnemy) {
        cameraMode = 'tracking';
        trackedEntity = clickedEnemy;
        updateCameraModeUI();
      } else {
        const wasFocused = (cameraMode === 'tower' || cameraMode === 'tracking');
        cameraMode = 'free';
        trackedEntity = null;
        if (wasFocused) {
          engine.centerCamera();
        }
        updateCameraModeUI();
      }
    }
  };

  // [#17] Let the engine suppress camera panning while a tower is being placed.
  engine.shouldSuppressPan = () => !!draggingTowerType;

  // [#16] Tap-to-select must distinguish a real tap from a camera pan/zoom, and
  // must not fire twice on touch (once for touchstart, once for the synthesized
  // click). Strategy: touch selects on touchEND, only if a single finger barely
  // moved; desktop selects on click, only if the mouse barely dragged. A short
  // timestamp guard swallows the click that iOS synthesizes after a touch tap.
  const TAP_MOVE_THRESHOLD = 12; // px
  let lastTouchTapTime = 0;
  let tapStart = null; // {x,y} for the current single-finger candidate

  engine.canvas.addEventListener('click', (e) => {
    if (Date.now() - lastTouchTapTime < 600) return;       // synthesized after a touch tap
    if (engine.dragDist > TAP_MOVE_THRESHOLD) return;      // this was a camera drag, not a tap
    handleCanvasTap(e.clientX, e.clientY);
  });

  engine.canvas.addEventListener('touchstart', (e) => {
    // Only a lone finger (not a pinch/zoom) is a tap candidate.
    tapStart = (e.touches.length === 1)
      ? { x: e.touches[0].clientX, y: e.touches[0].clientY }
      : null;
  });
  engine.canvas.addEventListener('touchend', (e) => {
    if (!tapStart) return;
    if (e.touches.length !== 0) { tapStart = null; return; } // other fingers still down
    const t = e.changedTouches[0];
    const moved = Math.hypot(t.clientX - tapStart.x, t.clientY - tapStart.y);
    tapStart = null;
    if (moved <= TAP_MOVE_THRESHOLD) {
      lastTouchTapTime = Date.now();                        // suppress the synthesized click
      handleCanvasTap(t.clientX, t.clientY);
    }
  });

  // Render drag preview over everything else
  engine.postRender = (ctx) => {
    if (draggingTowerType && dragPosLogical) {
      ctx.save();
      // Re-apply camera
      ctx.translate(engine.offsetX + engine.camX, engine.offsetY + engine.camY);
      ctx.scale(engine.camZoom, engine.camZoom);
      ctx.translate(-engine.virtualWidth / 2, -engine.virtualHeight / 2);

      // [#27] Same placement computation as the drop handler.
      const { snappedX, snappedY, valid } = computeTowerPlacement(dragPosLogical);
      
      // Compute the Level 1 range for the tower type being dragged
      let range = 300;
      if (draggingTowerType.type === 'spotlight') {
        range = 500;
      } else if (draggingTowerType.type === 'billboard') {
        range = 190;
      }

      const pos = engine.toIso(snappedX, snappedY);

      if (!valid) {
        ctx.fillStyle = 'rgba(231, 76, 60, 0.25)'; // Translucent Red for invalid
        ctx.strokeStyle = 'rgba(231, 76, 60, 0.7)';
      } else {
        ctx.fillStyle = 'rgba(46, 204, 113, 0.25)'; // Translucent Green for valid
        ctx.strokeStyle = 'rgba(46, 204, 113, 0.7)';
      }
      ctx.lineWidth = 2;

      ctx.beginPath();
      // Draw the exact attack range ellipse (isotropic 2:1 projection)
      ctx.ellipse(pos.x, pos.y, range, range * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      // Draw a solid center dot to pinpoint the exact drop location
      ctx.fillStyle = valid ? '#2ecc71' : '#e74c3c';
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, 8, 0, Math.PI * 2);
      ctx.fill();

      // Draw the actual tower sprite as a ghost
      let def = null;
      if (engine.spriteManifest && engine.spriteManifest.towers) {
        def = engine.spriteManifest.towers[draggingTowerType.type];
      }
      if (def && def.levels && def.levels.length > 0) {
        const spritePath = draggingTowerType.type === 'news_chopper' ? 'sprites/towers/news_chopper/lvl_1.png' : `sprites/towers/${draggingTowerType.type}/lvl_1.png`;
        const img = engine.assets.get(spritePath);
        if (img) {
          ctx.globalAlpha = 0.6;
          const assetDef = engine.spriteManifest[spritePath];
          if (assetDef) {
            const f = assetDef.frame;
            const p = assetDef.pivot;
            // Towers are drawn scaled by 0.35 in the actual Tower class
            const dw = def.size.w * 0.35;
            const dh = def.size.h * 0.35;
            // Base pivot aligns exactly at pos.y + 48 to match real Tower drawing!
            ctx.drawImage(img, f.x, f.y, f.w, f.h, pos.x - dw * p.x, pos.y - dh * p.y + 48, dw, dh);
          }
          ctx.globalAlpha = 1.0;
        }
      }
      
      ctx.restore();
    }
    
    // Highlight selected tower
    if (selectedTower) {
      ctx.save();
      ctx.translate(engine.offsetX + engine.camX, engine.offsetY + engine.camY);
      ctx.scale(engine.camZoom, engine.camZoom);
      ctx.translate(-engine.virtualWidth / 2, -engine.virtualHeight / 2);

      const pos = engine.toIso(selectedTower.x, selectedTower.y);
      
      // Draw range circle
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.ellipse(pos.x, pos.y, selectedTower.range, selectedTower.range * 0.5, 0, 0, Math.PI * 2);
      ctx.stroke();

      ctx.restore();
    }
  };

  // Start Loop
  let actionCamTimer = 0;

  engine.start((dt) => {
    // Override trackedEntity in Demo Mode
    let currentTarget = trackedEntity;
    let activeCameraMode = cameraMode;
    
    if (isDemoMode) {
      currentTarget = demoTargetEntity;
      activeCameraMode = demoTargetEntity ? 'tracking' : 'free';
    }

    // Action Cam Logic (pick a new target occasionally, cycling categories)
    if (!isDemoMode && activeCameraMode === 'action') {
      actionCamTimer -= dt;
      if (actionCamTimer <= 0 || !currentTarget || currentTarget.dead) {
        // Highly exciting priority cycling: Floats -> Balloons -> Towers/Helipads
        let targets = engine.entities.filter(e => e.constructor.name === 'Float' && !e.dead);
        if (targets.length === 0) {
          targets = engine.entities.filter(e => e.constructor.name === 'Balloon' && !e.dead);
        }
        if (targets.length === 0) {
          targets = engine.entities.filter(e => (e.constructor.name === 'Tower' || e.constructor.name === 'Helipad') && !e.dead);
        }
        
        if (targets.length > 0) {
          currentTarget = targets[Math.floor(Math.random() * targets.length)];
          trackedEntity = currentTarget;
          actionCamTimer = 4.0 + Math.random() * 3.0; // Track for 4 to 7 seconds
        }
      }
    }

    // Disengage tracking/focus if the user manually pans or scrolls
    if (engine.cameraInteracted) {
      engine.cameraInteracted = false;
      if (!isDemoMode && cameraMode !== 'free') {
        cameraMode = 'free';
        activeCameraMode = 'free'; // [#15] also clear the local copy so the tracking block below
                                   // doesn't overwrite the manual pan/zoom for this one frame
        trackedEntity = null;
        selectedTower = null;
        hideContextMenu();
        updateCameraModeUI();
      }
    }

    // Camera Tracking Logic
    if (activeCameraMode !== 'free' && currentTarget) {
      if (currentTarget.dead) {
        if (!isDemoMode && activeCameraMode !== 'action') {
          cameraMode = 'free';
          updateCameraModeUI();
        }
        trackedEntity = null;
      } else {
        const isoPos = engine.toIso(currentTarget.x, currentTarget.y);
        
        // Zoom in a bit during follow mode, less zoom for towers so we don't cover them with the menu
        const targetZoom = activeCameraMode === 'tower' ? 1.0 : 1.2;
        engine.targetCamZoom = targetZoom;
        
        // Center the camera exactly on the targeted entity's isometric coordinate
        engine.targetCamX = -(isoPos.x - engine.virtualWidth / 2) * engine.camZoom;
        engine.targetCamY = -(isoPos.y - engine.virtualHeight / 2) * engine.camZoom;
      }
    }

    if (!engine.isPaused) {
      if (engine.gameState.status === 'won') {
        // Check if we should show the modal (wait for all floats to die)
        const floatsAlive = engine.entities.some(e => e instanceof Float && !e.dead);
        if (!floatsAlive && document.getElementById('victory-modal').classList.contains('hidden')) {
          const finalScore = Math.floor(engine.gameState.money * (engine.gameState.difficultyMultiplier || 1.0));
          const lives = Math.floor(engine.gameState.lives);

          // ── Rebalanced star rating ────────────────────────────────────────────
          // Two factors, measured against the run's SNAPSHOTTED starting resources
          // (so buying extra lives/Permits can't inflate the rating):
          //   • Lives lost (primary, tight): 0 lost is required for the top tier.
          //   • Efficiency (secondary): finish at least as Permit-rich as you started
          //     — i.e. you kept economic control instead of scrambling to survive.
          // The top tier is gated to Hard+ difficulty; the curated campaign tour is
          // exempt so its Easy/Medium levels can still be 3-starred (their achievements
          // depend on it).
          const startLives = (engine.gameState.startLivesSnapshot != null) ? engine.gameState.startLivesSnapshot : ECONOMY.BASE_LIVES;
          const livesLost = Math.max(0, startLives - lives);
          const startPermits = (engine.gameState.startPermitsSnapshot != null) ? engine.gameState.startPermitsSnapshot : BASE_MONEY;
          const endPermits = Math.floor(engine.gameState.money);
          const efficient = endPermits >= startPermits;
          const diffLabel = engine.gameState.difficultyLabel || 'Normal';
          const isHardPlus = /^(Hard|Expert|Master)$/.test(diffLabel);
          const isCampaign = !!engine.gameState.campaignLevel;

          let rawStars;
          if (lives >= 20 && efficient) rawStars = 3;   // finishing with 20+ lives AND economically dominant
          else if (lives >= 10) rawStars = 2;            // finishing with 10 - 19 lives
          else rawStars = 1;                             // just finished (1 - 9 lives)

          let stars = rawStars;
          if (rawStars >= 3 && !isHardPlus && !isCampaign) stars = 2; // top tier gated to Hard+

          let starsHtml = '';
          for(let i=0; i<3; i++) {
              starsHtml += i < stars ? '★' : '☆';
          }
          const starsEl = document.getElementById('vic-stat-stars');
          if (starsEl) starsEl.innerHTML = starsHtml;

          // Explain WHY this rating was earned so the two-factor model is transparent.
          const whyEl = document.getElementById('vic-stat-stars-why');
          if (whyEl) {
            const parts = [
              lives >= 20 ? 'Excellent — 20+ lives remaining' : (lives >= 10 ? 'Solid — 10-19 lives remaining' : 'Cleared — <10 lives remaining'),
              efficient ? 'efficient economy ✓' : 'economy ran low'
            ];
            let why = parts.join(' · ');
            if (rawStars >= 3 && !isHardPlus && !isCampaign) why += ` · ★★★ needs Hard+ (this map: ${diffLabel})`;
            whyEl.innerText = why;
          }

          document.getElementById('vic-stat-lives').innerText = lives;
          document.getElementById('vic-stat-base').innerText = Math.floor(engine.gameState.money);
          document.getElementById('vic-stat-mult').innerText = (engine.gameState.difficultyMultiplier || 1.0).toFixed(1) + 'x';
           document.getElementById('vic-stat-score').innerText = finalScore;
          document.getElementById('victory-modal').classList.remove('hidden');
          
          // Dynamically adjust Next Level button text
          const nextBtn = document.getElementById('btn-next-level');
          if (nextBtn) {
            if (engine.gameState.campaignLevel) {
              if (engine.gameState.campaignLevel < 5) {
                nextBtn.innerHTML = `Next Campaign Lvl`;
              } else {
                nextBtn.innerHTML = `Play Another Level`;
              }
            } else {
              nextBtn.innerHTML = `Play Another Level`;
            }
          }

          engine.gameState.victoryComplete = true;
          const proceeds = Math.max(0, Math.floor(engine.gameState.money));
          // Persist the result for EVERY win. Progression levels keep their own key
          // (campaign:… / seed:…); quick-play & random maps fall back to their unique
          // map seed so their stars, level count, and prestige all accrue too.
          const saveKey = currentLevelKey || (map && map.seed != null ? 'seed:' + map.seed : null);
          // ENFORCED server authority: logged-in players are credited only by the
          // session (payout/progress/xp/leaderboard + any 3-star route unlock, all
          // server-side). Guests keep a purely local economy.
          (async () => {
            if (authToken) {
              await completeLevelSession({ won: true, lives, waves: engine.gameState.wave });
              applyLocalLevelResult(saveKey, finalScore, stars); // reflect progress for display
            } else {
              bankEarnings(proceeds);
              saveLevelResult(saveKey, finalScore, stars);
              if (stars >= 3) unlockRouteFor3Star(); // guest-local map unlock
            }
          })();

          // Campaign-complete flourish shows only when the whole tour is done.
          const ccEl = document.getElementById('vic-campaign-complete');
          if (ccEl) ccEl.style.display = 'none';

          // Unlock next campaign level if playing campaign
          if (engine.gameState.campaignLevel) {
            const levelId = engine.gameState.campaignLevel;
            // Whole-tour celebration: treat the level just won as complete.
            const tourDone = [1, 2, 3, 4, 5].every(i => i === levelId ||
              (levelProgress['campaign:' + i] && levelProgress['campaign:' + i].completed));
            if (ccEl) ccEl.style.display = tourDone ? 'block' : 'none';
            let unlocked = parseInt(localStorage.getItem('parade_campaign_unlocked') || 1);
            let nextUnlocked = unlocked;
            if (levelId === unlocked) {
               nextUnlocked = Math.min(5, unlocked + 1);
               localStorage.setItem('parade_campaign_unlocked', nextUnlocked);
            }

            // Save best score and stars
            const prevScore = parseInt(localStorage.getItem(`parade_campaign_${levelId}_score`) || 0);
            const shouldUpdate = finalScore > prevScore;
            if (shouldUpdate) {
                localStorage.setItem(`parade_campaign_${levelId}_score`, finalScore);
                localStorage.setItem(`parade_campaign_${levelId}_stars`, stars);
            }
            
            // Sync up to PostgreSQL if logged in
            if (authToken) {
              apiFetch('/campaign/progress', {
                method: 'POST',
                body: JSON.stringify({
                  unlocked_level: nextUnlocked,
                  level_id: levelId,
                  score: shouldUpdate ? finalScore : undefined,
                  stars: shouldUpdate ? stars : undefined
                })
              }).catch(err => console.error('Failed to save campaign progress remotely:', err));
            }
          }
        }

        if (engine.gameState.victoryComplete) {
          if (Math.random() < 0.3) { // 30% chance per frame to spawn a particle
            // Spawn confetti randomly around the map
            const rx = Math.random() * map.gridWidth * map.tileSize;
            const ry = Math.random() * map.gridHeight * map.tileSize;
            CelebrateParticle.spawn(rx, ry, engine);
          }
        }
      }
      // Wave Countdown Logic
      if (!engine.gameState.waveActive && engine.gameState.waveCountdown > 0) {
        engine.gameState.waveCountdown -= dt;
        if (engine.gameState.waveCountdown <= 0) {
          engine.gameState.waveCountdown = 0;
          document.getElementById('btn-start').click();
        }
      }

      // Wave Spawning Logic
      if (engine.gameState.waveActive) {
        if (engine.gameState.floatSpawns.length > 0) {
          engine.gameState.floatSpawnTimer -= dt;
          if (engine.gameState.floatSpawnTimer <= 0) {
            const spawn = engine.gameState.floatSpawns.shift();
            const startWp = map.waypoints[0];
            const f = new Float(startWp.x, startWp.y, spawn.type, engine);
            if (spawn.hpMult && spawn.hpMult !== 1 && f.maxHealth != null) { f.maxHealth *= spawn.hpMult; f.health = f.maxHealth; }
            engine.addEntity(f);
            // Stochastic spawn interval: +/- 50% of the base spacing (guard NaN/undefined
            // so a bad config can never stall the wave).
            const fsp = spawn.spacing > 0 ? spawn.spacing : 1.5;
            engine.gameState.floatSpawnTimer = fsp * (0.5 + Math.random());
          }
        }

        if (engine.gameState.balloonSpawns.length > 0) {
          engine.gameState.balloonSpawnTimer -= dt;
          if (engine.gameState.balloonSpawnTimer <= 0) {
            const spawn = engine.gameState.balloonSpawns.shift();
            const startWp = map.waypoints[0];
            const b = new Balloon(startWp.x, startWp.y, spawn.type, engine);
            if (spawn.hpMult && spawn.hpMult !== 1 && b.maxHealth != null) { b.maxHealth *= spawn.hpMult; b.health = b.maxHealth; }
            if (spawn.isBoss) {
              b.isBoss = true;
              // Boss appearance: announce + a sting so its arrival reads as the wave finale.
              const bossName = (typeof BALLOON_LABELS !== 'undefined' && BALLOON_LABELS[spawn.type]) || 'BOSS';
              if (typeof showMusicToaster === 'function') showMusicToaster('⚠️ BOSS INCOMING', bossName);
              if (engine.playMiscSound) engine.playMiscSound('misc_celebrate_stab', startWp.x, startWp.y);
            }
            engine.addEntity(b);
            const bsp = spawn.spacing > 0 ? spawn.spacing : 1.0; // guard NaN/undefined
            if (Math.random() < 0.25) {
              // 25% chance of a long gap between clumps
              engine.gameState.balloonSpawnTimer = bsp * (4.0 + Math.random() * 2.0);
            } else {
              // 75% chance of a rapid clump, but don't spawn them *too* fast
              engine.gameState.balloonSpawnTimer = bsp * (0.8 + Math.random() * 0.8);
            }
          }
        }

        if (engine.gameState.floatSpawns.length === 0 && engine.gameState.balloonSpawns.length === 0) {
          // Stall watchdog: all spawns are out, so the wave should end once enemies clear.
          // If the live-enemy count STOPS dropping for a long stretch, one is stuck (e.g. a
          // balloon that never reaches the finish) and the level would otherwise hang with
          // no victory screen forever. Force-resolve the stragglers so the wave completes.
          const aliveEnemies = engine.entities.filter(e => (e instanceof Balloon || e instanceof Float) && !e.dead);
          const aliveCount = aliveEnemies.length;
          if (aliveCount > 0) {
            if (engine.gameState._waveAliveCount === aliveCount) {
              engine.gameState._waveStallT = (engine.gameState._waveStallT || 0) + dt;
            } else {
              engine.gameState._waveAliveCount = aliveCount;
              engine.gameState._waveStallT = 0;
            }
            if (engine.gameState._waveStallT > 30) {
              aliveEnemies.forEach(e => { try { if (typeof e.expire === 'function') e.expire(); else e.dead = true; } catch (_) { e.dead = true; } });
              engine.gameState._waveStallT = 0;
              engine.gameState._waveAliveCount = 0;
            }
          } else {
            engine.gameState._waveStallT = 0;
            engine.gameState._waveAliveCount = 0;
          }

          const balloonsAlive = engine.entities.some(e => e instanceof Balloon && !e.dead);
          if (!balloonsAlive) {
            // Generate billboard wave-end passive income
            engine.entities.forEach(ent => {
              if (ent.type === 'billboard') {
                const waveEnd = getTowerCfg(engine, 'billboard').incomeWaveEnd || [35, 55, 0, 0, 0];
                let income = waveEnd[ent.level - 1] || 0;
                if (income > 0) {
                  engine.gameState.money += income;
                  engine.addEntity(new FloatingText(ent.x, ent.y, `+${income} Ad Rev!`, '#ffd700', engine));
                  engine.playCoinChime();
                }
              }
            });

            // Wave complete logic
            if (engine.gameState.wave === waves.length && !engine.gameState.isEndlessMode) {
              // Final wave logic
              if (engine.gameState.status !== 'won') {
                if (isDemoMode) {
                  engine.gameState.wave = 1;
                  engine.gameState.waveActive = false;
                  engine.gameState.waveCountdown = 5.0;
                } else {
                  engine.gameState.status = 'won'; // Mark as won immediately to start confetti
                  runContinues = 0; // level cleared → reset continue-cost escalation
                  engine.gameState.waveActive = false;
                  document.getElementById('btn-start').style.display = 'none';
                  pauseThemeMusic();          // was safePauseThemeMusic() - undefined, threw here every victory
                  themeMusic.currentTime = 0; // was safeResetThemeMusicTime() - also undefined
                  engine.playTaDaSound();
                  
                  // Expire remaining floats
                  const remainingFloats = engine.entities.filter(e => e instanceof Float && !e.dead);
                  remainingFloats.forEach((f, i) => {
                    setTimeout(() => { if (!f.dead) f.expire(); }, i * 200);
                  });
                }
              }
            } else {
              // Normal wave complete
              // In endless mode, this wave was survived past the base win — claim its
              // capped money + XP bonus (waves survived = current wave − base waves).
              if (engine.gameState.isEndlessMode) {
                claimEndlessBonus(engine.gameState.wave - waves.length);
              }
              engine.gameState.waveActive = false;
              engine.gameState.wave++;
              document.getElementById('btn-start').style.display = 'block';
              document.getElementById('btn-start').innerHTML = `▶️ Start Wave ${engine.gameState.wave} <span id="wave-countdown" style="opacity: 0.7; font-size: 0.9em; margin-left: 8px;"></span>`;
              engine.gameState.waveCountdown = 10.0;
            }
          }
        }
      }
      
      // Lose Condition Check
      // [#26] Exclude 'won' so lives hitting 0 during the victory/confetti phase can't flip a win to a loss.
      if (engine.gameState.lives <= 0 && engine.gameState.status !== 'lost' && engine.gameState.status !== 'won') {
        if (isDemoMode) {
          engine.gameState.lives = 10; // Demo mode never dies
        } else if (engine.gameState.isEndlessMode) {
          // Endless Mode Loss: the level win is ALREADY banked, so just end the run and
          // show the summary — identical to a manual End Run (single shared code path).
          window.finishEndlessRun();
        } else {
          engine.gameState.status = 'lost';
          engine.gameState.waveActive = false;
          engine.isPaused = true;
          pauseThemeMusic();
          engine.playMiscSound('game_over', engine.camX + engine.virtualWidth/2, engine.camY + engine.virtualHeight/2);
          document.getElementById('btn-start').style.display = 'none';
          const finalScore = Math.floor(engine.gameState.money * (engine.gameState.difficultyMultiplier || 1.0));
          document.getElementById('go-stat-wave').innerText = engine.gameState.wave;
          document.getElementById('go-stat-base').innerText = Math.floor(engine.gameState.money);
          document.getElementById('go-stat-mult').innerText = (engine.gameState.difficultyMultiplier || 1.0).toFixed(1) + 'x';
          document.getElementById('go-stat-score').innerText = finalScore;
          updateWalletUI();
          
          document.getElementById('game-over-modal').classList.remove('hidden');
          if (window._refreshContinueBtn) window._refreshContinueBtn(); // price the Continue buy-in
          // ENFORCED server authority: redeem the session as a loss (no payout, but
          // records leaderboard/xp + burns the token). Guests submit nothing (scores
          // require auth), keeping the economy server-authoritative.
          if (authToken) completeLevelSession({ won: false, lives: engine.gameState.lives, waves: engine.gameState.wave });
          // Flat broke with a clean tab? Rocco slinks out of the alley with an offer.
          if (canBailout()) setTimeout(() => { try { showRoccoBailout(); } catch (e) {} }, 900);
        }
      }
    }

    // Keep UI updated
    // Cache UI text updates to prevent massive layout thrashing on every frame
    const newMoney = Math.floor(engine.gameState.money).toString();
    const uiMoney = document.getElementById('ui-money');
    if (uiMoney.innerText !== newMoney) uiMoney.innerText = newMoney;

    const newLives = Math.floor(engine.gameState.lives).toString();
    const uiLives = document.getElementById('ui-lives');
    if (uiLives.innerText !== newLives) uiLives.innerText = newLives;

    const newWave = Math.min(engine.gameState.wave, waves.length).toString();
    const uiWave = document.getElementById('ui-wave');
    if (uiWave.innerText !== newWave) uiWave.innerText = newWave;
    
    const diffLabel = engine.gameState.difficultyLabel || 'Medium';
    const uiDiffIcon = document.getElementById('ui-difficulty-icon');
    if (uiDiffIcon) {
      const srcPath = `sprites/ui/icons/${diffLabel.toLowerCase()}.png`;
      if (!uiDiffIcon.src.endsWith(srcPath)) {
        uiDiffIcon.src = srcPath;
      }
    }
    const diffMult = engine.gameState.difficultyMultiplier || 1.0;

    const uiTooltipLabel = document.getElementById('tooltip-difficulty-label');
    const uiTooltipMult = document.getElementById('tooltip-difficulty-mult');
    if (uiTooltipLabel && uiTooltipLabel.innerText !== diffLabel) {
      uiTooltipLabel.innerText = diffLabel;
    }
    const tooltipMultText = `Multiplier: ${diffMult.toFixed(1)}x`;
    if (uiTooltipMult && uiTooltipMult.innerText !== tooltipMultText) {
      uiTooltipMult.innerText = tooltipMultText;
    }

    const uiDiffTitle = document.getElementById('ui-difficulty-title');
    if (uiDiffTitle && map.seed) {
      const matchedCourse = PRECOMPUTED_COURSES.find(c => c.seed === map.seed);
      const sillyName = matchedCourse ? matchedCourse.name : generateSillyName(map.seed);
      if (uiDiffTitle.innerText !== sillyName) {
        uiDiffTitle.innerText = sillyName;
      }
      // Dynamically update the interactive popover details and render the schematic route map
      const popoverSillyName = document.getElementById('popover-silly-name');
      const popoverSeedId = document.getElementById('popover-seed-id');
      const popoverCanvas = document.getElementById('popover-blueprint-canvas');

      if (popoverSillyName) popoverSillyName.innerText = sillyName;
      if (popoverSeedId) popoverSeedId.innerText = `Seed: #${map.seed}`;

      if (popoverCanvas) {
        // Key the cache on the map's generation id, not the seed: a replay/next
        // regenerates the same seed with a different deterministic layout, and a
        // seed-keyed cache would keep showing the stale schematic.
        const genKey = String(map.generationId || map.seed);
        if (popoverCanvas.dataset.renderedGen !== genKey) {
          // Draw the map that is actually loaded (not a regeneration from the
          // seed, which diverges for random target-difficulty maps).
          try {
            const blueprint = buildLiveBlueprint(map, engine);
            // Only commit (stamp the cache) once we actually have tiles/path to
            // draw. A frame can run after generationId bumps but before the map's
            // tiles exist; stamping then would leave the canvas permanently blank.
            const hasContent = (blueprint.tiles && blueprint.tiles.length) ||
                               (blueprint.pathCoords && blueprint.pathCoords.length);
            if (hasContent) {
              const ctx = popoverCanvas.getContext('2d');
              if (ctx) drawRouteBlueprint(ctx, blueprint, popoverCanvas.width, popoverCanvas.height);
              popoverCanvas.dataset.renderedGen = genKey;
              // Fresh map: start locked unless the seed is already owned.
              routeRevealedThisRound = ownsCurrentRoute();
              // If it looks unowned but we're logged in, our in-memory set may be
              // stale (purchase made on another device/session). Reconcile against
              // Postgres once; the per-frame check below reveals it if it's owned.
              if (!routeRevealedThisRound && authToken) refreshOwnedFromServer('popover');
            }
          } catch (e) {
            console.error('[route blueprint] render failed:', e);
          }
        }

        // Do we own the current map's route? Recomputed every frame so a late
        // server sync reveals it without needing a map change; a just-completed
        // purchase is already in ownedItems (revealUI adds it), so it flips true
        // immediately. This alone drives the popover reveal (no on-board drawing).
        const owned = ownsCurrentRoute();
        routeRevealedThisRound = owned;

        const price = SCHEMATIC_PRICES[diffLabel] || 250;
        const lockOverlay = document.getElementById('popover-blueprint-lock');
        const btnReveal = document.getElementById('btn-reveal-route');

        if (owned) {
          popoverCanvas.style.filter = 'none';
          if (lockOverlay) lockOverlay.classList.add('hidden');
        } else {
          popoverCanvas.style.filter = 'blur(4px)';
          if (lockOverlay) lockOverlay.classList.remove('hidden');
          if (btnReveal && btnReveal.innerText !== `$${price}`) btnReveal.innerText = `$${price}`;
        }
      }
    }

    const uiCampaignWatermark = document.getElementById('campaign-watermark');
    const uiCampaignWatermarkText = document.getElementById('campaign-watermark-text');
    if (uiCampaignWatermark && uiCampaignWatermarkText) {
      if (engine.gameState.campaignLevel) {
        if (uiCampaignWatermark.classList.contains('hidden')) {
          uiCampaignWatermark.classList.remove('hidden');
        }
        const campaignData = campaignLevels.find(l => l.id === engine.gameState.campaignLevel);
        const newCampaignText = campaignData ? `Campaign: ${campaignData.title}` : `Campaign Level ${engine.gameState.campaignLevel}`;
        if (uiCampaignWatermarkText.innerText !== newCampaignText) {
          uiCampaignWatermarkText.innerText = newCampaignText;
        }
      } else {
        if (!uiCampaignWatermark.classList.contains('hidden')) {
          uiCampaignWatermark.classList.add('hidden');
        }
      }
    }
    
    // Dynamic upgrade button state — MUST use the same real per-tower upgrade cost
    // that showContextMenu() used (info.costs[level]); the old hardcoded 150*level
    // mismatched it, so the button lit up too early or stayed greyed while affordable.
    if (document.getElementById('context-menu').style.display === 'block' && selectedTower) {
      const upgradeLevel = selectedTower.level + 1;
      if (upgradeLevel <= 3) {
        const btnAction = document.getElementById(`btn-action-lvl${upgradeLevel}`);
        if (btnAction && btnAction.style.display === 'block') {
          const info = window.TOWER_INFO && window.TOWER_INFO[selectedTower.type];
          const cost = (info && info.costs && info.costs[selectedTower.level] != null)
            ? info.costs[selectedTower.level]
            : 150 * selectedTower.level;
          btnAction.disabled = engine.gameState.money < cost;
        }
      }
    }
    
    if (engine.gameState.waveCountdown > 0) {
      const countdownSpan = document.getElementById('wave-countdown');
      if (countdownSpan) {
        const newText = `(Auto-start in ${Math.ceil(engine.gameState.waveCountdown)}s)`;
        if (countdownSpan.innerText !== newText) {
          countdownSpan.innerText = newText;
        }
      }
    }
  });
}

function showContextMenu(tower, x, y) {
  const menu = document.getElementById('context-menu');
  menu.style.display = 'block';
  // Selecting a tower by touch fires a synthesized `click` ~300ms later at the SAME
  // screen point. If the tapped tower sits where a menu button (esp. Sell) renders, that
  // ghost click would hit it and delete/upgrade the tower the instant it's selected.
  // Stamp the open time; the action handlers ignore clicks within this window.
  window._ctxOpenedAt = Date.now();
  if (window.closeTowerInfoPanel) window.closeTowerInfoPanel();

  const info = window.TOWER_INFO[tower.type] || { 
    name: 'Tower', 
    flavors: ['A basic defensive structure.', 'Improved defensive structure.', 'Maxed defensive structure.'] 
  };

  document.getElementById('ctx-title').innerText = `${info.name}`;

  const btnMove = document.getElementById('btn-move');
  if (tower.type === 'street_sweeper') {
    btnMove.classList.remove('hidden');
    const maxMoves = (getTowerCfg(engine, 'street_sweeper').maxMoves || [2, 4, 6])[tower.level - 1] || 2;
    const remaining = Math.max(0, maxMoves - (tower.movesUsed || 0));
    btnMove.innerText = `Move (${remaining} left)`;
    btnMove.disabled = remaining <= 0;
  } else {
    btnMove.classList.add('hidden');
  }
  
  const maxLevels = info.costs.length;

  // Show or hide Level 4 & 5 cards inside context menu based on max levels
  for (let i = 4; i <= 5; i++) {
    const card = document.getElementById(`ctx-lvl${i}`);
    if (card) card.style.display = i <= maxLevels ? '' : 'none';
  }

  // Update Level Cards
  for (let i = 1; i <= maxLevels; i++) {
    const card = document.getElementById(`ctx-lvl${i}`);
    const img = document.getElementById(`ctx-img-lvl${i}`);
    const flavor = document.getElementById(`ctx-flavor-lvl${i}`);
    const status = document.getElementById(`ctx-status-lvl${i}`);
    if (!card || !img || !flavor || !status) continue;
    
    // Determine sprite path based on tower type
    let spritePath = `sprites/towers/${tower.type}/lvl_${i}.png`;
    if (tower.type === 'kickettes') {
      spritePath = `sprites/towers/kickettes/Kickette_icon.png`;
    } else if (tower.type === 'fireboat') {
      spritePath = `sprites/towers/fireboat/se.png`; // no per-level art — use the regular boat sprite for L1/2/3
    } else if (tower.type === 'blimp') {
      spritePath = `sprites/towers/blimp/blimp${i}_se.png`; // per-level airship art (SE facing)
    } else if (tower.type === 'plane') {
      spritePath = `sprites/towers/plane/plane${i}_e.png`; // per-level plane art (E facing)
    } else if (tower.type === 'spotlight') {
      // Use different idle directions as placeholder upgrade sprites
      const spotlightFrame = i === 1 ? '01' : (i === 2 ? '03' : '05');
      spritePath = `sprites/towers/spotlight/spotlight_idle_${spotlightFrame}.png`;
    } else if (tower.type === 'billboard') {
      spritePath = `sprites/towers/billboards/tier_${i}_1.png`;
    }
    img.src = spritePath;
    
    flavor.innerText = info.flavors[i - 1];
    
    // Reset classes
    card.classList.remove('active', 'locked');
    status.style.display = 'block';
    const btnAction = document.getElementById(`btn-action-lvl${i}`);
    if (!btnAction) continue;
    btnAction.style.display = 'none';
    btnAction.disabled = false;
    
    if (i === tower.level) {
      card.classList.add('active');
      status.innerText = 'Current';
    } else if (i < tower.level) {
      status.innerText = 'Unlocked';
      if (i === tower.level - 1) {
        const refund = info.costs[i];
        btnAction.innerHTML = `Downgrade (+${PERMIT}${refund})`;
        btnAction.style.display = 'block';
        btnAction.className = 'btn btn-action-card btn-downgrade';
      }
    } else if (i === tower.level + 1) {
      status.style.display = 'none';
      const cost = info.costs[tower.level];
      btnAction.innerHTML = `Upgrade (${PERMIT}${cost})`;
      btnAction.disabled = engine.gameState.money < cost;
      btnAction.style.display = 'block';
      btnAction.className = 'btn btn-action-card btn-upgrade';
    } else {
      card.classList.add('locked');
      status.innerText = 'Locked';
    }
  }

  // Sell Button
  const btnSell = document.getElementById('btn-sell');
  const totalInvested = info.costs.slice(0, tower.level).reduce((sum, c) => sum + c, 0);
  const sellRefund = Math.floor(totalInvested * 0.6);
  btnSell.innerHTML = `Sell (+${PERMIT}${sellRefund})`;
}

function hideContextMenu() {
  document.getElementById('context-menu').style.display = 'none';
}

// Hamburger Menu Toggle
document.getElementById('btn-hamburger').addEventListener('click', (e) => {
  e.stopPropagation();
  const controls = document.getElementById('game-controls');
  controls.classList.toggle('show-menu');
});

// Close hamburger menu when clicking outside
document.addEventListener('click', (e) => {
  const controls = document.getElementById('game-controls');
  const hamburgerBtn = document.getElementById('btn-hamburger');
  if (controls.classList.contains('show-menu') && !controls.contains(e.target) && !hamburgerBtn.contains(e.target)) {
    controls.classList.remove('show-menu');
  }
});

// ── Game Center (iOS only) ───────────────────────────────────────────────────
//
// A MIRROR, not a replacement. The server stays authoritative for scores, progress and
// the anti-cheat session flow; on iOS we additionally hand the same numbers to Game
// Center. Everything here degrades to a no-op off-native, so web and Android are
// untouched and the shared codebase holds — same pattern as the NativeAudio gating.
//
// The reason this matters beyond leaderboards: GKLocalPlayer is authenticated by the OS,
// so there is no login UI of ours involved. GUESTS get leaderboards and achievements
// without an account, which is what makes guest mode a complete experience rather than a
// degraded one. Note the deliberate ordering below — GC submission happens BEFORE the
// `!authToken` guard, precisely so guests are included.
//
// These IDs must match what is configured in App Store Connect → Game Center exactly;
// a mismatch fails silently (Game Center just ignores unknown IDs). The achievement IDs
// are the same ids already used by the ACHIEVEMENTS table above, so there is one list to
// maintain rather than two.
const GC_LEADERBOARD_ID = 'parade_panic_high_score';
const gcPlugin = () => {
  const native = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  return (native && window.Capacitor.Plugins && window.Capacitor.Plugins.GameCenter) || null;
};

let gcAuthed = false;

// Fire-and-forget. Called once at startup; never blocks or surfaces errors, because an
// optional social feature must not delay or break entering the game.
async function gcAuthenticate() {
  const gc = gcPlugin();
  if (!gc) return false;
  try {
    const res = await gc.authenticate();
    gcAuthed = !!(res && res.authenticated);
    return gcAuthed;
  } catch (e) {
    return false;
  }
}
window.gcAuthenticate = gcAuthenticate;

async function gcSubmitScore(score) {
  const gc = gcPlugin();
  if (!gc || !Number.isFinite(score)) return;
  try {
    await gc.submitScore({ value: Math.max(0, Math.floor(score)), leaderboardId: GC_LEADERBOARD_ID });
  } catch (e) { /* non-fatal by design */ }
}

// Reports the whole achievement set in one call. GKAchievement.report ignores a
// percentComplete lower than what Game Center already holds, so re-reporting everything
// is idempotent and cheaper than tracking deltas ourselves.
async function gcReportAchievements() {
  const gc = gcPlugin();
  if (!gc) return;
  try {
    const stats = getPlayerStats();
    const achievements = ACHIEVEMENTS
      .map(a => ({ id: a.id, percent: a.test(stats) ? 100 : 0 }))
      .filter(a => a.percent > 0); // nothing to say about un-earned ones
    if (!achievements.length) return;
    await gc.reportAchievements({ achievements });
  } catch (e) { /* non-fatal by design */ }
}
window.gcReportAchievements = gcReportAchievements;

// Kick off Game Center sign-in now that gcPlugin/GC_LEADERBOARD_ID are initialised.
// Deliberately not awaited: the OS may present its own sign-in sheet, and the game must
// be playable regardless of whether the player ever completes or dismisses it.
gcAuthenticate();

// --- API & UI Integration ---

// Score submission
const submitScore = async (score, wave, difficulty) => {
  // Game Center first and OUTSIDE the auth guard: guests have no server account but do
  // have a Game Center identity, so this is the one leaderboard they can appear on.
  gcSubmitScore(score);
  gcReportAchievements();

  if (!authToken) return;
  try {
    await apiFetch('/scores', {
      method: 'POST',
      body: JSON.stringify({ score, wave, difficulty })
    });
    
    const profile = await apiFetch('/profile');
    if (profile) {
      window.profileHasPassword = profile.hasPassword !== false;
      localStorage.setItem('parade_xp', profile.xp);
      localStorage.setItem('parade_games', profile.totalGames);
      updateAuthUI();
    }
  } catch (err) {
    console.error('Failed to submit score:', err);
  }
};

// Auth
const updateAuthUI = () => {
  const isLogged = !!authToken;
  const loggedIn = document.getElementById('auth-logged-in');

  const modalContent = document.querySelector('#auth-modal .modal-content');
  if (modalContent) modalContent.classList.toggle('profile-wide-mode', isLogged);

  // #auth-modal is the Profile view only now (login was unified into the onboarding
  // card). Show the profile when logged in; keep the close button available.
  if (loggedIn) loggedIn.classList.toggle('hidden', !isLogged);
  const h2 = document.querySelector('#auth-modal .modal-header h2');
  if (h2) h2.innerText = 'Profile';
  const closeBtn = document.getElementById('btn-close-auth');
  if (closeBtn) closeBtn.classList.remove('hidden');
  
  const adminBtn = document.getElementById('btn-admin');
  if (isLogged && isAdmin) {
    adminBtn.classList.remove('hidden');
  } else {
    adminBtn.classList.add('hidden');
  }

  // Reflect auth state in the header profile button: show the signed-in name plus a
  // green "logged-in" dot, or revert to the generic label when signed out. This is the
  // persistent login-status indicator the header was missing.
  const profileBtn = document.getElementById('btn-profile');
  if (profileBtn) {
    const label = profileBtn.querySelector('.menu-label');
    if (isLogged) {
      const uname = localStorage.getItem('parade_username') || 'Player';
      if (label) label.textContent = uname;
      profileBtn.title = 'Signed in as ' + uname;
      profileBtn.classList.add('logged-in');
    } else {
      if (label) label.textContent = 'Profile / Login';
      profileBtn.title = 'Profile & Login';
      profileBtn.classList.remove('logged-in');
    }
  }

  if (isLogged) {
    try { renderProfile(); } catch (e) { console.error('renderProfile failed', e); }
  }
};

document.getElementById('btn-profile').addEventListener('click', async () => {
  // Logged out → the single unified login/register/reset UI is the onboarding card.
  // Logged in → this button opens the Profile (the #auth-modal is profile-only now).
  if (!authToken) {
    if (typeof window.showOnboardingLogin === 'function') window.showOnboardingLogin();
    return;
  }
  document.getElementById('auth-modal').classList.remove('hidden');
  updateAuthUI();
});
document.getElementById('btn-close-auth').addEventListener('click', () => {
  document.getElementById('auth-modal').classList.add('hidden');
});

// Username editor (Profile). The schema is normalized, so a successful change is
// reflected everywhere (leaderboard/ledger/etc.) on the server; here we just swap
// the local caches + the re-issued JWT and refresh the header/profile.
(function wireUsernameEditor() {
  const editBtn = document.getElementById('btn-edit-username');
  const editor = document.getElementById('username-editor');
  const input = document.getElementById('username-input');
  const saveBtn = document.getElementById('btn-username-save');
  const cancelBtn = document.getElementById('btn-username-cancel');
  const statusEl = document.getElementById('username-editor-status');
  if (!editBtn || !editor) return;
  const open = () => {
    editor.style.display = 'block';
    if (input) { input.value = localStorage.getItem('parade_username') || ''; input.focus(); input.select(); }
    if (statusEl) statusEl.textContent = '';
  };
  const close = () => { editor.style.display = 'none'; };
  editBtn.addEventListener('click', open);
  editBtn.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } });
  if (cancelBtn) cancelBtn.addEventListener('click', close);
  if (saveBtn) saveBtn.addEventListener('click', async () => {
    const newName = (input ? input.value : '').trim();
    if (!/^[a-zA-Z0-9_]{3,20}$/.test(newName)) {
      if (statusEl) { statusEl.style.color = '#f87171'; statusEl.textContent = '3–20 letters, numbers, or underscores.'; }
      return;
    }
    if (statusEl) { statusEl.style.color = '#38bdf8'; statusEl.textContent = 'Saving…'; }
    saveBtn.disabled = true;
    try {
      const res = await apiFetch('/profile/change-username', { method: 'POST', body: JSON.stringify({ username: newName }) });
      if (res && res.success && res.token) {
        const oldName = localStorage.getItem('parade_username');
        authToken = res.token;
        localStorage.setItem('parade_token', authToken);
        localStorage.setItem('parade_username', res.username);
        if (localStorage.getItem('parade_remembered_username') === oldName) localStorage.setItem('parade_remembered_username', res.username);
        try {
          const oldSettings = localStorage.getItem('parade_settings_' + oldName);
          if (oldSettings != null) { localStorage.setItem('parade_settings_' + res.username, oldSettings); localStorage.removeItem('parade_settings_' + oldName); }
        } catch (e) {}
        close();
        if (typeof updateAuthUI === 'function') updateAuthUI();
        if (typeof renderProfile === 'function') renderProfile();
        if (typeof showToast === 'function') showToast('Username changed to ' + res.username, 'success');
      } else if (statusEl) { statusEl.style.color = '#f87171'; statusEl.textContent = 'Could not change username.'; }
    } catch (err) {
      // Surfaces server messages: "already taken", the 30-day cooldown, etc.
      if (statusEl) { statusEl.style.color = '#f87171'; statusEl.textContent = (err && err.message) || 'Change failed.'; }
    } finally { saveBtn.disabled = false; }
  });
})();

const btnRefreshHistory = document.getElementById('btn-refresh-history');
if (btnRefreshHistory) btnRefreshHistory.addEventListener('click', () => { try { renderPurchaseHistory(); } catch (e) { console.error(e); } });

function showRequirePasswordChangeModal(onSuccess) {
  const modal = document.createElement('div');
  modal.id = 'require-password-change-modal';
  modal.style.cssText = `
    position: fixed;
    top: 0; left: 0; width: 100vw; height: 100vh;
    background: rgba(0, 0, 0, 0.85);
    backdrop-filter: blur(10px);
    display: flex; align-items: center; justify-content: center;
    z-index: 99999;
  `;
  
  const box = document.createElement('div');
  box.style.cssText = `
    width: 380px;
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    border: 1px solid rgba(56, 189, 248, 0.3);
    box-shadow: 0 10px 30px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1);
    border-radius: 12px;
    padding: 24px;
    color: white;
    font-family: inherit;
    text-align: center;
  `;
  
  box.innerHTML = `
    <h2 style="font-size: 20px; font-weight: bold; margin-bottom: 8px; color: #38bdf8;">Update Your Password</h2>
    <p style="font-size: 12px; color: #94a3b8; margin-bottom: 20px; line-height: 1.5;">An administrator has reset your password. For security, please choose a new secure password before continuing.</p>
    
    <div style="text-align: left; margin-bottom: 16px;">
      <label style="display: block; font-size: 11px; text-transform: uppercase; color: #64748b; font-weight: bold; margin-bottom: 6px;">New Password</label>
      <input type="password" id="change-new-pwd" placeholder="Enter at least 8 characters..." 
        style="width: 100%; box-sizing: border-box; padding: 10px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.15); background: rgba(0,0,0,0.3); color: white; outline: none; font-size: 14px;">
    </div>
    
    <div style="text-align: left; margin-bottom: 20px;">
      <label style="display: block; font-size: 11px; text-transform: uppercase; color: #64748b; font-weight: bold; margin-bottom: 6px;">Confirm New Password</label>
      <input type="password" id="change-confirm-pwd" placeholder="Re-enter your password..." 
        style="width: 100%; box-sizing: border-box; padding: 10px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.15); background: rgba(0,0,0,0.3); color: white; outline: none; font-size: 14px;">
    </div>
    
    <div id="change-pwd-error" style="color: #f43f5e; font-size: 12px; margin-bottom: 16px; text-align: left; min-height: 18px;"></div>
    
    <button id="btn-save-new-password" style="width: 100%; background: linear-gradient(90deg, #38bdf8 0%, #0284c7 100%); border: none; color: white; padding: 12px; border-radius: 6px; font-weight: bold; font-size: 14px; cursor: pointer; transition: all 0.2s; box-shadow: 0 4px 12px rgba(56, 189, 248, 0.3);">
      Save Password & Continue
    </button>
  `;
  
  modal.appendChild(box);
  document.body.appendChild(modal);
  
  const newPwdInput = modal.querySelector('#change-new-pwd');
  const confirmPwdInput = modal.querySelector('#change-confirm-pwd');
  const errorEl = modal.querySelector('#change-pwd-error');
  const saveBtn = modal.querySelector('#btn-save-new-password');
  
  newPwdInput.focus();
  
  const keyUpHandler = (e) => {
    if (e.key === 'Enter') saveBtn.click();
  };
  newPwdInput.addEventListener('keyup', keyUpHandler);
  confirmPwdInput.addEventListener('keyup', keyUpHandler);
  
  saveBtn.addEventListener('click', async () => {
    const val = newPwdInput.value;
    const confirmVal = confirmPwdInput.value;
    errorEl.innerText = '';
    
    if (val.length < 8) {
      errorEl.innerText = 'Password must be at least 8 characters.';
      return;
    }
    if (val !== confirmVal) {
      errorEl.innerText = 'Passwords do not match.';
      return;
    }
    
    saveBtn.innerText = 'Saving...';
    saveBtn.disabled = true;
    
    try {
      const res = await apiFetch('/profile/change-password-on-login', {
        method: 'POST',
        body: JSON.stringify({ password: val })
      });
      if (res && res.success) {
        if (res.token) {
          authToken = res.token;
          localStorage.setItem('parade_token', authToken);
        }
        modal.remove();
        showToast('Password updated successfully!', 'success');
        if (onSuccess) onSuccess();
      } else {
        errorEl.innerText = 'Failed to update password.';
        saveBtn.innerText = 'Save Password & Continue';
        saveBtn.disabled = false;
      }
    } catch (err) {
      console.error(err);
      errorEl.innerText = err.message || 'An error occurred while saving.';
      saveBtn.innerText = 'Save Password & Continue';
      saveBtn.disabled = false;
    }
  });
}

// Shared post-auth finalizer for social sign-in (the password login/register path
// lives on the onboarding card). Stores the session, syncs progress, updates the
// header, and dismisses the onboarding gate.
window.finishAuthSuccess = (data) => {
  if (!data || !data.token) return;
  authToken = data.token;
  isAdmin = data.isAdmin || false;
  localStorage.setItem('parade_token', authToken);
  // Same forced-change guard as the password login path above.
  if (data.requirePasswordChange) {
    showRequirePasswordChangeModal(() => window.location.reload());
    return;
  }
  localStorage.setItem('parade_username', data.username);
  localStorage.setItem('parade_xp', data.xp);
  localStorage.setItem('parade_games', data.totalGames);
  localStorage.setItem('parade_is_admin', isAdmin);
  if (data.settings) {
    const settingsKey = `parade_settings_${data.username}`;
    localStorage.setItem(settingsKey, JSON.stringify(data.settings));
    localStorage.setItem('parade_settings', JSON.stringify(data.settings));
    if (typeof window.loadSettings === 'function') window.loadSettings(data.settings);
  }
  Promise.resolve()
    // First link: syncPlayerProgress replaces levelProgress with the server's copy, so
    // guest progress made on this device has to be uploaded before that happens.
    .then(() => (typeof migrateGuestProgress === 'function' ? migrateGuestProgress() : null))
    .then(() => (typeof syncCampaignProgress === 'function' ? syncCampaignProgress() : null))
    .then(() => (typeof syncPlayerProgress === 'function' ? syncPlayerProgress() : null))
    .catch(console.error)
    .finally(() => {
      if (typeof updateAuthUI === 'function') updateAuthUI();
      if (typeof showToast === 'function') showToast('Signed in as ' + (data.username || 'Player'), 'success');
      if (window.checkAndBypassOnboarding) window.checkAndBypassOnboarding();
    });
};

window.handleGoogleCallback = async (response) => {
  try {
    const data = await apiFetch('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential: response.credential })
    });
    window.finishAuthSuccess(data);
  } catch (err) {
    if (typeof showToast === 'function') showToast(err.message || 'Google sign-in failed.', 'error');
  }
};

// Stop the login background video from decoding once we've left the login screen.
// It's only faded to opacity 0 otherwise, and an autoplay-loop <video> keeps
// decoding frames invisibly for the whole session — a continuous CPU/GPU/battery
// drain that shows up as lower in-game FPS (worst on mobile). Pause it and drop the
// source so it's fully released.
window.stopLoginVideo = () => {
  const v = document.getElementById('login-success-video');
  if (!v) return;
  try {
    v.pause();
    v.removeAttribute('autoplay');
    v.removeAttribute('src');
    while (v.firstChild) v.removeChild(v.firstChild); // drop <source> children
    v.load(); // release the decoded buffer
  } catch (e) {}
};

window.checkAndBypassOnboarding = () => {
  if (window.resolveOnboardingLaunch) {
    const card = document.getElementById('onboarding-card');
    if (card) {
      card.style.opacity = '0';
      card.style.transform = 'translate(-50%, -40%)';
      setTimeout(() => {
        card.classList.add('hidden');
        if (window.stopLoginVideo) window.stopLoginVideo(); // stop the bg video decoding
        if (window.resolveOnboardingLaunch) {
          window.resolveOnboardingLaunch();
          window.resolveOnboardingLaunch = null;
        }
      }, 400);
    }
  }
};

// Apple sign-in via the onboarding button (usePopup → resolves with the authorization).
window.handleAppleSignIn = async () => {
  try {
    if (!window.AppleID) return;
    const res = await AppleID.auth.signIn();
    const data = await apiFetch('/auth/apple', {
      method: 'POST',
      body: JSON.stringify({
        id_token: res && res.authorization ? res.authorization.id_token : null,
        user: res && res.user ? JSON.stringify(res.user) : null
      })
    });
    window.finishAuthSuccess(data);
  } catch (err) {
    const code = err && err.error;
    if (code === 'popup_closed_by_user' || code === 'user_cancelled_authorize' || code === 'user_trigger_new_signin_flow') return;
    if (typeof showToast === 'function') showToast('Apple sign-in failed.', 'error');
  }
};

// Native sign-in (packaged app only): the web SDKs above (google.accounts.id,
// AppleID.auth) need a real browser popup/redirect, which a native WebView can't do
// reliably — so the app uses Capacitor's SocialLogin plugin instead, which drives the
// OS-native Google/Apple sign-in sheets. Both paths end at the SAME backend endpoints
// (/auth/google, /auth/apple) and window.finishAuthSuccess — the backend can't tell
// (or care) which one a given login came from.
//
// Google: SocialLogin's `webClientId`/`iOSServerClientId` make the native SDK mint an
// ID token audienced to the same client_id /auth/google already verifies against — no
// backend change needed for Google.
// Apple: native Sign in with Apple always mints a token audienced to the app's BUNDLE
// ID (never the web Services ID used by AppleID.auth), so the backend additionally
// accepts APPLE_NATIVE_AUDIENCES (see server.cjs) — configure it with the bundle id
// (com.paradetd.app) once you enable the Sign In with Apple capability in Xcode.
window._socialLoginInitialized = false;
async function wireNativeOnboardingOAuth(cfg, gBtn, aBtn) {
  const SocialLogin = window.Capacitor.Plugins && window.Capacitor.Plugins.SocialLogin;
  if (!SocialLogin) return;
  if (!window._socialLoginInitialized && (cfg.google_client_id || cfg.apple_client_id)) {
    try {
      await SocialLogin.initialize({
        google: cfg.google_client_id ? {
          webClientId: cfg.google_client_id,
          iOSClientId: cfg.google_ios_client_id || cfg.google_client_id,
          iOSServerClientId: cfg.google_client_id
        } : undefined,
        apple: cfg.apple_client_id ? { clientId: cfg.apple_client_id, redirectUrl: '' } : undefined
      });
      window._socialLoginInitialized = true;
    } catch (e) { console.error('SocialLogin.initialize failed', e); }
  }
  if (gBtn && cfg.google_client_id && !gBtn.dataset.wired) {
    gBtn.style.pointerEvents = 'auto'; gBtn.style.cursor = 'pointer';
    gBtn.addEventListener('click', async () => {
      try {
        const { result } = await SocialLogin.login({ provider: 'google', options: {} });
        const data = await apiFetch('/auth/google', {
          method: 'POST',
          body: JSON.stringify({ credential: result.idToken })
        });
        window.finishAuthSuccess(data);
      } catch (err) {
        if (err && err.code === 'USER_CANCELLED') return;
        if (typeof showToast === 'function') showToast(err.message || 'Google sign-in failed.', 'error');
      }
    });
    gBtn.dataset.wired = '1';
  }
  if (aBtn && cfg.apple_client_id && !aBtn.dataset.wired) {
    aBtn.style.pointerEvents = 'auto'; aBtn.style.cursor = 'pointer';
    aBtn.addEventListener('click', async () => {
      try {
        const { result } = await SocialLogin.login({ provider: 'apple', options: { scopes: ['email', 'name'] } });
        const givenName = result.profile && result.profile.givenName;
        const data = await apiFetch('/auth/apple', {
          method: 'POST',
          body: JSON.stringify({
            id_token: result.idToken,
            user: givenName ? JSON.stringify({ name: { firstName: givenName } }) : null
          })
        });
        window.finishAuthSuccess(data);
      } catch (err) {
        if (err && err.code === 'USER_CANCELLED') return;
        if (typeof showToast === 'function') showToast('Apple sign-in failed.', 'error');
      }
    });
    aBtn.dataset.wired = '1';
  }
}

// Wire the onboarding social buttons once the async Google/Apple SDKs are ready:
// initialize() (with the client_id from /config) + renderButton for Google, and a
// click handler for Apple. Idempotent (dataset.wired) so it's safe to call often.
window._onboardOAuthConfig = null;
window.initOnboardingOAuth = async () => {
  try {
    if (!window._onboardOAuthConfig) window._onboardOAuthConfig = await apiFetch('/config').catch(() => ({}));
    const cfg = window._onboardOAuthConfig || {};
    const gBtn = document.getElementById('onboard-google-btn');
    const aBtn = document.getElementById('onboard-apple-btn');

    if (window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()) {
      await wireNativeOnboardingOAuth(cfg, gBtn, aBtn);
      return;
    }

    if (gBtn && window.google && window.google.accounts && cfg.google_client_id && !gBtn.dataset.wired) {
      google.accounts.id.initialize({ client_id: cfg.google_client_id, callback: window.handleGoogleCallback });
      gBtn.innerHTML = '';
      gBtn.style.pointerEvents = 'auto'; gBtn.style.cursor = 'default';
      gBtn.style.background = 'none'; gBtn.style.border = 'none'; gBtn.style.boxShadow = 'none';
      google.accounts.id.renderButton(gBtn, { theme: 'outline', size: 'large', width: 320 });
      gBtn.dataset.wired = '1';
    }
    if (aBtn && window.AppleID && cfg.apple_client_id && !aBtn.dataset.wired) {
      AppleID.auth.init({ clientId: cfg.apple_client_id, scope: 'name email', redirectURI: window.location.origin, usePopup: true });
      aBtn.style.pointerEvents = 'auto'; aBtn.style.cursor = 'pointer';
      aBtn.addEventListener('click', window.handleAppleSignIn);
      aBtn.dataset.wired = '1';
    }
  } catch (e) { console.error('initOnboardingOAuth failed', e); }
};
// GSI calls this when its (async) script finishes loading — handles the load race.
window.onGoogleLibraryLoad = () => { window.initOnboardingOAuth(); };
// Fallback attempt shortly after boot in case the SDK was already cached/ready.
setTimeout(() => { try { window.initOnboardingOAuth(); } catch (e) {} }, 1200);

// Native Capacitor's WKWebView has no concept of "open a new tab" — a plain
// target="_blank" link to our own bundled privacy.html silently does nothing there
// (it works fine on web, where the browser handles target="_blank" natively). Intercept
// those specific links on native and navigate the same webview instead; privacy.html
// already has its own "Return to Game" link back to index.html for the way back.
if (window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()) {
  // Covers every bundled standalone page, not just privacy.html — support.html ships in
  // the app bundle too now, and a link to it would fail the same silent way.
  document.querySelectorAll('a[href="privacy.html"], a[href="support.html"]').forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      window.location.href = link.getAttribute('href');
    });
  });
}

// Re-show the unified login (onboarding card) — used by the logged-out header button.
// Re-show the unified login card AFTER the game has started (the logged-out header
// button). This needs more than un-hiding the card.
//
// #onboarding-card is a child of #loading-overlay, and the intro sets
// `loadingOverlay.style.display = 'none'` once play begins. Un-hiding the card alone
// therefore did nothing at all — its ancestor was display:none, so the button appeared
// dead. That was a latent bug made reachable by guest mode: before guests existed you
// could not reach gameplay while logged out, so this button was unreachable.
//
// Rather than resurrect the whole splash overlay (which would redisplay "Loading
// Assets..." and the splash art over a live game), the card is moved into a dedicated
// backdrop attached to <body> the first time it is needed. It is only ever moved once;
// the launch-time flow keeps using it in place.
window.showOnboardingLogin = () => {
  const card = document.getElementById('onboarding-card');
  if (!card) return;

  let backdrop = document.getElementById('onboarding-backdrop');
  if (!backdrop) {
    backdrop = document.createElement('div');
    backdrop.id = 'onboarding-backdrop';
    backdrop.style.cssText =
      'position:fixed;inset:0;z-index:3000;background:rgba(0,0,0,0.72);' +
      'backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);display:none;';
    // Dismissable by tapping outside. The launch-time card deliberately has no close
    // affordance (there was nowhere to go), but opened mid-game the player must be able
    // to back out without being trapped.
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) window.hideOnboardingLogin();
    });
    document.body.appendChild(backdrop);

    const close = document.createElement('button');
    close.id = 'onboarding-close';
    close.setAttribute('aria-label', 'Close');
    close.textContent = '✕';
    close.style.cssText =
      'position:absolute;top:calc(14px + env(safe-area-inset-top));' +
      'right:calc(16px + env(safe-area-inset-right));z-index:3002;background:rgba(0,0,0,0.5);' +
      'color:#fff;border:1px solid rgba(255,255,255,0.25);border-radius:50%;width:34px;' +
      'height:34px;font-size:15px;cursor:pointer;line-height:1;';
    close.addEventListener('click', () => window.hideOnboardingLogin());
    backdrop.appendChild(close);
  }

  if (card.parentElement !== backdrop) backdrop.appendChild(card);
  backdrop.style.display = 'block';
  card.classList.remove('hidden');
  card.style.opacity = '1';
  card.style.transform = 'translate(-50%, -50%)';
  // The card's own controls — "Play as guest", the submit button, the login/register
  // toggle, forgot-password, Enter-to-submit — are all wired in setupOnboardingCard,
  // which ran ONLY inside the splash "Start Game" handler.  Opening the card any
  // other way (the Profile/Login menu item, or a relaunch that lands straight here)
  // left every one of them dead while the two OAuth buttons kept working, because
  // those are wired separately just below.  The dataset.wired guards make this
  // idempotent, so calling it on every show is safe.
  if (typeof window.setupOnboardingCard === 'function') {
    window.setupOnboardingCard(() => { if (window.hideOnboardingLogin) window.hideOnboardingLogin(); });
  }
  window.initOnboardingOAuth();
};

window.hideOnboardingLogin = () => {
  const card = document.getElementById('onboarding-card');
  const backdrop = document.getElementById('onboarding-backdrop');
  if (card) card.classList.add('hidden');
  if (backdrop) backdrop.style.display = 'none';
};

// (Old auth-modal forgot-password wiring removed — the onboarding card now owns
// password reset via /api/forgot-password.)

document.getElementById('btn-logout').addEventListener('click', () => {
  authToken = null;
  isAdmin = false;
  localStorage.removeItem('parade_token');
  localStorage.removeItem('parade_username');
  localStorage.removeItem('parade_xp');
  localStorage.removeItem('parade_games');
  localStorage.removeItem('parade_is_admin');
  
  // Clear campaign progress caches
  localStorage.removeItem('parade_campaign_unlocked');
  for (let i = 1; i <= 5; i++) {
    localStorage.removeItem(`parade_campaign_${i}_score`);
    localStorage.removeItem(`parade_campaign_${i}_stars`);
  }
  
  // Load guest/default settings
  try {
    const saved = JSON.parse(localStorage.getItem('parade_settings'));
    if (saved) window.loadSettings(saved);
    else {
      window.loadSettings({
        showGrid: true,
        showHealth: true,
        panSpeed: 1.4,
        volume: 1.0,
        musicVolume: 1.0,
        musicMuted: false,
        sfxVolume: 1.0,
        sfxMuted: false,
        firstLoadDifficulty: 'random',
        demoModeEnabled: true
      });
    }
  } catch(e) {}

  const authModal = document.getElementById('auth-modal');
  if (authModal) authModal.classList.add('hidden');

  showToast('Signing out...', 'info');
  
  setTimeout(() => {
    window.location.reload();
  }, 150);
});

// Profile Dashboard - Change Password Form Toggle & Submit Handler
const btnToggleChangePassword = document.getElementById('btn-toggle-change-password');
const btnCancelChangePassword = document.getElementById('btn-cancel-change-pwd');
const profileChangePasswordForm = document.getElementById('profile-change-password-form');
const btnSubmitChangePassword = document.getElementById('btn-submit-change-pwd');

if (btnToggleChangePassword && profileChangePasswordForm) {
  btnToggleChangePassword.addEventListener('click', () => {
    profileChangePasswordForm.classList.toggle('hidden');
    // Clear old inputs and errors
    const currentInput = document.getElementById('profile-pwd-current');
    const newInput = document.getElementById('profile-pwd-new');
    const confirmInput = document.getElementById('profile-pwd-confirm');
    const errEl = document.getElementById('profile-pwd-error');
    if (currentInput) currentInput.value = '';
    if (newInput) newInput.value = '';
    if (confirmInput) confirmInput.value = '';
    if (errEl) errEl.innerText = '';
  });
}

if (btnCancelChangePassword && profileChangePasswordForm) {
  btnCancelChangePassword.addEventListener('click', () => {
    profileChangePasswordForm.classList.add('hidden');
  });
}

if (btnSubmitChangePassword) {
  btnSubmitChangePassword.addEventListener('click', async () => {
    const currentPassword = document.getElementById('profile-pwd-current').value;
    const newPassword = document.getElementById('profile-pwd-new').value;
    const confirmPassword = document.getElementById('profile-pwd-confirm').value;
    const errorEl = document.getElementById('profile-pwd-error');
    
    errorEl.innerText = '';
    
    if (!currentPassword) {
      errorEl.innerText = 'Please enter your current password.';
      return;
    }
    if (newPassword.length < 8) {
      errorEl.innerText = 'New password must be at least 8 characters.';
      return;
    }
    if (newPassword !== confirmPassword) {
      errorEl.innerText = 'New passwords do not match.';
      return;
    }
    
    btnSubmitChangePassword.disabled = true;
    btnSubmitChangePassword.innerText = 'Saving...';
    
    try {
      const data = await apiFetch('/profile/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword })
      });
      
      if (data && data.success) {
        if (data.token) {
          authToken = data.token;
          localStorage.setItem('parade_token', authToken);
        }
        profileChangePasswordForm.classList.add('hidden');
        showToast('Password updated successfully!', 'success');
      } else {
        errorEl.innerText = data.error || 'Failed to change password.';
      }
    } catch (err) {
      console.error(err);
      errorEl.innerText = err.message || 'An error occurred.';
    } finally {
      btnSubmitChangePassword.disabled = false;
      btnSubmitChangePassword.innerText = 'Save Changes';
    }
  });
}

// Profile Dashboard - Delete Account Form Toggle & Submit Handler
const btnToggleDeleteAccount = document.getElementById('btn-toggle-delete-account');
const btnCancelDeleteAccount = document.getElementById('btn-cancel-delete-account');
const profileDeleteAccountForm = document.getElementById('profile-delete-account-form');
const btnSubmitDeleteAccount = document.getElementById('btn-submit-delete-account');

if (btnToggleDeleteAccount && profileDeleteAccountForm) {
  btnToggleDeleteAccount.addEventListener('click', () => {
    profileDeleteAccountForm.classList.toggle('hidden');
    const pwdInput = document.getElementById('profile-delete-pwd');
    const confirmInput = document.getElementById('profile-delete-confirm-text');
    const errEl = document.getElementById('profile-delete-error');
    if (pwdInput) pwdInput.value = '';
    if (confirmInput) confirmInput.value = '';
    if (errEl) errEl.innerText = '';
  });
}

if (btnCancelDeleteAccount && profileDeleteAccountForm) {
  btnCancelDeleteAccount.addEventListener('click', () => {
    profileDeleteAccountForm.classList.add('hidden');
  });
}

if (btnSubmitDeleteAccount) {
  btnSubmitDeleteAccount.addEventListener('click', async () => {
    const password = document.getElementById('profile-delete-pwd').value;
    const confirmText = document.getElementById('profile-delete-confirm-text').value;
    const errorEl = document.getElementById('profile-delete-error');
    const oauthOnly = window.profileHasPassword === false;

    errorEl.innerText = '';

    if (!oauthOnly && !password) {
      errorEl.innerText = 'Please enter your current password.';
      return;
    }
    if (confirmText.trim().toUpperCase() !== 'DELETE') {
      errorEl.innerText = 'Type DELETE (in capital letters) to confirm.';
      return;
    }

    btnSubmitDeleteAccount.disabled = true;
    btnSubmitDeleteAccount.innerText = 'Deleting...';

    try {
      const data = await apiFetch('/account', {
        method: 'DELETE',
        body: JSON.stringify({ password })
      });

      if (data && data.success) {
        showToast('Account deleted. Sorry to see you go!', 'info');
        // Same local cleanup as Logout — the account (and its token) no longer exist.
        authToken = null;
        isAdmin = false;
        localStorage.removeItem('parade_token');
        localStorage.removeItem('parade_username');
        localStorage.removeItem('parade_xp');
        localStorage.removeItem('parade_games');
        localStorage.removeItem('parade_is_admin');
        localStorage.removeItem('parade_campaign_unlocked');
        for (let i = 1; i <= 5; i++) {
          localStorage.removeItem(`parade_campaign_${i}_score`);
          localStorage.removeItem(`parade_campaign_${i}_stars`);
        }
        setTimeout(() => {
          window.location.reload();
        }, 800);
      } else {
        errorEl.innerText = (data && data.error) || 'Failed to delete account.';
      }
    } catch (err) {
      console.error(err);
      errorEl.innerText = err.message || 'An error occurred.';
    } finally {
      btnSubmitDeleteAccount.disabled = false;
      btnSubmitDeleteAccount.innerText = 'Permanently Delete';
    }
  });
}

// Admin Dashboard
const RUBRIC_KEYS = [
  'straight_line_bonus',
  'mostly_straight_bonus',
  'heavy_winding_penalty',
  'moderate_winding_penalty',
  'intersection_penalty',
  'switchback_onroute_bonus',
  'switchback_offroute_penalty',
  'concealed_turn_bonus',
  'obscuring_building_bonus',
  'tall_building_bonus',
  'spawn_blocked_bonus',
  'loop_overlap_penalty',
  'low_density_penalty',
  'threshold_master',
  'threshold_expert',
  'threshold_hard',
  'threshold_medium'
];

const RUBRIC_DEFS = {
  straight_line_bonus:       { type: 'bonus',     min: 0,   max: 15 },
  mostly_straight_bonus:     { type: 'bonus',     min: 0,   max: 10 },
  heavy_winding_penalty:     { type: 'penalty',   min: -10, max: 0 },
  moderate_winding_penalty:  { type: 'penalty',   min: -5,  max: 0 },
  intersection_penalty:      { type: 'penalty',   min: -10, max: 0 },
  loop_overlap_penalty:      { type: 'penalty',   min: -3,  max: 0 },
  switchback_onroute_bonus:    { type: 'penalty', min: -6, max: 0 }, // route drives into stub => easier
  switchback_offroute_penalty: { type: 'bonus',   min: 0,  max: 3 }, // untraveled stub => harder
  concealed_turn_bonus:      { type: 'bonus',     min: 0,   max: 5 },
  obscuring_building_bonus:  { type: 'bonus',     min: 0,   max: 3 },
  tall_building_bonus:       { type: 'bonus',     min: 0,   max: 5 },
  spawn_blocked_bonus:       { type: 'bonus',     min: 0,   max: 2 },
  low_density_penalty:       { type: 'penalty',   min: -10, max: 0 },
  threshold_master:          { type: 'threshold', min: 30,  max: 15 }, // Left = easiest (high threshold), Right = hardest (low threshold)
  threshold_expert:          { type: 'threshold', min: 20,  max: 12 },
  threshold_hard:            { type: 'threshold', min: 16,  max: 8 },
  threshold_medium:          { type: 'threshold', min: 12,  max: 5 }
};

// Converts backend coefficient value to a unified 0-10 slider value
function coeffToSlider(key, val) {
  const def = RUBRIC_DEFS[key];
  if (!def) return 5;
  const ratio = (val - def.min) / (def.max - def.min);
  return Math.max(0, Math.min(10, ratio * 10));
}

// Converts unified 0-10 slider value back to the backend coefficient
function sliderToCoeff(key, sliderVal) {
  const def = RUBRIC_DEFS[key];
  if (!def) return 0;
  const ratio = sliderVal / 10;
  return def.min + ratio * (def.max - def.min);
}

function updateRubricValueDisplay(key, sliderValue) {
  const span = document.getElementById(`val-${key}`);
  if (!span) return;
  const actualCoeff = sliderToCoeff(key, Number(sliderValue));
  
  if (key.startsWith('threshold_')) {
    span.innerText = actualCoeff.toFixed(1);
    span.style.color = key === 'threshold_master' ? '#ec4899' : key === 'threshold_expert' ? '#f59e0b' : key === 'threshold_hard' ? '#3b82f6' : '#10b981';
  } else {
    const sign = actualCoeff > 0 ? '+' : '';
    span.innerText = `${sign}${actualCoeff.toFixed(2)}`;
    span.style.color = actualCoeff >= 0 ? '#10b981' : '#ef4444';
  }
}

// Bind slider inputs update display dynamically on drag
RUBRIC_KEYS.forEach(key => {
  const input = document.getElementById(`input-${key}`);
  if (input) {
    input.addEventListener('input', (e) => {
      updateRubricValueDisplay(key, e.target.value);
    });
  }
});

// Helper to toggle admin modal backdrop blur and overlay opacity so tuner adjustments are visible in real-time
function setAdminOverlaySoft(soft) {
  const modal = document.getElementById('admin-modal');
  if (!modal) return;
  if (soft) {
    modal.style.background = 'rgba(0, 0, 0, 0.15)';
    modal.style.backdropFilter = 'none';
    modal.style.webkitBackdropFilter = 'none';
  } else {
    modal.style.background = '';
    modal.style.backdropFilter = '';
    modal.style.webkitBackdropFilter = '';
  }
}

function activateAdminTab(activeTabName) {
  const adminTabs = ['creds', 'smtp', 'users', 'rubric', 'tuner', 'towers', 'balloons', 'water', 'rewards', 'audit'];
  adminTabs.forEach(t => {
    const btn = document.getElementById('tab-admin-' + t);
    const panel = document.getElementById('admin-panel-' + t);
    if (btn) {
      if (t === activeTabName) {
        btn.style.background = 'rgba(56, 189, 248, 0.2)';
        btn.style.borderColor = '#38bdf8';
        btn.style.color = 'white';
      } else {
        btn.style.background = 'transparent';
        btn.style.borderColor = 'rgba(255,255,255,0.2)';
        btn.style.color = '#aaa';
      }
    }
    if (panel) {
      if (t === activeTabName) {
        panel.classList.remove('hidden');
      } else {
        panel.classList.add('hidden');
      }
    }
  });
}

// Player catalog state
window.adminUsersList = null;

function renderAdminUsersList(users) {
  const tbody = document.getElementById('admin-users-list-body');
  if (!tbody) return;
  tbody.innerHTML = '';
  
  if (users.length === 0) {
    tbody.innerHTML = `<tr><td colspan="4" style="text-align: center; padding: 15px; color: #888;">No players found matching that search.</td></tr>`;
    return;
  }
  
  users.forEach(user => {
    const isMe = (user.id === (window.userProfile ? window.userProfile.id : null));
    const statusText = user.disabled ? 'Disabled' : 'Active';
    const statusBg = user.disabled ? 'rgba(239, 68, 68, 0.2)' : 'rgba(34, 197, 94, 0.2)';
    const statusColor = user.disabled ? '#ef4444' : '#22c55e';
    const disableBtnText = user.disabled ? 'Enable' : 'Disable';
    const disableBtnStyle = user.disabled 
      ? 'background: rgba(34, 197, 94, 0.15); border: 1px solid #22c55e; color: #22c55e;' 
      : 'background: rgba(239, 68, 68, 0.15); border: 1px solid #ef4444; color: #ef4444;';
    
    const roleBadges = [];
    if (user.is_admin) roleBadges.push(`<span style="background: rgba(56, 189, 248, 0.2); color: #38bdf8; padding: 2px 5px; border-radius: 3px; font-size: 9px; font-weight: bold; text-transform: uppercase;">Admin</span>`);
    if (isMe) roleBadges.push(`<span style="background: rgba(168, 85, 247, 0.2); color: #a855f7; padding: 2px 5px; border-radius: 3px; font-size: 9px; font-weight: bold; text-transform: uppercase;">You</span>`);
    
    const badgeMarkup = roleBadges.length > 0 ? `<div style="display: flex; gap: 4px; margin-top: 3px;">${roleBadges.join('')}</div>` : '';
    
    const emailDisplay = user.email 
      ? `<span class="admin-user-email-text" style="font-size: 10px; color: #38bdf8; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 120px; display: inline-block; vertical-align: middle;">${escapeHtml(user.email)}</span>` 
      : `<span class="admin-user-email-text" style="font-size: 10px; color: #ef4444; font-style: italic; display: inline-block; vertical-align: middle;">No Email</span>`;

    const emailMarkup = `
      <div class="email-container-el" data-id="${user.id}" style="display: flex; align-items: center; gap: 4px; margin-top: 2px; min-height: 18px;">
        ${emailDisplay}
        <button class="btn-edit-user-email" data-id="${user.id}" title="Edit Email" style="background: none; border: none; padding: 2px; color: #888; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; outline: none; transition: color 0.2s;">
          <svg style="width: 10px; height: 10px; fill: currentColor;" viewBox="0 0 24 24">
            <path d="M3 17.25V21h4.25L17.81 11.17l-4.25-4.25L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 4.25 4.25 1.83-1.83z"/>
          </svg>
        </button>
      </div>
    `;
    
    const tr = document.createElement('tr');
    tr.style.borderBottom = '1px solid rgba(255,255,255,0.08)';
    
    tr.innerHTML = `
      <td style="padding: 10px 10px;">
        <span style="font-weight: bold; color: white; display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 140px;">${escapeHtml(user.username)}</span>
        ${emailMarkup}
        ${badgeMarkup}
      </td>
      <td style="padding: 10px 10px; text-align: center; color: #ccc;">${user.xp}</td>
      <td style="padding: 10px 10px; text-align: center;">
        <span style="display: inline-block; padding: 2px 6px; border-radius: 12px; font-size: 10px; font-weight: bold; background: ${statusBg}; color: ${statusColor}; text-transform: uppercase;">${statusText}</span>
      </td>
      <td style="padding: 10px 10px; text-align: right;">
        <div style="display: flex; gap: 6px; justify-content: flex-end;">
          <button class="btn btn-disable-user" data-id="${user.id}" ${isMe ? 'disabled' : ''} style="padding: 3px 8px; font-size: 10px; border-radius: 4px; cursor: pointer; font-weight: bold; ${disableBtnStyle} opacity: ${isMe ? '0.4' : '1'}; transition: all 0.2s; border-radius:4px;">
            ${disableBtnText}
          </button>
          <button class="btn btn-reset-user-pw" data-id="${user.id}" style="padding: 3px 8px; font-size: 10px; border-radius: 4px; cursor: pointer; background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); color: white; font-weight: bold; transition: all 0.2s; border-radius:4px;">
            Reset PW
          </button>
        </div>
      </td>
    `;
    tbody.appendChild(tr);
  });
  
  // Wire up action listeners
  tbody.querySelectorAll('.btn-edit-user-email').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const userId = e.currentTarget.dataset.id;
      const user = window.adminUsersList.find(u => u.id == userId);
      if (!user) return;
      
      const container = tbody.querySelector(`.email-container-el[data-id="${userId}"]`);
      if (!container) return;
      
      container.innerHTML = `
        <div style="display: flex; align-items: center; gap: 4px;">
          <input class="input-edit-email" type="email" value="${escapeHtml(user.email || '')}" placeholder="Add email..." 
            style="width: 110px; font-size: 10px; padding: 1px 4px; border-radius: 3px; border: 1px solid #38bdf8; background: rgba(0,0,0,0.8); color: white; outline: none; height: 16px;">
          <button class="btn-save-email-inline" data-id="${user.id}" style="background: rgba(34, 197, 94, 0.2); border: 1px solid #22c55e; color: #22c55e; padding: 0 4px; font-size: 10px; font-weight: bold; border-radius: 3px; cursor: pointer; height: 16px; display: inline-flex; align-items: center; justify-content: center; outline: none;">✓</button>
          <button class="btn-cancel-email-inline" data-id="${user.id}" style="background: rgba(239, 68, 68, 0.2); border: 1px solid #ef4444; color: #ef4444; padding: 0 4px; font-size: 10px; font-weight: bold; border-radius: 3px; cursor: pointer; height: 16px; display: inline-flex; align-items: center; justify-content: center; outline: none;">✗</button>
        </div>
      `;
      
      const input = container.querySelector('.input-edit-email');
      if (input) {
        input.focus();
        input.select();
        
        input.addEventListener('keyup', (ev) => {
          if (ev.key === 'Enter') {
            container.querySelector('.btn-save-email-inline').click();
          } else if (ev.key === 'Escape') {
            container.querySelector('.btn-cancel-email-inline').click();
          }
        });
      }
      
      container.querySelector('.btn-cancel-email-inline').addEventListener('click', (ev) => {
        ev.stopPropagation();
        applyAdminUsersFilter(); // Restore view
      });
      
      container.querySelector('.btn-save-email-inline').addEventListener('click', async (ev) => {
        ev.stopPropagation();
        const inputVal = input.value.trim();
        
        if (inputVal !== '') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(inputVal)) {
            alert('Invalid email format.');
            return;
          }
        }
        
        try {
          const res = await apiFetch(`/admin/users/${userId}/update-email`, {
            method: 'POST',
            body: JSON.stringify({ email: inputVal })
          });
          if (res && res.success) {
            user.email = res.email;
            applyAdminUsersFilter(); // Re-render updated state
          } else {
            alert('Failed to update email.');
          }
        } catch (err) {
          console.error(err);
          alert('Failed to update email: ' + (err.message || err));
        }
      });
    });
  });

  tbody.querySelectorAll('.btn-disable-user').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const userId = e.currentTarget.dataset.id;
      const user = window.adminUsersList.find(u => u.id == userId);
      if (!user) return;
      
      const isDisabling = !user.disabled;
      const actionWord = isDisabling ? 'disable' : 'enable';
      showConfirm(
        `${isDisabling ? 'Disable' : 'Enable'} User Account`,
        `Are you sure you want to ${actionWord} the account of player "${user.username}"?`,
        async () => {
          try {
            const res = await apiFetch(`/admin/users/${userId}/toggle-disabled`, { method: 'POST' });
            if (res && res.success) {
              user.disabled = res.disabled;
              applyAdminUsersFilter();
            } else {
              alert('Failed to update user status.');
            }
          } catch (err) {
            console.error(err);
            alert('Failed to update user status: ' + (err.message || err));
          }
        }
      );
    });
  });
  
  tbody.querySelectorAll('.btn-reset-user-pw').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const userId = e.currentTarget.dataset.id;
      const user = window.adminUsersList.find(u => u.id == userId);
      if (!user) return;
      
      showConfirm(
        'Reset Player Password',
        `Are you sure you want to initiate a password reset for player "${user.username}"? This will set a new temporary password.`,
        async () => {
          try {
            const res = await apiFetch(`/admin/users/${userId}/reset-password`, { method: 'POST' });
             if (res && res.success) {
               const emailText = user.email 
                 ? `<strong style="color: #38bdf8;">${escapeHtml(user.email)}</strong>` 
                 : '<em style="color: #ef4444; font-size: 11px;">No email address registered for this player</em>';

               let smtpStatusMarkup = '';
               if (res.emailSent) {
                 smtpStatusMarkup = `
                   <div style="background: rgba(34, 197, 94, 0.15); border: 1px solid rgba(34, 197, 94, 0.3); border-radius: 8px; padding: 12px; margin-top: 15px; color: #ccc; display: flex; align-items: flex-start; gap: 8px; font-size: 11.5px; line-height: 1.45;">
                     <span style="font-size: 16px; line-height: 1;">🚀</span>
                     <div>
                       <strong style="color: #4ade80; display: block; margin-bottom: 2px;">Email Sent Automatically!</strong>
                       The branded HTML password reset notification has been successfully sent directly to the player's registered inbox.
                     </div>
                   </div>
                 `;
               } else {
                 let reasonText = res.emailSentError || 'SMTP not configured on server';
                 let noticeHeader = 'Automatic Email Skipped';
                 let alertColor = '#fbbf24';
                 let alertBg = 'rgba(251, 191, 36, 0.12)';
                 let alertBorder = 'rgba(251, 191, 36, 0.25)';
                 let alertIcon = '💡';

                 if (reasonText.includes('not configured')) {
                   noticeHeader = 'SMTP is Not Configured Yet';
                   reasonText = 'To enable automatic email delivery, configure SMTP_HOST, SMTP_USER, and SMTP_PASS in your .env configuration file on the server.';
                 } else if (reasonText.includes('No valid email address')) {
                   noticeHeader = 'No Registered Email';
                   reasonText = 'This player does not have a registered email address. You can copy the layout below to send it manually.';
                 } else {
                   noticeHeader = 'Email Dispatch Failed';
                   alertColor = '#f87171';
                   alertBg = 'rgba(239, 68, 68, 0.12)';
                   alertBorder = 'rgba(239, 68, 68, 0.25)';
                   alertIcon = '⚠️';
                   reasonText = `SMTP delivery failed: ${reasonText}`;
                 }

                 smtpStatusMarkup = `
                   <div style="background: ${alertBg}; border: 1px solid ${alertBorder}; border-radius: 8px; padding: 12px; margin-top: 15px; color: #ccc; display: flex; align-items: flex-start; gap: 8px; font-size: 11.5px; line-height: 1.45;">
                     <span style="font-size: 16px; line-height: 1;">${alertIcon}</span>
                     <div>
                       <strong style="color: ${alertColor}; display: block; margin-bottom: 2px;">${noticeHeader}</strong>
                       ${escapeHtml(reasonText)}
                     </div>
                   </div>
                 `;
               }

               const alertContent = `
                 <div style="font-size: 13px; line-height: 1.5; color: #ccc;">
                   A temporary password has been successfully generated for <strong style="color: white;">${escapeHtml(res.username)}</strong>:
                   <div style="background: rgba(0,0,0,0.4); border: 1px solid rgba(255,255,255,0.1); padding: 10px 12px; border-radius: 6px; font-family: monospace; font-size: 18px; font-weight: bold; color: #38bdf8; text-align: center; margin: 12px 0; letter-spacing: 1px; box-shadow: inset 0 2px 4px rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; gap: 12px;">
                     <span id="temp-password-text" style="user-select: all;">${escapeHtml(res.temporaryPassword)}</span>
                     <button id="btn-copy-temp-pw" style="background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15); color: #94a3b8; padding: 4px 10px; border-radius: 4px; font-size: 11px; font-weight: bold; cursor: pointer; transition: all 0.2s; font-family: inherit; user-select: none;">
                       📋 Copy
                     </button>
                   </div>
                   Please share this password with the player. They can use it to log in and update their password in settings.
                   <hr style="border: 0; border-top: 1px solid rgba(255,255,255,0.08); margin: 15px 0;">
                   <div style="display: flex; justify-content: space-between; align-items: center; background: rgba(255,255,255,0.03); padding: 8px 12px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.05); font-size: 12px;">
                     <span>Recipient Email:</span>
                     <span>${emailText}</span>
                   </div>
                   ${smtpStatusMarkup}
                   <div style="text-align: center; margin-top: 15px;">
                     <button id="btn-open-html-email-preview" style="background: linear-gradient(135deg, #FF6B6B 0%, #FFD93D 50%, #6BCBFF 100%); color: #0c0c14; padding: 11px 22px; border-radius: 8px; font-weight: 800; border: none; font-size: 11.5px; box-shadow: 0 4px 14px rgba(255, 107, 107, 0.35); cursor: pointer; transition: all 0.2s; text-transform: uppercase; font-family: inherit; letter-spacing: 0.5px;">
                       🎨 Preview & Copy Branded Email
                     </button>
                   </div>
                 </div>
               `;

               showAlert('Password Reset Complete', alertContent);

              const previewBtn = document.getElementById('btn-open-html-email-preview');
              if (previewBtn) {
                previewBtn.addEventListener('click', () => {
                  showHtmlEmailPreview(res.username, res.temporaryPassword);
                });
              }

              const copyBtn = document.getElementById('btn-copy-temp-pw');
              if (copyBtn) {
                copyBtn.addEventListener('click', () => {
                  const textToCopy = res.temporaryPassword;
                  
                  const handleCopySuccess = () => {
                    copyBtn.innerText = '✓ Copied!';
                    copyBtn.style.color = '#22c55e';
                    copyBtn.style.borderColor = 'rgba(34, 197, 94, 0.4)';
                    setTimeout(() => {
                      copyBtn.innerText = '📋 Copy';
                      copyBtn.style.color = '#94a3b8';
                      copyBtn.style.borderColor = 'rgba(255,255,255,0.15)';
                    }, 2000);
                  };

                  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
                    navigator.clipboard.writeText(textToCopy)
                      .then(handleCopySuccess)
                      .catch(err => {
                        console.error('Modern copy failed: ', err);
                        fallbackCopyToClipboard(textToCopy);
                      });
                  } else {
                    fallbackCopyToClipboard(textToCopy);
                  }

                  function fallbackCopyToClipboard(text) {
                    try {
                      const textarea = document.createElement('textarea');
                      textarea.value = text;
                      textarea.style.top = '0';
                      textarea.style.left = '0';
                      textarea.style.position = 'fixed';
                      textarea.style.opacity = '0';
                      document.body.appendChild(textarea);
                      textarea.focus();
                      textarea.select();
                      textarea.setSelectionRange(0, 99999);
                      
                      const successful = document.execCommand('copy');
                      document.body.removeChild(textarea);
                      
                      if (successful) {
                        handleCopySuccess();
                      } else {
                        throw new Error('execCommand returned false');
                      }
                    } catch (err) {
                      console.error('Fallback copy failed: ', err);
                      alert('Failed to copy password. You can select and copy it manually.');
                    }
                  }
                });
              }
            } else {
              alert('Failed to reset user password.');
            }
          } catch (err) {
            console.error(err);
            alert('Failed to reset password: ' + (err.message || err));
          }
        }
      );
    });
  });
}

function applyAdminUsersFilter() {
  const searchInp = document.getElementById('admin-users-search');
  const query = searchInp ? searchInp.value.trim().toLowerCase() : '';
  if (!window.adminUsersList) return;
  
  const filtered = window.adminUsersList.filter(user => {
    const username = (user.username || '').toLowerCase();
    const email = (user.email || '').toLowerCase();
    return username.includes(query) || email.includes(query);
  });
  
  renderAdminUsersList(filtered);
}

async function loadAdminUsers() {
  const loadingEl = document.getElementById('admin-users-loading');
  const containerEl = document.getElementById('admin-users-container');
  if (loadingEl) {
    loadingEl.textContent = 'Loading player catalog...';
    loadingEl.classList.remove('hidden');
  }
  if (containerEl) containerEl.classList.add('hidden');
  
  try {
    const users = await apiFetch('/admin/users');
    window.adminUsersList = users;
    if (loadingEl) loadingEl.classList.add('hidden');
    if (containerEl) containerEl.classList.remove('hidden');
    applyAdminUsersFilter();
  } catch (err) {
    console.error('Failed to load admin user catalog:', err);
    if (loadingEl) loadingEl.textContent = 'Failed to load user list. Please try again.';
  }
}

document.getElementById('btn-admin').addEventListener('click', async () => {
  setAdminOverlaySoft(false); // Default to creds tab (not soft)
  document.getElementById('admin-modal').classList.remove('hidden');
  activateAdminTab('creds');

  try {
    const settings = await apiFetch('/admin/settings');
    document.getElementById('admin-google-client-id').value = settings.google_client_id || '';
    document.getElementById('admin-google-ios-client-id').value = settings.google_ios_client_id || '';
    document.getElementById('admin-apple-client-id').value = settings.apple_client_id || '';
    
    // Load SMTP fields
    document.getElementById('admin-smtp-host').value = settings.smtp_host || '';
    document.getElementById('admin-smtp-port').value = settings.smtp_port || '587';
    document.getElementById('admin-smtp-user').value = settings.smtp_user || '';
    document.getElementById('admin-smtp-pass').value = settings.smtp_pass ? '••••••••••••' : '';
    document.getElementById('admin-smtp-from').value = settings.smtp_from || '';
    document.getElementById('admin-smtp-secure').checked = settings.smtp_secure === 'true';
    document.getElementById('admin-smtp-tls-reject').checked = settings.smtp_tls_reject === 'true';
  } catch (err) {
    console.error("Failed to fetch admin settings", err);
  }
});

document.getElementById('tab-admin-creds').addEventListener('click', () => {
  setAdminOverlaySoft(false); // Credentials tab (not soft)
  activateAdminTab('creds');
});

document.getElementById('tab-admin-smtp').addEventListener('click', () => {
  setAdminOverlaySoft(false); // SMTP tab (not soft)
  activateAdminTab('smtp');
});

const saveSmtpBtn = document.getElementById('btn-save-smtp');
if (saveSmtpBtn) {
  saveSmtpBtn.addEventListener('click', async () => {
    const host = document.getElementById('admin-smtp-host').value.trim();
    const port = document.getElementById('admin-smtp-port').value.trim();
    const user = document.getElementById('admin-smtp-user').value.trim();
    const pass = document.getElementById('admin-smtp-pass').value;
    const from = document.getElementById('admin-smtp-from').value.trim();
    const secure = document.getElementById('admin-smtp-secure').checked ? 'true' : 'false';
    const tlsReject = document.getElementById('admin-smtp-tls-reject').checked ? 'true' : 'false';

    try {
      await apiFetch('/admin/settings', {
        method: 'POST',
        body: JSON.stringify({
          settings: {
            smtp_host: host,
            smtp_port: port,
            smtp_user: user,
            smtp_pass: pass,
            smtp_from: from,
            smtp_secure: secure,
            smtp_tls_reject: tlsReject
          }
        })
      });
      showToast('SMTP Configuration Saved successfully!');
    } catch (err) {
      showAlert('Error Saving Settings', 'Failed to save SMTP configuration: ' + err.message);
    }
  });
}

const testSmtpBtn = document.getElementById('btn-test-smtp');
if (testSmtpBtn) {
  testSmtpBtn.addEventListener('click', async () => {
    const host = document.getElementById('admin-smtp-host').value.trim();
    const port = document.getElementById('admin-smtp-port').value.trim();
    const user = document.getElementById('admin-smtp-user').value.trim();
    const pass = document.getElementById('admin-smtp-pass').value;
    const from = document.getElementById('admin-smtp-from').value.trim();
    let to = document.getElementById('admin-smtp-test-to').value.trim();
    const secure = document.getElementById('admin-smtp-secure').checked ? 'true' : 'false';
    const tlsReject = document.getElementById('admin-smtp-tls-reject').checked ? 'true' : 'false';

    // If test recipient email is empty (left as placeholder), provide smart fallbacks
    if (!to) {
      if (user && user.includes('@')) {
        to = user;
      } else if (from && from.includes('@')) {
        const match = from.match(/<([^>]+)>/);
        to = match ? match[1] : from;
      } else {
        to = 'admin@example.com';
      }
      // Populate back into input field so user sees it in action!
      document.getElementById('admin-smtp-test-to').value = to;
    }

    if (!host || !user || !pass || !to) {
      showAlert('Incomplete Information', 'Please fill in SMTP Host, Username, Password, and the Test Recipient Email.');
      return;
    }

    const originalText = testSmtpBtn.innerText;
    testSmtpBtn.innerText = '⚡ Connecting...';
    testSmtpBtn.disabled = true;

    try {
      const res = await apiFetch('/admin/settings/test-smtp', {
        method: 'POST',
        body: JSON.stringify({ host, port, user, pass, from, to, secure, tlsReject })
      });

      if (res && res.success) {
        showAlert('Test Successful! 🚀', `Verification test email has been successfully sent to <strong style="color: #4ade80;">${escapeHtml(to)}</strong>!`);
      } else {
        showAlert('SMTP Connection Failed ⚠️', `Could not send test email.<br><br><span style="color: #f87171; font-family: monospace; font-size: 11px;">Error: ${escapeHtml(res.error || 'Unknown error')}</span>`);
      }
    } catch (err) {
      showAlert('Connection Error ⚠️', 'Failed to communicate with SMTP test endpoint: ' + err.message);
    } finally {
      testSmtpBtn.innerText = originalText;
      testSmtpBtn.disabled = false;
    }
  });
}

document.getElementById('tab-admin-users').addEventListener('click', () => {
  setAdminOverlaySoft(false); // Users tab (not soft)
  activateAdminTab('users');
  loadAdminUsers();
});

// Bind user search field
const searchInp = document.getElementById('admin-users-search');
if (searchInp) {
  searchInp.addEventListener('input', applyAdminUsersFilter);
}

document.getElementById('tab-admin-rubric').addEventListener('click', async () => {
  setAdminOverlaySoft(false); // Rubric tab (not soft)
  activateAdminTab('rubric');

  // Load rubric weights
  try {
    const rubric = await apiFetch('/admin/rubric');
    RUBRIC_KEYS.forEach(key => {
      const input = document.getElementById(`input-${key}`);
      if (input && rubric[key] !== undefined) {
        input.value = coeffToSlider(key, rubric[key]);
        updateRubricValueDisplay(key, input.value);
      }
    });
  } catch (err) {
    console.error("Failed to load rubric config", err);
  }
});

document.getElementById('tab-admin-tuner').addEventListener('click', () => {
  setAdminOverlaySoft(true); // Tuner tab! Soften overlay and unblur so play area adjustments are fully visible
  activateAdminTab('tuner');

  // Build and render the train tuner in its mount container inside the modal
  if (window.trainTuner) {
    window.trainTuner.build();
    window.trainTuner.refreshRows();
  }
});

// ---- Tower Balancing admin tab ----
// Editable field spec: label + keys. Scalar keys are single numbers; array keys
// are comma-separated lists (per level/tier). Only keys present per tower show.
const TOWER_FIELD_SPEC = [
  ['Base cost', 'cost', 'scalar', 'Cash needed to place this tower (level 1).'],
  ['Upgrade costs (per level)', 'upgradeCosts', 'array', 'Cash for each upgrade in order — to level 2, then 3, etc. Comma-separated.'],
  ['Max level', 'maxLevel', 'scalar', 'Highest level this tower can be upgraded to. Fixed — the number of levels is built into the game (per-level stats, sprites, and ad tiers), so this is read-only.'],
  ['Range', 'range', 'scalar', 'Targeting/effect radius in pixels at level 1. Bigger = reaches balloons further away.'],
  ['Range +/level', 'rangePerLevel', 'scalar', 'How much range is added with each upgrade.'],
  ['Damage', 'damage', 'scalar', 'Damage per shot at level 1. (Spotlight: this is the base value multiplied by the DPS × below, applied per second.)'],
  ['Damage +/level', 'damagePerLevel', 'scalar', 'Damage added with each upgrade.'],
  ['Cooldown (s)', 'cooldown', 'scalar', 'Seconds between shots at level 1. Lower = fires faster.'],
  ['Cooldown ×/level', 'cooldownMult', 'scalar', 'Cooldown is multiplied by this on each upgrade. Below 1 means faster reload per level (0.7 = 30% faster each level).'],
  ['Spotlight DPS ×', 'dpsMultiplier', 'scalar', 'Spotlight only: continuous burn damage = base damage × this value, applied every second while locked on.'],
  ['Knockback steps/level', 'knockbackPerLevel', 'scalar', 'Kickettes only: path steps a balloon is knocked backward = tower level × this (e.g. 3 → 3/6/9 steps at L1/2/3).'],
  ['Move speeds (per level)', 'moveSpeeds', 'array', 'Street Sweeper only: how fast it drives to a new spot, per level (pixels/sec). Comma-separated.'],
  ['Max moves (per level)', 'maxMoves', 'array', 'Street Sweeper only: how many times it can be repositioned, per level. Comma-separated.'],
  ['Wave-end income (per tier)', 'incomeWaveEnd', 'array', 'Billboard: cash paid when a wave is cleared, per level (tiers 1–5). Tiers 1–2 earn here; higher tiers earn per-second instead.'],
  ['Income /sec (per tier)', 'incomePerSec', 'array', 'Billboard: cash earned every second during a wave, per level (tiers 1–5).'],
  ['$/sec per enemy (per tier)', 'perEnemyPerSec', 'array', 'Billboard: extra cash/sec for each balloon in range, per level. Tier 4 scales income with nearby traffic.'],
  ['Viral payout $', 'viralAmount', 'scalar', 'Billboard tier 4: one-time cash burst when a viral moment triggers.'],
  ['Viral threshold (balloons)', 'viralThreshold', 'scalar', 'Billboard tier 4: number of balloons in range required to trigger a viral payout.'],
  ['Viral cooldown (s)', 'viralCooldown', 'scalar', 'Billboard tier 4: seconds before another viral payout can fire.'],
  ['Crowd bonus min $', 'crowdBonusMin', 'scalar', 'Billboard tier 2: smallest random crowd bonus paid.'],
  ['Crowd bonus max $', 'crowdBonusMax', 'scalar', 'Billboard tier 2: largest random crowd bonus paid.'],
  ['Crowd interval (s)', 'crowdInterval', 'scalar', 'Billboard tier 2: seconds between crowd-bonus attempts (pays only if balloons are in range).'],
  ['Buff fire-rate ×', 'buffFireRateMult', 'scalar', 'Fire-rate multiplier for towers buffed by billboard ad campaigns (1.35 = 35% faster firing while buffed).'],
  ['Buff duration (per tier)', 'buffDuration', 'array', 'Billboard: seconds the nearby-tower fire-rate buff lasts, per level. Tiers 3 & 5 grant it.'],
  ['Slow factor (per tier)', 'slowFactor', 'array', 'Billboard: balloon speed multiplier while slowed, per level. 0.7 = 30% slower; 0.05 = almost frozen.'],
  ['Slow duration (per tier)', 'slowDuration', 'array', 'Billboard: seconds the balloon slow lasts, per level.'],
];

function renderTowerConfigFields(useDefaults = false) {
  const host = document.getElementById('tower-config-fields');
  if (!host) return;
  host.innerHTML = '';
  const overrides = useDefaults ? {} : (engine.towerConfig || {});
  for (const type of Object.keys(TOWER_DEFAULTS)) {
    const def = TOWER_DEFAULTS[type];
    const cur = { ...def, ...(overrides[type] || {}) };
    const info = (window.TOWER_INFO && window.TOWER_INFO[type]) || { name: type };
    const section = document.createElement('div');
    section.style.cssText = 'margin-bottom:14px;';
    let rows = `<h4 style="color:#38bdf8;border-bottom:1px solid rgba(56,189,248,0.2);padding-bottom:3px;margin:0 0 8px 0;font-size:12px;text-transform:uppercase;">${info.name || type}</h4>`;
    for (const [label, key, kind, desc] of TOWER_FIELD_SPEC) {
      if (!(key in def)) continue;
      const val = cur[key];
      const strVal = Array.isArray(val) ? val.join(', ') : val;
      const tip = escapeHtml(desc || '');
      const readOnly = (key === 'maxLevel');
      // maxLevel is fixed (the number of levels is hardcoded across the game), so
      // its input is read-only and visibly muted.
      const inputStyle = readOnly
        ? 'width:120px;padding:3px 6px;font-size:11px;border-radius:4px;border:1px solid rgba(255,255,255,0.12);background:rgba(255,255,255,0.06);color:#888;text-align:right;cursor:not-allowed;'
        : 'width:120px;padding:3px 6px;font-size:11px;border-radius:4px;border:1px solid rgba(255,255,255,0.2);background:rgba(0,0,0,0.5);color:white;text-align:right;';
      // Whole row + label carry the description as a native tooltip, and a small
      // ⓘ badge makes it discoverable (also hover-able).
      rows += `<div title="${tip}" style="display:flex;justify-content:space-between;align-items:center;gap:8px;margin-bottom:5px;cursor:help;">
        <span style="font-size:11px;color:#ccc;flex:1;display:flex;align-items:center;gap:5px;">
          <span>${label}${readOnly ? ' <span style="color:#777;font-size:9px;">(fixed)</span>' : ''}</span>
          <span title="${tip}" style="display:inline-flex;align-items:center;justify-content:center;width:13px;height:13px;min-width:13px;border-radius:50%;border:1px solid rgba(56,189,248,0.5);color:#38bdf8;font-size:9px;font-style:italic;font-family:Georgia,serif;cursor:help;">i</span>
        </span>
        <input data-tower="${type}" data-key="${key}" data-kind="${kind}" title="${tip}" value="${strVal}"${readOnly ? ' readonly' : ''}
          style="${inputStyle}">
      </div>`;
    }
    section.innerHTML = rows;
    host.appendChild(section);
  }
}

function gatherTowerConfigFromFields() {
  const cfg = {};
  document.querySelectorAll('#tower-config-fields input').forEach(inp => {
    const type = inp.dataset.tower, key = inp.dataset.key, kind = inp.dataset.kind;
    if (key === 'maxLevel') return; // read-only: never persist an override
    if (!cfg[type]) cfg[type] = {};
    if (kind === 'array') {
      const arr = inp.value.split(',').map(s => Number(s.trim())).filter(n => Number.isFinite(n));
      if (arr.length) cfg[type][key] = arr;
    } else {
      const n = Number(inp.value);
      if (Number.isFinite(n)) cfg[type][key] = n;
    }
  });
  return cfg;
}

document.getElementById('tab-admin-towers').addEventListener('click', () => {
  setAdminOverlaySoft(false); // Towers tab (not soft)
  activateAdminTab('towers');
  document.getElementById('tower-config-status').textContent = '';
  renderTowerConfigFields(false);
});

document.getElementById('btn-reset-towers').addEventListener('click', () => {
  renderTowerConfigFields(true);
  document.getElementById('tower-config-status').textContent = 'Loaded defaults into the form (not yet saved).';
});

document.getElementById('btn-clear-towers').addEventListener('click', () => {
  showConfirm('Clear Tower Override',
    'Delete the saved tower config on the server? The game reverts to its built-in defaults for everyone. This cannot be undone.',
    async () => {
      const statusEl = document.getElementById('tower-config-status');
      statusEl.style.color = '#38bdf8'; statusEl.textContent = 'Clearing…';
      try {
        const res = await apiFetch('/admin/config/reset', { method: 'POST', body: JSON.stringify({ key: 'tower_config' }) });
        if (res && res.success) {
          engine.towerConfig = null;      // drop the override in-memory
          renderTowerConfigFields(true);  // show built-in defaults
          applyTowerCostConfig();         // refresh tray/costs immediately
          statusEl.style.color = '#4ade80';
          statusEl.textContent = res.cleared ? 'Override cleared. Using built-in defaults.' : 'No override was set; already on defaults.';
        } else {
          statusEl.style.color = '#f87171'; statusEl.textContent = 'Clear failed.';
        }
      } catch (err) {
        statusEl.style.color = '#f87171';
        statusEl.textContent = 'Clear failed: ' + (err && err.message ? err.message : 'error');
      }
    });
});

document.getElementById('btn-save-towers').addEventListener('click', async () => {
  const statusEl = document.getElementById('tower-config-status');
  statusEl.textContent = 'Saving…';
  try {
    const config = gatherTowerConfigFromFields();
    const res = await apiFetch('/admin/tower-config', { method: 'POST', body: JSON.stringify({ config }) });
    if (res && res.success) {
      engine.towerConfig = res.config;   // adopt sanitized server copy
      applyTowerCostConfig();            // refresh tray/costs immediately
      statusEl.style.color = '#4ade80';
      statusEl.textContent = 'Saved. Reload the game to apply combat stats to newly-placed towers.';
    } else {
      statusEl.style.color = '#f87171';
      statusEl.textContent = 'Save failed.';
    }
  } catch (err) {
    statusEl.style.color = '#f87171';
    statusEl.textContent = 'Save failed: ' + (err && err.message ? err.message : 'error');
  }
});

// ---- Balloon Balancing admin tab ----
const BALLOON_FIELD_SPEC = [
  ['Health', 'maxHealth', 'How much damage it takes to pop this balloon.'],
  ['Speed', 'speed', 'How fast it travels the route (pixels/sec). Higher = harder to stop in time.'],
  ['Bounty ($)', 'bounty', 'Cash awarded when this balloon is popped. Scale it with the balloon’s toughness.'],
  ['Score', 'score', 'Points awarded when popped (feeds the final score / XP).'],
  ['Lives cost', 'livesCost', 'Lives lost if this balloon reaches the end. Bosses should cost more than 1.'],
  ['Attack damage', 'attackDamage', 'Damage of the bombs/feathers it drops on nearby parade floats.'],
  ['Attack interval (s)', 'attackCooldown', 'Seconds between its dropped attacks (lower = more frequent).'],
];
const BALLOON_LABELS = {
  hero_balloon: 'Hero Balloon', felix: 'Felix', detective_dog: 'Detective Dog',
  speedy_turkey: 'Speedy Turkey', dragon: 'Dragon', electric_fox: 'Electric Fox',
  happy_green_oaf: 'Happy Green Oaf',
};

function renderBalloonConfigFields(useDefaults = false) {
  const host = document.getElementById('balloon-config-fields');
  if (!host) return;
  host.innerHTML = '';
  const overrides = useDefaults ? {} : (engine.balloonConfig || {});
  for (const type of Object.keys(BALLOON_DEFAULTS)) {
    const def = BALLOON_DEFAULTS[type];
    const cur = { ...def, ...(overrides[type] || {}) };
    const section = document.createElement('div');
    section.style.cssText = 'margin-bottom:14px;';
    let rows = `<h4 style="color:#f472b6;border-bottom:1px solid rgba(244,114,182,0.25);padding-bottom:3px;margin:0 0 8px 0;font-size:12px;text-transform:uppercase;">${BALLOON_LABELS[type] || type}</h4>`;
    for (const [label, key, desc] of BALLOON_FIELD_SPEC) {
      if (!(key in def)) continue;
      const tip = escapeHtml(desc || '');
      rows += `<div title="${tip}" style="display:flex;justify-content:space-between;align-items:center;gap:8px;margin-bottom:5px;cursor:help;">
        <span style="font-size:11px;color:#ccc;flex:1;display:flex;align-items:center;gap:5px;">
          <span>${label}</span>
          <span title="${tip}" style="display:inline-flex;align-items:center;justify-content:center;width:13px;height:13px;min-width:13px;border-radius:50%;border:1px solid rgba(244,114,182,0.5);color:#f472b6;font-size:9px;font-style:italic;font-family:Georgia,serif;cursor:help;">i</span>
        </span>
        <input data-balloon="${type}" data-key="${key}" title="${tip}" value="${cur[key]}"
          style="width:110px;padding:3px 6px;font-size:11px;border-radius:4px;border:1px solid rgba(255,255,255,0.2);background:rgba(0,0,0,0.5);color:white;text-align:right;">
      </div>`;
    }
    section.innerHTML = rows;
    host.appendChild(section);
  }
}

function gatherBalloonConfigFromFields() {
  const cfg = {};
  document.querySelectorAll('#balloon-config-fields input').forEach(inp => {
    const type = inp.dataset.balloon, key = inp.dataset.key;
    if (!cfg[type]) cfg[type] = {};
    const n = Number(inp.value);
    if (Number.isFinite(n)) cfg[type][key] = n;
  });
  return cfg;
}

document.getElementById('tab-admin-balloons').addEventListener('click', () => {
  setAdminOverlaySoft(false); // Balloons tab (not soft)
  activateAdminTab('balloons');
  document.getElementById('balloon-config-status').textContent = '';
  renderBalloonConfigFields(false);
});

document.getElementById('btn-reset-balloons').addEventListener('click', () => {
  renderBalloonConfigFields(true);
  document.getElementById('balloon-config-status').textContent = 'Loaded defaults into the form (not yet saved).';
});

document.getElementById('btn-clear-balloons').addEventListener('click', () => {
  showConfirm('Clear Balloon Override',
    'Delete the saved balloon config on the server? The game reverts to its built-in defaults for everyone. This cannot be undone.',
    async () => {
      const statusEl = document.getElementById('balloon-config-status');
      statusEl.style.color = '#38bdf8'; statusEl.textContent = 'Clearing…';
      try {
        const res = await apiFetch('/admin/config/reset', { method: 'POST', body: JSON.stringify({ key: 'balloon_config' }) });
        if (res && res.success) {
          engine.balloonConfig = null;
          renderBalloonConfigFields(true);
          statusEl.style.color = '#4ade80';
          statusEl.textContent = res.cleared ? 'Override cleared. Using built-in defaults.' : 'No override was set; already on defaults.';
        } else {
          statusEl.style.color = '#f87171'; statusEl.textContent = 'Clear failed.';
        }
      } catch (err) {
        statusEl.style.color = '#f87171';
        statusEl.textContent = 'Clear failed: ' + (err && err.message ? err.message : 'error');
      }
    });
});

document.getElementById('btn-save-balloons').addEventListener('click', async () => {
  const statusEl = document.getElementById('balloon-config-status');
  statusEl.textContent = 'Saving…';
  try {
    const config = gatherBalloonConfigFromFields();
    const res = await apiFetch('/admin/balloon-config', { method: 'POST', body: JSON.stringify({ config }) });
    if (res && res.success) {
      engine.balloonConfig = res.config;
      statusEl.style.color = '#4ade80';
      statusEl.textContent = 'Saved. Reload the game to apply to newly-spawned balloons.';
    } else {
      statusEl.style.color = '#f87171';
      statusEl.textContent = 'Save failed.';
    }
  } catch (err) {
    statusEl.style.color = '#f87171';
    statusEl.textContent = 'Save failed: ' + (err && err.message ? err.message : 'error');
  }
});

// ---- Water map-generation admin tab (frequency + feature mix) ----
const WATER_DEFAULTS = { any: 0.5, river: 0.6, lake: 0.5, coast: 0.35 };
function _waterSliders() {
  return {
    any: document.getElementById('water-any'), river: document.getElementById('water-river'),
    lake: document.getElementById('water-lake'), coast: document.getElementById('water-coast'),
  };
}
function _syncWaterLabels() {
  const s = _waterSliders();
  document.getElementById('water-any-val').textContent = s.any.value + '%';
  document.getElementById('water-river-val').textContent = s.river.value + '%';
  document.getElementById('water-lake-val').textContent = s.lake.value + '%';
  document.getElementById('water-coast-val').textContent = s.coast.value + '%';
}
function setWaterForm(cfg) {
  const s = _waterSliders();
  s.any.value = Math.round((cfg.any ?? WATER_DEFAULTS.any) * 100);
  s.river.value = Math.round((cfg.river ?? WATER_DEFAULTS.river) * 100);
  s.lake.value = Math.round((cfg.lake ?? WATER_DEFAULTS.lake) * 100);
  s.coast.value = Math.round((cfg.coast ?? WATER_DEFAULTS.coast) * 100);
  _syncWaterLabels();
}
function gatherWaterConfig() {
  const s = _waterSliders();
  return { any: +s.any.value / 100, river: +s.river.value / 100, lake: +s.lake.value / 100, coast: +s.coast.value / 100 };
}
['water-any', 'water-river', 'water-lake', 'water-coast'].forEach(id => {
  const el = document.getElementById(id);
  if (el) el.addEventListener('input', _syncWaterLabels);
});
document.getElementById('tab-admin-water').addEventListener('click', async () => {
  setAdminOverlaySoft(false);
  activateAdminTab('water');
  const statusEl = document.getElementById('water-config-status');
  statusEl.textContent = 'Loading…'; statusEl.style.color = '#38bdf8';
  try {
    const res = await apiFetch('/admin/water-config', { method: 'GET' });
    setWaterForm((res && res.config) || WATER_DEFAULTS);
    statusEl.textContent = '';
  } catch (err) {
    setWaterForm(WATER_DEFAULTS);
    statusEl.style.color = '#f87171';
    statusEl.textContent = 'Could not load saved config; showing defaults.';
  }
});
document.getElementById('btn-reset-water').addEventListener('click', () => {
  setWaterForm(WATER_DEFAULTS);
  document.getElementById('water-config-status').style.color = '#38bdf8';
  document.getElementById('water-config-status').textContent = 'Loaded defaults into the form (not yet saved).';
});
document.getElementById('btn-clear-water').addEventListener('click', () => {
  showConfirm('Clear Water Override',
    'Delete the saved water config on the server? Map generation reverts to built-in defaults for everyone. This cannot be undone.',
    async () => {
      const statusEl = document.getElementById('water-config-status');
      statusEl.style.color = '#38bdf8'; statusEl.textContent = 'Clearing…';
      try {
        const res = await apiFetch('/admin/config/reset', { method: 'POST', body: JSON.stringify({ key: 'water_config' }) });
        if (res && res.success) {
          setWaterForm(WATER_DEFAULTS);
          engine.waterFeatureFrequency = WATER_DEFAULTS.any; engine.riverOdds = WATER_DEFAULTS.river;
          engine.lakeOdds = WATER_DEFAULTS.lake; engine.coastOdds = WATER_DEFAULTS.coast;
          statusEl.style.color = '#4ade80';
          statusEl.textContent = res.cleared ? 'Override cleared. Using built-in defaults.' : 'No override was set; already on defaults.';
        } else { statusEl.style.color = '#f87171'; statusEl.textContent = 'Clear failed.'; }
      } catch (err) {
        statusEl.style.color = '#f87171';
        statusEl.textContent = 'Clear failed: ' + (err && err.message ? err.message : 'error');
      }
    });
});
document.getElementById('btn-save-water').addEventListener('click', async () => {
  const statusEl = document.getElementById('water-config-status');
  statusEl.style.color = '#38bdf8'; statusEl.textContent = 'Saving…';
  try {
    const config = gatherWaterConfig();
    const res = await apiFetch('/admin/water-config', { method: 'POST', body: JSON.stringify({ config }) });
    if (res && res.success) {
      const c = res.config || config;
      engine.waterFeatureFrequency = c.any; engine.riverOdds = c.river;
      engine.lakeOdds = c.lake; engine.coastOdds = c.coast;
      statusEl.style.color = '#4ade80';
      statusEl.textContent = 'Saved. Applies to maps generated from now on.';
    } else { statusEl.style.color = '#f87171'; statusEl.textContent = 'Save failed.'; }
  } catch (err) {
    statusEl.style.color = '#f87171';
    statusEl.textContent = 'Save failed: ' + (err && err.message ? err.message : 'error');
  }
});

// ---- Reward Rubric admin tab (server-authoritative stars/score/payout + caps) ----
// GUI form over the reward_config JSON. _rewardCfg holds the last loaded config so
// unshown keys (version, payout.formula) survive a save; gatherRewardConfig reads
// the form back into that object. Server sanitizes on save regardless.
let _rewardCfg = null;
const REWARD_DIFFS = ['Easy', 'Medium', 'Hard', 'Expert', 'Master'];
const _rkGet = (o, p) => p.split('.').reduce((x, k) => (x == null ? undefined : x[k]), o);
const _rkSet = (o, p, v) => { const ks = p.split('.'); let x = o; for (let i = 0; i < ks.length - 1; i++) { if (x[ks[i]] == null || typeof x[ks[i]] !== 'object') x[ks[i]] = {}; x = x[ks[i]]; } x[ks[ks.length - 1]] = v; };

function renderRewardConfigForm(cfg) {
  const host = document.getElementById('reward-config-fields');
  if (!host) return;
  const inNum = 'width:92px;padding:5px 6px;background:rgba(0,0,0,0.4);border:1px solid rgba(255,255,255,0.15);border-radius:4px;color:#d7e3f4;font-size:12px;';
  const inCap = 'width:100%;box-sizing:border-box;padding:4px 5px;background:rgba(0,0,0,0.4);border:1px solid rgba(255,255,255,0.12);border-radius:4px;color:#d7e3f4;font-size:11px;';
  const hdr = (t) => `<div style="color:#38bdf8;font-weight:bold;font-size:11px;text-transform:uppercase;letter-spacing:.5px;margin:16px 0 6px;border-bottom:1px solid rgba(255,255,255,0.08);padding-bottom:4px;">${t}</div>`;
  const rowNum = (label, path, hint) => `<div style="display:flex;justify-content:space-between;align-items:center;gap:12px;margin:7px 0;"><span style="color:#cbd5e1;font-size:12px;">${label}${hint ? `<span style="color:#6b7280;font-size:10px;display:block;">${hint}</span>` : ''}</span><input type="number" step="any" data-rk="${path}" value="${_rkGet(cfg, path) != null ? _rkGet(cfg, path) : ''}" style="${inNum}"></div>`;
  const rowChk = (label, path) => `<label style="display:flex;justify-content:space-between;align-items:center;gap:12px;margin:7px 0;cursor:pointer;"><span style="color:#cbd5e1;font-size:12px;">${label}</span><input type="checkbox" data-rk="${path}" ${_rkGet(cfg, path) ? 'checked' : ''} style="width:16px;height:16px;cursor:pointer;"></label>`;

  const ttd = (_rkGet(cfg, 'stars.topTierDifficulties')) || [];
  const ttdBoxes = REWARD_DIFFS.map(d => `<label style="display:inline-flex;align-items:center;gap:4px;margin:0 10px 4px 0;color:#cbd5e1;font-size:12px;cursor:pointer;"><input type="checkbox" data-ttd="${d}" ${ttd.includes(d) ? 'checked' : ''} style="cursor:pointer;">${d}</label>`).join('');

  const multRows = REWARD_DIFFS.map(d => rowNum(d, `difficultyMultipliers.${d}`)).join('');

  const capCols = ['maxProceeds', 'maxScore', 'maxWaves', 'maxLives'];
  const capHead = `<tr><th style="text-align:left;color:#8b93a6;font-weight:600;padding:4px;">Difficulty</th>${capCols.map(c => `<th style="color:#8b93a6;font-weight:600;padding:4px;">${c.replace('max', 'Max ')}</th>`).join('')}</tr>`;
  const capRow = (label, base) => `<tr><td style="padding:3px 4px;color:#cbd5e1;font-size:11px;white-space:nowrap;">${label}</td>${capCols.map(c => `<td style="padding:3px 4px;"><input type="number" step="1" data-rk="${base}.${c}" data-cap="1" value="${_rkGet(cfg, base + '.' + c) != null ? _rkGet(cfg, base + '.' + c) : ''}" style="${inCap}"></td>`).join('')}</tr>`;
  const capsTable = `<table style="width:100%;border-collapse:collapse;">${capHead}${capRow('Default', 'caps.default')}${REWARD_DIFFS.map(d => capRow(d, `caps.byDifficulty.${d}`)).join('')}</table>
    <div style="color:#6b7280;font-size:10px;margin-top:5px;">Ceilings that bound client-reported values. Per-difficulty cells override Default; blank = inherit Default.</div>`;

  host.innerHTML =
    hdr('Star Ratings') +
    rowNum('★★★ minimum lives', 'stars.threeStar.minLives') +
    rowChk('★★★ requires efficient economy (end ≥ start Permits)', 'stars.threeStar.requireEfficient') +
    rowNum('★★ minimum lives', 'stars.twoStar.minLives') +
    rowChk('Campaign levels exempt from the Hard+ gate', 'stars.campaignExemptFromGate') +
    `<div style="margin:7px 0;"><div style="color:#cbd5e1;font-size:12px;margin-bottom:4px;">Difficulties that may award ★★★</div>${ttdBoxes}</div>` +
    hdr('Difficulty score multipliers') +
    `<div style="color:#6b7280;font-size:10px;margin-bottom:4px;">score = floor(end Permits × multiplier). Must match in-game multipliers.</div>` +
    multRows +
    hdr('Anti-cheat caps (per difficulty)') +
    capsTable;
}

function gatherRewardConfig() {
  const base = _rewardCfg ? JSON.parse(JSON.stringify(_rewardCfg)) : {};
  const host = document.getElementById('reward-config-fields');
  if (host) {
    host.querySelectorAll('[data-rk]').forEach(inp => {
      const path = inp.getAttribute('data-rk');
      if (inp.type === 'checkbox') { _rkSet(base, path, inp.checked); return; }
      const raw = String(inp.value).trim();
      if (raw === '') return; // blank number (esp. a cap) -> leave as-is / unset
      _rkSet(base, path, Number(raw));
    });
    const ttd = [];
    host.querySelectorAll('[data-ttd]').forEach(cb => { if (cb.checked) ttd.push(cb.getAttribute('data-ttd')); });
    _rkSet(base, 'stars.topTierDifficulties', ttd);
  }
  return base;
}

async function loadRewardConfig(useDefaults = false) {
  const statusEl = document.getElementById('reward-config-status');
  if (!document.getElementById('reward-config-fields')) return;
  try {
    const res = await apiFetch('/admin/reward-config');
    _rewardCfg = (useDefaults ? (res && res.defaults) : (res && res.config)) || {};
    renderRewardConfigForm(_rewardCfg);
    if (statusEl) { statusEl.style.color = '#38bdf8'; statusEl.textContent = useDefaults ? 'Loaded built-in defaults (not yet saved).' : ''; }
  } catch (err) {
    if (statusEl) { statusEl.style.color = '#f87171'; statusEl.textContent = 'Load failed: ' + (err && err.message ? err.message : 'error'); }
  }
}
{
  const rewTab = document.getElementById('tab-admin-rewards');
  if (rewTab) rewTab.addEventListener('click', () => { activateAdminTab('rewards'); loadRewardConfig(false); });

  const rewReset = document.getElementById('btn-reset-rewards');
  if (rewReset) rewReset.addEventListener('click', () => loadRewardConfig(true));

  const rewClear = document.getElementById('btn-clear-rewards');
  if (rewClear) rewClear.addEventListener('click', async () => {
    const statusEl = document.getElementById('reward-config-status');
    try {
      const res = await apiFetch('/admin/config/reset', { method: 'POST', body: JSON.stringify({ key: 'reward_config' }) });
      if (res && res.success) { if (statusEl) { statusEl.style.color = '#4ade80'; statusEl.textContent = 'Override cleared — reverted to built-in defaults.'; } loadRewardConfig(false); }
    } catch (err) {
      if (statusEl) { statusEl.style.color = '#f87171'; statusEl.textContent = 'Clear failed: ' + (err && err.message ? err.message : 'error'); }
    }
  });

  const rewSave = document.getElementById('btn-save-rewards');
  if (rewSave) rewSave.addEventListener('click', async () => {
    const statusEl = document.getElementById('reward-config-status');
    if (statusEl) { statusEl.style.color = '#38bdf8'; statusEl.textContent = 'Saving…'; }
    try {
      const config = gatherRewardConfig();
      const res = await apiFetch('/admin/reward-config', { method: 'POST', body: JSON.stringify({ config }) });
      if (res && res.success) {
        _rewardCfg = res.config; renderRewardConfigForm(_rewardCfg); // reflect the sanitized result
        if (statusEl) { statusEl.style.color = '#4ade80'; statusEl.textContent = 'Saved. Applies to the next level completion.'; }
      } else if (statusEl) { statusEl.style.color = '#f87171'; statusEl.textContent = 'Save failed.'; }
    } catch (err) {
      if (statusEl) { statusEl.style.color = '#f87171'; statusEl.textContent = 'Save failed: ' + (err && err.message ? err.message : 'error'); }
    }
  });
}

document.getElementById('btn-close-admin').addEventListener('click', () => {
  setAdminOverlaySoft(false); // Restore default overlay behavior when closing
  document.getElementById('admin-modal').classList.add('hidden');
});

document.getElementById('btn-save-admin').addEventListener('click', async () => {
  const googleClientId = document.getElementById('admin-google-client-id').value;
  const googleIosClientId = document.getElementById('admin-google-ios-client-id').value;
  const appleClientId = document.getElementById('admin-apple-client-id').value;
  try {
    await apiFetch('/admin/settings', {
      method: 'POST',
      body: JSON.stringify({
        settings: {
          google_client_id: googleClientId,
          google_ios_client_id: googleIosClientId,
          apple_client_id: appleClientId
        }
      })
    });
    alert('Admin settings saved successfully!');
    document.getElementById('admin-modal').classList.add('hidden');
  } catch (err) {
    alert('Failed to save settings: ' + err.message);
  }
});

// Save Rubric Button
document.getElementById('btn-save-rubric').addEventListener('click', async () => {
  const rubric = {};
  RUBRIC_KEYS.forEach(key => {
    const input = document.getElementById(`input-${key}`);
    if (input) {
      rubric[key] = Number(sliderToCoeff(key, Number(input.value)));
    }
  });
  try {
    const res = await apiFetch('/admin/rubric', {
      method: 'POST',
      body: JSON.stringify(rubric)
    });
    if (res.success) {
      if (!engine.gameState) engine.gameState = {};
      engine.gameState.rubricConfig = res.rubric;
      alert('Difficulty rubric saved successfully!');
    }
  } catch (err) {
    alert('Failed to save rubric: ' + err.message);
  }
});

// Run Re-scoring Job Button
document.getElementById('btn-rescore-rubric').addEventListener('click', async () => {
  const rubric = {};
  RUBRIC_KEYS.forEach(key => {
    const input = document.getElementById(`input-${key}`);
    if (input) {
      rubric[key] = Number(sliderToCoeff(key, Number(input.value)));
    }
  });

  const spinner = document.getElementById('rubric-spinner');
  const reportPanel = document.getElementById('rubric-report');
  const reportList = document.getElementById('rubric-report-list');
  const summaryText = document.getElementById('rubric-report-summary');

  spinner.classList.remove('hidden');
  reportPanel.classList.add('hidden');

  try {
    const res = await apiFetch('/admin/rubric/rescore', {
      method: 'POST',
      body: JSON.stringify(rubric)
    });

    spinner.classList.add('hidden');
    reportPanel.classList.remove('hidden');

    if (res.success) {
      summaryText.innerText = `Simulation complete: ${res.changes.length} courses shifted tiers!`;
      
      if (res.changes.length === 0) {
        reportList.innerHTML = `<div style="color: #888; text-align: center; padding: 10px;">No courses shifted categories. Rubric matches existing library classifications perfectly.</div>`;
      } else {
        reportList.innerHTML = res.changes.map(chg => {
          let oldBadge = chg.old_difficulty === 'Master' ? '🟣 Master' : chg.old_difficulty === 'Expert' ? '🔴 Expert' : chg.old_difficulty === 'Hard' ? '🟡 Hard' : chg.old_difficulty === 'Medium' ? '🔵 Medium' : '🟢 Easy';
          let newBadge = chg.new_difficulty === 'Master' ? '🟣 Master' : chg.new_difficulty === 'Expert' ? '🔴 Expert' : chg.new_difficulty === 'Hard' ? '🟡 Hard' : chg.new_difficulty === 'Medium' ? '🔵 Medium' : '🟢 Easy';
          return `<div style="padding: 4px 0; border-bottom: 1px solid rgba(255,255,255,0.05);">
            <span style="color: #FFD700; font-weight: bold;">[Seed ${chg.seed}]</span> 
            <span style="color: #fff;">${chg.name}</span>: 
            <span style="text-decoration: line-through; opacity: 0.6; font-size: 10px;">${oldBadge}</span> &rarr; 
            <span style="font-weight: bold;">${newBadge}</span> 
            <span style="color: #38bdf8; font-size: 10px;">(Score: ${chg.score.toFixed(1)})</span>
          </div>`;
        }).join('');
      }

      // Re-fetch courses list in real-time
      const coursesRes = await fetch(`${window.API_ORIGIN || ''}/api/courses`);
      if (coursesRes.ok) {
        PRECOMPUTED_COURSES = await coursesRes.json();
        coursesRendered = false; // Reset rendering cache to allow re-render on next open
        if (typeof window.initializeCourseFilters === 'function') {
          window.initializeCourseFilters();
        }
      }
    }
  } catch (err) {
    spinner.classList.add('hidden');
    alert('Re-scoring job failed: ' + err.message);
  }
});

// Leaderboard
document.getElementById('btn-leaderboard').addEventListener('click', async () => {
  document.getElementById('leaderboard-modal').classList.remove('hidden');
  const tbody = document.getElementById('leaderboard-list');
  tbody.innerHTML = '<tr><td colspan="5" style="text-align:center;">Loading...</td></tr>';
  try {
    const data = await apiFetch('/leaderboard');
    tbody.innerHTML = data.map((row, i) => `
      <tr style="border-bottom: 1px solid rgba(255,255,255,0.1);">
        <td style="padding: 8px;">${i + 1}</td>
        <td style="padding: 8px; font-weight: bold; color: ${i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : 'white'};">${escapeHtml(row.username)}</td>
        <td style="padding: 8px;">${Math.floor(Number(row.score) || 0)}</td>
        <td style="padding: 8px;">${escapeHtml(row.difficulty || 'Normal')}</td>
        <td style="padding: 8px;">${escapeHtml(row.wave_reached)}</td>
      </tr>
    `).join('');
  } catch (err) {
    tbody.innerHTML = `<tr><td colspan="5" style="text-align:center; color:red;">${escapeHtml(err.message)}</td></tr>`;
  }
});
document.getElementById('btn-close-leaderboard').addEventListener('click', () => {
  document.getElementById('leaderboard-modal').classList.add('hidden');
});

// Save/Load
document.getElementById('btn-saves').addEventListener('click', async () => {
  if (!authToken) {
    showMusicToaster("🔒 Login Required", "Please sign in to save or load games!");
    document.getElementById('btn-profile').click();
    return;
  }
  document.getElementById('saves-modal').classList.remove('hidden');
  const grid = document.getElementById('saves-grid');
  grid.innerHTML = '<div style="grid-column: span 2; text-align: center;">Loading slots...</div>';
  try {
    const data = await apiFetch('/saves');
    const usedSlots = {};
    data.forEach(s => {
      usedSlots[s.slot_id] = {
        time: new Date(s.saved_at).toLocaleString(),
        thumbnail: s.thumbnail
      };
    });
    
    grid.innerHTML = '';
    for (let i = 1; i <= 10; i++) {
      const slot = usedSlots[i];
      const isUsed = !!slot;
      const thumbnailHtml = isUsed && slot.thumbnail
        ? `<img src="${slot.thumbnail}" style="width: 140px; height: 80px; object-fit: cover; border-radius: 4px; border: 1px solid rgba(255,255,255,0.2); box-shadow: 0 4px 10px rgba(0,0,0,0.4);" />`
        : `<div style="width: 140px; height: 80px; background: rgba(0,0,0,0.3); border-radius: 4px; border: 1px dashed rgba(255,255,255,0.1); display: flex; align-items: center; justify-content: center; color: #555; font-size: 11px;">No Thumbnail</div>`;
      
      const html = `
        <div class="save-slot glass-panel" style="padding: 12px; display: flex; flex-direction: column; align-items: center; gap: 8px; min-width: 160px; box-sizing: border-box;">
          <div style="font-weight: bold; font-size: 14px; text-shadow: 0 1px 2px rgba(0,0,0,0.5);">Slot ${i}</div>
          ${thumbnailHtml}
          <div style="font-size: 11px; text-align: center; color: ${isUsed ? '#A0E8AF' : '#888'}; height: 16px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; width: 100%;">${isUsed ? slot.time : 'Empty'}</div>
          <div style="display: flex; gap: 5px; width: 100%;">
            <button class="btn btn-action-card" style="flex:1; font-size:11px; padding:6px 0;" onclick="saveGame(${i})">Save</button>
            <button class="btn btn-action-card" style="flex:1; font-size:11px; padding:6px 0; background: rgba(0,0,0,0.3);" onclick="loadGame(${i})" ${!isUsed ? 'disabled' : ''}>Load</button>
          </div>
        </div>
      `;
      grid.innerHTML += html;
    }
  } catch (err) {
    grid.innerHTML = `<div style="grid-column: span 2; text-align: center; color: red;">${escapeHtml(err.message)}</div>`;
  }
});
document.getElementById('btn-close-saves').addEventListener('click', () => {
  document.getElementById('saves-modal').classList.add('hidden');
});
 
window.saveGame = async (slotId) => {
  showConfirm("Save Game", `Overwrite slot ${slotId}?`, async () => {
    const state = engine.serializeState();
    
    // Generate scaled-down base64 jpeg thumbnail
    let thumbnail = null;
    try {
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = 160;
      tempCanvas.height = 90;
      const tempCtx = tempCanvas.getContext('2d');
      if (tempCtx && engine.canvas) {
        tempCtx.drawImage(engine.canvas, 0, 0, tempCanvas.width, tempCanvas.height);
        thumbnail = tempCanvas.toDataURL('image/jpeg', 0.6);
      }
    } catch (e) {
      console.warn("Failed to generate save thumbnail:", e);
    }

    try {
      await apiFetch('/saves', {
        method: 'POST',
        body: JSON.stringify({ slot_id: slotId, save_data: state, thumbnail })
      });
      showMusicToaster("💾 Game Saved", `Successfully written to Slot ${slotId}!`);
      document.getElementById('btn-saves').click(); // refresh
    } catch(e) {
      showMusicToaster("🚫 Save Failed", e.message);
    }
  });
};

window.loadGame = async (slotId) => {
  showConfirm("Load Game", `Load slot ${slotId}? Current progress will be lost.`, async () => {
    try {
      const data = await apiFetch('/saves/' + slotId);
      engine.deserializeState(data);
      
      // We also need to restore towers dynamically from the saved data
      if (data && data.towers) {
        data.towers.forEach(tData => {
            const tKind = (tData.kind || tData.constructorName);
            let newTower = tKind === 'Helipad'
              ? new Helipad(tData.logicalX, tData.logicalY, engine)
              : tKind === 'Blimp'
              ? new Blimp(tData.logicalX, tData.logicalY, engine)
              : tKind === 'Plane'
              ? new Plane(tData.logicalX, tData.logicalY, engine)
              : new Tower(tData.logicalX, tData.logicalY, tData.type, engine);
            newTower.level = tData.level;
            engine.addEntity(newTower);
        });
      }
      
      document.getElementById('saves-modal').classList.add('hidden');
      showMusicToaster("🎮 Game Loaded", `Slot ${slotId} loaded! Let the parade roll! 🎉`);
    } catch(e) {
      showMusicToaster("🚫 Load Failed", e.message);
    }
  });
};

// --- Idle Demo Mode ---
let idleTimer = null;
let backupDemoState = null;
let demoBoardBackup = null; // pre-demo board snapshot (for undoing demo's additions)
let demoAIInterval = null;

// --- Low-power idle (used when the "Idle Demo Mode" setting is OFF) --------------
// Instead of the attract demo, the game dims and throttles to a minimal framerate,
// then snaps back to full speed on the next mouse/touch/key/wheel event.
let isLowPower = false;
function enterLowPower() {
  if (isLowPower || !window.engine || !engine.gameState || engine.isPaused) return;
  // Don't dim behind an open dialog — the player may be reading it.
  if (document.querySelector('.modal-overlay:not(.hidden)')) return;
  isLowPower = true;
  engine._lowPower = true; // engine loop drops to ~4fps while this is set
  let ov = document.getElementById('lowpower-overlay');
  if (!ov) {
    ov = document.createElement('div');
    ov.id = 'lowpower-overlay';
    ov.style.cssText = 'position:fixed;inset:0;z-index:1400;background:rgba(0,0,0,0.55);' +
      'display:flex;align-items:center;justify-content:center;opacity:0;' +
      'transition:opacity 0.7s ease;';
    ov.innerHTML = '<div style="color:rgba(255,255,255,0.55);font:600 15px system-ui,sans-serif;' +
      'letter-spacing:0.4px;text-align:center;padding:12px 18px;">💤 Idle — move or tap to resume</div>';
    // The overlay captures the wake tap itself (auto pointer-events) so resuming
    // doesn't also click the board underneath.
    const wake = (ev) => { ev.preventDefault(); ev.stopPropagation(); exitLowPower(); };
    ov.addEventListener('pointerdown', wake);
    ov.addEventListener('touchstart', wake, { passive: false });
    document.body.appendChild(ov);
  }
  ov.style.pointerEvents = 'auto';
  ov.style.display = 'flex';
  requestAnimationFrame(() => { ov.style.opacity = '1'; });
}
function exitLowPower() {
  if (!isLowPower) return;
  isLowPower = false;
  if (window.engine) { engine._lowPower = false; engine.lastTime = performance.now(); } // reset dt so no catch-up jump
  const ov = document.getElementById('lowpower-overlay');
  if (ov) {
    ov.style.pointerEvents = 'none'; // stop blocking the board immediately, even during fade
    ov.style.opacity = '0';
    setTimeout(() => { if (!isLowPower && ov) ov.style.display = 'none'; }, 700);
  }
}
window.enterLowPower = enterLowPower;
window.exitLowPower = exitLowPower;
let demoTargetEntity = null;

// Undo the demo's additions on the player's real map: drop every entity the demo
// spawned (demo towers, balloons, floats, trains, particles, popups) while keeping
// the player's original entities and all map furniture, undo AI tower upgrades, and
// restore tower-occupancy flags. The map itself is never regenerated, so the exact
// selected level remains. Used by both the periodic demo refresh and demo exit.
function cleanDemoAdditions() {
  if (!demoBoardBackup) return;
  engine.entities = engine.entities.filter(e => demoBoardBackup.entitySet.has(e));
  engine.particles = [];
  demoBoardBackup.towerLevels.forEach((lvl, t) => { t.level = lvl; });
  engine.tiles.forEach(t => { t.hasTower = demoBoardBackup.towerTiles.has(t); });
}

// Close every open menu / dropdown / modal / panel so nothing floats over the
// demo. Called when demo mode kicks in (menus may already be open at that point).
function closeAllOverlaysForDemo() {
  const modalIds = ['map-select-modal', 'course-library-modal', 'campaign-modal',
    'settings-modal', 'auth-modal', 'leaderboard-modal', 'saves-modal', 'admin-modal',
    'confirm-modal', 'alert-modal'];
  modalIds.forEach(id => { const el = document.getElementById(id); if (el) el.classList.add('hidden'); });
  const gc = document.getElementById('game-controls'); if (gc) gc.classList.remove('show-menu');      // hamburger
  const camDrop = document.getElementById('top-camera-dropdown'); if (camDrop) camDrop.classList.add('hidden');
  const camGroup = document.getElementById('camera-control-group'); if (camGroup) camGroup.classList.remove('dropdown-active');
  if (typeof hideContextMenu === 'function') hideContextMenu();
  if (window.paradeMusicUI && window.paradeMusicUI.setOpen) window.paradeMusicUI.setOpen(false); // music panel
}

function startDemoMode() {
  // A paused game must sit on the paused screen and wait for the user — never
  // slide into the idle demo.
  if (isDemoMode || engine.isPaused || engine.gameState.status === 'won' || engine.gameState.status === 'lost' || engine.gameState.waveActive) return;
  
  isDemoMode = true;
  closeAllOverlaysForDemo();   // clear any open menus/modals/panels so none float over the demo
  document.body.classList.add('demo-mode-active');
  const demoOverlay = document.getElementById('demo-overlay');
  if (demoOverlay) demoOverlay.classList.remove('hidden');
  
  // Backup state
  backupDemoState = engine.serializeState();

  // The demo plays on the player's ACTUAL selected map — it is never regenerated,
  // so exiting returns to the exact same level. We only snapshot enough to undo
  // the demo's ADDITIONS afterwards: which entities existed pre-demo, which tiles
  // were tower-occupied, and the original tower levels (the AI may upgrade them).
  const _isTower = e => { const k = e.kind || (e.constructor && e.constructor.name); return k === 'Tower' || k === 'Helipad'; };
  demoBoardBackup = {
    entitySet: new Set(engine.entities),
    towerTiles: new Set(engine.tiles.filter(t => t.hasTower)),
    towerLevels: new Map(engine.entities.filter(_isTower).map(t => [t, t.level]))
  };

  // Make game loop run infinitely
  engine.gameState.waveActive = true;
  engine.isPaused = false;
  
  engine.isDemoMode = true;
  
  // Resume AudioContext if suspended (Web Audio SFX).
  if (engine.audioCtx && engine.audioCtx.state !== 'running') {
    engine.audioCtx.resume();
  }

  engine.updateSfxMasterGain();

  // (demo-start music reassert removed with the old player)

  // Pre-populate some towers
  const towerTypes = ['confetti_cannon', 'street_sweeper', 'marching_band', 'news_chopper', 'kickettes', 'spotlight', 'billboard'];
  
  // Helper to find all valid tiles that don't have buildings or towers
  const getValidTiles = () => {
    return engine.tiles.filter(t => {
      if (t.isRoad || t.isDecor || t.hasTower) return false;
      // No land towers on water/bridge (fireboat handled separately). Check by grid
      // cell so the terrain tile beneath a water cell is excluded too.
      if (map.hasWaterway) {
        const ck = Math.floor((t.x - map.startX) / map.tileSize) + ',' + Math.floor((t.y - map.startY) / map.tileSize);
        if (map.waterCells.has(ck) || map.bridgeCells.has(ck)) return false;
      }
      if (typeof t.typeId === 'string' && t.typeId.startsWith('railroad_')) return false; // no towers on tracks
      const snappedX = t.x;
      const snappedY = t.y;
      const hasBuilding = engine.entities.some(ent => {
        if ((ent.kind || ent.constructor.name) === 'BuildingFacade') {
          const halfSize = (ent.footprintSize * ent.size) / 2;
          return snappedX >= ent.x - halfSize && snappedX < ent.x + halfSize && 
                 snappedY >= ent.y - halfSize && snappedY < ent.y + halfSize;
        }
        return false;
      });
      return !hasBuilding;
    });
  };

  // Helper to populate towers
  const populateDemoTowers = (count) => {
    let validTiles = getValidTiles();
    for (let i = 0; i < count && validTiles.length > 0; i++) {
      const tileIdx = Math.floor(Math.random() * validTiles.length);
      const tile = validTiles.splice(tileIdx, 1)[0];
      
      const type = towerTypes[Math.floor(Math.random() * towerTypes.length)];
      let newTower = type === 'news_chopper' ? new Helipad(tile.x, tile.y, engine) : new Tower(tile.x, tile.y, type, engine);
      newTower.level = Math.floor(Math.random() * 3) + 1;
      engine.addEntity(newTower);
      tile.hasTower = true;
    }
  };

  populateDemoTowers(6);
  
  let demoTicks = 0;
  // Start autonomous AI ghost player
  if (demoAIInterval) clearInterval(demoAIInterval);
  demoAIInterval = setInterval(() => {
    if (!isDemoMode || engine.isPaused) return;
    
    demoTicks++;
    if (demoTicks >= 180) {
      demoTicks = 0;
      // Periodically refresh the tower showcase — on the SAME selected map. The
      // wave loop self-restarts via waveCountdown, so we never regenerate the map.
      cleanDemoAdditions();
      engine.gameState.lives = 10;
      engine.gameState.money = 99999;
      demoTargetEntity = null;
      populateDemoTowers(6);
      return;
    }
    
    // Infinite money for demo
    engine.gameState.money = 99999;
    
    const currentTowers = engine.entities.filter(e => (e.kind || e.constructor.name) === 'Tower' || (e.kind || e.constructor.name) === 'Helipad');
    
    // Auto place towers occasionally, but keep a strict max of 6
    if (currentTowers.length < 6 && Math.random() < 0.2) {
      const type = towerTypes[Math.floor(Math.random() * towerTypes.length)];
      
      // Find a random valid tile using the helper
      let currentValid = getValidTiles();
      if (currentValid.length > 0) {
        const tile = currentValid[Math.floor(Math.random() * currentValid.length)];
        let newTower = type === 'news_chopper' ? new Helipad(tile.x, tile.y, engine) : new Tower(tile.x, tile.y, type, engine);
        engine.addEntity(newTower);
        tile.hasTower = true;
      }
    }
    
    // Auto upgrade towers occasionally
    if (Math.random() < 0.1) {
      if (currentTowers.length > 0) {
        const t = currentTowers[Math.floor(Math.random() * currentTowers.length)];
        if (t.level < 3) t.level++;
      }
    }
    
    // Pick a new target occasionally
    if (Math.random() < 0.2 || !demoTargetEntity || demoTargetEntity.dead) {
      const targets = engine.entities.filter(e => (e.kind || e.constructor.name) === 'Balloon' || (e.kind || e.constructor.name) === 'Float');
      if (targets.length > 0) {
        demoTargetEntity = targets[Math.floor(Math.random() * targets.length)];
      } else {
        demoTargetEntity = null;
      }
    }
  }, 1000);
}

function stopDemoMode() {
  if (!isDemoMode) return;
  isDemoMode = false;
  engine.isDemoMode = false;
  
  document.body.classList.remove('demo-mode-active');
  const demoOverlay = document.getElementById('demo-overlay');
  if (demoOverlay) demoOverlay.classList.add('hidden');
  
  if (demoAIInterval) {
    clearInterval(demoAIInterval);
    demoAIInterval = null;
  }
  
  demoTargetEntity = null;
  engine.camZoom = 0.625; // Reset zoom
  engine.centerCamera();
  
  // Restore state
  if (backupDemoState && demoBoardBackup) {
    // Remove everything the demo added; the player's map + original towers remain
    // (the map was never regenerated), so the exact same selected level returns.
    cleanDemoAdditions();

    // Game numbers back to the pre-demo snapshot (mirror deserialize's clamping).
    const gs = JSON.parse(JSON.stringify(backupDemoState.gameState));
    gs.money = Math.max(0, Math.floor(Number(gs.money) || 0));
    gs.lives = Math.max(0, Math.floor(Number(gs.lives) || 0));
    gs.wave  = Math.max(1, Math.floor(Number(gs.wave)  || 1));
    gs.score = Math.max(0, Math.floor(Number(gs.score) || 0));
    engine.gameState = gs;
    engine.isPaused = backupDemoState.isPaused;
    engine.centerCamera();

    // Restore SFX gain. Leave MUSIC exactly as the player set it via the Music
    // menu — demo never changed it, so exiting demo must not override their choice.
    engine.updateSfxMasterGain();
    updateMusicVolume();

    // Explicitly restore the start wave button state and visibility
    const btnStart = document.getElementById('btn-start');
    if (btnStart) {
      if (engine.gameState.waveActive) {
        btnStart.style.display = 'none';
      } else {
        btnStart.style.display = 'block';
        btnStart.innerHTML = `▶️ Start Wave ${engine.gameState.wave} <span id="wave-countdown" style="opacity: 0.7; font-size: 0.9em; margin-left: 8px;"></span>`;
        btnStart.disabled = false;
      }
    }

    backupDemoState = null;
    demoBoardBackup = null;
  }
}

// General-purpose corner notifier. Despite the name it is used for a dozen unrelated
// things — boss arrivals, Sweeper relocation, route unlocks, saves/loads, login prompts,
// tutorial completion — and it used to hardcode a 🎵 in the icon chip for all of them.
// A musical note next to "BOSS INCOMING" is simply wrong, and it also duplicated the
// emoji every caller already puts at the front of its title.
//
// The icon is now derived from the title's own leading emoji, which is stripped from the
// title text so it is not shown twice. That fixes every existing call site without
// touching any of them: ⚠️ BOSS INCOMING, 💾 Game Saved, 🔓 Route Unlocked, and so on.
// An explicit `icon` argument overrides, and 🎵 remains the fallback for a genuine
// music notification with no emoji of its own.
function showMusicToaster(title, flavor, icon) {
  let iconChar = icon || '';
  let displayTitle = title;
  if (!iconChar) {
    // Extended_Pictographic covers emoji proper; the optional FE0F swallows a trailing
    // variation selector so "⚠️" is captured whole rather than leaving a stray joiner.
    const m = /^(\p{Extended_Pictographic}\uFE0F?)\s*([\s\S]*)$/u.exec(String(title || ''));
    if (m) { iconChar = m[1]; displayTitle = m[2]; }
  }
  if (!iconChar) iconChar = '🎵';

  let toaster = document.getElementById('music-toaster');
  if (!toaster) {
    toaster = document.createElement('div');
    toaster.id = 'music-toaster';
    toaster.className = 'music-toaster';
    toaster.innerHTML = `
      <div class="music-icon-container" id="music-toaster-icon">🎵</div>
      <div class="music-toaster-info">
        <div class="music-toaster-title" id="music-toaster-title"></div>
        <div class="music-toaster-flavor" id="music-toaster-flavor"></div>
      </div>
    `;
    document.getElementById('game-container').appendChild(toaster);
  }
  
  // Set every call — the toaster element is created once and reused, so an icon written
  // only at creation time would stick on whatever the first notification happened to be.
  const iconEl = document.getElementById('music-toaster-icon');
  if (iconEl) iconEl.innerText = iconChar;
  document.getElementById('music-toaster-title').innerText = displayTitle;
  document.getElementById('music-toaster-flavor').innerText = flavor;
  
  // Trigger transition
  toaster.classList.remove('active');
  void toaster.offsetWidth; // Reflow to reset animation
  toaster.classList.add('active');
  
  // Hide after 5 seconds
  if (window.musicToasterTimeout) clearTimeout(window.musicToasterTimeout);
  window.musicToasterTimeout = setTimeout(() => {
    toaster.classList.remove('active');
  }, 5000);
}

function resetIdleTimer(e) {
  // Any input immediately wakes a dimmed low-power idle (incl. mousemove/wheel).
  if (isLowPower) exitLowPower();

  if (isDemoMode) {
    if (e && (e.type === 'mousedown' || e.type === 'touchstart' || e.type === 'click')) {
      stopDemoMode();
    } else {
      return; // Ignore mouse moves and wheel scrolls while in demo mode
    }
  }

  if (idleTimer) clearTimeout(idleTimer);

  // Only start idle timer if we've successfully loaded the game
  if (engine.gameState) {
    idleTimer = setTimeout(() => {
      const hasTowers = engine.entities.some(e => { const k = e.kind || e.constructor.name; return k === 'Tower' || k === 'Helipad' || k === 'Blimp' || k === 'Plane'; });
      const isGameInProgress = engine.gameState.waveActive || engine.gameState.wave > 1 || hasTowers;

      if (isGameInProgress || engine.isPaused) return;

      // Attract demo, unless the player disabled it — then just dim to low power and
      // sit on the current board until they interact again.
      if (engine.settings && engine.settings.demoModeEnabled === false) {
        enterLowPower();
      } else {
        startDemoMode();
      }
    }, 30000);
  }
}

// Intercept interactions to reset idle timer
window.startDemoMode = startDemoMode;   // exposed for diagnostics
window.stopDemoMode = stopDemoMode;

// --- On-screen camera & zoom controls capsule ------------------------------
// The unified camera controls tray on the right side of the screen.
(function initZoomControls() {
  if (typeof engine === 'undefined') return;
  const wrap = document.createElement('div');
  wrap.id = 'zoom-controls';
  wrap.className = 'hidden';
  
  // Tune stroke weights and opacities for perfect perceived optical weight and brightness matching
  const strokeZoom = 2.0;    // Simple lines (+ / -)
  const strokeCenter = 1.6;  // Circle + intersecting crosshairs (denser shape)
  const strokeCamera = 1.7;  // Rectangle + triangle body (densest shape)
  const iconOpacity = 0.85;  // Consistent soft brightness
  
  // 1. Zoom Buttons helper
  const mkZoom = (type, aria, factor) => {
    const b = document.createElement('button');
    b.className = 'zoom-btn';
    b.setAttribute('aria-label', aria);
    b.title = aria;
    
    if (type === '+') {
      b.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${strokeZoom}" stroke-linecap="round" stroke-linejoin="round" style="display: block; opacity: ${iconOpacity};">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
      </svg>`;
    } else {
      b.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${strokeZoom}" stroke-linecap="round" stroke-linejoin="round" style="display: block; opacity: ${iconOpacity};">
        <line x1="5" y1="12" x2="19" y2="12"></line>
      </svg>`;
    }
    
    const act = (e) => { e.preventDefault(); e.stopPropagation(); engine.zoomBy(factor); };
    b.addEventListener('click', act);
    b.addEventListener('touchend', act, { passive: false });
    return b;
  };
  
  // 2. Re-Center button helper
  const mkCenter = () => {
    const b = document.createElement('button');
    b.className = 'zoom-btn';
    b.setAttribute('aria-label', 'Re-Center Map');
    b.title = 'Re-Center Map';
    b.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${strokeCenter}" stroke-linecap="round" stroke-linejoin="round" style="display: block; opacity: ${iconOpacity};">
      <circle cx="12" cy="12" r="6"></circle>
      <line x1="12" y1="2" x2="12" y2="22"></line>
      <line x1="2" y1="12" x2="22" y2="12"></line>
    </svg>`;
    const act = (e) => {
      e.preventDefault(); e.stopPropagation();
      engine.centerCamera();
      cameraMode = 'free';
      trackedEntity = null;
      updateCameraModeUI();
    };
    b.addEventListener('click', act);
    b.addEventListener('touchend', act, { passive: false });
    return b;
  };

  // 3. Camera mode toggle helper
  const mkCameraToggle = () => {
    const b = document.createElement('button');
    b.className = 'zoom-btn camera-toggle-btn';
    b.setAttribute('aria-label', 'Toggle Camera Mode');
    b.title = 'Toggle Camera Mode';
    b.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${strokeCamera}" stroke-linecap="round" stroke-linejoin="round" style="display: block; opacity: ${iconOpacity};">
      <path d="M23 7l-7 5 7 5V7z"></path>
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
    </svg>`;
    const act = (e) => {
      e.preventDefault(); e.stopPropagation();
      if (cameraMode === 'action') {
        cameraMode = 'free';
      } else {
        cameraMode = 'action';
      }
      trackedEntity = null;
      updateCameraModeUI();
    };
    b.addEventListener('click', act);
    b.addEventListener('touchend', act, { passive: false });
    return b;
  };

  wrap.appendChild(mkZoom('+', 'Zoom in', 1.25));
  wrap.appendChild(mkZoom('−', 'Zoom out', 0.8));
  wrap.appendChild(mkCenter());
  wrap.appendChild(mkCameraToggle());

  // Insert the capsule into the page. Without this the whole camera/zoom control
  // group is built but never attached to the DOM — which is why it vanished.
  if (!document.getElementById('zoom-controls')) document.body.appendChild(wrap);
})();

// --- Input diagnostics overlay (opt-in via ?inputdebug) --------------------
// Load the game with ?inputdebug to see exactly which events a pinch / two-finger
// scroll produces on this device (crucial for iPad, where the delivery differs).
if (location.search.indexOf('debug') !== -1) {
  const box = document.createElement('div');
  box.style.cssText = 'position:fixed;left:8px;bottom:8px;z-index:2147483647;background:rgba(0,0,0,0.85);color:#7CFC7C;font:13px/1.5 monospace;padding:10px;border-radius:8px;white-space:pre;pointer-events:none;';
  document.body.appendChild(box);
  const S = { wheel: 0, wheelCtrl: 0, gs: 0, gc: 0, ge: 0, ts: 0, tm: 0, tm2: 0, scale: '-', dist: '-', lastW: '-' };
  const render = () => {
    box.textContent =
      'INPUT DEBUG  (pinch, then 2-finger scroll)\n' +
      'GestureEvent=' + (typeof GestureEvent !== 'undefined') + '  touch=' + (typeof TouchEvent !== 'undefined') + '\n' +
      'wheel=' + S.wheel + '  ctrl-wheels=' + S.wheelCtrl + '\n' +
      '   last wheel: ' + S.lastW + '\n' +
      'gesture start/change/end = ' + S.gs + '/' + S.gc + '/' + S.ge + '   scale=' + S.scale + '\n' +
      'touch start/move/2-finger = ' + S.ts + '/' + S.tm + '/' + S.tm2 + '   dist=' + S.dist;
  };
  const opt = { capture: true, passive: true };
  addEventListener('wheel', (e) => { S.wheel++; if (e.ctrlKey) S.wheelCtrl++; S.lastW = 'ctrl=' + e.ctrlKey + ' dm=' + e.deltaMode + ' dx=' + Math.round(e.deltaX) + ' dy=' + Math.round(e.deltaY); render(); }, opt);
  addEventListener('gesturestart', (e) => { S.gs++; if (e.scale != null) S.scale = e.scale.toFixed(3); render(); }, opt);
  addEventListener('gesturechange', (e) => { S.gc++; if (e.scale != null) S.scale = e.scale.toFixed(3); render(); }, opt);
  addEventListener('gestureend', () => { S.ge++; render(); }, opt);
  addEventListener('touchstart', () => { S.ts++; render(); }, opt);
  addEventListener('touchmove', (e) => { S.tm++; if (e.touches && e.touches.length >= 2) { S.tm2++; const a = e.touches[0], b = e.touches[1]; S.dist = Math.round(Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)); } render(); }, opt);
  render();
}

// ===================== Meta-economy spend UI =====================
// One "Spend Dollars" shop (top-bar 💵 button) covering: convert $→Permits any
// time (#4), and pre-wave starting-Permit (#1) + extra-life (#2) tiers. Plus a
// Continue buy-in (#3) added to the game-over modal. All spending routes through
// spendDollars() (server-authoritative). Built programmatically with null-guards
// so a missing element can never crash the game.
(function initSpendUI() {
  if (typeof engine === 'undefined') return;
  const fmt$ = n => '$' + (n | 0).toLocaleString();

  // Toast Notification System for Weather feedback
  function showWeatherToast(message, isWarning = false) {
    const toast = document.createElement('div');
    toast.className = 'weather-toast';
    toast.style.cssText = `
      position: fixed;
      /* Same notch / Dynamic Island clearance as the toast host above. */
      top: calc(24px + env(safe-area-inset-top));
      left: 50%;
      transform: translateX(-50%) translateY(-20px);
      background: ${isWarning ? 'rgba(231, 76, 60, 0.2)' : 'rgba(46, 204, 113, 0.2)'};
      backdrop-filter: blur(25px) saturate(180%);
      -webkit-backdrop-filter: blur(25px) saturate(180%);
      border: 1px solid ${isWarning ? 'rgba(231, 76, 60, 0.35)' : 'rgba(46, 204, 113, 0.35)'};
      color: ${isWarning ? '#ff7675' : '#2ecc71'};
      padding: 12px 24px;
      border-radius: 30px;
      font-family: 'Outfit', sans-serif;
      font-weight: 600;
      font-size: 14px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
      z-index: 2147483647;
      transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
      opacity: 0;
      pointer-events: none;
      letter-spacing: 0.5px;
      display: flex;
      align-items: center;
      gap: 8px;
    `;
    
    const icon = isWarning ? '⚠️' : '⚡';
    toast.innerHTML = `<span>${icon}</span><span>${message}</span>`;
    document.body.appendChild(toast);
    
    requestAnimationFrame(() => {
      toast.style.opacity = '1';
      toast.style.transform = 'translateX(-50%) translateY(0)';
    });
    
    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateX(-50%) translateY(-20px)';
      setTimeout(() => {
        toast.remove();
      }, 400);
    }, 2500);
  }

  const modal = document.createElement('div');
  modal.id = 'spend-modal';
  modal.className = 'modal-overlay hidden';
  modal.innerHTML =
    '<div style="max-width:420px;width:90%;background:#141824;border:1px solid rgba(255,255,255,0.12);border-radius:14px;padding:20px;box-shadow:0 12px 40px rgba(0,0,0,0.6);color:#e8ecf4;font-family:Outfit,sans-serif;">' +
      '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">' +
        '<h3 style="margin:0;font-size:1.2em;display:flex;align-items:center;gap:8px;">' +
          '<svg width="1.15em" height="1.15em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style="vertical-align:-0.2em;flex:none;">' +
            '<defs>' +
              '<linearGradient id="spendBagGrad2" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="#FFE066"/><stop offset="100%" stop-color="#F5A623"/></linearGradient>' +
            '</defs>' +
            '<path d="M7.5,6.5 C7.5,0.8 16.5,0.8 16.5,6.5" fill="none" stroke="#C77F14" stroke-width="1.7" stroke-linecap="round"/>' +
            '<path d="M5,6.5 L19,6.5 L18,17.5 Q18,19.5 16,19.5 L8,19.5 Q6,19.5 6,17.5 Z" fill="url(#spendBagGrad2)" stroke="#C77F14" stroke-width="1.3" stroke-linejoin="round"/>' +
            '<path d="M5.4,6.5 L18.6,6.5 L18.35,9.1 L5.65,9.1 Z" fill="#FFD24D"/>' +
            '<text x="12" y="16" text-anchor="middle" font-size="9" font-weight="700" fill="#7A4A00" font-family="sans-serif">$</text>' +
          '</svg><span>Spend Dollars</span></h3>' +
        '<button id="spend-close" class="btn" style="padding:2px 10px;">✕</button></div>' +
      '<div id="spend-balance" style="color:#4ade80;font-weight:700;margin-bottom:12px;"></div>' +
      '<div id="spend-body" style="display:flex;flex-direction:column;gap:9px;"></div>' +
      '<div style="margin-top:12px;font-size:0.75em;color:#8b93a6;">Dollars ($) are your winnings. Permits are the in-match build resource.</div>' +
    '</div>';
  document.body.appendChild(modal);
  modal.addEventListener('click', e => { if (e.target === modal) modal.classList.add('hidden'); });
  modal.querySelector('#spend-close').addEventListener('click', () => modal.classList.add('hidden'));

  const mkRow = (label, cost, enabled, onClick) => {
    const b = document.createElement('button');
    b.className = 'btn';
    b.style.cssText = 'display:flex;justify-content:space-between;gap:12px;width:100%;padding:10px 14px;opacity:' + (enabled ? '1' : '0.45') + ';';
    b.innerHTML = '<span>' + label + '</span><span style="color:' + (enabled ? '#ffd93d' : '#8b93a6') + ';font-weight:700;">' + fmt$(cost) + '</span>';
    b.disabled = !enabled;
    if (enabled) b.addEventListener('click', onClick);
    return b;
  };

  function renderSpend() {
    const bal = modal.querySelector('#spend-balance');
    if (bal) bal.textContent = 'Bank: ' + fmt$(playerCash);
    const body = modal.querySelector('#spend-body');
    if (!body) return;
    body.innerHTML = '';
    const isWaveActive = !!(engine.gameState && engine.gameState.waveActive);
    const preWave = !isWaveActive;

    const wCost = ECONOMY.DOLLARS_PER_PERMIT * ECONOMY.WITHDRAW_PERMITS;
    body.appendChild(mkRow('Convert to ' + PERMIT + ECONOMY.WITHDRAW_PERMITS + ' Permits', wCost, canAffordDollars(wCost), async () => {
      if (await spendDollars(wCost)) { engine.gameState.money += ECONOMY.WITHDRAW_PERMITS; renderSpend(); }
    }));

    // In-match weather spells (only clickable during active wave)
    const hWeather = document.createElement('div');
    hWeather.textContent = 'Weather Spells (Cast during active wave)';
    hWeather.style.cssText = 'margin-top:8px;font-size:0.8em;color:#8b93a6;';
    body.appendChild(hWeather);

    // Lightning Strike
    const lightningCost = getTowerCfg(engine, 'lightning_spell').cost;
    const lEnabled = isWaveActive && canAffordDollars(lightningCost);
    body.appendChild(mkRow('⚡ Lightning Strike (Pops up to 6 balloons)', lightningCost, lEnabled, async () => {
      modal.classList.add('hidden');
      
      const balloons = engine.entities.filter(ent => (ent.kind || ent.constructor.name) === 'Balloon' && !ent.dead && ent.animState !== 'pop' && !ent.isWindBlown);
      if (balloons.length === 0) {
        showWeatherToast("No active balloons to strike!", true);
        return;
      }
      
      const ok = await spendDollars(lightningCost);
      if (!ok) return;

      const shuffled = [...balloons].sort(() => Math.random() - 0.5);
      const targets = shuffled.slice(0, Math.min(6, shuffled.length));

      targets.forEach(b => {
        b.takeDamage(200);
        engine.addEntity(new FloatingText(b.x, b.y, "⚡ STRIKE!", "#ff9f1a", engine));
      });

      engine.addEntity(new LightningSpellEffect(engine, targets));
      engine.playSynthThunder();
      showWeatherToast("Lightning Strike Cast!", false);
    }));

    // Wind Gust
    const windCost = getTowerCfg(engine, 'wind_spell').cost;
    const wEnabled = isWaveActive && canAffordDollars(windCost);
    body.appendChild(mkRow('🌪️ Wind Gust (Slows all & blows 4 away)', windCost, wEnabled, async () => {
      modal.classList.add('hidden');
      
      const balloons = engine.entities.filter(ent => (ent.kind || ent.constructor.name) === 'Balloon' && !ent.dead && ent.animState !== 'pop' && !ent.isWindBlown);
      if (balloons.length === 0) {
        showWeatherToast("No active balloons on course!", true);
        return;
      }

      const ok = await spendDollars(windCost);
      if (!ok) return;

      balloons.forEach(b => {
        b.slowTimer = 6.0;
        b.slowFactor = 0.4;
        engine.addEntity(new FloatingText(b.x, b.y, "🌪️ SLOW!", "#54a0ff", engine));
      });

      // Pick ONE gust direction for this cast so the blown balloons and the wind
      // visual agree. World +x maps to screen-right, so blowRight → windVx > 0; the
      // visual's screen angle points up-and-to-that-side to match the fly-off.
      const blowRight = Math.random() > 0.5;
      const windDir = blowRight ? -Math.PI / 3 : -2 * Math.PI / 3; // screen angle (up-right / up-left)

      const shuffled = [...balloons].sort(() => Math.random() - 0.5);
      const blowAway = shuffled.slice(0, Math.min(4, shuffled.length));
      blowAway.forEach(b => {
        b.isWindBlown = true;
        b.windVx = (blowRight ? 1 : -1) * (170 + Math.random() * 90);  // consistent side
        b.windVy = (blowRight ? -1 : 1) * (40 + Math.random() * 50);
        b.windVz = 170 + Math.random() * 130;                          // lift off the ground
        engine.addEntity(new FloatingText(b.x, b.y, "🎈 BLOWN AWAY!", "#a55eea", engine));
      });

      engine.addEntity(new WindSpellEffect(engine, windDir));
      engine.playSynthWindGust();
      showWeatherToast("Wind Gust Triggered!", false);
    }));

    // Float Armor (15-second invulnerability for parade floats)
    const armorCfg = getTowerCfg(engine, 'float_armor_spell');
    const armorCost = (armorCfg && armorCfg.cost) ? armorCfg.cost : 2500;
    const aEnabled = isWaveActive && canAffordDollars(armorCost);
    body.appendChild(mkRow('🛡️ Float Armor (15s Invulnerability for Floats)', armorCost, aEnabled, async () => {
      modal.classList.add('hidden');
      
      const floats = engine.entities.filter(ent => (ent.kind || ent.constructor.name) === 'Float' && !ent.dead);
      if (floats.length === 0) {
        showWeatherToast("No active floats on course to protect!", true);
        return;
      }

      const ok = await spendDollars(armorCost);
      if (!ok) return;

      engine.floatArmorTimer = 15.0;
      floats.forEach(f => {
        engine.addEntity(new FloatingText(f.x, f.y, "🛡️ ARMORED! (15s)", "#ffd700", engine));
      });

      engine.addEntity(new FloatArmorSpellEffect(engine));
      if (engine.playMiscSound) engine.playMiscSound('misc_celebrate_stab', 0, 0);
      showWeatherToast("🛡️ Float Armor Activated! 15s Invulnerability", false);
    }));

    if (preWave) {
      const h = document.createElement('div');
      h.textContent = 'Starting boosts (this run)';
      h.style.cssText = 'margin-top:8px;font-size:0.8em;color:#8b93a6;';
      body.appendChild(h);
      ECONOMY.START_PERMIT_TIERS.forEach(t => body.appendChild(
        mkRow(PERMIT + '+' + t.permits + ' starting Permits', t.dollars, canAffordDollars(t.dollars), async () => {
          if (await spendDollars(t.dollars)) { engine.gameState.money += t.permits; renderSpend(); }
        })));
      ECONOMY.START_LIFE_TIERS.forEach(t => body.appendChild(
        mkRow('❤️ +' + t.lives + ' starting lives', t.dollars, canAffordDollars(t.dollars), async () => {
          if (await spendDollars(t.dollars)) { engine.gameState.lives += t.lives; renderSpend(); }
        })));
    }
  }

  // Call-to-action polish for the Spend button: a periodic light sweep across the
  // bag, a twinkle, and a gentle bob. Honors prefers-reduced-motion.
  if (!document.getElementById('spend-cta-style')) {
    const st = document.createElement('style');
    st.id = 'spend-cta-style';
    st.textContent =
      '#btn-spend{transition:transform .12s ease;}' +
      '#btn-spend svg{overflow:visible;animation:spendBob 3s ease-in-out infinite;}' +
      '#btn-spend:hover svg{animation:none;transform:scale(1.14);}' +
      '@keyframes spendBob{0%{transform:translateY(0) rotate(0deg) scale(1);}4%{transform:translateY(-1.5px) rotate(-9deg) scale(1.08);}8%{transform:translateY(-1px) rotate(9deg) scale(1.06);}12%{transform:translateY(0) rotate(-4deg) scale(1.02);}16%,100%{transform:translateY(0) rotate(0deg) scale(1);}}' +
      '.bag-shine{fill:#ffffff;opacity:.85;animation:bagShine 4.2s ease-in-out infinite;}' +
      '@keyframes bagShine{0%,70%,100%{transform:translateX(-13px);}83%{transform:translateX(17px);}}' +
      '.bag-sparkle{transform-box:fill-box;transform-origin:center;animation:bagSparkle 4.2s ease-in-out infinite;}' +
      '@keyframes bagSparkle{0%,58%,100%{opacity:0;transform:scale(.2);}74%{opacity:1;transform:scale(1);}86%{opacity:0;transform:scale(.4);}}' +
      '@media (prefers-reduced-motion:reduce){#btn-spend svg,.bag-shine,.bag-sparkle{animation:none;}.bag-shine,.bag-sparkle{opacity:0;}}';
    document.head.appendChild(st);
  }

  // Place the Spend button in the left stat cluster, right after the bank (Dollars)
  // readout — "here are your Dollars, tap to spend them."
  const statsWrap = document.querySelector('.stats-container');
  const bankStat = document.querySelector('.stat-item.bank');
  if (statsWrap) {
    const btn = document.createElement('button');
    btn.id = 'btn-spend';
    btn.className = 'icon-btn';
    btn.title = 'Spend Dollars → Permits';
    btn.setAttribute('aria-label', 'Spend Dollars');
    btn.style.cssText = 'display:inline-flex;align-items:center;justify-content:center;align-self:center;height:24px;margin:0 4px;padding:0;background:transparent;border:none;cursor:pointer;line-height:0;vertical-align:middle;';
    btn.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="display:block;">' +
      '<defs>' +
        '<linearGradient id="spendBagGrad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="#FFE066"/><stop offset="100%" stop-color="#F5A623"/></linearGradient>' +
        '<filter id="spendBagGlow"><feDropShadow dx="0" dy="1" stdDeviation="1.2" flood-color="#F5A623" flood-opacity="0.45"/></filter>' +
        '<clipPath id="spendBagClip"><path d="M5,6.5 L19,6.5 L18,17.5 Q18,19.5 16,19.5 L8,19.5 Q6,19.5 6,17.5 Z"/></clipPath>' +
      '</defs>' +
      '<path d="M7.5,6.5 C7.5,0.8 16.5,0.8 16.5,6.5" fill="none" stroke="#C77F14" stroke-width="1.7" stroke-linecap="round"/>' +
      '<path d="M5,6.5 L19,6.5 L18,17.5 Q18,19.5 16,19.5 L8,19.5 Q6,19.5 6,17.5 Z" fill="url(#spendBagGrad)" fill-opacity="0.92" stroke="#C77F14" stroke-width="1.3" stroke-linejoin="round" filter="url(#spendBagGlow)"/>' +
      '<path d="M5.4,6.5 L18.6,6.5 L18.35,9.1 L5.65,9.1 Z" fill="#FFD24D"/>' +
      '<g clip-path="url(#spendBagClip)"><polygon class="bag-shine" points="9,5 11.5,5 7,20 4.5,20"/></g>' +
      '<text x="12" y="16" text-anchor="middle" font-size="9" font-weight="700" fill="#7A4A00" font-family="sans-serif">$</text>' +
      '<polygon class="bag-sparkle" points="18.5,2.6 19.2,4.5 21.1,5.2 19.2,5.9 18.5,7.8 17.8,5.9 15.9,5.2 17.8,4.5" fill="#FFF6CC"/>' +
      '</svg>';
    btn.addEventListener('click', () => { renderSpend(); modal.classList.remove('hidden'); });
    if (bankStat && bankStat.nextSibling) statsWrap.insertBefore(btn, bankStat.nextSibling);
    else statsWrap.appendChild(btn);
  }

  // Continue-after-loss buy-in on the game-over modal.
  const goModal = document.getElementById('game-over-modal');
  const retryBtn = document.getElementById('btn-retry-level');
  if (goModal && retryBtn && retryBtn.parentElement) {
    const cont = document.createElement('button');
    cont.id = 'btn-continue-run';
    cont.className = 'btn';
    cont.style.cssText = 'background:linear-gradient(90deg,#16a34a,#22c55e);color:#fff;margin-right:8px;';
    retryBtn.parentElement.insertBefore(cont, retryBtn);
    window._refreshContinueBtn = () => {
      const cost = ECONOMY.CONTINUE_BASE_DOLLARS * (runContinues + 1);
      const afford = canAffordDollars(cost);
      cont.textContent = '❤️ Continue (' + fmt$(cost) + ')';
      cont.disabled = !afford;
      cont.style.opacity = afford ? '1' : '0.45';
    };
    cont.addEventListener('click', async () => {
      const cost = ECONOMY.CONTINUE_BASE_DOLLARS * (runContinues + 1);
      if (!canAffordDollars(cost)) return;
      if (!(await spendDollars(cost))) return;
      runContinues++;
      engine.gameState.lives = ECONOMY.BASE_LIVES;
      engine.gameState.status = 'playing';
      engine.gameState.waveActive = true;
      engine.isPaused = false;
      goModal.classList.add('hidden');
      const bs = document.getElementById('btn-start');
      if (bs) bs.style.display = 'none';
      try { if (typeof playThemeMusic === 'function') playThemeMusic(); } catch (e) {}
    });
  }
})();
window.addEventListener('mousemove', resetIdleTimer);
window.addEventListener('mousedown', resetIdleTimer);
window.addEventListener('touchstart', resetIdleTimer, { passive: true });
window.addEventListener('touchmove', resetIdleTimer, { passive: true });
window.addEventListener('keydown', resetIdleTimer);
window.addEventListener('wheel', resetIdleTimer, { passive: true });

// --- Android Native Back-Gesture & Back Button Interceptor ---
(function() {
  // Push initial dummy state onto history stack so there is always a state to "go back" from
  window.history.pushState({ app: "parade-td-android-gesture" }, "");

  let isPoppingState = false;

  window.addEventListener('popstate', (e) => {
    // Re-push immediately so we ALWAYS have a history buffer to intercept subsequent swiping gestures!
    window.history.pushState({ app: "parade-td-android-gesture" }, "");

    if (isPoppingState) return;
    isPoppingState = true;
    setTimeout(() => { isPoppingState = false; }, 80);

    let intercepted = false;

    // 1. Check all standard modal overlays
    const modalIds = ['map-select-modal', 'course-library-modal', 'campaign-modal',
      'settings-modal', 'auth-modal', 'leaderboard-modal', 'saves-modal', 'admin-modal',
      'confirm-modal', 'alert-modal', 'tutorial-modal'];
    
    for (const id of modalIds) {
      const el = document.getElementById(id);
      if (el && !el.classList.contains('hidden')) {
        el.classList.add('hidden');
        intercepted = true;
      }
    }

    // 2. Check hamburger menu/game-controls
    const gc = document.getElementById('game-controls');
    if (gc && gc.classList.contains('show-menu')) {
      gc.classList.remove('show-menu');
      intercepted = true;
    }

    // 3. Check Parade Panic Radio music panel
    const musicPanel = document.getElementById('pp-music-panel');
    if (musicPanel && musicPanel.style.display !== 'none' && !musicPanel.classList.contains('hidden')) {
      if (window.paradeMusicUI && window.paradeMusicUI.setOpen) {
        window.paradeMusicUI.setOpen(false);
        intercepted = true;
      }
    }

    // 4. Check Tower Upgrades Info Panel
    const infoPanel = document.getElementById('tower-info-panel');
    if (infoPanel && !infoPanel.classList.contains('hidden')) {
      if (window.closeTowerInfoPanel) {
        window.closeTowerInfoPanel();
        intercepted = true;
      }
    }

    // 5. Check active tower dragging state (cancel tower placement)
    if (draggingTowerType) {
      draggingTowerType = null;
      dragPosLogical = null;
      document.querySelectorAll('.tower-btn').forEach(b => b.classList.remove('selected'));
      intercepted = true;
    }

    // 6. If absolutely no overlay was open, trigger the pause button to pause the active game!
    if (!intercepted) {
      const btnPause = document.getElementById('btn-pause');
      if (btnPause && !btnPause.classList.contains('hidden')) {
        btnPause.click(); // Trigger native click which handles visual pause state, music, and sound syncing!
      }
    }
  });
})();

// --- Interactive How-To Play Tutorial System ---
(function() {
  const tutorialSlides = [
    {
      title: `Welcome to Parade Panic! ${GameIcons.get('balloon', 16)}`,
      html: `
        <div style="text-align: center; padding: 5px 0;">
          <p style="font-size: 15px; color: #cbd5e1; line-height: 1.5; margin-bottom: 12px;">
            It's parade day &mdash; and it's under attack! Friendly <strong style="color:#4ade80;">parade floats</strong> roll down the avenue while runaway giant <strong style="color:#f87171;">balloons</strong> swarm in and pelt them with bombs. Your job is to <strong>save the floats</strong>.
          </p>
          
          <!-- Interactive Mock Game Scene with Live Game Sprites & Backgrounds -->
          <div style="height: 145px; display: flex; align-items: center; justify-content: center; position: relative; overflow: hidden; background: radial-gradient(circle at center, #1e293b, #0f172a); border-radius: 12px; border: 1px solid rgba(255,255,255,0.08); margin-bottom: 12px; box-shadow: inset 0 0 20px rgba(0,0,0,0.6);">
            <!-- Simulated isometric background grid -->
            <div style="position: absolute; inset: -50px; background-image: linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px); background-size: 30px 30px; transform: rotateX(60deg) rotateZ(-45deg); opacity: 0.5;"></div>
            
            <!-- Simulated road -->
            <div style="position: absolute; width: 450px; height: 45px; background: #334155; transform: rotate(-25deg) translateY(12px); box-shadow: 0 4px 10px rgba(0,0,0,0.3); border-top: 2px solid #475569; border-bottom: 2px solid #475569; display: flex; align-items: center; justify-content: center;">
              <!-- Yellow dashed center line -->
              <div style="width: 100%; height: 2px; border-top: 2px dashed #eab308; opacity: 0.6;"></div>
            </div>
            
            <!-- Sidewalk (Towers area) -->
            <div style="position: absolute; width: 450px; height: 35px; background: #1e293b; transform: rotate(-25deg) translateY(-28px); border-bottom: 2px solid #0f172a; display: flex; align-items: center;">
              <!-- Sidewalk joint lines -->
              <div style="position: absolute; left: 20%; top: 0; bottom: 0; width: 1px; background: rgba(255,255,255,0.1);"></div>
              <div style="position: absolute; left: 40%; top: 0; bottom: 0; width: 1px; background: rgba(255,255,255,0.1);"></div>
              <div style="position: absolute; left: 60%; top: 0; bottom: 0; width: 1px; background: rgba(255,255,255,0.1);"></div>
              <div style="position: absolute; left: 80%; top: 0; bottom: 0; width: 1px; background: rgba(255,255,255,0.1);"></div>
            </div>
            
            <!-- Confetti Popper (Tower) -->
            <div style="position: absolute; left: 22%; top: 20%; z-index: 5; text-align: center; display: flex; flex-direction: column; align-items: center;">
              <img src="sprites/towers/confetti_cannon/lvl_1.png" style="width: 48px; height: 48px; filter: drop-shadow(0 6px 12px rgba(0,0,0,0.5)); transform: scaleX(-1);">
              <span style="font-size: 10px; color: #ffd93d; font-weight: bold; background: rgba(0,0,0,0.7); padding: 1px 4px; border-radius: 4px; border: 1px solid rgba(255,217,61,0.2); margin-top: -6px;"> Popper </span>
            </div>

            <!-- News Chopper hover shadow and chopper -->
            <div style="position: absolute; right: 18%; top: -12%; z-index: 10; animation: tutorialFloat 4s ease-in-out infinite;">
              <img src="sprites/towers/news_chopper/lvl_1.png" style="width: 56px; height: 56px; filter: drop-shadow(0 15px 8px rgba(0,0,0,0.4));">
            </div>

            <!-- Target Laser Connection -->
            <svg style="position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; z-index: 8;">
              <line x1="33%" y1="52%" x2="52%" y2="55%" stroke="#38bdf8" stroke-width="2.5" stroke-dasharray="4,4" style="animation: dash 1s linear infinite; filter: drop-shadow(0 0 5px #38bdf8);" />
              <line x1="33%" y1="52%" x2="52%" y2="55%" stroke="#fff" stroke-width="1" opacity="0.9" />
            </svg>
            
            <!-- Bouncing Red Balloon -->
            <div style="position: absolute; left: 47%; top: 18%; z-index: 6; animation: tutorialFloat 3s ease-in-out infinite;">
              <img src="sprites/enemies/hero_balloon_v5/idle_01.png" style="width: 56px; height: 56px; filter: drop-shadow(0 10px 12px rgba(239, 68, 68, 0.45));">
              <!-- Targeted Ring -->
              <div style="position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%) rotateX(60deg); width: 56px; height: 56px; border: 2.5px solid #38bdf8; border-radius: 50%; box-shadow: 0 0 10px #38bdf8; opacity: 0.9; animation: targetPulse 1.5s infinite;"></div>
            </div>

            <!-- Friendly parade float — a small platform cart rolling ON the road (the
            thing you protect). Small + grounded (no hover) to match real gameplay,
            where floats trundle along the route and the balloons are the big hovering
            threats above them. -->
            <div style="position: absolute; left: 41%; top: 50%; z-index: 7; text-align: center; display: flex; flex-direction: column; align-items: center;">
              <img src="sprites/environment/floats/star.png" style="width: 44px; height: 44px; object-fit: contain; filter: drop-shadow(0 4px 5px rgba(0,0,0,0.6));">
              <span style="font-size: 9.5px; color: #4ade80; font-weight: bold; background: rgba(0,0,0,0.72); padding: 1px 6px; border-radius: 4px; border: 1px solid rgba(74,222,128,0.4); margin-top: -1px; white-space: nowrap;">FLOAT — protect it!</span>
            </div>
          </div>

          <p style="font-size: 14.5px; color: #cbd5e1; line-height: 1.5;">
            The <strong style="color:#4ade80;">floats are not enemies</strong> &mdash; they're what you're protecting. Pop the balloons with your sidewalk defenses before they can bomb the floats. Every float you guide safely through the route rewards you with <strong>money and lives</strong> &mdash; but if your floats are destroyed, the parade is over!
          </p>
        </div>
      `
    },
    {
      title: `Sidewalk Defense Arsenal (Towers) ${GameIcons.get('tools', 16)}`,
      html: () => {
        const popper = getTowerCfg(window.engine, 'confetti_cannon');
        const sweeper = getTowerCfg(window.engine, 'street_sweeper');
        const band = getTowerCfg(window.engine, 'marching_band');
        const chopper = getTowerCfg(window.engine, 'news_chopper');
        const blimp = getTowerCfg(window.engine, 'blimp');
        const plane = getTowerCfg(window.engine, 'plane');
        const kickettes = getTowerCfg(window.engine, 'kickettes');
        const spotlight = getTowerCfg(window.engine, 'spotlight');
        const fireboat = getTowerCfg(window.engine, 'fireboat');
        const billboard = getTowerCfg(window.engine, 'billboard');

        return `
        <div style="overflow-y: auto; max-height: 340px; padding-right: 5px;">
          <p style="font-size: 15px; color: #cbd5e1; line-height: 1.5; margin-bottom: 12px;">
            Place defenses along the sidewalks to pop runaway balloons. All deployment costs dynamically reflect active admin adjustments:
          </p>
          
          <div style="display: flex; flex-direction: column; gap: 8px; margin-bottom: 10px;">
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.04); border-radius: 6px; border: 1px solid rgba(255,255,255,0.06); flex-shrink: 0;">
                <img src="sprites/towers/confetti_cannon/lvl_1.png" style="width: 32px; height: 32px; object-fit: contain; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <strong style="color: #ffd93d; font-size: 14px;">Confetti Popper:</strong>
                  <span style="font-size: 13px; background: rgba(255,217,61,0.12); padding: 2px 6px; border-radius: 4px; color: #ffd93d; font-weight: bold; display: flex; align-items: center; gap: 2px;">${PERMIT}${popper.cost}</span>
                </div>
                <div style="font-size: 13px; color: #cbd5e1; margin-top: 2px;">Cheap, fast single-target projectile launcher. Great baseline defense. Range: ${popper.range}px.</div>
              </div>
            </div>
            
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.04); border-radius: 6px; border: 1px solid rgba(255,255,255,0.06); flex-shrink: 0;">
                <img src="sprites/towers/street_sweeper/lvl_1.png" style="width: 32px; height: 32px; object-fit: contain; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <strong style="color: #ffd93d; font-size: 14px;">Street Sweeper:</strong>
                  <span style="font-size: 13px; background: rgba(255,217,61,0.12); padding: 2px 6px; border-radius: 4px; color: #ffd93d; font-weight: bold; display: flex; align-items: center; gap: 2px;">${PERMIT}${sweeper.cost}</span>
                </div>
                <div style="font-size: 13px; color: #cbd5e1; margin-top: 2px;">Mobile ground unit. Deals heavy damage on roads. Can be relocated to any intersection! Range: ${sweeper.range}px.</div>
              </div>
            </div>
            
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.04); border-radius: 6px; border: 1px solid rgba(255,255,255,0.06); flex-shrink: 0;">
                <img src="sprites/towers/marching_band/lvl_1.png" style="width: 32px; height: 32px; object-fit: contain; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <strong style="color: #ffd93d; font-size: 14px;">Marching Band:</strong>
                  <span style="font-size: 13px; background: rgba(255,217,61,0.12); padding: 2px 6px; border-radius: 4px; color: #ffd93d; font-weight: bold; display: flex; align-items: center; gap: 2px;">${PERMIT}${band.cost}</span>
                </div>
                <div style="font-size: 13px; color: #cbd5e1; margin-top: 2px;">Plays upbeat music that inspires nearby defenses, boosting their fire rates. Support Range: ${band.range}px.</div>
              </div>
            </div>
            
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.04); border-radius: 6px; border: 1px solid rgba(255,255,255,0.06); flex-shrink: 0;">
                <img src="sprites/towers/news_chopper/lvl_1.png" style="width: 32px; height: 32px; object-fit: contain; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <strong style="color: #ffd93d; font-size: 14px;">News Chopper:</strong>
                  <span style="font-size: 13px; background: rgba(255,217,61,0.12); padding: 2px 6px; border-radius: 4px; color: #ffd93d; font-weight: bold; display: flex; align-items: center; gap: 2px;">${PERMIT}${chopper.cost}</span>
                </div>
                <div style="font-size: 13px; color: #cbd5e1; margin-top: 2px;">Air-patrol unit. Hovers over target zones and drops film reels for area damage. Radar Range: ${chopper.range}px.</div>
              </div>
            </div>

            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.04); border-radius: 6px; border: 1px solid rgba(255,255,255,0.06); flex-shrink: 0;">
                <img src="sprites/towers/blimp/blimp1_se.png" style="width: 34px; height: 34px; object-fit: contain; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <strong style="color: #ffd93d; font-size: 14px;">Blimp:</strong>
                  <span style="font-size: 13px; background: rgba(255,217,61,0.12); padding: 2px 6px; border-radius: 4px; color: #ffd93d; font-weight: bold; display: flex; align-items: center; gap: 2px;">${PERMIT}${blimp.cost}</span>
                </div>
                <div style="font-size: 13px; color: #cbd5e1; margin-top: 2px;">Aerial crowd-control airship. Drops advertising leaflets that <strong>glue</strong> enemy balloons — L1 slows them &amp; weakens their bombs, L2 freezes them, L3 sends them wandering off the route.</div>
              </div>
            </div>

            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.04); border-radius: 6px; border: 1px solid rgba(255,255,255,0.06); flex-shrink: 0;">
                <img src="sprites/towers/plane/plane1_e.png" style="width: 34px; height: 34px; object-fit: contain; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <strong style="color: #ffd93d; font-size: 14px;">Banner Plane:</strong>
                  <span style="font-size: 13px; background: rgba(255,217,61,0.12); padding: 2px 6px; border-radius: 4px; color: #ffd93d; font-weight: bold; display: flex; align-items: center; gap: 2px;">${PERMIT}${plane.cost}</span>
                </div>
                <div style="font-size: 13px; color: #cbd5e1; margin-top: 2px;">Morale squadron. L1 skywrites "SURRENDER!" (enemy attacks −10% all level), L2 tows a "NEVER SURRENDER!" banner (your floats +10% armor), L3 sends jet formations that vaporize the strongest balloon on a cadence.</div>
              </div>
            </div>

            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.04); border-radius: 6px; border: 1px solid rgba(255,255,255,0.06); flex-shrink: 0;">
                <img src="sprites/towers/kickettes/Kickette_icon.png" style="width: 32px; height: 32px; object-fit: contain; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <strong style="color: #ffd93d; font-size: 14px;">The Kickettes:</strong>
                  <span style="font-size: 13px; background: rgba(255,217,61,0.12); padding: 2px 6px; border-radius: 4px; color: #ffd93d; font-weight: bold; display: flex; align-items: center; gap: 2px;">${PERMIT}${kickettes.cost}</span>
                </div>
                <div style="font-size: 13px; color: #cbd5e1; margin-top: 2px;">Crowd control line-dancers. Deals no damage, but kicks balloons back down the path! Range: ${kickettes.range}px.</div>
              </div>
            </div>
            
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.04); border-radius: 6px; border: 1px solid rgba(255,255,255,0.06); flex-shrink: 0;">
                <img src="sprites/towers/spotlight/spotlight_idle_01.png" style="width: 32px; height: 32px; object-fit: contain; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <strong style="color: #ffd93d; font-size: 14px;">Spotlight:</strong>
                  <span style="font-size: 13px; background: rgba(255,217,61,0.12); padding: 2px 6px; border-radius: 4px; color: #ffd93d; font-weight: bold; display: flex; align-items: center; gap: 2px;">${PERMIT}${spotlight.cost}</span>
                </div>
                <div style="font-size: 13px; color: #cbd5e1; margin-top: 2px;">Concentrated, persistent high-intensity thermal laser. Burns boss targets. Never misses. Range: ${spotlight.range}px.</div>
              </div>
            </div>

            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(56,189,248,0.18); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.04); border-radius: 6px; border: 1px solid rgba(255,255,255,0.06); flex-shrink: 0;">
                <img src="sprites/towers/fireboat/se.png" style="width: 32px; height: 32px; object-fit: contain; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <strong style="color: #ffd93d; font-size: 14px;">Fireboat:</strong>
                  <span style="font-size: 13px; background: rgba(255,217,61,0.12); padding: 2px 6px; border-radius: 4px; color: #ffd93d; font-weight: bold; display: flex; align-items: center; gap: 2px;">${PERMIT}${fireboat.cost}</span>
                </div>
                <div style="font-size: 13px; color: #cbd5e1; margin-top: 2px;"><strong style="color:#38bdf8;">Water only</strong> — deploys on rivers, lakes &amp; coastlines. Blasts a water cannon that splashes the target and nearby balloons. Range: ${fireboat.range}px.</div>
              </div>
            </div>

            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.04); border-radius: 6px; border: 1px solid rgba(255,255,255,0.06); flex-shrink: 0;">
                <img src="sprites/towers/billboards/tier_1_1.png" style="width: 32px; height: 32px; object-fit: contain; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <strong style="color: #ffd93d; font-size: 14px;">Billboard:</strong>
                  <span style="font-size: 13px; background: rgba(255,217,61,0.12); padding: 2px 6px; border-radius: 4px; color: #ffd93d; font-weight: bold; display: flex; align-items: center; gap: 2px;">${PERMIT}${billboard.cost}</span>
                </div>
                <div style="font-size: 13px; color: #cbd5e1; margin-top: 2px;">Generates wave-end and wave-time passive cash, and triggers temporary fire-rate buffs! Range: ${billboard.range}px.</div>
              </div>
            </div>
          </div>
        </div>
        `;
      }
    },
    {
      title: `Runaway Float Directory (Balloons) ${GameIcons.get('balloon', 16)}`,
      html: () => {
        const b1 = getBalloonCfg(window.engine, 'hero_balloon');
        const b2 = getBalloonCfg(window.engine, 'felix');
        const b3 = getBalloonCfg(window.engine, 'detective_dog');
        const b4 = getBalloonCfg(window.engine, 'speedy_turkey');
        const b5 = getBalloonCfg(window.engine, 'dragon');
        const b6 = getBalloonCfg(window.engine, 'electric_fox');
        const b7 = getBalloonCfg(window.engine, 'happy_green_oaf');

        return `
        <div style="overflow-y: auto; max-height: 340px; padding-right: 5px;">
          <p style="font-size: 15px; color: #cbd5e1; line-height: 1.5; margin-bottom: 12px;">
            The runaway parade has several distinct balloon types. Stats below are live-bound and hot-loaded from active balances:
          </p>
          
          <div style="display: flex; flex-direction: column; gap: 8px; margin-bottom: 10px;">
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.04); border-radius: 6px; border: 1px solid rgba(255,255,255,0.06); flex-shrink: 0;">
                <img src="sprites/enemies/hero_balloon_v5/idle_01.png" style="width: 32px; height: 32px; object-fit: contain; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;"><strong style="color: #ff5e57; font-size: 14px;">Hero Balloon:</strong> Red classic. Health: ${b1.maxHealth} HP, Speed: ${b1.speed} px/s, Pop Reward: +${b1.bounty} Permits. Standard target.</div>
            </div>
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.04); border-radius: 6px; border: 1px solid rgba(255,255,255,0.06); flex-shrink: 0;">
                <img src="sprites/enemies/felix/idle_01.png" style="width: 32px; height: 32px; object-fit: contain; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;"><strong style="color: #54a0ff; font-size: 14px;">Felix Cat:</strong> Slick black cat balloon. Health: ${b2.maxHealth} HP, Speed: ${b2.speed} px/s, Pop Reward: +${b2.bounty} Permits. Slides on curves efficiently.</div>
            </div>
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.04); border-radius: 6px; border: 1px solid rgba(255,255,255,0.06); flex-shrink: 0;">
                <img src="sprites/enemies/detective_dog/idle_01.png" style="width: 32px; height: 32px; object-fit: contain; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;"><strong style="color: #ffd2af; font-size: 14px;">Detective Dog:</strong> Heavy-duty balloon dog. Health: ${b3.maxHealth} HP, Speed: ${b3.speed} px/s, Pop Reward: +${b3.bounty} Permits. Slow but absorbs massive damage.</div>
            </div>
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.04); border-radius: 6px; border: 1px solid rgba(255,255,255,0.06); flex-shrink: 0;">
                <img src="sprites/enemies/speedy_turkey/idle_01.png" style="width: 32px; height: 32px; object-fit: contain; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;"><strong style="color: #ff9f43; font-size: 14px;">Speedy Turkey:</strong> Feathered sprinter! Health: ${b4.maxHealth} HP, Speed: ${b4.speed} px/s, Pop Reward: +${b4.bounty} Permits. Extreme speed! Pop quickly.</div>
            </div>
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.04); border-radius: 6px; border: 1px solid rgba(255,255,255,0.06); flex-shrink: 0;">
                <img src="sprites/enemies/dragon/idle_01.png" style="width: 32px; height: 32px; object-fit: contain; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;"><strong style="color: #a55eea; font-size: 14px;">Dragon Balloon:</strong> Colossal dragon. Health: ${b5.maxHealth} HP, Speed: ${b5.speed} px/s, Pop Reward: +${b5.bounty} Permits. Takes ${b5.livesCost} lives if it breaks past the exit!</div>
            </div>
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.04); border-radius: 6px; border: 1px solid rgba(255,255,255,0.06); flex-shrink: 0;">
                <img src="sprites/enemies/electric_fox/idle_01.png" style="width: 32px; height: 32px; object-fit: contain; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;"><strong style="color: #ff7675; font-size: 14px;">Electric Fox:</strong> Level Boss! Health: ${b6.maxHealth} HP, Speed: ${b6.speed} px/s, Pop Reward: +${b6.bounty} Permits. Epic boss unit. Takes ${b6.livesCost} lives if it escapes!</div>
            </div>
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(120,220,90,0.25); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.04); border-radius: 6px; border: 1px solid rgba(255,255,255,0.06); flex-shrink: 0;">
                <img src="sprites/enemies/happy_green_oaf/idle_01.png" style="width: 34px; height: 34px; object-fit: contain; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;"><strong style="color: #7ed957; font-size: 14px;">Happy Green Oaf:</strong> <strong style="color:#7ed957;">BOSS</strong> — a giant leafy bruiser. Health: ${b7.maxHealth} HP, Speed: ${b7.speed} px/s, Pop Reward: +${b7.bounty} Permits. STOMPS and pelts your floats with a veggie volley (corn cobs, pea pods &amp; broccoli). Takes ${b7.livesCost} lives if it stomps past the exit!</div>
            </div>
          </div>
        </div>
        `;
      }
    },
    {
      title: `Persistent Economy & Currencies ${GameIcons.get('dollars', 16)}${GameIcons.get('permits', 16)}`,
      html: () => {
        return `
        <div style="overflow-y: auto; max-height: 340px; padding-right: 5px;">
          <p style="font-size: 15px; color: #cbd5e1; line-height: 1.5; margin-bottom: 12px;">
            Manage two distinct, dynamically live-bound currencies inside Parade Panic:
          </p>
          
          <div style="display: flex; flex-direction: column; gap: 10px; margin-bottom: 12px;">
            <div style="display: flex; gap: 12px; background: rgba(255, 217, 61, 0.03); border: 1px solid rgba(255, 217, 61, 0.12); padding: 10px; border-radius: 8px; align-items: center; box-shadow: 0 4px 6px rgba(0,0,0,0.15);">
              <span style="flex-shrink: 0; width: 44px; height: 44px; background: rgba(255, 217, 61, 0.1); border-radius: 8px; border: 1px solid rgba(255, 217, 61, 0.2); display: flex; align-items: center; justify-content: center; filter: drop-shadow(0 0 5px rgba(255,217,61,0.25));">${GameIcons.get('permits', 24)}</span>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;">
                <div style="font-weight: bold; color: #ffd93d; font-size: 15px;">Permits (In-Match Currency)</div>
                <div style="font-size: 13px; color: #cbd5e1; line-height: 1.4; margin-top: 3px;">Earned by popping balloons during a level. Spent inside the active match to deploy and upgrade defenses. This wallet resets at the end of each city district.</div>
              </div>
            </div>
            
            <div style="display: flex; gap: 12px; background: rgba(56, 189, 248, 0.03); border: 1px solid rgba(56, 189, 248, 0.12); padding: 10px; border-radius: 8px; align-items: center; box-shadow: 0 4px 6px rgba(0,0,0,0.15);">
              <span style="flex-shrink: 0; width: 44px; height: 44px; background: rgba(56, 189, 248, 0.1); border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.2); display: flex; align-items: center; justify-content: center; filter: drop-shadow(0 0 5px rgba(56,189,248,0.25));">${GameIcons.get('dollars', 24)}</span>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;">
                <div style="font-weight: bold; color: #38bdf8; font-size: 15px;">Dollars Cash (Persistent Wallet)</div>
                <div style="font-size: 13px; color: #cbd5e1; line-height: 1.4; margin-top: 3px;">Earned from wave completion and passive Billboard ad revenue. This balance persists between levels and profile sessions! Spent in the Shop for powerful, wave-clearing spells.</div>
              </div>
            </div>
          </div>
          
          <p style="font-size: 14px; color: #94a3b8; text-align: center; margin-top: 15px;">
            ${GameIcons.get('warning', 14, 'margin-right:2px;')} <strong>Pro Tip:</strong> Build <strong>Billboards</strong> early in a match to maximize your Dollar Cash yield for spelling!
          </p>
        </div>
        `;
      }
    },
    {
      title: `Active Power-Up Weather Spells ${GameIcons.get('lightning', 16)}${GameIcons.get('wind', 16)}`,
      html: () => {
        const lightning = getTowerCfg(window.engine, 'lightning_spell');
        const wind = getTowerCfg(window.engine, 'wind_spell');

        return `
        <div style="overflow-y: auto; max-height: 340px; padding-right: 5px;">
          <p style="font-size: 15px; color: #cbd5e1; line-height: 1.5; margin-bottom: 12px; text-align: center;">
            Turn the tide of a runaway wave instantly! Power-up spells are purchased with persistent Dollars (💵) in the Shop and cast during active combat.
          </p>
          
          <div style="display: flex; flex-direction: column; gap: 10px; margin-bottom: 10px;">
            
            <!-- Lightning Spell Card -->
            <div style="background: rgba(168, 85, 247, 0.06); border: 1px solid rgba(168, 85, 247, 0.15); padding: 10px; border-radius: 8px; display: flex; gap: 12px; align-items: center; box-shadow: 0 4px 6px rgba(0,0,0,0.15);">
              <div style="width: 48px; height: 48px; background: rgba(168, 85, 247, 0.12); border-radius: 8px; border: 1px solid rgba(168, 85, 247, 0.25); display: flex; align-items: center; justify-content: center; flex-shrink: 0; filter: drop-shadow(0 0 4px rgba(168, 85, 247, 0.4));">
                <img src="sprites/effects/projectiles/lightning.png" style="width: 38px; height: 38px; object-fit: contain;">
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <strong style="color: #c084fc; font-size: 15px;">${GameIcons.get('lightning', 15, 'margin-right:2px;')} Lightning Strike:</strong>
                  <span style="font-size: 10px; background: rgba(168,85,247,0.18); padding: 2px 6px; border-radius: 4px; color: #c084fc; font-weight: bold; border: 1px solid rgba(168,85,247,0.15);">$${(lightning.cost || 2000).toLocaleString()}</span>
                </div>
                <div style="font-size: 13px; color: #cbd5e1; margin-top: 3px; line-height: 1.4;">
                  Strikes up to <strong>6 random balloons</strong> instantly on the course with raw high-voltage electricity, dealing massive instant damage. Perfect for saving a failing lane! (Level Cap: ${lightning.levelCap || 1})
                </div>
              </div>
            </div>

            <!-- Wind Spell Card -->
            <div style="background: rgba(14, 165, 233, 0.06); border: 1px solid rgba(14, 165, 233, 0.15); padding: 10px; border-radius: 8px; display: flex; gap: 12px; align-items: center; box-shadow: 0 4px 6px rgba(0,0,0,0.15);">
              <div style="width: 48px; height: 48px; background: rgba(14, 165, 233, 0.12); border-radius: 8px; border: 1px solid rgba(14, 165, 233, 0.25); display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                ${GameIcons.get('wind', 26)}
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <strong style="color: #38bdf8; font-size: 15px;">${GameIcons.get('wind', 15, 'margin-right:2px;')} Wind Gust:</strong>
                  <span style="font-size: 10px; background: rgba(14,165,233,0.18); padding: 2px 6px; border-radius: 4px; color: #38bdf8; font-weight: bold; border: 1px solid rgba(14,165,233,0.15);">$${(wind.cost || 2000).toLocaleString()}</span>
                </div>
                <div style="font-size: 13px; color: #cbd5e1; margin-top: 3px; line-height: 1.4;">
                  Blows up to <strong>4 balloons backward</strong> down the path, and covers the entire city block in strong headwinds, **slowing all active balloons** on the screen by 60% for 6 seconds. (Level Cap: ${wind.levelCap || 1})
                </div>
              </div>
            </div>

            <!-- Float Armor Card -->
            <div style="background: rgba(255, 215, 0, 0.06); border: 1px solid rgba(255, 215, 0, 0.2); padding: 10px; border-radius: 8px; display: flex; gap: 12px; align-items: center; box-shadow: 0 4px 6px rgba(0,0,0,0.15);">
              <div style="width: 48px; height: 48px; background: rgba(255, 215, 0, 0.12); border-radius: 8px; border: 1px solid rgba(255, 215, 0, 0.3); display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                ${GameIcons.get('shield', 26)}
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <strong style="color: #ffd700; font-size: 15px;">${GameIcons.get('shield', 15, 'margin-right:2px;')} Float Armor:</strong>
                  <span style="font-size: 10px; background: rgba(255,215,0,0.18); padding: 2px 6px; border-radius: 4px; color: #ffd700; font-weight: bold; border: 1px solid rgba(255,215,0,0.25);">$2,500</span>
                </div>
                <div style="font-size: 13px; color: #cbd5e1; margin-top: 3px; line-height: 1.4;">
                  Surrounds all active parade floats in an impenetrable <strong>15-second golden shield</strong> with repulsor pulse waves. Floats take 0 damage from balloon attacks for the full duration!
                </div>
              </div>
            </div>
            
          </div>
          
          <p style="font-size: 13px; color: #94a3b8; font-style: italic; text-align: center; margin-top: 8px;">
            ${GameIcons.get('warning', 14, 'margin-right:2px;')} Note: Spells are active items and can only be cast while a wave is actively rolling!
          </p>
        </div>
        `;
      }
    },
    {
      title: `Earning Your Stars ${GameIcons.get('stars', 16)}`,
      html: () => {
        return `
        <div style="overflow-y: auto; max-height: 340px; padding-right: 5px;">
          <p style="font-size: 15px; color: #cbd5e1; line-height: 1.5; margin-bottom: 12px; text-align: center;">
            Your tactical mastery is evaluated on a premium <strong>3-Star Rating system</strong> at the end of each victorious match!
          </p>
          
          <div style="display: flex; flex-direction: column; gap: 10px; margin-bottom: 10px;">
            
            <!-- Three Star Card (The Pinnacle) -->
            <div style="background: rgba(234, 179, 8, 0.05); border: 1px solid rgba(234, 179, 8, 0.18); padding: 10px; border-radius: 8px; display: flex; gap: 12px; align-items: center; box-shadow: 0 4px 6px rgba(0,0,0,0.15);">
              <div style="width: 44px; height: 44px; background: rgba(234, 179, 8, 0.1); border-radius: 8px; border: 1px solid rgba(234, 179, 8, 0.25); display: flex; align-items: center; justify-content: center; flex-shrink: 0; filter: drop-shadow(0 0 6px rgba(234,179,8,0.45));">
                ${GameIcons.get('stars', 18)}${GameIcons.get('stars', 18)}${GameIcons.get('stars', 18)}
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;">
                <strong style="color: #facc15; font-size: 15px;">3 Stars — Flawless & Dominant</strong>
                <div style="font-size: 13px; color: #cbd5e1; margin-top: 3px; line-height: 1.4;">
                  Awarded for finishing with **20 lives or above** AND **economic efficiency** (retaining at least your snapshot starting Permits). 
                  <br><span style="color: #a78bfa; font-weight: bold;">Gated to Hard+ difficulty</span> (Campaign levels are exempt!).
                </div>
              </div>
            </div>

            <!-- Two Star Card (High Performance) -->
            <div style="background: rgba(226, 232, 240, 0.05); border: 1px solid rgba(226, 232, 240, 0.15); padding: 10px; border-radius: 8px; display: flex; gap: 12px; align-items: center; box-shadow: 0 4px 6px rgba(0,0,0,0.15);">
              <div style="width: 44px; height: 44px; background: rgba(226, 232, 240, 0.08); border-radius: 8px; border: 1px solid rgba(226, 232, 240, 0.2); display: flex; align-items: center; justify-content: center; flex-shrink: 0; filter: drop-shadow(0 0 4px rgba(255,255,255,0.1));">
                ${GameIcons.get('stars', 16)}${GameIcons.get('stars', 16)}<span style="opacity:0.3; filter:grayscale(1);">${GameIcons.get('stars', 16)}</span>
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;">
                <strong style="color: #cbd5e1; font-size: 15px;">2 Stars — Accomplished Defender</strong>
                <div style="font-size: 13px; color: #cbd5e1; margin-top: 3px; line-height: 1.4;">
                  Awarded for finishing with **10 to 19 lives remaining**, OR if you completed the map with 20+ lives remaining but allowed your final match economy to run low.
                </div>
              </div>
            </div>

            <!-- One Star Card (Victory) -->
            <div style="background: rgba(185, 28, 28, 0.04); border: 1px solid rgba(185, 28, 28, 0.15); padding: 10px; border-radius: 8px; display: flex; gap: 12px; align-items: center; box-shadow: 0 4px 6px rgba(0,0,0,0.15);">
              <div style="width: 44px; height: 44px; background: rgba(185, 28, 28, 0.08); border-radius: 8px; border: 1px solid rgba(185, 28, 28, 0.2); display: flex; align-items: center; justify-content: center; flex-shrink: 0; filter: drop-shadow(0 0 4px rgba(239,68,68,0.15));">
                ${GameIcons.get('stars', 14)}<span style="opacity:0.3; filter:grayscale(1);">${GameIcons.get('stars', 14)}</span><span style="opacity:0.3; filter:grayscale(1);">${GameIcons.get('stars', 14)}</span>
              </div>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;">
                <strong style="color: #f87171; font-size: 15px;">1 Star — Clear Victory</strong>
                <div style="font-size: 13px; color: #cbd5e1; margin-top: 3px; line-height: 1.4;">
                  Awarded for successfully surviving all waves of the map and finishing with **1 to 9 lives remaining**. You got the job done and secured the zone!
                </div>
              </div>
            </div>
            
          </div>
          
          <div style="background: rgba(15, 23, 42, 0.4); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; font-size: 13px; color: #94a3b8; line-height: 1.4; margin-top: 8px; text-align: center;">
            ${GameIcons.get('warning', 13, 'margin-right:2px;')} <strong>Pro Tip:</strong> Extra lives you buy in the pre-wave shop carry all the way to the finish — stocking up is a legit way to reach the 20-life mark for that 3-star run on a tough map!
          </div>
        </div>
        `;
      }
    },
    {
      title: `Social Hub, Music & Profile Sync ${GameIcons.get('music', 16)}${GameIcons.get('trophy', 16)}`,
      html: `
        <div style="overflow-y: auto; max-height: 340px; padding-right: 5px;">
          <p style="font-size: 15px; color: #cbd5e1; line-height: 1.5; margin-bottom: 12px;">
            Create an account to unlock competitive community features and persist progress:
          </p>
          
          <div style="display: flex; flex-direction: column; gap: 8px; margin-bottom: 12px;">
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <span style="flex-shrink: 0; width: 30px; display: flex; justify-content: center; align-items: center;">${GameIcons.get('profile', 18)}</span>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;"><strong style="color: #f472b6; font-size: 14px;">Cloud Profile Sync:</strong> Syncs your sound settings, persistent dollars, high scores, and campaign progress securely on our database.</div>
            </div>
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <span style="flex-shrink: 0; width: 30px; display: flex; justify-content: center; align-items: center;">${GameIcons.get('trophy', 18)}</span>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;"><strong style="color: #38bdf8; font-size: 14px;">Global Leaderboard:</strong> Compete on high scores with other players. Aim for the highest rank on the global arena!</div>
            </div>
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <span style="flex-shrink: 0; width: 30px; display: flex; justify-content: center; align-items: center;">${GameIcons.get('medal', 18)}</span>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;"><strong style="color: #4ade80; font-size: 14px;">Achievements Badges:</strong> Earn achievements as you complete specific in-match accomplishments (Gold Medals, cash thresholds).</div>
            </div>
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <span style="flex-shrink: 0; width: 30px; display: flex; justify-content: center; align-items: center;">${GameIcons.get('music', 18)}</span>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;"><strong style="color: #c084fc; font-size: 14px;">Retro Radio Player:</strong> Fully integrated retro synthwave music player. Adjust individual music and SFX tracks to your liking.</div>
            </div>
          </div>
        </div>
      `
    },
    {
      title: `Tactical Navigation & Cameras ${GameIcons.get('map', 16)}`,
      html: `
        <div style="overflow-y: auto; max-height: 340px; padding-right: 5px;">
          <p style="font-size: 15px; color: #cbd5e1; line-height: 1.5; margin-bottom: 12px;">
            Choose your battle district and control your perspective for absolute battlefield mastery:
          </p>
          
          <div style="display: flex; flex-direction: column; gap: 8px; margin-bottom: 12px;">
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <span style="flex-shrink: 0; width: 30px; display: flex; justify-content: center; align-items: center;">${GameIcons.get('map', 18)}</span>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;"><strong style="color: #60a5fa; font-size: 14px;">Campaign Tour Mode:</strong> Progress sequentially through handcrafted, increasing-difficulty districts of Parade City.</div>
            </div>
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <span style="flex-shrink: 0; width: 30px; display: flex; justify-content: center; align-items: center;">${GameIcons.get('guide', 18)}</span>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;"><strong style="color: #60a5fa; font-size: 14px;">Course Library:</strong> Browse and select pre-computed custom blueprints, complete with detailed tactical radar previews!</div>
            </div>
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <span style="flex-shrink: 0; width: 30px; display: flex; justify-content: center; align-items: center;">${GameIcons.get('unlocked', 18)}</span>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;"><strong style="color: #60a5fa; font-size: 14px;">Random Level Loader:</strong> Generates a completely fresh procedural path on each click! Choose "Expert", "Master" etc.</div>
            </div>
            <div style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); padding: 8px; border-radius: 8px; display: flex; gap: 10px; align-items: center;">
              <span style="flex-shrink: 0; width: 30px; display: flex; justify-content: center; align-items: center;">${GameIcons.get('tools', 18)}</span>
              <div style="flex: 1; font-size: 13px; color: #cbd5e1; line-height: 1.45;">
                <strong style="color: #ffd93d; font-size: 14px;">Advanced Camera Controls:</strong>
                <p style="font-size: 13px; color: #aaa; margin-top: 3px; line-height: 1.4;">
                  • <strong>Orbit / Pan / Zoom:</strong> Use mouse click-and-drag (or finger swiping) to rotate, and mouse wheel to zoom.<br>
                  • <strong>Action Cam:</strong> Locks onto active towers, flying floats, and runaway balloons, automatically cycling cinematic vantage points!
                </p>
              </div>
            </div>
          </div>
        </div>
      `
    }
  ];

  let currentSlideIndex = 0;

  // Append keyframe animations head style
  const styleId = 'tutorial-animations-style';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      @keyframes tutorialFloat {
        0%, 100% { transform: translateY(0px) rotate(0deg); }
        50% { transform: translateY(-10px) rotate(3deg); }
      }
      @keyframes tutorialDrive {
        0%, 100% { left: 5%; transform: scaleX(1); }
        45% { left: 75%; transform: scaleX(1); }
        50% { left: 75%; transform: scaleX(-1); }
        95% { left: 5%; transform: scaleX(-1); }
      }
      @keyframes dash {
        to { stroke-dashoffset: -20; }
      }
      @keyframes targetPulse {
        0% { transform: translate(-50%, -50%) rotateX(60deg) scale(0.8); opacity: 0.4; }
        50% { transform: translate(-50%, -50%) rotateX(60deg) scale(1.1); opacity: 1; }
        100% { transform: translate(-50%, -50%) rotateX(60deg) scale(0.8); opacity: 0.4; }
      }
      .tutorial-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.25);
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        cursor: pointer;
      }
      .tutorial-dot.active {
        width: 24px;
        border-radius: 6px;
        background: #38bdf8;
        box-shadow: 0 0 10px rgba(56, 189, 248, 0.6);
      }
      .tutorial-dot:hover:not(.active) {
        background: rgba(255, 255, 255, 0.5);
        transform: scale(1.25);
      }
    `;
    document.head.appendChild(style);
  }

  function renderSlide(index) {
    const container = document.getElementById('tutorial-slides-container');
    const prevBtn = document.getElementById('btn-tutorial-prev');
    const nextBtn = document.getElementById('btn-tutorial-next');
    const dotsContainer = document.getElementById('tutorial-dots');
    
    if (!container || !prevBtn || !nextBtn || !dotsContainer) return;

    const slide = tutorialSlides[index];
    
    // Update contents
    const htmlContent = typeof slide.html === 'function' ? slide.html() : slide.html;
    container.innerHTML = `
      <div style="animation: fadeIn 0.3s ease;">
        <h3 style="font-size: 16px; margin: 0 0 10px 0; color: #fff; border-bottom: 1px solid rgba(255,255,255,0.06); padding-bottom: 6px;">${slide.title}</h3>
        ${htmlContent}
      </div>
    `;

    // Trigger slice init actions if present
    if (typeof slide.init === 'function') {
      slide.init();
    }

    // Toggle button visibilities/labels
    prevBtn.disabled = index === 0;
    prevBtn.style.opacity = index === 0 ? '0.5' : '1';
    
    if (index === tutorialSlides.length - 1) {
      nextBtn.textContent = "Start Play";
      nextBtn.style.background = 'linear-gradient(135deg, #10b981, #059669)';
      nextBtn.style.boxShadow = '0 0 15px rgba(16, 185, 129, 0.4)';
    } else {
      nextBtn.textContent = "Next";
      nextBtn.style.background = '';
      nextBtn.style.boxShadow = '';
    }

    // Render Dots with index binding
    dotsContainer.innerHTML = tutorialSlides.map((_, i) => 
      `<div class="tutorial-dot ${i === index ? 'active' : ''}" data-index="${i}" title="Go to Slide ${i + 1}"></div>`
    ).join('');
  }

  // Setup event handling.
  //
  // Rewritten to be robust: the OLD version used addEventListener and was invoked
  // twice (DOMContentLoaded + a setTimeout fallback), so every button ended up with
  // duplicate listeners — a single "Next" click fired twice and jumped two slides,
  // which is exactly the flakiness reported. This version (a) wires exactly once via
  // a guard, (b) uses `.onclick =` assignment so even a re-invocation can only ever
  // REPLACE a handler, never stack one, and (c) routes every navigation through a
  // single clamped goToSlide(), so the index can never run out of bounds.
  function initTutorialHandler() {
    const tutorialBtn = document.getElementById('btn-tutorial');
    const closeBtn = document.getElementById('btn-close-tutorial');
    const modal = document.getElementById('tutorial-modal');
    const prevBtn = document.getElementById('btn-tutorial-prev');
    const nextBtn = document.getElementById('btn-tutorial-next');
    const dotsContainer = document.getElementById('tutorial-dots');

    if (!tutorialBtn || !closeBtn || !modal || !prevBtn || !nextBtn || !dotsContainer) return;
    if (initTutorialHandler._wired) return; // wire exactly once
    initTutorialHandler._wired = true;

    const lastIndex = () => tutorialSlides.length - 1;

    // The one and only way slides change: clamp, store, render. No caller touches
    // currentSlideIndex directly, so it can never desync or overflow the array.
    function goToSlide(i) {
      currentSlideIndex = Math.max(0, Math.min(lastIndex(), i | 0));
      renderSlide(currentSlideIndex);
    }

    const openTutorial = () => {
      const gc = document.getElementById('game-controls');
      if (gc) gc.classList.remove('show-menu'); // close hamburger dropdown
      goToSlide(0);
      modal.classList.remove('hidden');
    };
    const closeTutorial = () => modal.classList.add('hidden');

    tutorialBtn.onclick = openTutorial;
    closeBtn.onclick = closeTutorial;

    prevBtn.onclick = () => goToSlide(currentSlideIndex - 1);

    nextBtn.onclick = () => {
      if (currentSlideIndex >= lastIndex()) {
        // On the final slide, "Next" becomes the finish action.
        closeTutorial();
        if (typeof showMusicToaster === 'function') {
          showMusicToaster("🎮 Tutorial Complete", "Go get those balloons! Let the parade roll! 🎉");
        }
      } else {
        goToSlide(currentSlideIndex + 1);
      }
    };

    // Dot navigation via delegation (survives the per-render innerHTML rebuild).
    dotsContainer.onclick = (e) => {
      const dot = e.target.closest('.tutorial-dot');
      if (!dot) return;
      const t = parseInt(dot.getAttribute('data-index'), 10);
      if (!isNaN(t)) goToSlide(t);
    };
  }

  window.addEventListener('DOMContentLoaded', initTutorialHandler);
  // Fallback in case DOMContentLoaded already fired; the _wired guard above makes
  // this second call a safe no-op rather than a source of duplicate handlers.
  setTimeout(initTutorialHandler, 50);
})();

// Kickoff
window.addEventListener('DOMContentLoaded', () => {
  init();
  updateAuthUI();
  resetIdleTimer();
});

// Bind Audit Tab Click and fetch logs
const tabAdminAudit = document.getElementById('tab-admin-audit');
if (tabAdminAudit) {
  tabAdminAudit.addEventListener('click', () => {
    setAdminOverlaySoft(false);
    activateAdminTab('audit');
    loadAdminAuditLogs();
  });
}

async function loadAdminAuditLogs() {
  const container = document.getElementById('admin-audit-logs-body');
  if (!container) return;

  container.innerHTML = '<tr><td colspan="4" style="text-align: center; padding: 20px 0; color: #888;">Fetching latest security audit events...</td></tr>';

  try {
    const logs = await apiFetch('/admin/audit-logs');
    if (!logs || logs.length === 0) {
      container.innerHTML = '<tr><td colspan="4" style="text-align: center; padding: 20px 0; color: #888;">No security events found in audit log.</td></tr>';
      return;
    }

    container.innerHTML = logs.map(log => {
      const dateStr = new Date(log.created_at).toLocaleString();
      let badgeColor = '#8b93a6'; // gray default
      let badgeBg = 'rgba(139, 147, 166, 0.15)';
      
      if (log.action_type === 'PASSWORD_RESET_REQUESTED') {
        badgeColor = '#38bdf8'; // blue info
        badgeBg = 'rgba(56, 189, 248, 0.15)';
      } else if (log.action_type === 'PASSWORD_RESET_COMPLETED') {
        badgeColor = '#4ade80'; // green success
        badgeBg = 'rgba(74, 222, 128, 0.15)';
      }

      return `
        <tr style="border-bottom: 1px solid rgba(255,255,255,0.06);">
          <td style="padding: 10px 4px; white-space: nowrap; color: #8b93a6;">${dateStr}</td>
          <td style="padding: 10px 4px; white-space: nowrap;">
            <span style="color: ${badgeColor}; background: ${badgeBg}; border: 1px solid ${badgeColor}40; padding: 2px 6px; border-radius: 4px; font-weight: bold; font-size: 10px;">${escapeHtml(log.action_type)}</span>
          </td>
          <td style="padding: 10px 4px; color: #eee; line-height: 1.4;">${escapeHtml(log.description)}</td>
          <td style="padding: 10px 4px; color: #8b93a6; font-family: monospace;">${escapeHtml(log.ip_address || 'Unknown')}</td>
        </tr>
      `;
    }).join('');
  } catch (err) {
    console.error('Failed to load audit logs:', err);
    container.innerHTML = `<tr><td colspan="4" style="text-align: center; padding: 20px 0; color: #f87171;">Failed to load logs: ${escapeHtml(String(err.message || err))}</td></tr>`;
  }
}



